import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, Descriptions, Tag, Spin, Button, Tabs, Collapse, Table, Input, InputNumber, Form, Select, Switch, Tooltip, Modal, App, Progress, Upload, Checkbox, Dropdown, Popover, Popconfirm, Alert, Drawer, Radio, Typography, Space } from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  StopOutlined,
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
  MoreOutlined,
  SafetyOutlined,
  LockOutlined,
  FileProtectOutlined,
  FilterOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  DatabaseOutlined,
  FolderOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DownOutlined,
  RadarChartOutlined,
  SyncOutlined,
  FilePdfOutlined,
  LinkOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as API from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  formatReadableProjectId,
  formatUtcInstantForDisplay,
  formatProjectDisplayName,
  formatStatusLabel,
} from '../utils/helpers';
import { isProjectChatAvailable } from '../utils/projectDeployment';
import { colors } from '../theme';
import RetrieverSearchTypeSelect from '../components/RetrieverSearchTypeSelect';
import { FieldLabel } from '../components/FieldTooltip';
import {
  DEFAULT_RETRIEVER_SEARCH_TYPE,
  retrieverSearchTypeLabel,
} from '../constants/retrieverSearchTypes';
import { formatCollectionNameWithUser, extractRawCollectionName } from '../utils/collectionName';
import JiraIssueScopePicker, { DEFAULT_JIRA_ISSUE_TYPE_FILTER } from '../components/JiraIssueScopePicker';
import ConfluencePageScopePicker from '../components/ConfluencePageScopePicker';
import AzureWikiPageScopePicker from '../components/AzureWikiPageScopePicker';
import GithubFileScopePicker from '../components/GithubFileScopePicker';
import {
  JIRA_SCOPE,
  buildJiraScopePayload,
  buildJiraScopesFormValue,
  createDefaultJiraScope,
  jiraScopeFingerprint,
  normalizeJiraScopesFromConfig,
  switchJiraScopeType,
  getJiraPipelineIssueType,
  getJiraPipelineIssueStatus,
} from '../utils/jiraScope';
import {
  applyCollectionCheckResult,
  interpretCollectionCheckResponse,
} from '../utils/collectionCheck';
import { confirmDelete, confirmCritical } from '../utils/notifications';
import { formatPipelineError, getPipelineErrorReference } from '../utils/pipelineErrors';
import {
  ERROR_CONTEXT,
  humanizeApiError,
  humanizeCollectionExistsError,
} from '../utils/errorMessages';
import {
  formatFileUploadSuccess,
  formatKnowledgeSourceVerified,
  formatProjectDeleted,
  formatS3FileDeletedFromBucket,
  formatS3FileRemovedPendingCleanup,
  formatNeo4jPushSuccess,
  formatNeo4jPushPartialSuccess,
  formatNeo4jRemoveSuccess,
  formatNeo4jRemovePartialSuccess,
  formatRebuildVectorsSuccess,
  formatReuploadSuccess,
  formatVectorDbHealthySkip,
  formatVectorRetentionRemoved,
  humanizeConfigSaveResponse,
  humanizePipelineRunResponse,
} from '../utils/successMessages';
import { getDeployBadgeStyle } from '../components/ProjectDirectoryViews';
import { mergeLlmProvidersForUi } from '../utils/llmProviders';
import ProviderLogo from '../components/ProviderLogo';

const { TextArea } = Input;
const { Text } = Typography;

const getS3ConfigSnapshot = (s3 = {}) => ({
  bucket: String(s3.bucket || s3.name || '').trim(),
  region: String(s3.region || '').trim(),
});

const hasS3ValuesChanged = (currentS3, savedS3) => {
  const saved = getS3ConfigSnapshot(savedS3);
  const formBucket = String(currentS3?.bucket ?? currentS3?.name ?? '').trim();
  const formRegion = currentS3?.region != null ? String(currentS3.region).trim() : null;
  if (formBucket && formBucket !== saved.bucket) return true;
  if (formRegion !== null && formRegion !== saved.region) return true;
  return false;
};

const hasJiraConfigChanged = (currentJira, savedJira) => {
  const cur = currentJira || {};
  const saved = savedJira || {};
  if (String(cur.jira_base_url || '').trim() !== String(saved.jira_base_url || '').trim()) return true;
  if (String(cur.jira_user_email || '').trim() !== String(saved.jira_user_email || '').trim()) return true;
  const curScopes = normalizeJiraScopesFromConfig(cur).map(jiraScopeFingerprint).sort();
  const savedScopes = normalizeJiraScopesFromConfig(saved).map(jiraScopeFingerprint).sort();
  if (curScopes.length !== savedScopes.length) return true;
  for (let i = 0; i < curScopes.length; i += 1) {
    if (curScopes[i] !== savedScopes[i]) return true;
  }
  return false;
};

const hasJiraScopeChanged = (savedJira, selectedKeys, scopeByKey) => {
  const savedScopes = normalizeJiraScopesFromConfig(savedJira).map(jiraScopeFingerprint).sort();
  const currentScopes = (selectedKeys || []).map((projectKey) => jiraScopeFingerprint({
    project_key: projectKey,
    ...(scopeByKey?.[projectKey] || createDefaultJiraScope()),
  })).sort();
  if (currentScopes.length !== savedScopes.length) return true;
  for (let i = 0; i < currentScopes.length; i += 1) {
    if (currentScopes[i] !== savedScopes[i]) return true;
  }
  return false;
};

const scopeMapFromJiraScopes = (jiraScopes) => {
  const scopeMap = {};
  jiraScopes.forEach((scope) => {
    const key = String(scope?.project_key || '').trim();
    if (!key) return;
    scopeMap[key] = {
      type: scope.type,
      jql_query: String(scope.jql_query || '').trim(),
      issue_type_filter: scope.issue_type_filter || { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER },
      preview_jql: String(scope.jql_query || '').trim(),
    };
  });
  return scopeMap;
};

const mergeJiraProjectOptions = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    const key = String(item?.key || '').trim();
    if (!key) return;
    const existing = map.get(key);
    map.set(key, {
      key,
      name: String(item?.name || existing?.name || key).trim(),
    });
  });
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
};

const normalizeJiraProjectList = (projects) => {
  if (!Array.isArray(projects)) return [];
  return projects
    .map((item) => {
      const key = String(item?.key || item?.project_key || '').trim();
      if (!key) return null;
      return {
        key,
        name: String(item?.name || item?.projectName || key).trim(),
      };
    })
    .filter(Boolean);
};

const sanitizeJiraUserMessage = (message) => {
  if (!message || typeof message !== 'string') return '';
  let text = message
    .replace(/https?:\/\/[^\s,)]+/gi, 'Jira')
    .replace(/\b\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?\b/g, 'Jira');
  const cleanups = [
    [/not running at Jira/gi, 'is not reachable'],
    [/timed out at Jira/gi, 'timed out'],
    [/failed at Jira/gi, 'failed'],
    [/the connector service/gi, 'Jira'],
    [/connector service/gi, 'Jira'],
  ];
  cleanups.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  return text.trim();
};

const inferJiraProjectKeyFromRow = (row) => {
  const directProjectKey = String(
    row?.source_meta?.project_key
    || row?.source_meta?.projectKey
    || row?.project_key
    || '',
  ).trim().toUpperCase();
  if (directProjectKey) return directProjectKey;
  const candidates = [
    row?.source_meta?.issue_key,
    row?.source_meta?.issueKey,
    row?.issue_key,
    row?.file_name,
    row?.file_key,
    row?.doc_id,
  ];
  for (const value of candidates) {
    const text = String(value || '').trim().toUpperCase();
    const match = text.match(/^([A-Z][A-Z0-9]+)-\d+/);
    if (match?.[1]) return match[1];
  }
  return '';
};

const inferConfluenceSpaceKeyFromRow = (row) => {
  const direct = String(
    row?.source_meta?.space_key
    || row?.source_meta?.spaceKey
    || row?.space_key
    || '',
  ).trim();
  if (direct) return direct;
  // file_key / source_id patterns: confluence_<space>_<pageId> or confluence:<space>:<pageId>
  const candidates = [row?.file_key, row?.doc_id, row?.source_id];
  for (const value of candidates) {
    const text = String(value || '').trim();
    const colon = text.match(/^confluence:([^:]+):/i);
    if (colon?.[1]) return colon[1];
    const under = text.match(/^confluence[_-]([^/_-]+)/i);
    if (under?.[1]) return under[1];
  }
  return '';
};

const isJiraPipelineRow = (row) => {
  const sourceType = String(row?.source_type || '').toLowerCase();
  if (sourceType === 'confluence' || sourceType === 'github' || sourceType === 's3') return false;
  if (sourceType === 'jira') return true;
  const sourceHints = [
    row?.knowledge_source,
    row?.source,
    row?.provider,
    row?.source_meta?.source,
    row?.source_meta?.provider,
    row?.source_meta?.knowledge_source,
  ]
    .map((v) => String(v || '').toLowerCase())
    .filter(Boolean);
  if (sourceHints.some((v) => v.includes('jira'))) return true;
  if (getJiraPipelineIssueType(row) || getJiraPipelineIssueStatus(row)) return true;
  return Boolean(inferJiraProjectKeyFromRow(row));
};

const isConfluencePipelineRow = (row) => {
  const sourceType = String(row?.source_type || '').toLowerCase();
  if (sourceType === 'confluence') return true;
  if (sourceType && sourceType !== 'confluence') return false;
  if (inferConfluenceSpaceKeyFromRow(row)) return true;
  const sourceId = String(row?.source_id || row?.doc_id || '').toLowerCase();
  if (sourceId.startsWith('confluence:')) return true;
  const fileKey = String(row?.file_key || '').toLowerCase();
  if (fileKey.startsWith('confluence/') || fileKey.startsWith('confluence_')) return true;
  const hints = [
    row?.knowledge_source,
    row?.source,
    row?.provider,
    row?.source_meta?.source,
    row?.source_meta?.provider,
    row?.source_meta?.knowledge_source,
  ]
    .map((v) => String(v || '').toLowerCase())
    .filter(Boolean);
  return hints.some((v) => v.includes('confluence'));
};

const isAzureWikiPipelineRow = (row) => {
  const sourceType = String(row?.source_type || '').toLowerCase();
  if (sourceType === 'azure_wiki') return true;
  if (sourceType && sourceType !== 'azure_wiki') return false;
  const fileKey = String(row?.file_key || '').toLowerCase();
  if (fileKey.startsWith('azure_wiki/') || fileKey.startsWith('azure_wiki:')) return true;
  const sourceId = String(row?.source_id || row?.doc_id || '').toLowerCase();
  if (sourceId.startsWith('azure_wiki:')) return true;
  const hints = [
    row?.knowledge_source,
    row?.source,
    row?.provider,
    row?.source_meta?.source,
    row?.source_meta?.provider,
    row?.source_meta?.knowledge_source,
  ]
    .map((v) => String(v || '').toLowerCase())
    .filter(Boolean);
  return hints.some((v) => v.includes('azure_wiki') || v.includes('azure wiki'));
};

const inferPipelineRowSourceKey = (row) => {
  if (isJiraPipelineRow(row)) return 'jira';
  if (isConfluencePipelineRow(row)) return 'confluence';
  if (isAzureWikiPipelineRow(row)) return 'azure_wiki';
  const sourceType = String(row?.source_type || '').toLowerCase().trim();
  if (sourceType === 'github') return 'github';
  if (sourceType === 's3') return 's3';
  const hints = [
    row?.knowledge_source,
    row?.source,
    row?.provider,
    row?.source_meta?.source,
    row?.source_meta?.provider,
    row?.source_meta?.knowledge_source,
  ]
    .map((v) => String(v || '').toLowerCase().trim())
    .filter(Boolean)
    .join(' ');
  if (hints.includes('github') || hints.includes('git')) return 'github';
  if (hints.includes('s3') || hints.includes('bucket')) return 's3';
  return 's3';
};

const isJiraSetupStatusMessage = (message) => {
  const text = String(message || '').toLowerCase();
  return text.includes('not configured for this project')
    || text.includes('missing base url')
    || text.includes('missing credential')
    || text.includes('requires at least one project scope')
    || text.includes('configure project key');
};

const normalizeProjectS3ForForm = (s3) => {
  if (!s3 || typeof s3 !== 'object') return s3;
  const bucket = s3.bucket || s3.name;
  if (!bucket && s3.region == null) return s3;
  return { ...s3, bucket: bucket || s3.bucket };
};

const sortedGuardNames = (names) => [...(names || [])].sort();

const guardNamesEqual = (a, b) => {
  const sa = sortedGuardNames(a);
  const sb = sortedGuardNames(b);
  return sa.length === sb.length && sa.every((name, i) => name === sb[i]);
};

const guardConfigsEqual = (formCfg = {}, projCfg = {}, guardNames = []) => {
  const names = new Set([
    ...guardNames,
    ...Object.keys(formCfg),
    ...Object.keys(projCfg),
  ]);
  for (const name of names) {
    if (JSON.stringify(formCfg[name] ?? null) !== JSON.stringify(projCfg[name] ?? null)) {
      return false;
    }
  }
  return true;
};

const normalizeRetrieverConfig = (retriever = {}) => ({
  search_type: retriever.search_type || DEFAULT_RETRIEVER_SEARCH_TYPE,
  top_k: Number(retriever.top_k ?? 5),
  fetch_k: Number(retriever.fetch_k ?? 20),
  lambda_mult: Number(retriever.lambda_mult ?? 0.5),
  bm25_k: Number(retriever.bm25_k ?? 6),
  vector_k: Number(retriever.vector_k ?? 6),
  hybrid_alpha: Number(retriever.hybrid_alpha ?? 0.6),
  score_threshold:
    retriever.score_threshold == null || retriever.score_threshold === ''
      ? null
      : Number(retriever.score_threshold),
  cosine_similarity: retriever.cosine_similarity !== false,
});

const normalizeGraphragConfig = (graphrag = {}) => ({
  enabled: !!graphrag.enabled,
  ingest_endpoint: String(graphrag.ingest_endpoint || '').trim(),
  group_id: String(graphrag.group_id ?? '').trim(),
  timeout: Number(graphrag.timeout ?? 300),
});

const getConfluenceSelectedFolderIds = (syncScope = {}) => {
  const scopeType = String(syncScope.type || 'space').toLowerCase();
  if (scopeType === 'space') return [];
  const ids = [...(syncScope.parent_page_ids || [])];
  const legacy = syncScope.parent_page_id;
  if (legacy && !ids.includes(legacy)) ids.push(legacy);
  return ids;
};

const normalizeConfluenceScopesFromConfig = (confluenceCfg = {}) => {
  const rawScopes = Array.isArray(confluenceCfg?.scopes) ? confluenceCfg.scopes : [];
  const scopes = [];
  const seen = new Set();
  rawScopes.forEach((raw) => {
    const spaceKey = String(raw?.space_key || '').trim();
    if (!spaceKey || seen.has(spaceKey)) return;
    seen.add(spaceKey);
    scopes.push({
      space_key: spaceKey,
      space_id: raw?.space_id || '',
      space_name: raw?.space_name || spaceKey,
      sync_scope: raw?.sync_scope || {},
    });
  });
  if (scopes.length) return scopes;
  const legacyKey = String(confluenceCfg?.space_key || '').trim();
  if (!legacyKey) return [];
  return [{
    space_key: legacyKey,
    space_id: confluenceCfg?.space_id || '',
    space_name: confluenceCfg?.space_name || legacyKey,
    sync_scope: confluenceCfg?.sync_scope || {},
  }];
};

const scopeMapFromConfluenceScopes = (scopes = []) => {
  const map = {};
  scopes.forEach((scope) => {
    const key = String(scope?.space_key || '').trim();
    if (!key) return;
    map[key] = {
      space_key: key,
      space_id: scope?.space_id || '',
      space_name: scope?.space_name || key,
      sync_scope: scope?.sync_scope || {},
      folderIds: getConfluenceSelectedFolderIds(scope?.sync_scope || {}),
    };
  });
  return map;
};

const getConfluencePipelineFolderLabel = (record) => {
  const meta = record?.source_meta || {};
  return String(meta.sync_folder_label || '').trim() || null;
};

const buildConfluenceSyncScope = (folderIds, folders = [], existingScope = {}) => {
  const labels = { ...(existingScope.folder_labels || {}) };
  const rootTypes = { ...(existingScope.root_types || {}) };
  folders.forEach((folder) => {
    if (folderIds.includes(folder.id)) {
      labels[folder.id] = folder.title;
      rootTypes[folder.id] = folder.type || 'page';
    }
  });
  folderIds.forEach((id) => {
    if (!labels[id]) labels[id] = id;
    if (!rootTypes[id]) rootTypes[id] = 'page';
  });
  return {
    type: 'subtrees',
    parent_page_ids: folderIds,
    folder_labels: labels,
    root_types: rootTypes,
    include_attachments: false,
  };
};

const createDefaultConfluenceSpaceScope = (spaceKey, spaceMeta = {}) => ({
  space_key: spaceKey,
  space_id: spaceMeta?.id || spaceMeta?.space_id || '',
  space_name: spaceMeta?.name || spaceMeta?.space_name || spaceKey,
  sync_scope: buildConfluenceSyncScope([], [], {}),
  folderIds: [],
});

const normalizeConfluenceSyncScopeKey = (scope = {}) => {
  const scopeType = String(scope.type || 'space').toLowerCase();
  const ids = getConfluenceSelectedFolderIds(scope).slice().sort();
  return JSON.stringify({ type: scopeType, parent_page_ids: ids });
};

const hasConfluenceSyncScopeChanged = (formScope, savedScope) =>
  normalizeConfluenceSyncScopeKey(formScope) !== normalizeConfluenceSyncScopeKey(savedScope);

const renderConfluenceSelectedFolders = (syncScope = {}, confluenceCfg = {}) => {
  const scopes = normalizeConfluenceScopesFromConfig(confluenceCfg);
  if (scopes.length > 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {scopes.map((scope) => {
          const labels = scope.sync_scope?.folder_labels || {};
          const ids = getConfluenceSelectedFolderIds(scope.sync_scope);
          return (
            <div key={scope.space_key}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{scope.space_key}</div>
              {ids.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ids.map((folderId) => (
                    <Tag key={`${scope.space_key}-${folderId}`}>{labels[folderId] || folderId}</Tag>
                  ))}
                </div>
              ) : (
                <span>—</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const scopeType = String(syncScope.type || 'space').toLowerCase();
  if (scopeType === 'space') return 'Entire space';
  const labels = syncScope.folder_labels || {};
  const ids = getConfluenceSelectedFolderIds(syncScope);
  if (!ids.length) return '—';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ids.map((folderId) => (
        <Tag key={folderId}>{labels[folderId] || folderId}</Tag>
      ))}
    </div>
  );
};

const normalizeAzureWikiScopesFromConfig = (azureWikiCfg = {}) => {
  const rawScopes = Array.isArray(azureWikiCfg?.scopes) ? azureWikiCfg.scopes : [];
  const scopes = [];
  const seen = new Set();
  rawScopes.forEach((raw) => {
    const wikiId = String(raw?.wiki_id || '').trim();
    if (!wikiId || seen.has(wikiId)) return;
    seen.add(wikiId);
    const syncScope = (raw?.sync_scope && typeof raw.sync_scope === 'object') ? raw.sync_scope : {};
    const selectedPaths = Array.isArray(raw?.page_paths)
      ? raw.page_paths
      : (Array.isArray(syncScope?.page_paths) ? syncScope.page_paths : []);
    scopes.push({
      project: String(raw?.project || '').trim(),
      wiki_id: wikiId,
      wiki_name: raw?.wiki_name || wikiId,
      page_paths: selectedPaths.map((p) => String(p)),
    });
  });
  return scopes;
};

const scopeMapFromAzureWikiScopes = (scopes = []) => {
  const map = {};
  scopes.forEach((scope) => {
    const key = String(scope?.wiki_id || '').trim();
    if (!key) return;
    map[key] = {
      project: scope?.project || '',
      wiki_id: key,
      wiki_name: scope?.wiki_name || key,
      page_paths: Array.isArray(scope?.page_paths) ? scope.page_paths : [],
    };
  });
  return map;
};

const normalizeGithubScopesFromConfig = (githubCfg = {}) => {
  const rawScopes = Array.isArray(githubCfg?.scopes) ? githubCfg.scopes : [];
  const scopes = [];
  const seen = new Set();
  rawScopes.forEach((raw) => {
    const owner = String(raw?.owner || '').trim();
    const repo = String(raw?.repo || '').trim();
    const branch = String(raw?.branch || raw?.default_branch || 'main').trim() || 'main';
    if (!owner || !repo) return;
    const key = `${owner}/${repo}@${branch}`;
    if (seen.has(key)) return;
    seen.add(key);
    scopes.push({
      key,
      owner,
      repo,
      branch,
      path_prefix: String(raw?.path_prefix || '').trim(),
      include_globs: Array.isArray(raw?.include_globs)
        ? raw.include_globs.map((p) => String(p || '').trim()).filter(Boolean)
        : [],
      selected_paths: Array.isArray(raw?.selected_paths)
        ? raw.selected_paths.map((p) => String(p || '').trim()).filter(Boolean)
        : [],
    });
  });
  if (scopes.length) return scopes;
  const owner = String(githubCfg?.owner || '').trim();
  const repo = String(githubCfg?.repo || '').trim();
  if (!owner || !repo) return [];
  const branch = String(githubCfg?.default_branch || 'main').trim() || 'main';
  return [{
    key: `${owner}/${repo}@${branch}`,
    owner,
    repo,
    branch,
    path_prefix: String(githubCfg?.path_prefix || '').trim(),
    include_globs: [],
    selected_paths: [],
  }];
};

const hasActiveTabEdits = (tab, values, project, guardState) => {
  if (!project) return false;

  switch (tab) {
    case 'details':
      return (
        (values.name ?? '') !== (project.name ?? '')
        || (values.description ?? '') !== (project.description ?? '')
      );
    case 'embedding': {
      const cur = values?.config?.embedding || {};
      const saved = project?.config?.embedding || {};
      return (
        (cur.model != null && cur.model !== saved.model)
        || (cur.model_name != null && cur.model_name !== saved.model_name)
      );
    }
    case 'vector_store': {
      const username = localStorage.getItem('username') || '';
      const savedRaw = extractRawCollectionName(
        project?.config?.vector_store?.collection_name || '',
        username,
      ) || String(project?.config?.vector_store?.collection_name || '').trim();
      const currentRaw = String(values?.config?.vector_store?.collection_name || '').trim();
      return currentRaw !== savedRaw;
    }
    case 's3': {
      const isConfluence = (
        project?.config?.knowledge_provider === 'confluence'
        || project?.config?.confluence?.credential_id
      );
      if (isConfluence) {
        return hasConfluenceSyncScopeChanged(
          values?.config?.confluence?.sync_scope,
          project?.config?.confluence?.sync_scope,
        );
      }
      const isJira = (
        project?.config?.knowledge_provider === 'jira'
        || project?.config?.jira?.credential_id
        || project?.config?.jira?.jira_base_url
      );
      if (isJira) {
        return hasJiraConfigChanged(values?.config?.jira, project?.config?.jira);
      }
      return hasS3ValuesChanged(values?.config?.s3, project?.config?.s3);
    }
    case 'graphrag':
      return (
        JSON.stringify(normalizeGraphragConfig(values?.config?.graphrag))
        !== JSON.stringify(normalizeGraphragConfig(project?.config?.graphrag))
      );
    case 'retriever':
      return (
        JSON.stringify(normalizeRetrieverConfig(values?.config?.retriever))
        !== JSON.stringify(normalizeRetrieverConfig(project?.config?.retriever))
      );
    case 'llm': {
      const curLlm = values?.config?.llm || {};
      const savedLlm = project?.config?.llm || {};
      const curGuardrails = values?.config?.guardrails || {};
      const savedGuardrails = project?.config?.guardrails || {};
      const llmChanged = (
        (curLlm.provider != null && curLlm.provider !== savedLlm.provider)
        || (curLlm.model != null && curLlm.model !== savedLlm.model)
        || (
          curLlm.temperature != null
          && Number(curLlm.temperature) !== Number(savedLlm.temperature ?? 0)
        )
      );
      const enabledChanged = Boolean(curGuardrails.enabled) !== Boolean(savedGuardrails.enabled);
      const inputChanged = !guardNamesEqual(guardState.selectedInputGuards, savedGuardrails.input_guards);
      const preEmbeddingChanged = !guardNamesEqual(
        guardState.selectedPreEmbeddingGuards,
        savedGuardrails.pre_embedding_guards,
      );
      const contextChanged = !guardNamesEqual(
        guardState.selectedContextGuards,
        savedGuardrails.context_guards,
      );
      const outputChanged = !guardNamesEqual(
        guardState.selectedOutputGuards,
        savedGuardrails.output_guards,
      );
      const inputConfigChanged = !guardConfigsEqual(
        curGuardrails.input_guards_config,
        savedGuardrails.input_guards_config,
        guardState.selectedInputGuards,
      );
      const preEmbeddingConfigChanged = !guardConfigsEqual(
        curGuardrails.pre_embedding_guards_config,
        savedGuardrails.pre_embedding_guards_config,
        guardState.selectedPreEmbeddingGuards,
      );
      const contextConfigChanged = !guardConfigsEqual(
        curGuardrails.context_guards_config,
        savedGuardrails.context_guards_config,
        guardState.selectedContextGuards,
      );
      const outputConfigChanged = !guardConfigsEqual(
        curGuardrails.output_guards_config,
        savedGuardrails.output_guards_config,
        guardState.selectedOutputGuards,
      );
      return (
        llmChanged
        || enabledChanged
        || inputChanged
        || preEmbeddingChanged
        || contextChanged
        || outputChanged
        || inputConfigChanged
        || preEmbeddingConfigChanged
        || contextConfigChanged
        || outputConfigChanged
      );
    }
    default:
      return false;
  }
};

const S3_REGION_OPTIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'af-south-1',
  'ap-east-1',
  'ap-south-1',
  'ap-south-2',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-southeast-3',
  'ap-southeast-4',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ca-central-1',
  'ca-west-1',
  'eu-central-1',
  'eu-central-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'il-central-1',
  'me-south-1',
  'me-central-1',
  'sa-east-1',
];

const buildS3RegionSelectOptions = (savedRegion) => {
  const saved = String(savedRegion || '').trim();
  const regions = [...S3_REGION_OPTIONS];
  if (saved && !regions.includes(saved)) {
    regions.unshift(saved);
  }
  return regions.map((region) => ({ value: region, label: region }));
};

const PROJECT_DETAIL_TAB_ORDER = [
  'details',
  's3',
  'embedding',
  'vector_store',
  'retriever',
  'llm',
  'file_pipeline',
  'graphrag',
];

/**
 * Fixed width for the Progress column so the track and the line below stay the same width
 * (antd Progress is 100% of parent; without this, the bar matches the changing width of the footer text).
 */
const FILE_PIPELINE_PROGRESS_BLOCK_STYLE = {
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  lineHeight: 1.2,
  overflow: 'hidden',
};

const FILE_PIPELINE_COMPACT_BREAKPOINT = 1100;

const SHOW_VECTOR_RETENTION_UI = true;
const SHOW_VECTOR_DB_REBUILD = false;

const RETENTION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

const RETENTION_UNIT_LABEL = RETENTION_UNIT_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

/** Reusable floating retention editor for table cells. */
function FileVectorRetentionEditor({ record, token, projectId, disabled, onAfterSave, textColor }) {
  const { message } = App.useApp();
  const vr = record.vector_retention || {};
  const [enabled, setEnabled] = useState(vr.enabled ?? false);
  const [value, setValue] = useState(vr.value ?? 6);
  const [unit, setUnit] = useState(vr.unit ?? 'months');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const valueNumber = Number.isFinite(Number(value)) ? Math.max(1, Number(value)) : 6;
  const normalizedUnit = RETENTION_UNIT_LABEL[unit] ? unit : 'months';
  const displayText = enabled
    ? `${valueNumber} ${RETENTION_UNIT_LABEL[normalizedUnit] || 'Months'}`
    : 'Off';

  useEffect(() => {
    const v = record.vector_retention || {};
    setEnabled(v.enabled ?? false);
    setValue(v.value ?? 6);
    setUnit(v.unit ?? 'months');
  }, [record._id, record.vector_retention?.enabled, record.vector_retention?.value, record.vector_retention?.unit]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const persist = async () => {
    if (!token) {
      message.error('Sign in to save retention settings.');
      return;
    }
    const payload = {
      enabled,
      value: valueNumber,
      unit: normalizedUnit,
    };
    setSaving(true);
    try {
      await API.patchFilePipelineFileRetention(token, projectId, record._id, payload);
      message.success('Retention saved');
      setOpen(false);
      onAfterSave?.();
    } catch (e) {
      message.error(humanizeApiError(e, { context: ERROR_CONTEXT.RETENTION }));
    } finally {
      setSaving(false);
    }
  };

  const popupContent = (
    <div
      style={{
        width: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        animation: 'antFadeIn 0.16s ease-out',
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Retention Settings</div>
        <Switch checked={enabled} disabled={disabled || saving} onChange={setEnabled} size="small" />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <InputNumber
            min={1}
            max={9999}
            size="small"
          disabled={disabled || saving || !enabled}
            value={value}
            onChange={(v) => setValue(v ?? 1)}
          style={{ width: 70 }}
          />
          <Select
            size="small"
          disabled={disabled || saving || !enabled}
          value={normalizedUnit}
          style={{ width: 110 }}
            options={RETENTION_UNIT_OPTIONS}
            onChange={setUnit}
          getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.body}
          dropdownStyle={{ zIndex: 1400 }}
        />
      </div>
      <Button
        size="small"
        type="primary"
        disabled={disabled}
        loading={saving}
        onClick={persist}
        style={{ alignSelf: 'flex-end' }}
      >
            Save
          </Button>
    </div>
  );

  return (
    <div style={{ width: '100%', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <span
        title={displayText}
        style={{
          display: 'inline-block',
          maxWidth: disabled ? '100%' : 'calc(100% - 28px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: textColor,
          fontSize: 12,
        }}
      >
        {displayText}
      </span>
      {!disabled ? (
        <Popover
        trigger="click"
        open={open}
        onOpenChange={(nextOpen) => {
          if (!disabled) setOpen(nextOpen);
        }}
        placement="bottomRight"
        content={popupContent}
        overlayStyle={{ zIndex: 1300 }}
        styles={{
          body: {
            padding: 12,
            borderRadius: 12,
            boxShadow: '0 10px 24px rgba(0,0,0,0.24)',
          },
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          aria-label="Edit retention"
          disabled={disabled}
          style={{
            color: '#8c8c8c',
            minWidth: 24,
            width: 24,
            height: 24,
            padding: 0,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#a636d6';
            e.currentTarget.style.background = 'rgba(166, 54, 214, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#8c8c8c';
            e.currentTarget.style.background = 'transparent';
          }}
        />
        </Popover>
      ) : null}
    </div>
  );
}

const isPdfFile = (fileNameOrKey = '') => String(fileNameOrKey || '').toLowerCase().endsWith('.pdf');

const EMPTY_FILE_PREVIEW_MODAL = {
  open: false,
  title: '',
  url: '',
  blobUrl: '',
  textContent: '',
  provider: 'office',
  loading: false,
  error: '',
  contentFormat: '', // 'markdown' | ''
  sourceKind: '', // azure_wiki | confluence | jira | github
  webUrl: '',
  fileKey: '',
  docId: '',
  showRawMarkdown: false,
  pdfLoading: false,
};

const wikiMarkdownPreviewShellStyle = (isLight) => ({
  background: isLight ? '#ffffff' : (colors.bgElevated || '#1A1635'),
  border: `1px solid ${isLight ? 'rgba(15, 23, 42, 0.12)' : (colors.border || 'rgba(255,255,255,0.08)')}`,
  borderRadius: 8,
  padding: '20px 28px',
  maxHeight: '70vh',
  overflow: 'auto',
  overflowAnchor: 'none',
  fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
  fontSize: 14,
  lineHeight: 1.65,
  color: isLight ? '#24292f' : (colors.textPrimary || '#F0EDF5'),
});

const wikiRawPreviewShellStyle = (isLight) => ({
  background: isLight ? '#f8fafc' : (colors.bgElevated || '#1A1635'),
  border: `1px solid ${isLight ? 'rgba(15, 23, 42, 0.12)' : (colors.border || 'rgba(255,255,255,0.08)')}`,
  borderRadius: 8,
  padding: '20px 24px',
  maxHeight: '70vh',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  fontSize: 13,
  lineHeight: 1.7,
  color: isLight ? '#0f172a' : (colors.textPrimary || '#F0EDF5'),
});

const AZURE_WIKI_IMG_SCHEME = 'azure-wiki-img:';

const parseAzureWikiImageSrc = (src = '') => {
  const raw = String(src || '').trim();
  if (!raw) return null;
  let path = '';
  if (raw.toLowerCase().startsWith(AZURE_WIKI_IMG_SCHEME)) {
    path = raw.slice(AZURE_WIKI_IMG_SCHEME.length).trim();
  } else if (raw.startsWith('/.attachments/')) {
    path = raw.split('?')[0].split('#')[0];
  } else {
    return null;
  }
  try {
    path = decodeURIComponent(path);
  } catch (_e) {
    /* keep raw path if malformed encoding */
  }
  return path.split('?')[0].split('#')[0];
};

/** Keep azure-wiki-img: (and /.attachments/) — defaultUrlTransform strips unknown schemes. */
const azureWikiMarkdownUrlTransform = (url) => {
  const value = String(url || '');
  const lower = value.toLowerCase();
  if (lower.startsWith(AZURE_WIKI_IMG_SCHEME) || value.startsWith('/.attachments/')) {
    return value;
  }
  return defaultUrlTransform(value);
};

/** Authenticated Azure Wiki image for the preview modal (Bearer → blob URL). */
function AzureWikiAuthImage({
  src,
  alt,
  token,
  projectId,
  fileKey,
  docId,
  registerBlobUrl,
}) {
  const [blobUrl, setBlobUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef(null);

  // Fixed frame height so spinner → image never shifts scroll position.
  const FRAME_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 'min(420px, 50vh)',
    minHeight: 280,
    margin: '12px 0 8px',
    overflow: 'auto',
    overflowAnchor: 'none',
    scrollMargin: 0,
    background: 'rgba(127,127,127,0.06)',
    borderRadius: 4,
  };

  useEffect(() => {
    let cancelled = false;
    let createdUrl = '';
    const attachmentPath = parseAzureWikiImageSrc(src);
    if (!attachmentPath || !token || !projectId) {
      setLoading(false);
      setFailed(true);
      return undefined;
    }
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const resp = await API.getAzureWikiImage(token, projectId, {
          file_key: fileKey || undefined,
          doc_id: docId || undefined,
          path: attachmentPath,
        });
        const blob = resp?.data instanceof Blob
          ? resp.data
          : new Blob([resp?.data]);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        if (typeof registerBlobUrl === 'function') {
          registerBlobUrl(createdUrl);
        }
        setBlobUrl(createdUrl);
      } catch (_e) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, token, projectId, fileKey, docId, registerBlobUrl]);

  return (
    <div
      ref={wrapRef}
      style={FRAME_STYLE}
      onMouseDown={(e) => {
        // Prevent focus-driven scrollIntoView when clicking the diagram.
        e.preventDefault();
      }}
    >
      {loading ? <Spin size="small" /> : null}
      {!loading && (failed || !blobUrl) ? (
        <div style={{ color: '#8c8c8c', fontSize: 12, padding: 8 }}>
          {alt ? `[Image: ${alt}]` : '[Image unavailable]'}
        </div>
      ) : null}
      {!loading && blobUrl ? (
        <img
          src={blobUrl}
          alt={alt || ''}
          draggable={false}
          tabIndex={-1}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 4,
            overflowAnchor: 'none',
          }}
        />
      ) : null}
    </div>
  );
}

const ViewProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { message, notification } = App.useApp();
  const themeStyles = useThemeStyles();
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [rbacInfo, setRbacInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [selectedInputGuards, setSelectedInputGuards] = useState([]);
  const [selectedPreEmbeddingGuards, setSelectedPreEmbeddingGuards] = useState([]);
  const [selectedContextGuards, setSelectedContextGuards] = useState([]);
  const [selectedOutputGuards, setSelectedOutputGuards] = useState([]);
  const [chunkingStrategy, setChunkingStrategy] = useState('recursive');
  const [form] = Form.useForm();
  const watchedFormValues = Form.useWatch([], form);
  const s3BucketWatch = Form.useWatch(['config', 's3', 'bucket'], form);
  const s3RegionWatch = Form.useWatch(['config', 's3', 'region'], form);
  const hasS3ConfigChanges = useMemo(
    () => hasS3ValuesChanged(
      { bucket: s3BucketWatch, region: s3RegionWatch },
      project?.config?.s3,
    ),
    [s3BucketWatch, s3RegionWatch, project?.config?.s3],
  );
  const hasTabEdits = useMemo(() => {
    if (!editMode || !project) return false;
    const values = watchedFormValues ?? form.getFieldsValue(true);
    return hasActiveTabEdits(activeTab, values, project, {
      selectedInputGuards,
      selectedPreEmbeddingGuards,
      selectedContextGuards,
      selectedOutputGuards,
    });
  }, [
    editMode,
    activeTab,
    project,
    watchedFormValues,
    form,
    selectedInputGuards,
    selectedPreEmbeddingGuards,
    selectedContextGuards,
    selectedOutputGuards,
  ]);
  const [pipelineScheduleForm] = Form.useForm();
  const pipelineScheduleIntervalValue = Form.useWatch('interval_value', pipelineScheduleForm);
  const pipelineScheduleIntervalUnit = Form.useWatch('interval_unit', pipelineScheduleForm);
  const [filePipelineSettingsOpen, setFilePipelineSettingsOpen] = useState(false);
  const [checkingCollection, setCheckingCollection] = useState(false);
  const [collectionAvailable, setCollectionAvailable] = useState(true);
  const [collectionConnectionError, setCollectionConnectionError] = useState('');
  const [showCollectionStatus, setShowCollectionStatus] = useState(false);
  const [collectionNameUnchanged, setCollectionNameUnchanged] = useState(false);
  const [rebuildingVectorsFromCache, setRebuildingVectorsFromCache] = useState(false);
  const [localVectorCacheAssessment, setLocalVectorCacheAssessment] = useState(null);
  const [vectorDbHealth, setVectorDbHealth] = useState(null);
  const [filePipelineSchedule, setFilePipelineSchedule] = useState(null);
  const [filePipelineLoading, setFilePipelineLoading] = useState(false);
  const [filePipelineActionLoading, setFilePipelineActionLoading] = useState(null);
  const [filePipelineFiles, setFilePipelineFiles] = useState([]);
  const [selectedFilePipelineRowKeys, setSelectedFilePipelineRowKeys] = useState([]);
  const [filePipelineSearchInput, setFilePipelineSearchInput] = useState('');
  const [filePipelineSearch, setFilePipelineSearch] = useState('');
  const [filePipelineStatusFilter, setFilePipelineStatusFilter] = useState('all');
  const [tableSourceFilterKey, setTableSourceFilterKey] = useState('');
  const [jiraProjectFilter, setJiraProjectFilter] = useState('__all__');
  const [jiraIssueStatusFilter, setJiraIssueStatusFilter] = useState('__all__');
  const [jiraIssueTypeFilter, setJiraIssueTypeFilter] = useState('__all__');
  const [confluenceSpaceFilter, setConfluenceSpaceFilter] = useState('__all__');
  const [filePipelineCompactView, setFilePipelineCompactView] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < FILE_PIPELINE_COMPACT_BREAKPOINT : false),
  );
  const [showAdvancedPipelineColumns, setShowAdvancedPipelineColumns] = useState(false);
  const [pipelineDetailModal, setPipelineDetailModal] = useState({ open: false, title: '', detail: '' });
  const [filePipelineTriggerStatus, setFilePipelineTriggerStatus] = useState(null);
  /** Avoid overlapping schedule/files/s3-trigger polls when responses are slow (prevents dozens of pending XHRs). */
  const filePipelineLoadInFlightRef = useRef(null);
  /** Latest S3 bucket for pipeline permission probe (kept out of loadPipelineData deps to avoid duplicate full loads). */
  const pipelineS3BucketRef = useRef(null);
  const filePipelineFilesRef = useRef([]);
  const filePipelineScheduleRef = useRef(null);
  const [filePipelineS3Permissions, setFilePipelineS3Permissions] = useState(null);
  const [filePipelineS3Uploading, setFilePipelineS3Uploading] = useState(false);
  const [filePipelineRowActionLoading, setFilePipelineRowActionLoading] = useState(null);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [uploadCategoryId, setUploadCategoryId] = useState('all');
  const reuploadFileRef = useRef(null);
  const reuploadRecordRef = useRef(null);
  const filePreviewBlobUrlRef = useRef('');
  const filePreviewImageBlobUrlsRef = useRef(new Set());
  const [filePreviewModal, setFilePreviewModal] = useState(EMPTY_FILE_PREVIEW_MODAL);
  const registerFilePreviewImageBlobUrl = useCallback((url) => {
    if (url) filePreviewImageBlobUrlsRef.current.add(url);
  }, []);
  const resetFilePreviewModal = useCallback(() => {
    if (filePreviewBlobUrlRef.current) {
      URL.revokeObjectURL(filePreviewBlobUrlRef.current);
      filePreviewBlobUrlRef.current = '';
    }
    filePreviewImageBlobUrlsRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch (_e) { /* ignore */ }
    });
    filePreviewImageBlobUrlsRef.current.clear();
    setFilePreviewModal(EMPTY_FILE_PREVIEW_MODAL);
  }, []);

  const handleOpenAzureWikiPreviewPdf = useCallback(async () => {
    if (!token || !projectId) return;
    const fileKey = String(filePreviewModal.fileKey || '').trim();
    const docId = String(filePreviewModal.docId || '').trim();
    if (!fileKey && !docId) {
      message.error('Missing Azure Wiki file reference for PDF.');
      return;
    }
    setFilePreviewModal((prev) => ({ ...prev, pdfLoading: true, error: '' }));
    try {
      const resp = await API.getAzureWikiFilePreviewPdf(token, projectId, {
        file_key: fileKey || undefined,
        doc_id: docId || undefined,
      });
      const blob = resp?.data instanceof Blob
        ? resp.data
        : new Blob([resp?.data], { type: 'application/pdf' });
      if (filePreviewBlobUrlRef.current) {
        URL.revokeObjectURL(filePreviewBlobUrlRef.current);
      }
      const blobUrl = URL.createObjectURL(blob);
      filePreviewBlobUrlRef.current = blobUrl;
      const baseTitle = String(filePreviewModal.title || 'Azure Wiki page').replace(/\.pdf$/i, '');
      setFilePreviewModal((prev) => ({
        ...prev,
        url: blobUrl,
        title: `${baseTitle}.pdf`,
        textContent: '',
        contentFormat: '',
        provider: 'pdf',
        loading: false,
        pdfLoading: false,
        error: '',
        showRawMarkdown: false,
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (typeof e?.response?.data === 'string' ? e.response.data : null)
        || e?.message
        || 'Failed to generate Azure Wiki PDF.';
      setFilePreviewModal((prev) => ({
        ...prev,
        pdfLoading: false,
        error: typeof detail === 'string' ? detail : 'Failed to generate Azure Wiki PDF.',
      }));
    }
  }, [token, projectId, filePreviewModal.fileKey, filePreviewModal.docId, filePreviewModal.title, message]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteEmptyS3, setDeleteEmptyS3] = useState(false);
  const [s3Dirty, setS3Dirty] = useState(false);
  const [s3Verified, setS3Verified] = useState(false);
  const [s3Verifying, setS3Verifying] = useState(false);
  const [s3VerifyError, setS3VerifyError] = useState('');
  const [confluenceWebhookSetup, setConfluenceWebhookSetup] = useState(null);
  const [confluenceFolders, setConfluenceFolders] = useState([]);
  const [confluenceFoldersLoading, setConfluenceFoldersLoading] = useState(false);
  const [confluenceFoldersError, setConfluenceFoldersError] = useState('');
  const [selectedConfluenceFolderIds, setSelectedConfluenceFolderIds] = useState([]);
  const [confluenceSpaces, setConfluenceSpaces] = useState([]);
  const [confluenceSpacesLoading, setConfluenceSpacesLoading] = useState(false);
  const [confluenceSpacesError, setConfluenceSpacesError] = useState('');
  const [pipelineSelectedConfluenceSpaceKey, setPipelineSelectedConfluenceSpaceKey] = useState('');
  const [pipelineConfiguredConfluenceSpaceKeys, setPipelineConfiguredConfluenceSpaceKeys] = useState([]);
  const [pipelineConfluenceScopeByKey, setPipelineConfluenceScopeByKey] = useState({});
  const [azureWikiProjects, setAzureWikiProjects] = useState([]);
  const [azureWikiProjectsLoading, setAzureWikiProjectsLoading] = useState(false);
  const [azureWikiProjectsError, setAzureWikiProjectsError] = useState('');
  const [azureWikiWikis, setAzureWikiWikis] = useState([]);
  const [azureWikiWikisLoading, setAzureWikiWikisLoading] = useState(false);
  const [azureWikiWikisError, setAzureWikiWikisError] = useState('');
  const [azureWikiPages, setAzureWikiPages] = useState([]);
  const [azureWikiPagesLoading, setAzureWikiPagesLoading] = useState(false);
  const [azureWikiPagesError, setAzureWikiPagesError] = useState('');
  const [pipelineSelectedAzureWikiProjects, setPipelineSelectedAzureWikiProjects] = useState([]);
  const [pipelineConfiguredAzureWikiWikiIds, setPipelineConfiguredAzureWikiWikiIds] = useState([]);
  const [pipelineSelectedAzureWikiWikiId, setPipelineSelectedAzureWikiWikiId] = useState('');
  const [pipelineAzureWikiScopeByWikiId, setPipelineAzureWikiScopeByWikiId] = useState({});
  const [azureWikiSyncing, setAzureWikiSyncing] = useState(false);
  const [githubSyncing, setGithubSyncing] = useState(false);
  const [azureWikiVerified, setAzureWikiVerified] = useState(false);
  const [azureWikiVerifying, setAzureWikiVerifying] = useState(false);
  const [azureWikiVerifyError, setAzureWikiVerifyError] = useState('');
  const [azureWikiCredentialId, setAzureWikiCredentialId] = useState('');
  const azureWikiOrgUrlWatch = Form.useWatch(['config', 'azure_wiki', 'organization_url'], form);
  const [githubVerified, setGithubVerified] = useState(false);
  const [githubVerifying, setGithubVerifying] = useState(false);
  const [githubVerifyError, setGithubVerifyError] = useState('');
  const [githubCredentialId, setGithubCredentialId] = useState('');
  const githubBaseUrlWatch = Form.useWatch(['config', 'github', 'github_base_url'], form);
  const [jiraConnectorStatus, setJiraConnectorStatus] = useState(null);
  const [jiraConnectorStatusLoading, setJiraConnectorStatusLoading] = useState(false);
  const jiraStatusNoticeKeyRef = useRef(null);
  const jiraSetupPromptedRef = useRef(false);
  const [jiraVerified, setJiraVerified] = useState(false);
  const [jiraVerifying, setJiraVerifying] = useState(false);
  const [jiraVerifyError, setJiraVerifyError] = useState('');
  const [jiraCredentialId, setJiraCredentialId] = useState('');
  const [jiraProjects, setJiraProjects] = useState([]);
  const [selectedJiraProjectKeys, setSelectedJiraProjectKeys] = useState([]);
  const jiraCredentialResetLockRef = useRef(false);
  const jiraProjectLoadSeqRef = useRef(0);
  const [jiraScopeByProjectKey, setJiraScopeByProjectKey] = useState({});
  const [pipelineJiraPanelOpen, setPipelineJiraPanelOpen] = useState(false);
  const [sourceSettingsActiveKey, setSourceSettingsActiveKey] = useState('jira');
  const [githubRepos, setGithubRepos] = useState([]);
  const [githubReposLoading, setGithubReposLoading] = useState(false);
  const [githubReposError, setGithubReposError] = useState('');
  const [pipelineConfiguredGithubRepoKeys, setPipelineConfiguredGithubRepoKeys] = useState([]);
  const [pipelineConfiguredGithubScopeKeys, setPipelineConfiguredGithubScopeKeys] = useState([]);
  const [pipelineSelectedGithubRepoKey, setPipelineSelectedGithubRepoKey] = useState('');
  const [pipelineSelectedGithubScopeKey, setPipelineSelectedGithubScopeKey] = useState('');
  const [pipelineGithubScopeByScopeKey, setPipelineGithubScopeByScopeKey] = useState({});
  const [githubBranchesByRepoKey, setGithubBranchesByRepoKey] = useState({});
  const [githubBranchesLoadingByRepoKey, setGithubBranchesLoadingByRepoKey] = useState({});
  const [githubTreeByScopeKey, setGithubTreeByScopeKey] = useState({});
  const [githubTreeLoadingByScopeKey, setGithubTreeLoadingByScopeKey] = useState({});
  const [githubTreeErrorByScopeKey, setGithubTreeErrorByScopeKey] = useState({});
  const [pipelineSelectedJiraProjectKey, setPipelineSelectedJiraProjectKey] = useState('');
  const [pipelineConfiguredProjectKeys, setPipelineConfiguredProjectKeys] = useState([]);
  const [pipelineJiraScopeByKey, setPipelineJiraScopeByKey] = useState({});
  const [jiraMonitoredIssues, setJiraMonitoredIssues] = useState({});
  const [jiraMonitorPendingUpdates, setJiraMonitorPendingUpdates] = useState([]);
  const [jiraMonitorConsentOpen, setJiraMonitorConsentOpen] = useState(false);
  const [jiraMonitorApplying, setJiraMonitorApplying] = useState(false);
  const [jiraMonitorChecking, setJiraMonitorChecking] = useState(false);
  const [jiraMonitorActiveUpdate, setJiraMonitorActiveUpdate] = useState(null);
  const jiraMonitorCheckInFlightRef = useRef(false);
  const jiraMonitorConsentOpenRef = useRef(false);
  const jiraMonitorApplyingRef = useRef(false);
  const jiraMonitorActiveUpdateRef = useRef(null);
  const jiraMonitorHandledKeysRef = useRef(new Map());
  const [confluenceMonitoredPages, setConfluenceMonitoredPages] = useState({});
  const [confluenceMonitorPendingUpdates, setConfluenceMonitorPendingUpdates] = useState([]);
  const [confluenceMonitorConsentOpen, setConfluenceMonitorConsentOpen] = useState(false);
  const [confluenceMonitorApplying, setConfluenceMonitorApplying] = useState(false);
  const [confluenceMonitorChecking, setConfluenceMonitorChecking] = useState(false);
  const [confluenceMonitorActiveUpdate, setConfluenceMonitorActiveUpdate] = useState(null);
  const confluenceMonitorCheckInFlightRef = useRef(false);
  const confluenceMonitorConsentOpenRef = useRef(false);
  const confluenceMonitorApplyingRef = useRef(false);
  const confluenceMonitorActiveUpdateRef = useRef(null);
  const confluenceMonitorHandledKeysRef = useRef(new Map());
  const [azureWikiMonitoredPages, setAzureWikiMonitoredPages] = useState({});
  const [azureWikiMonitorPendingUpdates, setAzureWikiMonitorPendingUpdates] = useState([]);
  const [azureWikiMonitorConsentOpen, setAzureWikiMonitorConsentOpen] = useState(false);
  const [azureWikiMonitorApplying, setAzureWikiMonitorApplying] = useState(false);
  const [azureWikiMonitorChecking, setAzureWikiMonitorChecking] = useState(false);
  const [azureWikiMonitorAllLoading, setAzureWikiMonitorAllLoading] = useState(false);
  const [azureWikiMonitorActiveUpdate, setAzureWikiMonitorActiveUpdate] = useState(null);
  const azureWikiMonitorCheckInFlightRef = useRef(false);
  const azureWikiMonitorConsentOpenRef = useRef(false);
  const azureWikiMonitorApplyingRef = useRef(false);
  const azureWikiMonitorActiveUpdateRef = useRef(null);
  const azureWikiMonitorHandledKeysRef = useRef(new Map());
  const azureWikiAutoSyncInFlightRef = useRef(false);
  const azureWikiWikisRequestRef = useRef(0);
  const [dataLossModalOpen, setDataLossModalOpen] = useState(false);
  const [migrateChoiceModalOpen, setMigrateChoiceModalOpen] = useState(false);
  const [s3ChangeModalOpen, setS3ChangeModalOpen] = useState(false);
  const [deleteOldS3Files, setDeleteOldS3Files] = useState(null);
  const [pendingConfigValues, setPendingConfigValues] = useState(null);
  const [pendingJiraSave, setPendingJiraSave] = useState(null);
  const [pendingAzureWikiSave, setPendingAzureWikiSave] = useState(null);
  const [pendingGithubSave, setPendingGithubSave] = useState(null);
  const [pendingChangeType, setPendingChangeType] = useState(null);
  const [copyVectorsToNewCollection, setCopyVectorsToNewCollection] = useState(null);
  const [pendingEmbeddingAlsoChanged, setPendingEmbeddingAlsoChanged] = useState(false);
  const [dataLossConsent, setDataLossConsent] = useState(false);
  const [modelCatalog, setModelCatalog] = useState(null);
  const [modelCatalogLoading, setModelCatalogLoading] = useState(false);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState('openai');
  const [vectorRetentionRunning, setVectorRetentionRunning] = useState(false);
  const { pipelinePrimaryButtonStyle, pipelineSaveButtonStyle } = useMemo(() => {
    const primary = themeStyles.isLight
      ? {
          height: 36,
          minWidth: 146,
          borderRadius: 10,
          fontWeight: 600,
          border: '1px solid #E5E7EB',
          padding: '0 14px',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F3F4F6 100%)',
          color: '#4B5563',
        }
      : {
          height: 36,
          minWidth: 146,
          borderRadius: 10,
          fontWeight: 600,
          border: 'none',
          padding: '0 14px',
          boxShadow: '0 6px 18px rgba(166, 54, 214, 0.24)',
          background: 'linear-gradient(135deg, #F62E97 0%, #A636D6 52%, #5B58F6 100%)',
          color: '#ffffff',
        };
    const save = themeStyles.isLight
      ? {
          ...primary,
          border: '1px solid #BFDBFE',
          boxShadow: '0 1px 2px rgba(30, 64, 175, 0.06)',
          background: 'linear-gradient(180deg, #F0F9FF 0%, #E0F2FE 100%)',
          color: '#1E3A8A',
        }
      : {
          ...primary,
          boxShadow: '0 6px 18px rgba(82, 196, 26, 0.22)',
          background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
          color: '#ffffff',
        };
    return { pipelinePrimaryButtonStyle: primary, pipelineSaveButtonStyle: save };
  }, [themeStyles.isLight]);
  const pipelineSecondaryButtonStyle = {
    height: 36,
    minWidth: 146,
    borderRadius: 10,
    fontWeight: 600,
    padding: '0 14px',
    border: `1px solid ${themeStyles.borderColor}`,
    background: themeStyles.elevatedBg,
    color: themeStyles.textPrimary,
  };
  const pipelineRunButtonStyle = {
    ...pipelineSecondaryButtonStyle,
    border: '1px solid rgba(82, 196, 26, 0.4)',
    background: 'rgba(82, 196, 26, 0.13)',
    color: themeStyles.textPrimary,
  };
  const pipelineRefreshButtonStyle = {
    ...pipelineSecondaryButtonStyle,
    border: '1px solid rgba(64, 169, 255, 0.38)',
    background: 'rgba(64, 169, 255, 0.12)',
    color: themeStyles.textPrimary,
  };
  const pipelineRetentionButtonStyle = {
    ...pipelineSecondaryButtonStyle,
    border: '1px solid rgba(167, 139, 250, 0.42)',
    background: 'rgba(167, 139, 250, 0.12)',
    color: themeStyles.textPrimary,
  };
  const selectOptionHintStyle = {
    fontSize: '12px',
    color: themeStyles.textSecondary,
    marginTop: '2px',
  };
  const selectOptionHintStyleCompact = {
    fontSize: '12px',
    color: themeStyles.textSecondary,
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const tab = searchParams.get('tab');
    if (!tab) return;
    const allowedTabs = new Set(PROJECT_DETAIL_TAB_ORDER);
    if (allowedTabs.has(tab)) {
      setActiveTab(tab);
    } else {
      setActiveTab('details');
    }
  }, [location.search, isAdmin, isSuperAdmin]);

  const formatS3Error = (detailRaw) => {
    let detail = '';
    if (detailRaw == null) {
      return 'Unable to verify Knowledge Source settings. Please check source name and region.';
    }
    if (typeof detailRaw === 'string') {
      detail = detailRaw;
    } else if (Array.isArray(detailRaw)) {
      detail = detailRaw
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.msg === 'string') return item.msg;
          return '';
        })
        .filter(Boolean)
        .join(' ');
    } else if (typeof detailRaw === 'object' && typeof detailRaw.msg === 'string') {
      detail = detailRaw.msg;
    } else {
      try {
        detail = JSON.stringify(detailRaw);
      } catch {
        detail = String(detailRaw);
      }
    }
    if (!detail) return 'Unable to verify Knowledge Source settings. Please check source name and region.';
    if (detail.includes('Failed to access S3 bucket') && detail.includes('403')) {
      return 'Unable to access this Knowledge Source. Please check the source name, region, and that your AWS credentials have at least read (HeadBucket/List) access.';
    }
    if (detail.includes('already registered to another project')) {
      return 'This Knowledge Source is already linked to another project. Each project must use a unique source.';
    }
    const lower = detail.toLowerCase();
    const invalidBucket =
      lower.includes('invalid bucket name') ||
      lower.includes('bucket name must match') ||
      (lower.includes('parameter validation failed') && lower.includes('bucket')) ||
      (lower.includes('failed to access s3 bucket') && lower.includes('parameter validation failed'));
    if (invalidBucket) {
      return 'That name is not a valid S3 bucket. It cannot contain spaces or other disallowed characters. Use only letters, numbers, dots, hyphens, and underscores, between 3 and 63 characters, exactly as the bucket is named in the AWS S3 console.';
    }
    return detail;
  };

  // Watch guardrails enabled state at component level
  const guardrailsEnabled = Form.useWatch(['config', 'guardrails', 'enabled'], form) ?? false;
  const graphragFormEnabled = Form.useWatch(['config', 'graphrag', 'enabled'], form) ?? false;
  const graphragProjectEnabled = useMemo(() => {
    const cfg = project?.config?.graphrag || {};
    return !!cfg.enabled && !!(cfg.ingest_endpoint || '').trim();
  }, [project?.config?.graphrag]);
  const canEdit = isAdmin || isSuperAdmin;

  const documentCategoryOptions = useMemo(() => {
    const opts = [{ label: 'All users', value: 'all' }];
    (documentCategories || []).forEach((c) => {
      if (c?._id) opts.push({ label: c.name, value: c._id });
    });
    return opts;
  }, [documentCategories]);

  const categoryNameById = useMemo(() => {
    const map = { all: 'All users' };
    (documentCategories || []).forEach((c) => {
      if (c?._id) map[c._id] = c.name;
    });
    return map;
  }, [documentCategories]);

  useEffect(() => {
    if (!token || !canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.listCategories(token);
        const rows = Array.isArray(res.data?.roles)
          ? res.data.roles
          : (Array.isArray(res.data?.categories) ? res.data.categories : []);
        if (!cancelled) setDocumentCategories(rows);
      } catch {
        if (!cancelled) setDocumentCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, canEdit]);

  const isPipelineDeleting = (record) => {
    if (!record) return false;
    if (record.__ui_deleting) return true;
    const event = String(record?.event_type || record?.last_event_type || '').toLowerCase();
    const stage = String(record?.processing_stage || '').toLowerCase();
    const status = String(record?.processing_status || '').toLowerCase();
    if (stage === 'deleting') return true;
    if (event === 'deleted' && (status === 'processing' || status === 'picked')) return true;
    return false;
  };

  const getGraphRagStatus = (row) => String(row?.graphrag_status || '').toLowerCase();

  const fileMatchesPipelineStatusFilter = (row, filter) => {
    const status = String(row?.processing_status || '').toLowerCase();
    if (filter === 'all') return true;
    if (filter === 'processing') {
      return isPipelineDeleting(row) || status === 'processing' || status === 'picked';
    }
    if (filter === 'pending') return status === 'pending' && !isPipelineDeleting(row);
    if (filter === 'completed') return status === 'completed';
    if (filter === 'failed') return status === 'failed';
    return status === filter;
  };

  const activeFilePipelineFiles = useMemo(() => (
    (filePipelineFiles || []).filter(
      (row) => String(row?.processing_status || '').toLowerCase() !== 'deleted',
    )
  ), [filePipelineFiles]);

  const isFileVectorIndexed = (row) => {
    const status = String(row?.processing_status || '').toLowerCase();
    if (status !== 'completed') return false;
    const trace = row?.pipeline_trace;
    if (trace && trace.vector_push_skipped === true) {
      return true;
    }
    if (trace && Number.isFinite(trace.push_vectors_ms)) {
      return true;
    }
    // Completed pipeline rows without trace metadata still went through vector indexing.
    return true;
  };

  const graphRagEligibleFiles = useMemo(() => (
    activeFilePipelineFiles.filter(isFileVectorIndexed)
  ), [activeFilePipelineFiles]);

  const renderGraphRagFilesPanel = () => {
    // In edit mode, follow the form toggle immediately. After save (view mode),
    // follow the persisted project config. Never show files when Graph RAG is off.
    const showFiles = editMode ? !!graphragFormEnabled : !!graphragProjectEnabled;
    if (!showFiles) {
      return (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message={
            editMode && graphragProjectEnabled && !graphragFormEnabled
              ? 'Saving with Graph RAG disabled will remove this project\'s data from Neo4j.'
              : 'Enable Graph RAG to manage which files are included in the Neo4j knowledge graph.'
          }
        />
      );
    }
    return (
      <div className="graphrag-files-panel">
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
          Project files
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
          Only files indexed in the vector database appear here. Toggle on to add a file to GraphDB (Neo4j).
        </Typography.Paragraph>
        <Table
          className="graphrag-files-table"
          size="small"
          tableLayout="fixed"
          loading={filePipelineLoading}
          dataSource={graphRagEligibleFiles}
          rowKey={(record) => record?._id || record?.s3_key || record?.file_key || record?.doc_id}
          pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{
            emptyText: filePipelineLoading ? (
              <Spin />
            ) : (
              'No vector-indexed files yet. Run the RAG Pipeline through vector push before adding files to GraphDB.'
            ),
          }}
          columns={[
            {
              title: 'File Name',
              dataIndex: 'file_name',
              width: 280,
              ellipsis: true,
              render: (t, record) => (
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={t || record?.file_key || record?.doc_id || '—'}
                >
                  {t || record?.file_key || record?.doc_id || '—'}
                </span>
              ),
            },
            {
              title: 'GraphDB',
              key: 'graphrag_toggle',
              width: 132,
              align: 'center',
              render: (_, record) => {
                if (!record?._id) return '—';
                const gr = getGraphRagStatus(record);
                const checked = gr === 'ingested' || gr === 'ingesting';
                const rowKey = record._id;
                const loading = gr === 'ingesting'
                  || filePipelineRowActionLoading === `graphrag-toggle:${rowKey}`;
                return (
                  <Switch
                    size="small"
                    checked={checked}
                    loading={loading}
                    disabled={!canEdit || loading}
                    onChange={(on) => handleGraphRagFileToggle(record, on)}
                    aria-label={`Include ${record?.file_name || 'file'} in GraphDB`}
                  />
                );
              },
            },
          ]}
        />
      </div>
    );
  };

  const FILE_PIPELINE_STATUS_TAG_FILTERS = [
    { key: 'all', label: 'Total', countKey: 'total', color: 'blue' },
    { key: 'completed', label: 'Completed', countKey: 'completed', color: 'success' },
    { key: 'processing', label: 'In Progress', countKey: 'processing', color: 'processing' },
    { key: 'pending', label: 'Pending', countKey: 'pending', color: 'warning' },
    { key: 'failed', label: 'Failed', countKey: 'failed', color: 'error' },
  ];

  const formatPipelineStage = (stage, record = null) => {
    if (record && isPipelineDeleting(record)) return 'Deleting';
    const pipelineStatus = String(record?.processing_status || '').toLowerCase();
    const stageKey = String(stage || '').toLowerCase();
    if (pipelineStatus === 'completed' && stageKey === 'graphrag_ingestion') {
      return 'Completed';
    }
    if (pipelineStatus === 'completed' && getGraphRagStatus(record) === 'ingesting') {
      return 'Completed';
    }
    if (stageKey === 'syncing_document') {
      return 'Markdown conversion';
    }
    return formatStatusLabel(stage);
  };

  const formatPipelineStatusLabel = (status, record = null) => {
    if (record && isPipelineDeleting(record)) return 'Deleting';
    return formatStatusLabel(status, { fallback: 'Unknown' });
  };

  const getPipelineStatusTextColor = (status, record = null) => {
    if (record && isPipelineDeleting(record)) return '#FA8C16';
    const s = String(status || '').toLowerCase();
    if (s === 'completed') return '#52C41A';
    if (s === 'failed') return '#FF4D4F';
    if (s === 'processing' || s === 'picked') return '#1890FF';
    if (s === 'pending') return '#FAAD14';
    return themeStyles.textSecondary;
  };

  const patchFilePipelineRow = (record, patch) => {
    const rowId = record?._id;
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    setFilePipelineFiles((prev) => (prev || []).map((row) => {
      const matches = (rowId && row._id === rowId)
        || (fileKey && (row.s3_key === fileKey || row.file_key === fileKey || row.doc_id === fileKey));
      return matches ? { ...row, ...patch } : row;
    }));
  };

  const removePipelineRecordIfExists = async (fileId) => {
    if (!fileId || !token) return { removed: false, alreadyGone: true };
    try {
      await API.deleteFilePipelineFileRecord(token, projectId, fileId);
      return { removed: true, alreadyGone: false };
    } catch (e) {
      const detail = String(e?.response?.data?.detail || e?.message || '').toLowerCase();
      if (
        e?.response?.status === 400
        && (detail.includes('file record not found') || detail.includes('record not found'))
      ) {
        return { removed: false, alreadyGone: true };
      }
      throw e;
    }
  };

  const waitForPipelineFileDeleted = async (record, { timeoutMs = 600000, intervalMs = 2000 } = {}) => {
    const rowId = record?._id;
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await loadPipelineData(true);
      const rows = filePipelineFilesRef.current || [];
      const match = rows.find((row) => (
        (rowId && row._id === rowId)
        || (fileKey && (row.s3_key === fileKey || row.file_key === fileKey || row.doc_id === fileKey))
      ));
      const status = String(match?.processing_status || '').toLowerCase();
      if (!match || status === 'deleted') return { ok: true, row: match };
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return { ok: false, row: null };
  };

  const getPipelineFileSizeBytes = (record) => {
    const rawSize =
      record?.file_size ??
      record?.size_bytes ??
      record?.file_size_bytes ??
      record?.metadata?.file_size;
    const bytes = Number(rawSize);
    if (!Number.isFinite(bytes) || bytes < 0) return null;
    return bytes;
  };

  const formatFileSize = (record) => {
    const bytes = getPipelineFileSizeBytes(record);
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatResponseTime = (record) => {
    const rawMs =
      record?.response_time_ms ??
      record?.processing_time_ms ??
      record?.duration_ms ??
      record?.elapsed_ms ??
      record?.strategy_details?.elapsed_ms ??
      record?.strategy_details?.duration_ms ??
      record?.metrics?.elapsed_ms;
    const ms = Number(rawMs);
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remMinutes = minutes % 60;
      const parts = [`${hours}h`];
      if (remMinutes) parts.push(`${remMinutes}m`);
      if (seconds) parts.push(`${seconds}s`);
      return parts.join(' ');
    }
    if (minutes > 0) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const humanizeEtaMs = (valueMs, { compact = false } = {}) => {
    const ms = Number(valueMs);
    if (!Number.isFinite(ms) || ms < 0) return null;
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (compact) {
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remMinutes = minutes % 60;
        return remMinutes ? `~${hours}h ${remMinutes}m` : `~${hours}h`;
      }
      if (minutes > 0) return seconds > 0 ? `~${minutes}m ${seconds}s` : `~${minutes}m`;
      return totalSeconds <= 1 ? '<1s' : `~${seconds}s`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remMinutes = minutes % 60;
      return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
    }
    if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    return `${seconds}s`;
  };

  /** Precise duration for progress labels (always includes seconds when under 1h). */
  const formatPipelineDuration = (valueMs) => humanizeEtaMs(valueMs, { compact: false });

  const getPipelineWallElapsedMs = (record) => {
    const startedAt = record?.last_run_at || record?.updated_at;
    if (!startedAt) return null;
    const startedMs = new Date(startedAt).getTime();
    if (Number.isNaN(startedMs)) return null;
    return Math.max(0, Date.now() - startedMs);
  };

  const getPipelineRunElapsedMs = (record) => {
    const trace = record?.pipeline_trace || {};
    const stage = String(record?.processing_stage || '').toLowerCase();
    const wallElapsed = getPipelineWallElapsedMs(record);

    if (stage === 'syncing_document' || stage === 'parsing_chunks' || stage === 'parsing') {
      if (Number.isFinite(trace.marker_parse_elapsed_ms)) {
        return Number(trace.marker_parse_elapsed_ms);
      }
      if (Number.isFinite(trace.marker_poll_elapsed_s)) {
        return Number(trace.marker_poll_elapsed_s) * 1000;
      }
    }

    if (stage === 'choosing_strategy') {
      if (Number.isFinite(trace.choose_strategy_started_at_ms)) {
        return Math.max(0, Date.now() - Number(trace.choose_strategy_started_at_ms));
      }
      if (Number.isFinite(trace.choose_strategy_elapsed_ms)) {
        return Number(trace.choose_strategy_elapsed_ms);
      }
    }

    if (Number.isFinite(wallElapsed)) return wallElapsed;
    if (Number.isFinite(record?.elapsed_ms) && record.elapsed_ms > 0) {
      return Number(record.elapsed_ms);
    }
    return null;
  };

  const buildLiveTimingDisplay = ({
    stageLabel,
    elapsedMs,
    remainingMs,
    typicalTotalMs,
    extraTooltipLines = [],
  }) => {
    const elapsed = formatPipelineDuration(elapsedMs);
    if (!elapsed) return null;

    const typical = formatPipelineDuration(typicalTotalMs);
    const remainingFinite = Number.isFinite(remainingMs) ? Math.max(0, remainingMs) : null;
    const overTypical = Number.isFinite(typicalTotalMs) && elapsedMs > typicalTotalMs;
    const typicalLooksStale = overTypical && typicalTotalMs > 0 && elapsedMs > typicalTotalMs * 1.5;

    let remainingText = null;
    if (remainingFinite != null && remainingFinite > 3000) {
      const rem = humanizeEtaMs(remainingFinite, { compact: true });
      remainingText = rem ? `${rem} left` : null;
    } else if (remainingFinite != null && remainingFinite > 0) {
      remainingText = 'Almost done';
    } else if (typicalLooksStale) {
      remainingText = 'Still working…';
    } else if (overTypical) {
      remainingText = typical ? `Longer than typical (${typical})` : 'Finishing step…';
    } else if (remainingFinite === 0) {
      remainingText = 'Finishing step…';
    }

    const textParts = [`Elapsed ${elapsed}`];
    if (remainingText) textParts.push(remainingText);
    else if (typical) textParts.push(`Typical ${typical}`);

    const tooltip = [
      stageLabel ? `Stage: ${stageLabel}` : null,
      `Elapsed: ${elapsed}`,
      typical ? `Typical time for this step: ${typical}` : null,
      remainingFinite != null
        ? `Time remaining (estimate): ${remainingFinite <= 0 ? 'Finishing' : (formatPipelineDuration(remainingFinite) || '—')}`
        : null,
      ...extraTooltipLines,
    ].filter(Boolean).join('\n');

    return { text: textParts.join(' · '), tooltip };
  };

  const getParserEtaInfo = (record) => {
    const trace = record?.pipeline_trace || {};
    const stage = String(trace.marker_stage || record?.processing_stage || '').toLowerCase();
    const isParsingStage = stage === 'syncing_document' || stage === 'parsing_chunks' || stage === 'parsing';
    if (!isParsingStage) return null;
    const queueEtaMs = Number.isFinite(trace.marker_queue_eta_ms) ? Number(trace.marker_queue_eta_ms) : null;
    const parseEtaMs = Number.isFinite(trace.marker_parse_eta_ms) ? Number(trace.marker_parse_eta_ms) : null;
    let totalEtaMs = Number.isFinite(trace.marker_total_eta_ms) ? Number(trace.marker_total_eta_ms) : null;
    const parseElapsedMs = Number.isFinite(trace.marker_parse_elapsed_ms)
      ? Number(trace.marker_parse_elapsed_ms)
      : (
        Number.isFinite(trace.marker_poll_elapsed_s)
          ? Number(trace.marker_poll_elapsed_s) * 1000
          : null
      );
    const pagesTotal = Number.isFinite(trace.marker_total_pages) ? Number(trace.marker_total_pages) : null;
    const pagesDone = Number.isFinite(trace.marker_pages_processed)
      ? Number(trace.marker_pages_processed)
      : (Number.isFinite(trace.marker_current_page) ? Math.max(0, Number(trace.marker_current_page) - 1) : null);
    const progressFromPages = Number.isFinite(pagesTotal) && pagesTotal > 0 && Number.isFinite(pagesDone)
      ? Math.max(0, Math.min(100, (Number(pagesDone) / Number(pagesTotal)) * 100.0))
      : null;
    const splitTotal = Number.isFinite(trace.pdf_split_total) ? Number(trace.pdf_split_total) : null;
    const splitDone = Number.isFinite(trace.pdf_split_completed) ? Number(trace.pdf_split_completed) : null;
    const progressFromSplit = Number.isFinite(splitTotal) && splitTotal > 0 && Number.isFinite(splitDone)
      ? Math.max(0, Math.min(100, (Number(splitDone) / Number(splitTotal)) * 100.0))
      : null;
    const progressPct = Number.isFinite(trace.marker_progress_pct)
      ? Number(trace.marker_progress_pct)
      : (progressFromPages ?? progressFromSplit);
    const queuePosition = Number.isFinite(trace.marker_queue_position) ? Number(trace.marker_queue_position) : null;
    const confidence = typeof trace.marker_eta_confidence === 'string' && trace.marker_eta_confidence.trim()
      ? trace.marker_eta_confidence.trim()
      : null;
    const parseCompleteByPages = Number.isFinite(pagesTotal) && Number.isFinite(pagesDone) && pagesDone >= pagesTotal;
    const parseCompleteBySplits = Number.isFinite(splitTotal) && Number.isFinite(splitDone) && splitDone >= splitTotal;
    const parseCompleteByPct = Number.isFinite(progressPct) && progressPct >= 99.5;
    if (parseCompleteByPages || parseCompleteBySplits || parseCompleteByPct) return null;

    // Fallback remaining estimate when backend ETA is unavailable.
    if (!Number.isFinite(totalEtaMs) && Number.isFinite(parseElapsedMs) && Number.isFinite(progressPct) && progressPct > 0 && progressPct < 100) {
      const estTotalMs = parseElapsedMs / (progressPct / 100.0);
      totalEtaMs = Math.max(0, estTotalMs - parseElapsedMs);
    }
    let remainingMs = totalEtaMs ?? parseEtaMs ?? queueEtaMs;
    const elapsedMs = Number.isFinite(parseElapsedMs)
      ? parseElapsedMs
      : getPipelineRunElapsedMs(record);
    if (!Number.isFinite(elapsedMs) && !Number.isFinite(remainingMs)) return null;

    let typicalTotalMs = Number.isFinite(elapsedMs) && Number.isFinite(remainingMs)
      ? Number(elapsedMs) + Number(remainingMs)
      : (Number.isFinite(remainingMs) ? Number(remainingMs) : null);

    // Drop stale parser ETAs once elapsed time has clearly exceeded the original estimate.
    if (
      Number.isFinite(typicalTotalMs)
      && Number.isFinite(elapsedMs)
      && elapsedMs > typicalTotalMs * 1.25
    ) {
      remainingMs = null;
      typicalTotalMs = null;
    }

    const stageLabel = stage === 'waiting_for_slot'
      ? 'Waiting for parser slot'
      : 'Parsing document';

    const extraTooltipLines = [
      `Queue wait (estimate): ${humanizeEtaMs(queueEtaMs) || '—'}${Number.isFinite(queuePosition) ? ` (position ${queuePosition})` : ''}`,
      `Parse time (estimate): ${humanizeEtaMs(parseEtaMs) || '—'}${confidence ? ` (${confidence})` : ''}`,
      Number.isFinite(progressPct) ? `Progress: ${Math.round(progressPct)}%` : null,
    ].filter(Boolean);

    return buildLiveTimingDisplay({
      stageLabel,
      elapsedMs: elapsedMs || 0,
      remainingMs,
      typicalTotalMs,
      extraTooltipLines,
    });
  };

  const getStrategyEtaInfo = (record) => {
    const stage = String(record?.processing_stage || '').toLowerCase();
    const status = String(record?.processing_status || '').toLowerCase();
    if (!['processing', 'picked'].includes(status) || stage !== 'choosing_strategy') return null;
    const trace = record?.pipeline_trace || {};
    const startedAtMs = Number.isFinite(trace.choose_strategy_started_at_ms)
      ? Number(trace.choose_strategy_started_at_ms)
      : null;
    const estTotalMs = Number.isFinite(trace.choose_strategy_est_total_ms)
      ? Number(trace.choose_strategy_est_total_ms)
      : null;
    if (!Number.isFinite(estTotalMs) || estTotalMs <= 0) return null;
    let elapsedMs = 0;
    if (Number.isFinite(startedAtMs)) {
      elapsedMs = Math.max(0, Date.now() - startedAtMs);
    } else if (Number.isFinite(trace.choose_strategy_elapsed_ms)) {
      elapsedMs = Math.max(0, Number(trace.choose_strategy_elapsed_ms));
    } else {
      const runElapsed = getPipelineRunElapsedMs(record);
      if (Number.isFinite(runElapsed)) elapsedMs = runElapsed;
    }
    const remainingMs = Math.max(0, estTotalMs - elapsedMs);
    const estimateExpired = elapsedMs > estTotalMs * 1.5;
    return buildLiveTimingDisplay({
      stageLabel: 'Choosing chunking strategy',
      elapsedMs,
      remainingMs: estimateExpired ? null : remainingMs,
      typicalTotalMs: estimateExpired ? null : estTotalMs,
      extraTooltipLines: [
        Number.isFinite(trace.choose_strategy_est_samples)
          ? `Based on ${Math.max(0, Math.trunc(trace.choose_strategy_est_samples))} recent similar files`
          : null,
      ].filter(Boolean),
    });
  };

  const formatPipelineTraceLines = (trace) => {
    if (!trace || typeof trace !== 'object') return '';
    const lines = [];
    if (Number.isFinite(trace.sync_total_ms)) {
      lines.push(`Markdown conversion total: ${formatResponseTime({ processing_time_ms: trace.sync_total_ms })}`);
    }
    if (Number.isFinite(trace.guardrails_ms)) {
      lines.push(`Guardrails/uploads: ${formatResponseTime({ processing_time_ms: trace.guardrails_ms })}`);
    }
    if (Number.isFinite(trace.delete_existing_artifacts_ms)) {
      lines.push(`Cleanup old artifacts: ${formatResponseTime({ processing_time_ms: trace.delete_existing_artifacts_ms })}`);
    }
    const splitTotal = trace.pdf_split_total;
    const splitDone = trace.pdf_split_completed;
    if (Number.isFinite(splitTotal) && splitTotal > 1) {
      const done = Number.isFinite(splitDone) ? splitDone : splitTotal;
      lines.push(`PDF Marker splits: ${done}/${splitTotal}`);
    }
    if (Number.isFinite(trace.marker_progress_pct)) {
      lines.push(`Marker progress: ${Math.max(0, Math.min(100, Number(trace.marker_progress_pct))).toFixed(0)}%`);
    }
    if (Number.isFinite(trace.marker_current_page) && Number.isFinite(trace.marker_total_pages)) {
      lines.push(`Marker pages: ${trace.marker_current_page}/${trace.marker_total_pages}`);
    } else if (Number.isFinite(trace.marker_pages_processed) && Number.isFinite(trace.marker_total_pages)) {
      lines.push(`Marker pages: ${trace.marker_pages_processed}/${trace.marker_total_pages}`);
    }
    if (typeof trace.marker_status_text === 'string' && trace.marker_status_text.trim()) {
      lines.push(`Marker status: ${trace.marker_status_text.trim()}`);
    }
    if (Number.isFinite(trace.markdown_prep_ms)) {
      const cache = trace.markdown_prep_cache_hit ? ' (cached)' : '';
      lines.push(`Markdown prep${cache}: ${formatResponseTime({ processing_time_ms: trace.markdown_prep_ms })}`);
    }
    if (Number.isFinite(trace.markdown_chunk_stage_ms)) {
      const cache = trace.markdown_chunk_cache_hit ? ' (cached)' : '';
      lines.push(`Markdown at chunking${cache}: ${formatResponseTime({ processing_time_ms: trace.markdown_chunk_stage_ms })}`);
    }
    if (Number.isFinite(trace.choose_strategy_wall_ms)) {
      lines.push(`Strategy selection (wall): ${formatResponseTime({ processing_time_ms: trace.choose_strategy_wall_ms })}`);
    }
    if (Number.isFinite(trace.choose_strategy_est_total_ms)) {
      lines.push(`Strategy ETA baseline: ${humanizeEtaMs(trace.choose_strategy_est_total_ms) || '—'}`);
    }
    if (Number.isFinite(trace.chunk_generation_wall_ms)) {
      lines.push(`Chunk generation (wall): ${formatResponseTime({ processing_time_ms: trace.chunk_generation_wall_ms })}`);
    }
    if (Number.isFinite(trace.embed_chunks_ms)) {
      lines.push(`Embeddings: ${formatResponseTime({ processing_time_ms: trace.embed_chunks_ms })}`);
    }
    if (Number.isFinite(trace.push_vectors_ms)) {
      lines.push(`Vector push: ${formatResponseTime({ processing_time_ms: trace.push_vectors_ms })}`);
    }
    const markerQueue = Number.isFinite(trace.marker_queue_wait_total_ms)
      ? trace.marker_queue_wait_total_ms
      : trace.marker_queue_wait_ms;
    const markerParse = Number.isFinite(trace.marker_parse_elapsed_total_ms)
      ? trace.marker_parse_elapsed_total_ms
      : trace.marker_parse_elapsed_ms;
    const markerTotal = Number.isFinite(trace.marker_total_elapsed_total_ms)
      ? trace.marker_total_elapsed_total_ms
      : trace.marker_total_elapsed_ms;
    if (Number.isFinite(markerQueue)) {
      lines.push(`Marker queue wait: ${formatResponseTime({ processing_time_ms: markerQueue })}`);
    }
    if (Number.isFinite(markerParse)) {
      lines.push(`Marker parse: ${formatResponseTime({ processing_time_ms: markerParse })}`);
    }
    if (Number.isFinite(markerTotal)) {
      lines.push(`Marker total: ${formatResponseTime({ processing_time_ms: markerTotal })}`);
    }
    if (Number.isFinite(trace.marker_estimated_images_count)) {
      lines.push(`Estimated images: ${Math.max(0, Math.trunc(trace.marker_estimated_images_count))}`);
    }
    if (Number.isFinite(trace.marker_image_complexity_score)) {
      lines.push(`Image complexity score: ${Number(trace.marker_image_complexity_score).toFixed(2)}`);
    }
    if (Number.isFinite(trace.pipeline_total_ms)) {
      lines.push(`Total pipeline: ${formatResponseTime({ processing_time_ms: trace.pipeline_total_ms })}`);
    }
    return lines.join('\n');
  };

  const parsePipelineDetailRows = (detail) => {
    const rows = String(detail || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const idx = line.indexOf(':');
        if (idx === -1) {
          return { label: 'Detail', value: line, raw: line };
        }
        return {
          label: line.slice(0, idx).trim(),
          value: line.slice(idx + 1).trim(),
          raw: line,
        };
      });

    const groups = {
      summary: [],
      sync: [],
      marker: [],
      processing: [],
      other: [],
    };

    rows.forEach((row) => {
      const key = row.label.toLowerCase();
      if (key.includes('total pipeline')) {
        groups.summary.push(row);
      } else if (
        key.startsWith('markdown conversion')
        || key.startsWith('sync')
        || key.includes('guardrails/uploads')
        || key.includes('cleanup old artifacts')
      ) {
        groups.sync.push(row);
      } else if (key.startsWith('marker') || key.includes('pdf marker')) {
        groups.marker.push(row);
      } else if (
        key.includes('markdown') ||
        key.includes('strategy') ||
        key.includes('chunk') ||
        key.includes('embedding') ||
        key.includes('vector push')
      ) {
        groups.processing.push(row);
      } else {
        groups.other.push(row);
      }
    });

    return groups;
  };

  const getPipelineProgressDetail = (record) => {
    if ((record?.processing_status || '').toLowerCase() === 'deleted') {
      return 'File deleted from source.';
    }
    const explicit = typeof record?.processing_detail === 'string' ? record.processing_detail.trim() : '';
    const trace = record?.pipeline_trace || {};
    const totalPages = Number.isFinite(trace.marker_total_pages) ? Number(trace.marker_total_pages) : null;
    const pagesProcessed = Number.isFinite(trace.marker_pages_processed)
      ? Number(trace.marker_pages_processed)
      : null;
    const currentPage = Number.isFinite(trace.marker_current_page) ? Number(trace.marker_current_page) : null;
    const pagesDone = pagesProcessed ?? (Number.isFinite(currentPage) ? Math.max(0, currentPage - 1) : null);
    const pagesInProgress = Number.isFinite(totalPages) && Number.isFinite(pagesDone) && pagesDone < totalPages ? 1 : 0;
    const pagesSummary = Number.isFinite(totalPages) && Number.isFinite(pagesDone)
      ? `Pages done: ${pagesDone} | In progress: ${pagesInProgress} | Total: ${totalPages}`
      : '';
    const traceLines = formatPipelineTraceLines(record?.pipeline_trace);
    if (explicit) {
      if ((record?.processing_status || '').toLowerCase() === 'processing' && pagesSummary) {
        return `${explicit}\n${pagesSummary}`;
      }
      return explicit;
    }
    if (traceLines) return traceLines;
    const stage = record?.processing_stage;
    if (!stage) return '';
    const defaults = {
      queued: 'Waiting for worker pickup.',
      syncing_document: 'Converting document to markdown.',
      applying_guardrails: 'Running pre-embedding content guardrails.',
      guardrail_blocked: 'Document blocked by pre-embedding guardrails.',
      choosing_strategy: 'Choosing chunking strategy.',
      generating_chunks: 'Generating chunks.',
      parsing_chunks: 'Parsing document chunks.',
      generating_embeddings: 'Generating embeddings.',
      pushing_vectors: 'Pushing vectors to vector database.',
      graphrag_ingestion: 'Sending document to GraphRAG / Neo4j.',
      completed: 'Processing completed.',
      failed: 'Processing failed.',
    };
    return defaults[stage] || '';
  };

  const hasFinalPipelineMetrics = (record) => {
    const status = record?.processing_status;
    return status === 'completed' || status === 'failed' || status === 'deleted';
  };

  const getPipelineCostSortValue = (record) => {
    if (!hasFinalPipelineMetrics(record)) return null;
    const v = record?.processing_cost_usd ?? record?.cost_tracking?.total_usd;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    return null;
  };

  const getPipelineLastRunSortValue = (record) => {
    if (!hasFinalPipelineMetrics(record)) return null;
    const v = record?.last_run_at;
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  };

  const getShortPipelineDetail = (record) => {
    const full = getPipelineProgressDetail(record);
    if (!full) return '';
    const line = full.split('\n').find((l) => l.trim()) || '';
    if ((record?.processing_status || '').toLowerCase() === 'processing') return line;
    if (line.length <= 90) return line;
    return `${line.slice(0, 87)}...`;
  };

  const getPipelineTotalTimeMs = (record) => {
    const trace = record?.pipeline_trace || {};
    const candidates = [
      trace.pipeline_total_ms,
      record?.processing_time_ms,
      record?.response_time_ms,
      record?.elapsed_ms,
    ];
    for (const value of candidates) {
      const ms = Number(value);
      if (Number.isFinite(ms) && ms >= 0) return ms;
    }
    return null;
  };

  const getPipelineTotalTimeLabel = (record) => {
    const ms = getPipelineTotalTimeMs(record);
    if (!Number.isFinite(ms)) return '';
    return `Total time: ${formatResponseTime({ processing_time_ms: ms })}`;
  };

  /** Primary line under the progress bar: elapsed + remaining (in flight) or total time (done). */
  const getProgressUnderBarLabel = (record) => {
    if (isPipelineDeleting(record)) {
      const detail = String(record?.processing_detail || '').trim();
      return {
        text: detail || 'Removing file and indexed data…',
        tooltip: null,
      };
    }
    const st = String(record?.processing_status || '').toLowerCase();
    if (st === 'pending') {
      return { text: 'Not started yet.', tooltip: null };
    }

    const parserEta = getParserEtaInfo(record);
    if (parserEta) return parserEta;

    const strategyEta = getStrategyEtaInfo(record);
    if (strategyEta) return strategyEta;

    if (st === 'processing' || st === 'picked') {
      const stage = formatPipelineStage(record?.processing_stage, record);
      const elapsedMs = getPipelineRunElapsedMs(record);
      const live = Number.isFinite(elapsedMs)
        ? buildLiveTimingDisplay({
          stageLabel: stage || 'Processing',
          elapsedMs,
          remainingMs: null,
          typicalTotalMs: null,
        })
        : null;
      if (live) return live;
    }

    const totalTimeLabel = getPipelineTotalTimeLabel(record);
    if (totalTimeLabel) {
      return { text: totalTimeLabel, tooltip: 'Total wall-clock time for the last pipeline run.' };
    }

    return null;
  };

  const getSlideCountLabel = (record) => {
    const trace = record?.pipeline_trace || {};
    const totalFromTrace = Number.isFinite(trace.marker_total_pages) ? Number(trace.marker_total_pages) : null;
    const totalFromProgressTrace = Number.isFinite(trace.progress_total_pages)
      ? Number(trace.progress_total_pages)
      : null;
    const totalFromMeta = Number.isFinite(record?.page_count) ? Number(record.page_count) : null;
    const total = totalFromTrace ?? totalFromProgressTrace ?? totalFromMeta;
    if (!Number.isFinite(total) || total <= 0) return '';
    const ext = String(record?.file_name || record?.file_key || '').toLowerCase();
    const noun = ext.endsWith('.ppt') || ext.endsWith('.pptx') ? 'Slides' : 'Pages';
    return `${noun}: ${Math.trunc(total)}`;
  };

  const getPageDonePendingLabel = (record) => {
    const trace = record?.pipeline_trace || {};
    const stage = String(record?.processing_stage || '').toLowerCase();
    const isParseStage = stage === 'syncing_document' || stage === 'parsing_chunks';
    if (!isParseStage) return '';

    const total = Number.isFinite(trace.marker_total_pages)
      ? Number(trace.marker_total_pages)
      : (Number.isFinite(trace.progress_total_pages) ? Number(trace.progress_total_pages) : null);
    if (!Number.isFinite(total) || total <= 0) return '';

    const processedFromPages = Number.isFinite(trace.marker_pages_processed)
      ? Number(trace.marker_pages_processed)
      : (Number.isFinite(trace.marker_current_page) ? Math.max(0, Number(trace.marker_current_page) - 1) : 0);
    const processedFromPct = Number.isFinite(trace.marker_progress_pct)
      ? Math.round((Number(trace.marker_progress_pct) / 100) * Number(total))
      : null;
    const processed = Number.isFinite(processedFromPct)
      ? Math.max(processedFromPages, Number(processedFromPct))
      : processedFromPages;

    const done = Math.max(0, Math.min(Math.trunc(processed), Math.trunc(total)));
    const pending = Math.max(0, Math.trunc(total) - done);
    return `Done: ${done} | Pending: ${pending}`;
  };

  const filePipelineFilesMatchingToolbarFilters = useMemo(() => {
    const q = filePipelineSearch.trim().toLowerCase();
    return activeFilePipelineFiles.filter((row) => {
      const sourceKey = inferPipelineRowSourceKey(row);
      if (tableSourceFilterKey && sourceKey !== tableSourceFilterKey) return false;
      if (tableSourceFilterKey === 'jira' && jiraProjectFilter !== '__all__') {
        if (inferJiraProjectKeyFromRow(row) !== jiraProjectFilter) return false;
      }
      if (tableSourceFilterKey === 'confluence' && confluenceSpaceFilter !== '__all__') {
        if (inferConfluenceSpaceKeyFromRow(row) !== confluenceSpaceFilter) return false;
      }
      if (tableSourceFilterKey === 'jira' && jiraIssueStatusFilter !== '__all__') {
        const issueStatus = String(getJiraPipelineIssueStatus(row) || '').trim().toLowerCase();
        if (issueStatus !== jiraIssueStatusFilter.toLowerCase()) return false;
      }
      if (tableSourceFilterKey === 'jira' && jiraIssueTypeFilter !== '__all__') {
        const issueType = String(getJiraPipelineIssueType(row) || '').trim().toLowerCase();
        if (issueType !== jiraIssueTypeFilter.toLowerCase()) return false;
      }
      if (!q) return true;
      const roleId = row?.role_id || row?.category_id;
      const haystack = [
        row?.file_name,
        row?.file_key,
        row?.doc_id,
        row?.processing_status,
        row?.processing_stage,
        row?.event_type,
        row?.chosen_strategy,
        row?.source_meta?.sync_folder_label,
        row?.source_meta?.space_key,
        inferConfluenceSpaceKeyFromRow(row),
        getJiraPipelineIssueType(row),
        getJiraPipelineIssueStatus(row),
        roleId ? (categoryNameById[String(roleId)] || roleId) : null,
        formatPipelineStatusLabel(row?.processing_status, row),
        formatPipelineStage(row?.processing_stage, row),
        formatStatusLabel(row?.event_type),
        formatStatusLabel(row?.chosen_strategy),
      ];
      return haystack.some((part) => part != null && String(part).toLowerCase().includes(q));
    });
  }, [
    activeFilePipelineFiles,
    filePipelineSearch,
    tableSourceFilterKey,
    jiraProjectFilter,
    jiraIssueStatusFilter,
    jiraIssueTypeFilter,
    confluenceSpaceFilter,
    categoryNameById,
  ]);

  const filteredFilePipelineFiles = useMemo(() => (
    filePipelineFilesMatchingToolbarFilters.filter((row) => (
      fileMatchesPipelineStatusFilter(row, filePipelineStatusFilter)
    ))
  ), [filePipelineFilesMatchingToolbarFilters, filePipelineStatusFilter]);

  const jiraRowsForTable = useMemo(
    () => activeFilePipelineFiles.filter((row) => isJiraPipelineRow(row)),
    [activeFilePipelineFiles],
  );

  const jiraProjectFilterOptions = useMemo(() => {
    const keys = new Set();
    jiraRowsForTable.forEach((row) => {
      const key = inferJiraProjectKeyFromRow(row);
      if (key) keys.add(key);
    });
    return [
      { value: '__all__', label: 'All Jira projects' },
      ...[...keys].sort((a, b) => a.localeCompare(b)).map((key) => ({ value: key, label: key })),
    ];
  }, [jiraRowsForTable]);

  const confluenceRowsForTable = useMemo(
    () => activeFilePipelineFiles.filter((row) => isConfluencePipelineRow(row)),
    [activeFilePipelineFiles],
  );

  const confluenceSpaceFilterOptions = useMemo(() => {
    const keys = new Set();
    const labels = {};
    confluenceRowsForTable.forEach((row) => {
      const key = inferConfluenceSpaceKeyFromRow(row);
      if (!key) return;
      keys.add(key);
      const name = String(row?.source_meta?.space_name || '').trim();
      if (name && !labels[key]) labels[key] = name;
    });
    normalizeConfluenceScopesFromConfig(project?.config?.confluence || {}).forEach((scope) => {
      const key = String(scope?.space_key || '').trim();
      if (!key) return;
      keys.add(key);
      if (scope.space_name && !labels[key]) labels[key] = scope.space_name;
    });
    (confluenceSpaces || []).forEach((space) => {
      const key = String(space?.key || '').trim();
      if (!key) return;
      keys.add(key);
      if (space.name && !labels[key]) labels[key] = space.name;
    });
    return [
      { value: '__all__', label: 'All Space keys' },
      ...[...keys].sort((a, b) => a.localeCompare(b)).map((key) => ({
        value: key,
        label: labels[key] && labels[key] !== key ? `${key} — ${labels[key]}` : key,
      })),
    ];
  }, [confluenceRowsForTable, project?.config?.confluence, confluenceSpaces]);

  const jiraStatusFilterOptions = useMemo(() => {
    const statuses = new Set();
    jiraRowsForTable.forEach((row) => {
      const status = String(getJiraPipelineIssueStatus(row) || '').trim();
      if (status) statuses.add(status);
    });
    return [
      { value: '__all__', label: 'All Jira statuses' },
      ...[...statuses].sort((a, b) => a.localeCompare(b)).map((status) => ({
        value: status.toLowerCase(),
        label: status,
      })),
    ];
  }, [jiraRowsForTable]);

  const jiraIssueTypeFilterOptions = useMemo(() => {
    const issueTypes = new Set();
    jiraRowsForTable.forEach((row) => {
      const issueType = String(getJiraPipelineIssueType(row) || '').trim();
      if (issueType) issueTypes.add(issueType);
    });
    return [
      { value: '__all__', label: 'All Jira issue types' },
      ...[...issueTypes].sort((a, b) => a.localeCompare(b)).map((issueType) => ({
        value: issueType.toLowerCase(),
        label: issueType,
      })),
    ];
  }, [jiraRowsForTable]);

  useEffect(() => {
    if (jiraProjectFilterOptions.some((opt) => opt.value === jiraProjectFilter)) return;
    setJiraProjectFilter('__all__');
  }, [jiraProjectFilterOptions, jiraProjectFilter]);

  useEffect(() => {
    if (confluenceSpaceFilterOptions.some((opt) => opt.value === confluenceSpaceFilter)) return;
    setConfluenceSpaceFilter('__all__');
  }, [confluenceSpaceFilterOptions, confluenceSpaceFilter]);

  useEffect(() => {
    if (jiraStatusFilterOptions.some((opt) => opt.value === jiraIssueStatusFilter)) return;
    setJiraIssueStatusFilter('__all__');
  }, [jiraStatusFilterOptions, jiraIssueStatusFilter]);

  useEffect(() => {
    if (jiraIssueTypeFilterOptions.some((opt) => opt.value === jiraIssueTypeFilter)) return;
    setJiraIssueTypeFilter('__all__');
  }, [jiraIssueTypeFilterOptions, jiraIssueTypeFilter]);

  useEffect(() => {
    if (tableSourceFilterKey === 'jira') return;
    setJiraProjectFilter('__all__');
    setJiraIssueTypeFilter('__all__');
    setJiraIssueStatusFilter('__all__');
  }, [tableSourceFilterKey]);

  useEffect(() => {
    if (tableSourceFilterKey === 'confluence') return;
    setConfluenceSpaceFilter('__all__');
  }, [tableSourceFilterKey]);

  const filePipelineFiltersActive = Boolean(
    filePipelineSearchInput.trim()
      || filePipelineSearch.trim()
      || filePipelineStatusFilter !== 'all'
      || tableSourceFilterKey !== ''
      || (tableSourceFilterKey === 'jira' && jiraProjectFilter !== '__all__')
      || (tableSourceFilterKey === 'jira' && jiraIssueStatusFilter !== '__all__')
      || (tableSourceFilterKey === 'jira' && jiraIssueTypeFilter !== '__all__')
      || (tableSourceFilterKey === 'confluence' && confluenceSpaceFilter !== '__all__'),
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilePipelineSearch(filePipelineSearchInput);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [filePipelineSearchInput]);

  useEffect(() => {
    const onResize = () => {
      setFilePipelineCompactView(window.innerWidth < FILE_PIPELINE_COMPACT_BREAKPOINT);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const validKeys = new Set(
      (filteredFilePipelineFiles || [])
        .map((row) => row?._id)
        .filter((id) => typeof id === 'string' && id.trim()),
    );
    setSelectedFilePipelineRowKeys((prev) => (prev || []).filter((k) => validKeys.has(k)));
  }, [filteredFilePipelineFiles]);

  const filePipelineCounts = useMemo(() => (
    filePipelineFilesMatchingToolbarFilters.reduce(
      (acc, row) => {
        const status = String(row?.processing_status || '').toLowerCase();
        acc.total += 1;
        if (status === 'completed') acc.completed += 1;
        else if (isPipelineDeleting(row) || status === 'processing' || status === 'picked') acc.processing += 1;
        else if (status === 'failed') acc.failed += 1;
        else acc.pending += 1;
        return acc;
      },
      { total: 0, completed: 0, processing: 0, failed: 0, pending: 0 },
    )
  ), [filePipelineFilesMatchingToolbarFilters]);

  const pipelineScheduleDirty = useMemo(() => {
    const sched = filePipelineSchedule || {};
    const intervalUnit = pipelineScheduleIntervalUnit || 'hours';
    const intervalValue = Number(pipelineScheduleIntervalValue || 1);
    return (
      intervalUnit !== (sched.interval_unit || 'hours')
      || intervalValue !== Number(sched.interval_value ?? 1)
    );
  }, [
    filePipelineSchedule,
    pipelineScheduleIntervalUnit,
    pipelineScheduleIntervalValue,
  ]);

  const guardsConfig = {
    NSFWText: {
      label: 'NSFW Text',
      parameters: [
        { name: 'threshold', label: 'Threshold', type: 'float', default: 0.8 },
        { name: 'validation_method', label: 'Validation Method', type: 'select', default: 'sentence', options: ['sentence', 'full'] },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'exception', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'] }
      ]
    },
    RestrictToTopic: {
      label: 'Restrict To Topic',
      parameters: [
        { name: 'valid_topics', label: 'Valid Topics', type: 'tags', default: [] },
        { name: 'invalid_topics', label: 'Invalid Topics', type: 'tags', default: [] },
        { name: 'disable_llm', label: 'Disable LLM', type: 'boolean', default: false },
        { name: 'disable_classifier', label: 'Disable Classifier', type: 'boolean', default: true },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'exception', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'] }
      ]
    },
    DetectPII: {
      label: 'Detect PII',
      parameters: [
        { name: 'entities', label: 'PII Entities', type: 'multiselect', default: ['EMAIL_ADDRESS', 'PHONE_NUMBER'], options: ['EMAIL_ADDRESS', 'PHONE_NUMBER', 'CREDIT_CARD', 'SSN', 'IP_ADDRESS', 'PERSON', 'LOCATION', 'DATE_TIME'] },
        { name: 'pii_score_threshold', label: 'Confidence Threshold', type: 'float', default: 0.7 },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'noop', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'] }
      ]
    }
  };

  const renderGuardConfig = (guardName, type) => {
    const guard = guardsConfig[guardName];
    if (!guard) return null;

    return (
      <div key={guardName} style={{ 
        marginBottom: '16px', 
        padding: '12px', 
        background: 'rgba(148, 22, 127, 0.05)', 
        borderRadius: '4px', 
        border: '1px solid rgba(148, 22, 127, 0.2)' 
      }}>
        <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>{guard.label}</h4>
        {guard.parameters.map(param => {
          const fieldName = ['config', 'guardrails', `${type}_guards_config`, guardName, param.name];
          
          // Determine initial value based on parameter type and guard name
          let initialValue = param.default;
          if (param.name === 'on_fail') {
            if (guardName === 'DetectPII') {
              initialValue =
                type === 'pre_embedding' || type === 'context' ? 'fix' : 'noop';
            } else {
              initialValue = 'exception';
            }
          } else if (param.name === 'entities' && guardName === 'DetectPII') {
            initialValue = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
          }
          
          if (param.type === 'float') {
            return (
              <Form.Item key={param.name} label={param.label} name={fieldName} initialValue={initialValue}>
                <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            );
          } else if (param.type === 'select') {
            return (
              <Form.Item key={param.name} label={param.label} name={fieldName} initialValue={initialValue}>
                <Select>
                  {param.options.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
                </Select>
              </Form.Item>
            );
          } else if (param.type === 'multiselect') {
            return (
              <Form.Item key={param.name} label={param.label} name={fieldName} initialValue={initialValue}>
                <Select mode="multiple">
                  {param.options.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
                </Select>
              </Form.Item>
            );
          } else if (param.type === 'boolean') {
            return (
              <Form.Item key={param.name} label={param.label} name={fieldName} valuePropName="checked" initialValue={initialValue}>
                <Switch />
              </Form.Item>
            );
          } else if (param.type === 'tags') {
            return (
              <Form.Item key={param.name} label={param.label} name={fieldName} initialValue={initialValue}>
                <Select mode="tags" placeholder="Type and press enter" />
              </Form.Item>
            );
          }
          return null;
        })}
      </div>
    );
  };

  useEffect(() => {
    const ac = new AbortController();
    const fetchProject = async () => {
      setLoading(true);
      try {
        const response = await API.getProject(projectId, { signal: ac.signal, token });
        if (ac.signal.aborted) return;
        const inputGuardNames = response.data.config.guardrails?.input_guards?.map(g => g.guard_name) || [];
        const preEmbeddingGuardNames = response.data.config.guardrails?.pre_embedding_guards?.map(g => g.guard_name) || [];
        const contextGuardNames = response.data.config.guardrails?.context_guards?.map(g => g.guard_name) || [];
        const outputGuardNames = response.data.config.guardrails?.output_guards?.map(g => g.guard_name) || [];
        
        const inputGuardsConfig = {};
        response.data.config.guardrails?.input_guards?.forEach(guard => {
          inputGuardsConfig[guard.guard_name] = guard.parameters;
        });
        
        const outputGuardsConfig = {};
        response.data.config.guardrails?.output_guards?.forEach(guard => {
          outputGuardsConfig[guard.guard_name] = guard.parameters;
        });
        const preEmbeddingGuardsConfig = {};
        response.data.config.guardrails?.pre_embedding_guards?.forEach(guard => {
          preEmbeddingGuardsConfig[guard.guard_name] = guard.parameters;
        });
        const contextGuardsConfig = {};
        response.data.config.guardrails?.context_guards?.forEach(guard => {
          contextGuardsConfig[guard.guard_name] = guard.parameters;
        });
        
        // Extract raw collection name from formatted name for display in form
        const username = localStorage.getItem('username') || '';
        const formattedCollectionName = response.data.config.vector_store?.collection_name;
        const rawCollectionName = formattedCollectionName 
          ? extractRawCollectionName(formattedCollectionName, username)
          : formattedCollectionName;
        
        const projectData = {
          id: response.data.project_id,
          name: response.data.config.name,
          description: response.data.config.description,
          deployed: isProjectChatAvailable(response.data),
          config: {
            ...response.data.config,
            s3: normalizeProjectS3ForForm(response.data.config?.s3),
            vector_store: {
              ...response.data.config.vector_store,
              collection_name: rawCollectionName // Store raw name for form display
            },
            guardrails: {
              ...response.data.config.guardrails,
              input_guards: inputGuardNames,
              pre_embedding_guards: preEmbeddingGuardNames,
              context_guards: contextGuardNames,
              output_guards: outputGuardNames,
              input_guards_config: inputGuardsConfig,
              pre_embedding_guards_config: preEmbeddingGuardsConfig,
              context_guards_config: contextGuardsConfig,
              output_guards_config: outputGuardsConfig
            }
          },
          meta: response.data.meta,
          // Keep original formatted name for comparison
          _originalFormattedCollectionName: formattedCollectionName
        };
        if (ac.signal.aborted) return;
        setProject(projectData);
        form.setFieldsValue(projectData);
        setSelectedLlmProvider(projectData?.config?.llm?.provider || 'openai');
        setSelectedInputGuards(inputGuardNames);
        setSelectedPreEmbeddingGuards(preEmbeddingGuardNames);
        setSelectedContextGuards(contextGuardNames);
        setSelectedOutputGuards(outputGuardNames);
        setChunkingStrategy(projectData.config?.chunking?.strategy || 'recursive');

        if (token) {
          try {
            const rbacRes = await API.getRbacProjectByCoreId(token, projectId);
            if (ac.signal.aborted) return;
            setRbacInfo(rbacRes.data);
          } catch (e) {
            if (e.response?.status !== 404) console.error('RBAC project fetch:', e);
            if (!ac.signal.aborted) setRbacInfo(null);
          }
        } else {
          if (!ac.signal.aborted) setRbacInfo(null);
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
    return () => ac.abort();
  }, [projectId, form, token]);

  useEffect(() => {
    const ac = new AbortController();
    const loadModelCatalog = async () => {
      try {
        setModelCatalogLoading(true);
        const res = await API.getModelCatalog({ signal: ac.signal });
        setModelCatalog(res.data || null);
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setModelCatalog(null);
      } finally {
        setModelCatalogLoading(false);
      }
    };
    loadModelCatalog();
    return () => ac.abort();
  }, []);

  const knowledgeProvider =
    project?.config?.knowledge_provider
    || (project?.config?.jira?.credential_id || project?.config?.jira?.jira_base_url ? 'jira' : null)
    || (project?.config?.github?.credential_id ? 'github' : null)
    || (project?.config?.confluence?.credential_id ? 'confluence' : null)
    || (project?.config?.azure_wiki?.credential_id ? 'azure_wiki' : 's3');
  const isConfluenceKnowledgeSource = knowledgeProvider === 'confluence';
  const isGithubKnowledgeSource = knowledgeProvider === 'github';
  const isJiraKnowledgeSource = knowledgeProvider === 'jira';
  const isAzureWikiKnowledgeSource = knowledgeProvider === 'azure_wiki';
  const isPollKnowledgeSource =
    isConfluenceKnowledgeSource || isGithubKnowledgeSource || isJiraKnowledgeSource || isAzureWikiKnowledgeSource;

  const hasAzureWikiKnowledgeSourceEdits = useMemo(() => {
    if (!editMode || activeTab !== 's3' || !isAzureWikiKnowledgeSource) return false;
    const savedOrg = String(project?.config?.azure_wiki?.organization_url || '').trim().replace(/\/+$/, '');
    const currentOrg = String(azureWikiOrgUrlWatch || '').trim().replace(/\/+$/, '');
    const orgChanged = Boolean(currentOrg) && currentOrg !== savedOrg;
    const credentialChanged = Boolean(
      azureWikiCredentialId
      && azureWikiCredentialId !== String(project?.config?.azure_wiki?.credential_id || '').trim(),
    );
    return azureWikiVerified || orgChanged || credentialChanged;
  }, [
    editMode,
    activeTab,
    isAzureWikiKnowledgeSource,
    azureWikiVerified,
    azureWikiCredentialId,
    azureWikiOrgUrlWatch,
    project?.config?.azure_wiki?.organization_url,
    project?.config?.azure_wiki?.credential_id,
  ]);
  const hasGithubKnowledgeSourceEdits = useMemo(() => {
    if (!editMode || activeTab !== 's3' || !isGithubKnowledgeSource) return false;
    const savedBase = String(project?.config?.github?.github_base_url || '').trim().replace(/\/+$/, '');
    const currentBase = String(githubBaseUrlWatch || '').trim().replace(/\/+$/, '');
    const baseChanged = Boolean(currentBase) && currentBase !== savedBase;
    const credentialChanged = Boolean(
      githubCredentialId
      && githubCredentialId !== String(project?.config?.github?.credential_id || '').trim(),
    );
    return githubVerified || baseChanged || credentialChanged;
  }, [
    editMode,
    activeTab,
    isGithubKnowledgeSource,
    githubVerified,
    githubCredentialId,
    githubBaseUrlWatch,
    project?.config?.github?.github_base_url,
    project?.config?.github?.credential_id,
  ]);
  const knowledgeSourceHasTabEdits = hasTabEdits || hasAzureWikiKnowledgeSourceEdits || hasGithubKnowledgeSourceEdits;

  const configuredKnowledgeSources = useMemo(() => {
    const cfg = project?.config || {};
    const list = [];
    const pushUnique = (key, label) => {
      if (!list.some((item) => item.key === key)) {
        list.push({ key, label });
      }
    };

    const jiraScopes = normalizeJiraScopesFromConfig(cfg.jira || {});
    if (cfg.jira?.jira_base_url || cfg.jira?.credential_id || jiraScopes.length > 0) {
      pushUnique('jira', 'Jira');
    }
    if (cfg.confluence?.base_url || cfg.confluence?.space_key || cfg.confluence?.credential_id) {
      pushUnique('confluence', 'Confluence');
    }
    if (cfg.azure_wiki?.organization_url || cfg.azure_wiki?.credential_id) {
      pushUnique('azure_wiki', 'Azure Wiki');
    }
    if (cfg.github?.owner || cfg.github?.repo || cfg.github?.credential_id || normalizeGithubScopesFromConfig(cfg.github || {}).length) {
      pushUnique('github', 'GitHub');
    }
    if ((knowledgeProvider || 's3') === 's3' && (cfg.s3?.bucket || cfg.s3?.name)) {
      pushUnique('s3', 'S3');
    }
    if (!list.length) {
      pushUnique(knowledgeProvider || 's3', (knowledgeProvider || 's3').toUpperCase());
    }
    return list;
  }, [project?.config, knowledgeProvider]);

  useEffect(() => {
    if (!tableSourceFilterKey) return;
    if (configuredKnowledgeSources.some((source) => source.key === tableSourceFilterKey)) return;
    setTableSourceFilterKey('');
  }, [configuredKnowledgeSources, tableSourceFilterKey]);

  const jiraProjectSelectOptions = useMemo(() => {
    const merged = mergeJiraProjectOptions(
      jiraProjects,
      pipelineConfiguredProjectKeys.map((key) => ({ key, name: key })),
    );
    return merged.map((p) => ({
      value: p.key,
      label: `${p.key} — ${p.name}`,
    }));
  }, [jiraProjects, pipelineConfiguredProjectKeys]);

  const filterJiraProjectOption = useCallback((input, option) => {
    const q = String(input || '').trim().toLowerCase();
    if (!q) return true;
    const label = String(option?.label ?? '').toLowerCase();
    const value = String(option?.value ?? '').toLowerCase();
    return label.includes(q) || value.includes(q);
  }, []);

  const jiraNeedsProjectSetup = useMemo(() => {
    if (!isJiraKnowledgeSource) return false;
    const jiraCfg = project?.config?.jira || {};
    const scopes = normalizeJiraScopesFromConfig(jiraCfg);
    if (scopes.length === 0) return true;
    const hasCredential = Boolean(String(jiraCfg.credential_id || '').trim());
    const hasBaseUrl = Boolean(String(jiraCfg.jira_base_url || '').trim());
    return !(hasCredential && hasBaseUrl);
  }, [isJiraKnowledgeSource, project?.config?.jira]);

  const openPipelineJiraSetup = useCallback(() => {
    setSourceSettingsActiveKey('jira');
    setPipelineJiraPanelOpen(true);
  }, []);

  useEffect(() => {
    if (!pipelineJiraPanelOpen) return;
    if (configuredKnowledgeSources.some((item) => item.key === sourceSettingsActiveKey)) return;
    setSourceSettingsActiveKey(configuredKnowledgeSources[0]?.key || 'jira');
  }, [pipelineJiraPanelOpen, configuredKnowledgeSources, sourceSettingsActiveKey]);

  useEffect(() => {
    if (!jiraNeedsProjectSetup) {
      jiraSetupPromptedRef.current = false;
    }
  }, [jiraNeedsProjectSetup]);

  useEffect(() => {
    if (activeTab !== 'file_pipeline' || !jiraNeedsProjectSetup || jiraSetupPromptedRef.current) return;
    jiraSetupPromptedRef.current = true;
    setPipelineJiraPanelOpen(true);
  }, [activeTab, jiraNeedsProjectSetup]);

  useEffect(() => {
    if (!isJiraKnowledgeSource) return;
    const scopes = normalizeJiraScopesFromConfig(project?.config?.jira || {});
    const keys = scopes.map((scope) => String(scope?.project_key || '').trim()).filter(Boolean);
    setPipelineConfiguredProjectKeys(keys);
    setPipelineJiraScopeByKey(scopeMapFromJiraScopes(scopes));
    const monitored = project?.config?.jira?.monitored_issues;
    setJiraMonitoredIssues(monitored && typeof monitored === 'object' ? monitored : {});
  }, [isJiraKnowledgeSource, project?.config?.jira]);

  useEffect(() => {
    if (!isConfluenceKnowledgeSource) return;
    const monitored = project?.config?.confluence?.monitored_pages;
    setConfluenceMonitoredPages(monitored && typeof monitored === 'object' ? monitored : {});
    const scopes = normalizeConfluenceScopesFromConfig(project?.config?.confluence || {});
    const keys = scopes.map((scope) => String(scope?.space_key || '').trim()).filter(Boolean);
    const scopeMap = scopeMapFromConfluenceScopes(scopes);
    setPipelineConfiguredConfluenceSpaceKeys(keys);
    setPipelineConfluenceScopeByKey(scopeMap);
    setPipelineSelectedConfluenceSpaceKey((prev) => {
      const nextKey = (prev && keys.includes(prev)) ? prev : (keys[0] || '');
      setSelectedConfluenceFolderIds(nextKey ? (scopeMap[nextKey]?.folderIds || []) : []);
      return nextKey;
    });
  }, [isConfluenceKnowledgeSource, project?.config?.confluence]);

  useEffect(() => {
    if (!pipelineConfiguredConfluenceSpaceKeys.length) {
      if (pipelineSelectedConfluenceSpaceKey) setPipelineSelectedConfluenceSpaceKey('');
      return;
    }
    if (
      !pipelineSelectedConfluenceSpaceKey
      || !pipelineConfiguredConfluenceSpaceKeys.includes(pipelineSelectedConfluenceSpaceKey)
    ) {
      setPipelineSelectedConfluenceSpaceKey(pipelineConfiguredConfluenceSpaceKeys[0]);
    }
  }, [pipelineConfiguredConfluenceSpaceKeys, pipelineSelectedConfluenceSpaceKey]);

  useEffect(() => {
    if (!isAzureWikiKnowledgeSource) return;
    const scopes = normalizeAzureWikiScopesFromConfig(project?.config?.azure_wiki || {});
    const wikiIds = scopes.map((scope) => scope.wiki_id).filter(Boolean);
    const projects = Array.from(new Set(scopes.map((scope) => scope.project).filter(Boolean)));
    const scopeMap = scopeMapFromAzureWikiScopes(scopes);
    setPipelineConfiguredAzureWikiWikiIds(wikiIds);
    setPipelineAzureWikiScopeByWikiId(scopeMap);
    setPipelineSelectedAzureWikiProjects((prev) => (prev.length ? prev : projects));
    const monitored = project?.config?.azure_wiki?.monitored_pages;
    setAzureWikiMonitoredPages(monitored && typeof monitored === 'object' ? monitored : {});
  }, [isAzureWikiKnowledgeSource, project?.config?.azure_wiki]);

  useEffect(() => {
    if (!pipelineConfiguredAzureWikiWikiIds.length) {
      if (pipelineSelectedAzureWikiWikiId) setPipelineSelectedAzureWikiWikiId('');
      return;
    }
    if (
      !pipelineSelectedAzureWikiWikiId
      || !pipelineConfiguredAzureWikiWikiIds.includes(pipelineSelectedAzureWikiWikiId)
    ) {
      setPipelineSelectedAzureWikiWikiId(pipelineConfiguredAzureWikiWikiIds[0]);
    }
  }, [pipelineConfiguredAzureWikiWikiIds, pipelineSelectedAzureWikiWikiId]);

  useEffect(() => {
    if (!isGithubKnowledgeSource) return;
    const scopes = normalizeGithubScopesFromConfig(project?.config?.github || {});
    const repoKeys = Array.from(new Set(scopes.map((scope) => `${scope.owner}/${scope.repo}`)));
    const scopeKeys = scopes.map((scope) => scope.key);
    const scopeMap = {};
    scopes.forEach((scope) => {
      scopeMap[scope.key] = { ...scope };
    });
    setPipelineConfiguredGithubRepoKeys(repoKeys);
    setPipelineConfiguredGithubScopeKeys(scopeKeys);
    setPipelineGithubScopeByScopeKey(scopeMap);
    setPipelineSelectedGithubRepoKey((prev) => (repoKeys.includes(prev) ? prev : (repoKeys[0] || '')));
    setPipelineSelectedGithubScopeKey((prev) => (scopeKeys.includes(prev) ? prev : (scopeKeys[0] || '')));
  }, [isGithubKnowledgeSource, project?.config?.github]);

  const jiraMonitoredIssueKeys = useMemo(() => (
    new Set(
      Object.entries(jiraMonitoredIssues || {})
        .filter(([, meta]) => meta?.enabled !== false)
        .map(([key]) => String(key).trim())
        .filter(Boolean),
    )
  ), [jiraMonitoredIssues]);

  const jiraMonitorActiveRow = useMemo(() => {
    if (!jiraMonitorActiveUpdate) return null;
    const activeIssueKey = String(jiraMonitorActiveUpdate.issue_key || '').trim();
    const activeFileId = String(jiraMonitorActiveUpdate.file_id || '').trim();
    return (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowIssueKey = String(row?.source_meta?.issue_key || '').trim();
      if (activeFileId && rowId && rowId === activeFileId) return true;
      if (activeIssueKey && rowIssueKey && rowIssueKey === activeIssueKey) return true;
      return false;
    }) || null;
  }, [jiraMonitorActiveUpdate, activeFilePipelineFiles]);

  const jiraLiveProcessingRow = useMemo(() => {
    const jiraRows = (activeFilePipelineFiles || []).filter((row) => isJiraPipelineRow(row));
    const inFlight = jiraRows.filter((row) => {
      const st = String(row?.processing_status || '').toLowerCase();
      return st === 'pending' || st === 'processing' || st === 'picked';
    });
    if (!inFlight.length) return null;
    const toEpoch = (row) => {
      const raw = row?.updated_at || row?.source_meta?.updated_at || row?.created_at || null;
      const ms = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(ms) ? ms : 0;
    };
    return [...inFlight].sort((a, b) => toEpoch(b) - toEpoch(a))[0] || null;
  }, [activeFilePipelineFiles]);

  const isJiraMonitorQueuedRow = useCallback((record) => {
    if (!jiraMonitorActiveUpdate) return false;
    const st = String(record?.processing_status || '').toLowerCase();
    if (st !== 'pending') return false;
    const issueKey = String(jiraMonitorActiveUpdate.issue_key || '').trim();
    const fileId = String(jiraMonitorActiveUpdate.file_id || '').trim();
    const rowIssueKey = String(record?.source_meta?.issue_key || '').trim();
    const rowId = String(record?._id || '').trim();
    if (issueKey && rowIssueKey === issueKey) return true;
    if (fileId && rowId === fileId) return true;
    return false;
  }, [jiraMonitorActiveUpdate]);

  const matchesJiraMonitorActiveRow = useCallback((record, active) => {
    if (!record || !active) return false;
    const issueKey = String(active.issue_key || '').trim();
    const fileId = String(active.file_id || '').trim();
    const rowIssueKey = String(record?.source_meta?.issue_key || '').trim();
    const rowId = String(record?._id || '').trim();
    if (issueKey && rowIssueKey === issueKey) return true;
    if (fileId && rowId === fileId) return true;
    return false;
  }, []);

  const jiraMonitorIntervalMs = useMemo(() => {
    const configured = Number(project?.config?.jira?.monitor_interval_seconds);
    if (Number.isFinite(configured) && configured >= 60) {
      return Math.trunc(configured) * 1000;
    }
    return 5 * 60 * 1000;
  }, [project?.config?.jira?.monitor_interval_seconds]);

  const handleToggleJiraIssueMonitor = useCallback(async (issueKey, enabled, scopeProjectKey) => {
    if (!token || !projectId || !canEdit) return;
    const key = String(issueKey || '').trim();
    if (!key) return;
    try {
      const res = await API.setJiraIssueMonitor(token, projectId, {
        issue_key: key,
        project_key: scopeProjectKey || pipelineSelectedJiraProjectKey || undefined,
        enabled,
      });
      const monitored = res?.data?.monitored_issues || {};
      setJiraMonitoredIssues(monitored);
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              jira: {
                ...(prev.config?.jira || {}),
                monitored_issues: monitored,
              },
            },
          }
          : prev
      ));
      message.success(enabled ? `Monitoring enabled for ${key}` : `Monitoring stopped for ${key}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not update monitor for ${key}.`);
    }
  }, [
    token,
    projectId,
    canEdit,
    pipelineSelectedJiraProjectKey,
    message,
  ]);

  const mergeJiraMonitorChanges = useCallback((changes) => {
    if (!Array.isArray(changes) || !changes.length) return;
    const now = Date.now();
    const fresh = changes.filter((row) => {
      const key = String(row?.issue_key || '').trim();
      if (!key) return false;
      const handledUntil = jiraMonitorHandledKeysRef.current.get(key);
      return !(handledUntil && handledUntil > now);
    });
    if (!fresh.length) return;
    setJiraMonitorPendingUpdates((prev) => {
      const byKey = new Map((prev || []).map((row) => [row.issue_key, row]));
      fresh.forEach((row) => {
        if (row?.issue_key) byKey.set(row.issue_key, row);
      });
      return [...byKey.values()];
    });
    setJiraMonitorConsentOpen(true);
  }, []);

  const handleCheckJiraMonitoredUpdatesNow = useCallback(async () => {
    if (!token || !projectId) return;
    if (!jiraMonitoredIssueKeys.size) {
      message.info('Enable monitoring on at least one selected issue first.');
      return;
    }
    if (jiraMonitorCheckInFlightRef.current) return;
    jiraMonitorCheckInFlightRef.current = true;
    setJiraMonitorChecking(true);
    try {
      const res = await API.checkJiraMonitoredUpdates(token, projectId);
      const changes = res?.data?.changes || [];
      if (changes.length) {
        mergeJiraMonitorChanges(changes);
      } else {
        message.success('No content changes found on monitored issues.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(
        detail
          ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE })
          : 'Failed to check monitored issues for updates.',
      );
    } finally {
      jiraMonitorCheckInFlightRef.current = false;
      setJiraMonitorChecking(false);
    }
  }, [
    token,
    projectId,
    jiraMonitoredIssueKeys.size,
    message,
    mergeJiraMonitorChanges,
  ]);

  const confluenceMonitoredPageKeys = useMemo(() => (
    new Set(
      Object.entries(confluenceMonitoredPages || {})
        .filter(([, meta]) => meta?.enabled !== false)
        .map(([key]) => String(key).trim())
        .filter(Boolean),
    )
  ), [confluenceMonitoredPages]);

  const confluenceMonitorIntervalMs = useMemo(() => {
    const configured = Number(project?.config?.confluence?.monitor_interval_seconds);
    if (Number.isFinite(configured) && configured >= 60) {
      return Math.trunc(configured) * 1000;
    }
    return 5 * 60 * 1000;
  }, [project?.config?.confluence?.monitor_interval_seconds]);

  const confluenceMonitorActiveRow = useMemo(() => {
    if (!confluenceMonitorActiveUpdate) return null;
    const activePageId = String(confluenceMonitorActiveUpdate.page_id || '').trim();
    const activeFileId = String(confluenceMonitorActiveUpdate.file_id || '').trim();
    return (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowPageId = String(row?.source_meta?.page_id || '').trim();
      if (activeFileId && rowId && rowId === activeFileId) return true;
      if (activePageId && rowPageId && rowPageId === activePageId) return true;
      return false;
    }) || null;
  }, [confluenceMonitorActiveUpdate, activeFilePipelineFiles]);

  const isConfluenceMonitorQueuedRow = useCallback((record) => {
    if (!confluenceMonitorActiveUpdate) return false;
    const st = String(record?.processing_status || '').toLowerCase();
    if (st !== 'pending') return false;
    const pageId = String(confluenceMonitorActiveUpdate.page_id || '').trim();
    const fileId = String(confluenceMonitorActiveUpdate.file_id || '').trim();
    const rowPageId = String(record?.source_meta?.page_id || '').trim();
    const rowId = String(record?._id || '').trim();
    if (pageId && rowPageId === pageId) return true;
    if (fileId && rowId === fileId) return true;
    return false;
  }, [confluenceMonitorActiveUpdate]);

  const handleToggleConfluencePageMonitor = useCallback(async (pageId, enabled, spaceKey) => {
    if (!token || !projectId || !canEdit) return;
    const key = String(pageId || '').trim();
    if (!key) return;
    try {
      const res = await API.setConfluencePageMonitor(token, projectId, {
        page_id: key,
        space_key: spaceKey || undefined,
        enabled,
      });
      const monitored = res?.data?.monitored_pages || {};
      setConfluenceMonitoredPages(monitored);
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              confluence: {
                ...(prev.config?.confluence || {}),
                monitored_pages: monitored,
              },
            },
          }
          : prev
      ));
      message.success(enabled ? `Monitoring enabled for page ${key}` : `Monitoring stopped for page ${key}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not update monitor for page ${key}.`);
    }
  }, [token, projectId, canEdit, message]);

  const mergeConfluenceMonitorChanges = useCallback((changes) => {
    if (!Array.isArray(changes) || !changes.length) return;
    const now = Date.now();
    const fresh = changes.filter((row) => {
      const key = String(row?.page_id || '').trim();
      if (!key) return false;
      const handledUntil = confluenceMonitorHandledKeysRef.current.get(key);
      return !(handledUntil && handledUntil > now);
    });
    if (!fresh.length) return;
    setConfluenceMonitorPendingUpdates((prev) => {
      const byKey = new Map((prev || []).map((row) => [row.page_id, row]));
      fresh.forEach((row) => {
        if (row?.page_id) byKey.set(row.page_id, row);
      });
      return [...byKey.values()];
    });
    setConfluenceMonitorConsentOpen(true);
  }, []);

  const handleCheckConfluenceMonitoredUpdatesNow = useCallback(async () => {
    if (!token || !projectId) return;
    if (!confluenceMonitoredPageKeys.size) {
      message.info('Enable monitoring on at least one Confluence page first.');
      return;
    }
    if (confluenceMonitorCheckInFlightRef.current) return;
    confluenceMonitorCheckInFlightRef.current = true;
    setConfluenceMonitorChecking(true);
    try {
      const res = await API.checkConfluenceMonitoredUpdates(token, projectId);
      const changes = res?.data?.changes || [];
      if (changes.length) {
        mergeConfluenceMonitorChanges(changes);
      } else {
        message.success('No content changes found on monitored pages.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(
        detail
          ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE })
          : 'Failed to check monitored pages for updates.',
      );
    } finally {
      confluenceMonitorCheckInFlightRef.current = false;
      setConfluenceMonitorChecking(false);
    }
  }, [
    token,
    projectId,
    confluenceMonitoredPageKeys.size,
    message,
    mergeConfluenceMonitorChanges,
  ]);

  const azureWikiMonitoredPageKeys = useMemo(() => (
    new Set(
      Object.entries(azureWikiMonitoredPages || {})
        .filter(([, meta]) => meta?.enabled !== false)
        .map(([key]) => String(key).trim())
        .filter(Boolean),
    )
  ), [azureWikiMonitoredPages]);

  const azureWikiMonitoredPageKeysForSelectedWiki = useMemo(() => {
    const wikiId = String(pipelineSelectedAzureWikiWikiId || '').trim();
    return new Set(
      Object.entries(azureWikiMonitoredPages || {})
        .filter(([, meta]) => {
          if (meta?.enabled === false) return false;
          if (!wikiId) return true;
          return String(meta?.wiki_id || '').trim() === wikiId;
        })
        .map(([key]) => String(key).trim())
        .filter(Boolean),
    );
  }, [azureWikiMonitoredPages, pipelineSelectedAzureWikiWikiId]);

  const azureWikiMonitorIntervalMs = useMemo(() => {
    const configured = Number(project?.config?.azure_wiki?.monitor_interval_seconds);
    if (Number.isFinite(configured) && configured >= 60) {
      return Math.trunc(configured) * 1000;
    }
    return 5 * 60 * 1000;
  }, [project?.config?.azure_wiki?.monitor_interval_seconds]);

  const azureWikiMonitorActiveRow = useMemo(() => {
    if (!azureWikiMonitorActiveUpdate) return null;
    const activePageId = String(azureWikiMonitorActiveUpdate.page_id || '').trim();
    const activeFileId = String(azureWikiMonitorActiveUpdate.file_id || '').trim();
    return (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowPageId = String(row?.source_meta?.page_id || '').trim();
      if (activeFileId && rowId && rowId === activeFileId) return true;
      if (activePageId && rowPageId && rowPageId === activePageId) return true;
      return false;
    }) || null;
  }, [azureWikiMonitorActiveUpdate, activeFilePipelineFiles]);

  const applyAzureWikiPageMonitorTargets = useCallback(async (targetRows, enabled, { silent = false } = {}) => {
    if (!token || !projectId || !canEdit) {
      return { successCount: 0, failures: [], monitored: azureWikiMonitoredPages || {} };
    }

    const uniqueTargets = [];
    const seen = new Set();
    (targetRows || []).forEach((row) => {
      const pageId = String(row?.pageId || row?.pageNode?.id || '').trim();
      if (!pageId || seen.has(pageId)) return;
      seen.add(pageId);
      uniqueTargets.push({ pageId, pageNode: row.pageNode || row });
    });
    if (!uniqueTargets.length) {
      return { successCount: 0, failures: [], monitored: azureWikiMonitoredPages || {} };
    }

    const anchorNode = uniqueTargets[0].pageNode || {};
    const wikiId = String(
      anchorNode?.wiki_id
      || pipelineSelectedAzureWikiWikiId
      || '',
    ).trim();
    const adoProject = String(
      anchorNode?.project
      || pipelineAzureWikiScopeByWikiId[wikiId]?.project
      || (azureWikiWikis || []).find((w) => String(w.id) === wikiId)?.project
      || '',
    ).trim();

    let monitored = azureWikiMonitoredPages || {};
    let successCount = 0;
    const failures = [];

    for (const target of uniqueTargets) {
      const key = target.pageId;
      const pagePath = String(target.pageNode?.path || target.pageNode?.page_path || '').trim();
      try {
        const res = await API.setAzureWikiPageMonitor(token, projectId, {
          page_id: key,
          wiki_id: wikiId || undefined,
          project: adoProject || undefined,
          page_path: pagePath || undefined,
          enabled,
        });
        monitored = res?.data?.monitored_pages || monitored;
        successCount += 1;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        failures.push(detail ? String(detail) : key);
      }
    }

    if (successCount > 0) {
      setAzureWikiMonitoredPages(monitored);
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              azure_wiki: {
                ...(prev.config?.azure_wiki || {}),
                monitored_pages: monitored,
              },
            },
          }
          : prev
      ));
    }

    if (!silent) {
      if (failures.length === 0) {
        message.success(
          enabled
            ? `Monitoring enabled for ${successCount} page${successCount === 1 ? '' : 's'}.`
            : `Monitoring stopped for ${successCount} page${successCount === 1 ? '' : 's'}.`,
        );
      } else if (successCount > 0) {
        message.warning(
          `Monitoring updated for ${successCount} page${successCount === 1 ? '' : 's'}, `
          + `but ${failures.length} failed. Sync selected pages first, then retry.`,
        );
      } else {
        const detail = failures[0];
        message.error(
          detail
            ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE })
            : 'Could not update page monitoring.',
        );
      }
    }

    return { successCount, failures, monitored };
  }, [
    token,
    projectId,
    canEdit,
    message,
    pipelineSelectedAzureWikiWikiId,
    pipelineAzureWikiScopeByWikiId,
    azureWikiWikis,
    azureWikiMonitoredPages,
  ]);

  const handleToggleAzureWikiPageMonitor = useCallback(async (
    pageId,
    enabled,
    pageNode,
    descendantNodes = [],
    options = {},
  ) => {
    const remonitorNodes = Array.isArray(options?.remonitorNodes) ? options.remonitorNodes : [];
    const disableTargets = [
      { pageId, pageNode },
      ...(Array.isArray(descendantNodes) ? descendantNodes : []).map((node) => ({
        pageId: String(node?.id || '').trim(),
        pageNode: node,
      })),
    ].filter((row) => row.pageId);

    if (enabled || !remonitorNodes.length) {
      await applyAzureWikiPageMonitorTargets(disableTargets, enabled);
      return;
    }

    // Break out of parent monitoring: disable subtree (+ ancestors), then remonitor siblings.
    const disableResult = await applyAzureWikiPageMonitorTargets(disableTargets, false, { silent: true });
    const enableTargets = remonitorNodes
      .map((node) => ({
        pageId: String(node?.id || '').trim(),
        pageNode: node,
      }))
      .filter((row) => row.pageId);
    const enableResult = enableTargets.length
      ? await applyAzureWikiPageMonitorTargets(enableTargets, true, { silent: true })
      : { successCount: 0, failures: [] };

    const successCount = (disableResult.successCount || 0) + (enableResult.successCount || 0);
    const failures = [...(disableResult.failures || []), ...(enableResult.failures || [])];
    if (failures.length === 0) {
      message.success(
        `Monitoring updated. Stopped ${disableResult.successCount || 0} page`
        + `${(disableResult.successCount || 0) === 1 ? '' : 's'}`
        + (enableResult.successCount
          ? `, kept ${enableResult.successCount} sibling page${enableResult.successCount === 1 ? '' : 's'} monitored.`
          : '.'),
      );
      return;
    }
    if (successCount > 0) {
      message.warning(
        `Monitoring partially updated (${successCount} succeeded, ${failures.length} failed). `
        + 'Sync selected pages first, then retry.',
      );
      return;
    }
    message.error(
      failures[0]
        ? humanizeApiError(failures[0], { context: ERROR_CONTEXT.PROJECT_UPDATE })
        : 'Could not update page monitoring.',
    );
  }, [applyAzureWikiPageMonitorTargets, message]);

  const handleMonitorAllAzureWikiPages = useCallback(async (enabled, batches = []) => {
    if (!batches.length) return;
    setAzureWikiMonitorAllLoading(true);
    try {
      const targetRows = [];
      batches.forEach(({ rootNode, descendantNodes }) => {
        if (rootNode) {
          targetRows.push({ pageId: rootNode.id, pageNode: rootNode });
        }
        (descendantNodes || []).forEach((node) => {
          targetRows.push({ pageId: node.id, pageNode: node });
        });
      });
      const { successCount, failures } = await applyAzureWikiPageMonitorTargets(
        targetRows,
        enabled,
        { silent: true },
      );
      if (failures.length === 0) {
        message.success(
          enabled
            ? `Monitoring enabled for ${successCount} selected page${successCount === 1 ? '' : 's'}.`
            : `Monitoring stopped for ${successCount} selected page${successCount === 1 ? '' : 's'}.`,
        );
        return;
      }
      if (successCount > 0) {
        message.warning(
          `Monitoring updated for ${successCount} page${successCount === 1 ? '' : 's'}, `
          + `but ${failures.length} failed. Sync selected pages first, then retry.`,
        );
        return;
      }
      const detail = failures[0];
      message.error(
        detail
          ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE })
          : 'Could not update page monitoring.',
      );
    } finally {
      setAzureWikiMonitorAllLoading(false);
    }
  }, [applyAzureWikiPageMonitorTargets, message]);

  const mergeAzureWikiMonitorChanges = useCallback((changes) => {
    if (!Array.isArray(changes) || !changes.length) return;
    const now = Date.now();
    const fresh = changes.filter((row) => {
      const key = String(row?.page_id || '').trim();
      if (!key) return false;
      const handledUntil = azureWikiMonitorHandledKeysRef.current.get(key);
      return !(handledUntil && handledUntil > now);
    });
    if (!fresh.length) return;
    setAzureWikiMonitorPendingUpdates((prev) => {
      const byKey = new Map((prev || []).map((row) => [row.page_id, row]));
      fresh.forEach((row) => {
        if (row?.page_id) byKey.set(row.page_id, row);
      });
      return [...byKey.values()];
    });
    setAzureWikiMonitorConsentOpen(true);
  }, []);

  const handleCheckAzureWikiMonitoredUpdatesNow = useCallback(async () => {
    if (!token || !projectId) return;
    if (!azureWikiMonitoredPageKeys.size) {
      message.info('Enable monitoring on at least one Azure Wiki page first.');
      return;
    }
    if (azureWikiMonitorCheckInFlightRef.current) return;
    azureWikiMonitorCheckInFlightRef.current = true;
    setAzureWikiMonitorChecking(true);
    try {
      const res = await API.checkAzureWikiMonitoredUpdates(token, projectId);
      const changes = res?.data?.changes || [];
      if (changes.length) {
        mergeAzureWikiMonitorChanges(changes);
      } else {
        message.success('No content changes found on monitored pages.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(
        detail
          ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE })
          : 'Failed to check monitored pages for updates.',
      );
    } finally {
      azureWikiMonitorCheckInFlightRef.current = false;
      setAzureWikiMonitorChecking(false);
    }
  }, [
    token,
    projectId,
    azureWikiMonitoredPageKeys.size,
    message,
    mergeAzureWikiMonitorChanges,
  ]);

  useEffect(() => {
    if (!pipelineConfiguredProjectKeys.length) {
      setPipelineSelectedJiraProjectKey('');
      return;
    }
    if (!pipelineSelectedJiraProjectKey
      || !pipelineConfiguredProjectKeys.includes(pipelineSelectedJiraProjectKey)) {
      setPipelineSelectedJiraProjectKey(pipelineConfiguredProjectKeys[0]);
    }
  }, [pipelineConfiguredProjectKeys, pipelineSelectedJiraProjectKey]);

  const handlePipelineProjectKeysChange = useCallback((keys) => {
    const nextKeys = (keys || []).map((k) => String(k).trim()).filter(Boolean);
    setPipelineConfiguredProjectKeys(nextKeys);
    setPipelineJiraScopeByKey((prev) => {
      const next = { ...prev };
      nextKeys.forEach((key) => {
        if (!next[key]) next[key] = { ...createDefaultJiraScope() };
      });
      return next;
    });
    if (!nextKeys.includes(pipelineSelectedJiraProjectKey)) {
      setPipelineSelectedJiraProjectKey(nextKeys[0] || '');
    }
  }, [pipelineSelectedJiraProjectKey]);

  useEffect(() => {
    if (!token || !projectId || !isConfluenceKnowledgeSource) {
      setConfluenceWebhookSetup(null);
      return undefined;
    }
    const ac = new AbortController();
    (async () => {
      try {
        const res = await API.getConfluenceWebhookSetup(token, projectId);
        if (!ac.signal.aborted) setConfluenceWebhookSetup(res.data || null);
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        if (!ac.signal.aborted) setConfluenceWebhookSetup(null);
      }
    })();
    return () => ac.abort();
  }, [token, projectId, isConfluenceKnowledgeSource]);

  const applyConfluenceFolderSelection = useCallback((folderIds, folders = confluenceFolders, spaceKey = pipelineSelectedConfluenceSpaceKey) => {
    const syncScope = buildConfluenceSyncScope(
      folderIds,
      folders,
      pipelineConfluenceScopeByKey[spaceKey]?.sync_scope
        || project?.config?.confluence?.sync_scope,
    );
    setSelectedConfluenceFolderIds(folderIds);
    if (spaceKey) {
      const spaceMeta = (confluenceSpaces || []).find((row) => String(row.key) === String(spaceKey));
      setPipelineConfluenceScopeByKey((prev) => ({
        ...prev,
        [spaceKey]: {
          ...(prev[spaceKey] || createDefaultConfluenceSpaceScope(spaceKey, spaceMeta)),
          space_key: spaceKey,
          space_id: spaceMeta?.id || prev[spaceKey]?.space_id || '',
          space_name: spaceMeta?.name || prev[spaceKey]?.space_name || spaceKey,
          sync_scope: syncScope,
          folderIds,
        },
      }));
    }
    form.setFieldsValue({
      config: {
        ...(form.getFieldValue('config') || {}),
        confluence: {
          ...(form.getFieldValue(['config', 'confluence']) || {}),
          sync_scope: syncScope,
          ...(spaceKey ? { space_key: spaceKey } : {}),
        },
      },
    });
  }, [
    confluenceFolders,
    confluenceSpaces,
    form,
    pipelineConfluenceScopeByKey,
    pipelineSelectedConfluenceSpaceKey,
    project?.config?.confluence?.sync_scope,
  ]);

  const loadConfluenceFoldersForProject = useCallback(async ({ cancelled, spaceKey } = {}) => {
    if (!token || !projectId) return;
    setConfluenceFoldersLoading(true);
    setConfluenceFoldersError('');
    try {
      const selectedSpace = String(
        spaceKey
        || pipelineSelectedConfluenceSpaceKey
        || project?.config?.confluence?.space_key
        || '',
      ).trim();
      const res = await API.listConfluenceFoldersForProject(
        token,
        projectId,
        selectedSpace ? { space_key: selectedSpace } : {},
      );
      if (cancelled?.()) return;
      const folders = res.data?.folders || [];
      const selected = res.data?.selected_folder_ids?.length
        ? res.data.selected_folder_ids
        : (
          selectedSpace === String(project?.config?.confluence?.space_key || '').trim()
            ? getConfluenceSelectedFolderIds(project?.config?.confluence?.sync_scope)
            : []
        );
      setConfluenceFolders(folders);
      setSelectedConfluenceFolderIds(selected);
      if (folders.length === 0) {
        setConfluenceFoldersError('No folders or top-level pages found in this space.');
      }
      const syncScope = buildConfluenceSyncScope(
        selected,
        folders,
        selectedSpace === String(project?.config?.confluence?.space_key || '').trim()
          ? project?.config?.confluence?.sync_scope
          : {},
      );
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          confluence: {
            ...(form.getFieldValue(['config', 'confluence']) || {}),
            sync_scope: syncScope,
            ...(selectedSpace ? { space_key: selectedSpace } : {}),
          },
        },
      });
    } catch (error) {
      if (cancelled?.()) return;
      const detail = error?.response?.data?.detail || error?.message;
      setConfluenceFoldersError(String(detail || 'Failed to load Confluence folders.'));
      setConfluenceFolders([]);
    } finally {
      if (!cancelled?.()) setConfluenceFoldersLoading(false);
    }
  }, [
    token,
    projectId,
    form,
    pipelineSelectedConfluenceSpaceKey,
    project?.config?.confluence?.sync_scope,
    project?.config?.confluence?.space_key,
  ]);

  const loadConfluenceSpaceCatalog = useCallback(async ({ cancelled } = {}) => {
    if (!token || !projectId) return;
    setConfluenceSpacesLoading(true);
    setConfluenceSpacesError('');
    try {
      const res = await API.listConfluenceSpacesForProject(token, projectId);
      if (cancelled?.()) return;
      const spaces = Array.isArray(res.data?.spaces) ? res.data.spaces : [];
      const savedScopes = normalizeConfluenceScopesFromConfig(project?.config?.confluence || {});
      const savedKeys = savedScopes.map((scope) => String(scope.space_key || '').trim()).filter(Boolean);
      const withSaved = spaces.slice();
      savedScopes.forEach((scope) => {
        if (!withSaved.some((row) => String(row.key) === scope.space_key)) {
          withSaved.unshift({
            key: scope.space_key,
            name: scope.space_name || scope.space_key,
            id: scope.space_id || '',
          });
        }
      });
      setConfluenceSpaces(withSaved);
      if (savedKeys.length) {
        setPipelineConfiguredConfluenceSpaceKeys((prev) => (prev.length ? prev : savedKeys));
        setPipelineSelectedConfluenceSpaceKey((prev) => prev || savedKeys[0] || '');
      }
      if (withSaved.length === 0) {
        setConfluenceSpacesError('No Confluence spaces were returned for this account.');
      }
    } catch (error) {
      if (cancelled?.()) return;
      const detail = error?.response?.data?.detail || error?.message;
      setConfluenceSpacesError(String(detail || 'Failed to load Confluence spaces.'));
      const savedScopes = normalizeConfluenceScopesFromConfig(project?.config?.confluence || {});
      if (savedScopes.length) {
        setConfluenceSpaces(savedScopes.map((scope) => ({
          key: scope.space_key,
          name: scope.space_name || scope.space_key,
          id: scope.space_id || '',
        })));
        const keys = savedScopes.map((scope) => scope.space_key);
        setPipelineConfiguredConfluenceSpaceKeys((prev) => (prev.length ? prev : keys));
        setPipelineSelectedConfluenceSpaceKey((prev) => prev || keys[0] || '');
      } else {
        setConfluenceSpaces([]);
      }
    } finally {
      if (!cancelled?.()) setConfluenceSpacesLoading(false);
    }
  }, [
    token,
    projectId,
    project?.config?.confluence,
  ]);

  const confluenceSpaceSelectOptions = useMemo(() => (
    (confluenceSpaces || []).map((space) => ({
      value: space.key,
      label: space.name && space.name !== space.key
        ? `${space.key} — ${space.name}`
        : space.key,
    }))
  ), [confluenceSpaces]);

  const handlePipelineConfluenceSpaceKeysChange = useCallback((nextKeysRaw) => {
    const nextKeys = (Array.isArray(nextKeysRaw) ? nextKeysRaw : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean);
    setPipelineConfiguredConfluenceSpaceKeys(nextKeys);
    setPipelineConfluenceScopeByKey((prev) => {
      const next = { ...prev };
      nextKeys.forEach((key) => {
        if (!next[key]) {
          const spaceMeta = (confluenceSpaces || []).find((row) => String(row.key) === key);
          next[key] = createDefaultConfluenceSpaceScope(key, spaceMeta);
        }
      });
      Object.keys(next).forEach((key) => {
        if (!nextKeys.includes(key)) delete next[key];
      });
      return next;
    });
    setPipelineSelectedConfluenceSpaceKey((prev) => {
      if (nextKeys.includes(prev)) return prev;
      return nextKeys[0] || '';
    });
    if (!nextKeys.length) {
      setSelectedConfluenceFolderIds([]);
      setConfluenceFolders([]);
    }
  }, [confluenceSpaces]);

  const handlePipelineConfluenceEditSpaceChange = useCallback((spaceKey) => {
    const nextKey = String(spaceKey || '').trim();
    if (!nextKey || nextKey === pipelineSelectedConfluenceSpaceKey) return;
    // Persist current edit space selection before switching.
    if (pipelineSelectedConfluenceSpaceKey) {
      applyConfluenceFolderSelection(
        selectedConfluenceFolderIds,
        confluenceFolders,
        pipelineSelectedConfluenceSpaceKey,
      );
    }
    setPipelineSelectedConfluenceSpaceKey(nextKey);
    const existing = pipelineConfluenceScopeByKey[nextKey];
    setSelectedConfluenceFolderIds(existing?.folderIds || []);
    setConfluenceFolders([]);
  }, [
    applyConfluenceFolderSelection,
    confluenceFolders,
    pipelineConfluenceScopeByKey,
    pipelineSelectedConfluenceSpaceKey,
    selectedConfluenceFolderIds,
  ]);

  const fetchConfluencePageTreeForProject = useCallback(async (spaceKey) => {
    if (!token || !projectId) {
      throw new Error('Missing project credentials for Confluence page tree.');
    }
    const selectedSpace = String(spaceKey || '').trim();
    const res = await API.listConfluencePageTreeForProject(
      token,
      projectId,
      selectedSpace ? { space_key: selectedSpace } : {},
    );
    return res.data || {};
  }, [token, projectId]);

  useEffect(() => {
    const editingKnowledgeSource = editMode && activeTab === 's3';
    const sourceSettingsConfluence = (
      pipelineJiraPanelOpen && sourceSettingsActiveKey === 'confluence'
    );
    if (!isConfluenceKnowledgeSource || !token || !projectId) return undefined;
    if (!editingKnowledgeSource && !sourceSettingsConfluence) return undefined;
    let cancelled = false;
    if (sourceSettingsConfluence) {
      void loadConfluenceSpaceCatalog({ cancelled: () => cancelled });
    }
    void loadConfluenceFoldersForProject({ cancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [
    editMode,
    isConfluenceKnowledgeSource,
    activeTab,
    pipelineJiraPanelOpen,
    sourceSettingsActiveKey,
    token,
    projectId,
    loadConfluenceFoldersForProject,
    loadConfluenceSpaceCatalog,
  ]);

  useEffect(() => {
    if (!isConfluenceKnowledgeSource) return;
    const savedKey = String(project?.config?.confluence?.space_key || '').trim();
    if (savedKey && !pipelineSelectedConfluenceSpaceKey) {
      setPipelineSelectedConfluenceSpaceKey(savedKey);
    }
  }, [isConfluenceKnowledgeSource, project?.config?.confluence?.space_key, pipelineSelectedConfluenceSpaceKey]);

  const azureWikiProjectSelectOptions = useMemo(() => (
    (azureWikiProjects || [])
      .map((p) => (typeof p === 'string' ? p : (p?.name || p?.project || '')))
      .filter(Boolean)
      .map((name) => ({ value: name, label: name }))
  ), [azureWikiProjects]);

  const azureWikiWikiSelectOptions = useMemo(() => (
    (azureWikiWikis || []).map((wiki) => ({
      value: String(wiki.id),
      label: [wiki.project, wiki.name || wiki.id].filter(Boolean).join(' / '),
    }))
  ), [azureWikiWikis]);

  const loadAzureWikiProjectsForProject = useCallback(async ({ cancelled } = {}) => {
    if (!token || !projectId) return;
    setAzureWikiProjectsLoading(true);
    setAzureWikiProjectsError('');
    try {
      const res = await API.listAzureWikiProjectsForProject(token, projectId);
      if (cancelled?.()) return;
      setAzureWikiProjects(res.data?.projects || []);
    } catch (error) {
      if (cancelled?.()) return;
      const detail = error?.response?.data?.detail ?? error?.message;
      setAzureWikiProjectsError(String(detail || 'Failed to load Azure DevOps projects.'));
      setAzureWikiProjects([]);
    } finally {
      if (!cancelled?.()) setAzureWikiProjectsLoading(false);
    }
  }, [token, projectId]);

  const loadAzureWikiWikisForProject = useCallback(async (projects) => {
    if (!token || !projectId) return;
    const projectNames = (projects || []).map((p) => String(p || '').trim()).filter(Boolean);
    const requestId = ++azureWikiWikisRequestRef.current;
    if (!projectNames.length) {
      setAzureWikiWikis([]);
      setAzureWikiWikisError('');
      return;
    }
    setAzureWikiWikisLoading(true);
    setAzureWikiWikisError('');
    try {
      const res = await API.listAzureWikiWikisForProject(token, projectId, { projects: projectNames });
      if (requestId !== azureWikiWikisRequestRef.current) return;
      const wikiErrors = Array.isArray(res.data?.errors) ? res.data.errors : [];
      const wikis = res.data?.wikis || [];
      if (wikiErrors.length) {
        const detail = wikiErrors
          .map((err) => {
            const project = String(err?.project || '').trim();
            const message = String(err?.message || '').trim();
            return project ? `${project}: ${message}` : message;
          })
          .filter(Boolean)
          .join(' ');
        setAzureWikiWikisError(detail || 'Failed to load Azure Wiki wikis for one or more projects.');
      } else {
        setAzureWikiWikisError('');
      }
      setAzureWikiWikis(wikis);
    } catch (error) {
      if (requestId !== azureWikiWikisRequestRef.current) return;
      const detail = error?.response?.data?.detail ?? error?.message;
      setAzureWikiWikisError(String(detail || 'Failed to load Azure Wiki wikis.'));
      setAzureWikiWikis([]);
    } finally {
      if (requestId === azureWikiWikisRequestRef.current) {
        setAzureWikiWikisLoading(false);
      }
    }
  }, [token, projectId]);

  const loadGithubReposForProject = useCallback(async () => {
    if (!token || !projectId) return;
    setGithubReposLoading(true);
    setGithubReposError('');
    try {
      const res = await API.listGithubReposForProject(token, projectId);
      setGithubRepos(Array.isArray(res?.data?.repos) ? res.data.repos : []);
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      setGithubReposError(String(detail || 'Failed to load GitHub repositories.'));
      setGithubRepos([]);
    } finally {
      setGithubReposLoading(false);
    }
  }, [token, projectId]);

  const loadGithubBranchesForRepo = useCallback(async (repoKey) => {
    const key = String(repoKey || '').trim();
    const [owner = '', repo = ''] = key.split('/');
    if (!token || !projectId || !owner || !repo) return;
    setGithubBranchesLoadingByRepoKey((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await API.listGithubBranchesForProject(token, projectId, { owner, repo });
      setGithubBranchesByRepoKey((prev) => ({ ...prev, [key]: Array.isArray(res?.data?.branches) ? res.data.branches : [] }));
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Failed to load branches for ${key}.`);
      setGithubBranchesByRepoKey((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setGithubBranchesLoadingByRepoKey((prev) => ({ ...prev, [key]: false }));
    }
  }, [token, projectId, message]);

  const loadGithubTreeForScope = useCallback(async (scopeKey) => {
    const key = String(scopeKey || '').trim();
    if (!key || !token || !projectId) return;
    const scope = pipelineGithubScopeByScopeKey[key] || {};
    const owner = String(scope.owner || '').trim();
    const repo = String(scope.repo || '').trim();
    const branch = String(scope.branch || 'main').trim() || 'main';
    if (!owner || !repo) return;
    setGithubTreeLoadingByScopeKey((prev) => ({ ...prev, [key]: true }));
    setGithubTreeErrorByScopeKey((prev) => ({ ...prev, [key]: '' }));
    try {
      const res = await API.listGithubTreeForProject(token, projectId, { owner, repo, branch });
      setGithubTreeByScopeKey((prev) => ({
        ...prev,
        [key]: Array.isArray(res?.data?.nodes) ? res.data.nodes : [],
      }));
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      setGithubTreeErrorByScopeKey((prev) => ({
        ...prev,
        [key]: String(detail || `Failed to load files for ${owner}/${repo}@${branch}.`),
      }));
      setGithubTreeByScopeKey((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setGithubTreeLoadingByScopeKey((prev) => ({ ...prev, [key]: false }));
    }
  }, [token, projectId, pipelineGithubScopeByScopeKey]);

  const applyGithubPathSelection = useCallback((paths) => {
    const scopeKey = pipelineSelectedGithubScopeKey;
    if (!scopeKey) return;
    const nextPaths = Array.from(new Set((paths || []).map((p) => String(p || '').trim()).filter(Boolean)));
    setPipelineGithubScopeByScopeKey((prev) => ({
      ...prev,
      [scopeKey]: {
        ...(prev[scopeKey] || {}),
        selected_paths: nextPaths,
      },
    }));
  }, [pipelineSelectedGithubScopeKey]);

  const handlePipelineAzureWikiProjectsChange = useCallback((nextProjectsRaw) => {
    const nextProjects = Array.from(new Set((nextProjectsRaw || []).map((p) => String(p).trim()).filter(Boolean)));
    setPipelineSelectedAzureWikiProjects(nextProjects);
    setPipelineConfiguredAzureWikiWikiIds([]);
    setPipelineAzureWikiScopeByWikiId({});
    setPipelineSelectedAzureWikiWikiId('');
    setAzureWikiPages([]);
    setAzureWikiWikis([]);
    setAzureWikiWikisError('');
    void loadAzureWikiWikisForProject(nextProjects);
  }, [loadAzureWikiWikisForProject]);

  const handlePipelineAzureWikiWikisChange = useCallback((nextWikiIdsRaw) => {
    const nextWikiIds = Array.from(new Set((nextWikiIdsRaw || []).map((id) => String(id).trim()).filter(Boolean)));
    setPipelineConfiguredAzureWikiWikiIds(nextWikiIds);
    setPipelineAzureWikiScopeByWikiId((prev) => {
      const next = {};
      nextWikiIds.forEach((wikiId) => {
        if (prev[wikiId]) {
          next[wikiId] = prev[wikiId];
          return;
        }
        const meta = (azureWikiWikis || []).find((w) => String(w.id) === wikiId);
        next[wikiId] = {
          project: meta?.project || '',
          wiki_id: wikiId,
          wiki_name: meta?.name || wikiId,
          page_paths: [],
        };
      });
      return next;
    });
    setPipelineSelectedAzureWikiWikiId((prev) => (nextWikiIds.includes(prev) ? prev : (nextWikiIds[0] || '')));
    if (!nextWikiIds.length) setAzureWikiPages([]);
  }, [azureWikiWikis]);

  const handlePipelineAzureWikiEditWikiChange = useCallback((wikiId) => {
    const nextKey = String(wikiId || '').trim();
    if (!nextKey || nextKey === pipelineSelectedAzureWikiWikiId) return;
    setPipelineSelectedAzureWikiWikiId(nextKey);
    setAzureWikiPages([]);
  }, [pipelineSelectedAzureWikiWikiId]);

  const applyAzureWikiPageSelection = useCallback((pagePaths, wikiId = pipelineSelectedAzureWikiWikiId) => {
    const key = String(wikiId || '').trim();
    if (!key) return;
    setPipelineAzureWikiScopeByWikiId((prev) => {
      const meta = (azureWikiWikis || []).find((w) => String(w.id) === key);
      const existing = prev[key] || {
        project: meta?.project || '',
        wiki_id: key,
        wiki_name: meta?.name || key,
      };
      return {
        ...prev,
        [key]: { ...existing, page_paths: pagePaths || [] },
      };
    });
  }, [azureWikiWikis, pipelineSelectedAzureWikiWikiId]);

  const fetchAzureWikiPageTreeForWiki = useCallback(async (wikiId) => {
    const key = String(wikiId || '').trim();
    if (!key || !token || !projectId) return;
    const meta = (azureWikiWikis || []).find((w) => String(w.id) === key);
    const projectName = meta?.project || pipelineAzureWikiScopeByWikiId[key]?.project || '';
    setAzureWikiPagesLoading(true);
    setAzureWikiPagesError('');
    try {
      const res = await API.listAzureWikiPageTreeForProject(token, projectId, {
        project: projectName,
        wiki_id: key,
      });
      setAzureWikiPages(res.data?.pages || []);
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      setAzureWikiPagesError(String(detail || 'Failed to load Azure Wiki pages.'));
      setAzureWikiPages([]);
    } finally {
      setAzureWikiPagesLoading(false);
    }
  }, [token, projectId, azureWikiWikis, pipelineAzureWikiScopeByWikiId]);

  useEffect(() => {
    const editingKnowledgeSource = editMode && activeTab === 's3';
    const sourceSettingsAzureWiki = pipelineJiraPanelOpen && sourceSettingsActiveKey === 'azure_wiki';
    if (!isAzureWikiKnowledgeSource || !token || !projectId) return undefined;
    if (!editingKnowledgeSource && !sourceSettingsAzureWiki) return undefined;
    let cancelled = false;
    void loadAzureWikiProjectsForProject({ cancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [
    isAzureWikiKnowledgeSource,
    editMode,
    activeTab,
    pipelineJiraPanelOpen,
    sourceSettingsActiveKey,
    token,
    projectId,
    loadAzureWikiProjectsForProject,
  ]);

  useEffect(() => {
    const editingKnowledgeSource = editMode && activeTab === 's3';
    const sourceSettingsAzureWiki = pipelineJiraPanelOpen && sourceSettingsActiveKey === 'azure_wiki';
    if (!isAzureWikiKnowledgeSource || !token || !projectId) return;
    if (!editingKnowledgeSource && !sourceSettingsAzureWiki) return;
    if (!pipelineSelectedAzureWikiProjects.length) {
      setAzureWikiWikis([]);
      setAzureWikiWikisError('');
      return;
    }
    void loadAzureWikiWikisForProject(pipelineSelectedAzureWikiProjects);
  }, [
    isAzureWikiKnowledgeSource,
    editMode,
    activeTab,
    pipelineJiraPanelOpen,
    sourceSettingsActiveKey,
    token,
    projectId,
    pipelineSelectedAzureWikiProjects,
    loadAzureWikiWikisForProject,
  ]);

  useEffect(() => {
    const editingKnowledgeSource = editMode && activeTab === 's3';
    const sourceSettingsGithub = pipelineJiraPanelOpen && sourceSettingsActiveKey === 'github';
    if (!isGithubKnowledgeSource || !token || !projectId) return;
    if (!editingKnowledgeSource && !sourceSettingsGithub) return;
    void loadGithubReposForProject();
  }, [
    isGithubKnowledgeSource,
    editMode,
    activeTab,
    pipelineJiraPanelOpen,
    sourceSettingsActiveKey,
    token,
    projectId,
    loadGithubReposForProject,
  ]);

  useEffect(() => {
    if (!pipelineSelectedAzureWikiWikiId) {
      setAzureWikiPages([]);
      return;
    }
    void fetchAzureWikiPageTreeForWiki(pipelineSelectedAzureWikiWikiId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineSelectedAzureWikiWikiId]);

  useEffect(() => {
    if (!pipelineSelectedGithubRepoKey) return;
    void loadGithubBranchesForRepo(pipelineSelectedGithubRepoKey);
  }, [
    pipelineSelectedGithubRepoKey,
    loadGithubBranchesForRepo,
  ]);

  useEffect(() => {
    setPipelineSelectedGithubScopeKey((prev) => {
      const repoPrefix = `${pipelineSelectedGithubRepoKey}@`;
      const repoScopeKeys = pipelineConfiguredGithubScopeKeys.filter((scopeKey) => scopeKey.startsWith(repoPrefix));
      if (!repoScopeKeys.length) return '';
      if (prev && repoScopeKeys.includes(prev)) return prev;
      return repoScopeKeys[0];
    });
  }, [pipelineSelectedGithubRepoKey, pipelineConfiguredGithubScopeKeys]);

  useEffect(() => {
    if (!pipelineSelectedGithubScopeKey) return;
    if (githubTreeByScopeKey[pipelineSelectedGithubScopeKey]?.length) return;
    void loadGithubTreeForScope(pipelineSelectedGithubScopeKey);
  }, [
    pipelineSelectedGithubScopeKey,
    githubTreeByScopeKey,
    loadGithubTreeForScope,
  ]);

  const pipelineS3Bucket =
    isPollKnowledgeSource
      ? null
      : (project?.config?.s3?.bucket || project?.config?.s3?.name || null);
  pipelineS3BucketRef.current = pipelineS3Bucket;

  useEffect(() => {
    filePipelineFilesRef.current = filePipelineFiles;
  }, [filePipelineFiles]);

  useEffect(() => {
    filePipelineScheduleRef.current = filePipelineSchedule;
  }, [filePipelineSchedule]);

  useEffect(() => {
    const sched = filePipelineSchedule || {};
    pipelineScheduleForm.setFieldsValue({
      interval_unit: sched.interval_unit || 'hours',
      interval_value: sched.interval_value ?? 1,
    });
  }, [filePipelineSchedule, pipelineScheduleForm]);

  useEffect(() => {
    if (activeTab !== 'file_pipeline') {
      setFilePipelineSettingsOpen(false);
    }
  }, [activeTab]);

  // Reusable refetch of file pipeline schedule + files (silent = true avoids loading spinner, for polling)
  const loadPipelineData = useCallback(async (silent = false, opts = {}) => {
    if (!token) return false;
    const probeS3Permissions = opts.probeS3Permissions !== false;
    const existing = filePipelineLoadInFlightRef.current;
    if (existing) {
      if (silent) return false;
      await existing;
    }
    const job = (async () => {
      try {
        if (!silent) setFilePipelineLoading(true);
        const shouldSyncKnowledgeSource = !silent && !isPollKnowledgeSource;
        const filesReq = API.listFilePipelineFiles(token, projectId, { syncS3: shouldSyncKnowledgeSource });
        const schedReq = canEdit ? API.getFilePipelineSchedule(token, projectId) : null;
        const triggerReq = canEdit ? API.getFilePipelineS3TriggerStatus(token, projectId) : null;

        const [filesRes, schedRes, triggerRes] = await Promise.allSettled([
          filesReq,
          schedReq,
          triggerReq,
        ]);

        let mergedFiles = [];
        if (filesRes.status === 'fulfilled') {
          mergedFiles = Array.isArray(filesRes.value.data?.files) ? filesRes.value.data.files : [];
        }
        const bucketNow = pipelineS3BucketRef.current;
        if (canEdit && bucketNow && !isPollKnowledgeSource) {
          try {
            const s3Res = await API.listS3Files(token, 'upload/', bucketNow);
            const s3Rows = Array.isArray(s3Res?.data?.files) ? s3Res.data.files : [];
            const normalizeName = (value) =>
              String(value || '')
                .trim()
                .toLowerCase()
                .replace(/^upload\//, '')
                .split('/')
                .pop();

            const trackedKeys = new Set();
            const trackedNames = new Set();
            (mergedFiles || []).forEach((r) => {
              const key = String(r?.s3_key || r?.file_key || r?.doc_id || '').trim();
              if (key) {
                trackedKeys.add(key);
                trackedNames.add(normalizeName(key));
              }
              const fileName = String(r?.file_name || '').trim();
              if (fileName) trackedNames.add(normalizeName(fileName));
            });
            const inferredRows = s3Rows
              .map((row) => {
                const key = String(row?.key || '').trim();
                if (!key || key === 'upload/' || key.endsWith('/')) return null;
                if (trackedKeys.has(key)) return null;
                const fileName = key.startsWith('upload/') ? key.slice('upload/'.length) : key;
                if (trackedNames.has(normalizeName(fileName))) return null;
                return {
                  file_key: key,
                  s3_key: key,
                  doc_id: key,
                  file_name: fileName,
                  event_type: 'created',
                  processing_status: 'pending',
                  processing_stage: null,
                  progress_percent: 0,
                  processing_detail: 'Pending sync from S3 event.',
                  metadata: {
                    file_size: row?.size,
                  },
                  updated_at: row?.last_modified || null,
                  created_at: row?.last_modified || null,
                  __ui_only_untracked: true,
                };
              })
              .filter(Boolean);
            mergedFiles = [...mergedFiles, ...inferredRows];
          } catch {
            // Best effort only: keep file_details data if S3 list fails.
          }
        }
        const activeUpdate = jiraMonitorActiveUpdateRef.current;
        if (activeUpdate && !activeUpdate.server_acknowledged) {
          mergedFiles = mergedFiles.map((row) => {
            if (!matchesJiraMonitorActiveRow(row, activeUpdate)) return row;
            const st = String(row?.processing_status || '').toLowerCase();
            if (st !== 'completed') return row;
            return {
              ...row,
              processing_status: 'pending',
              processing_stage: null,
              event_type: 'updated',
              progress_percent: 0,
              processing_detail: 'Re-index queued…',
            };
          });
        }
        const activeConfluenceUpdate = confluenceMonitorActiveUpdateRef.current;
        if (activeConfluenceUpdate && !activeConfluenceUpdate.server_acknowledged) {
          mergedFiles = mergedFiles.map((row) => {
            const pageId = String(activeConfluenceUpdate.page_id || '').trim();
            const fileId = String(activeConfluenceUpdate.file_id || '').trim();
            const rowPageId = String(row?.source_meta?.page_id || '').trim();
            const rowId = String(row?._id || '').trim();
            const matches = (pageId && rowPageId === pageId) || (fileId && rowId === fileId);
            if (!matches) return row;
            const st = String(row?.processing_status || '').toLowerCase();
            if (st !== 'completed') return row;
            return {
              ...row,
              processing_status: 'pending',
              processing_stage: null,
              event_type: 'updated',
              progress_percent: 0,
              processing_detail: 'Re-index queued…',
            };
          });
        }
        const activeAzureWikiUpdate = azureWikiMonitorActiveUpdateRef.current;
        if (activeAzureWikiUpdate && !activeAzureWikiUpdate.server_acknowledged) {
          mergedFiles = mergedFiles.map((row) => {
            const pageId = String(activeAzureWikiUpdate.page_id || '').trim();
            const fileId = String(activeAzureWikiUpdate.file_id || '').trim();
            const rowPageId = String(row?.source_meta?.page_id || '').trim();
            const rowId = String(row?._id || '').trim();
            const matches = (pageId && rowPageId === pageId) || (fileId && rowId === fileId);
            if (!matches) return row;
            const st = String(row?.processing_status || '').toLowerCase();
            if (st !== 'completed') return row;
            return {
              ...row,
              processing_status: 'pending',
              processing_stage: null,
              event_type: 'updated',
              progress_percent: 0,
              processing_detail: 'Re-index queued…',
            };
          });
        }
        setFilePipelineFiles(mergedFiles);
        const completedPipelineCount = mergedFiles.filter(
          (row) => String(row?.processing_status || '').toLowerCase() === 'completed',
        ).length;
        if (completedPipelineCount > 0) {
          try {
            const projectRefresh = await API.getProject(projectId, { token });
            if (isProjectChatAvailable(projectRefresh?.data)) {
              setProject((prev) => (
                prev
                  ? {
                    ...prev,
                    deployed: true,
                    meta: projectRefresh.data?.meta || prev.meta,
                  }
                  : prev
              ));
            }
          } catch {
            // Non-fatal: pipeline table still updates; dashboard may reconcile on next load.
          }
        }
        setFilePipelineSchedule(
          canEdit && schedRes.status === 'fulfilled' ? (schedRes.value.data || {}) : {},
        );
        setFilePipelineTriggerStatus(
          canEdit && triggerRes.status === 'fulfilled' ? (triggerRes.value.data || null) : null,
        );
        if (canEdit && !silent && bucketNow && probeS3Permissions) {
          try {
            const permRes = await API.checkS3BucketPermissions(token, bucketNow);
            setFilePipelineS3Permissions(permRes?.data ?? null);
          } catch {
            setFilePipelineS3Permissions(null);
          }
        }
      } catch (e) {
        if (!silent) console.error('Error loading file pipeline info', e);
        setFilePipelineTriggerStatus(null);
        setFilePipelineS3Permissions(null);
      } finally {
        if (!silent) setFilePipelineLoading(false);
      }
    })();
    filePipelineLoadInFlightRef.current = job;
    try {
      await job;
      return true;
    } finally {
      if (filePipelineLoadInFlightRef.current === job) {
        filePipelineLoadInFlightRef.current = null;
      }
    }
  }, [token, projectId, canEdit, isPollKnowledgeSource, matchesJiraMonitorActiveRow]);

  const loadJiraConnectorStatus = useCallback(async () => {
    if (!token || !projectId || !isJiraKnowledgeSource) {
      setJiraConnectorStatus(null);
      return;
    }
    setJiraConnectorStatusLoading(true);
    try {
      const resp = await API.getJiraConnectorStatus(token, projectId);
      setJiraConnectorStatus(resp?.data || null);
    } catch (e) {
      setJiraConnectorStatus({
        available: false,
        message: e?.response?.data?.detail || 'Could not check Jira status.',
      });
    } finally {
      setJiraConnectorStatusLoading(false);
    }
  }, [token, projectId, isJiraKnowledgeSource]);

  const showJiraConnectorUnavailableNotice = useCallback((detail, { force = false } = {}) => {
    const safeDetail = sanitizeJiraUserMessage(detail)
      || 'Check the Jira base URL and credentials, then sync again.';
    const noticeKey = `unavailable:${safeDetail}`;
    if (!force && jiraStatusNoticeKeyRef.current === noticeKey) return;
    jiraStatusNoticeKeyRef.current = noticeKey;
    notification.warning({
      message: 'Jira is unreachable',
      description: safeDetail,
      duration: 10,
      placement: 'topRight',
    });
  }, [notification]);

  const showJiraSyncFailedNotice = useCallback((detail, { force = false } = {}) => {
    const safeDetail = sanitizeJiraUserMessage(detail) || 'Sync failed.';
    const noticeKey = `sync_error:${safeDetail}`;
    if (!force && jiraStatusNoticeKeyRef.current === noticeKey) return;
    jiraStatusNoticeKeyRef.current = noticeKey;
    notification.error({
      message: 'Last Jira sync failed',
      description: `${safeDetail} Click Sync from Jira to retry.`,
      duration: 10,
      placement: 'topRight',
    });
  }, [notification]);

  useEffect(() => {
    if (activeTab !== 'file_pipeline') {
      jiraStatusNoticeKeyRef.current = null;
      return;
    }
    if (!isJiraKnowledgeSource || jiraConnectorStatusLoading || !jiraConnectorStatus) return;

    if (jiraConnectorStatus.available === false) {
      if (jiraNeedsProjectSetup || isJiraSetupStatusMessage(jiraConnectorStatus.message)) {
        return;
      }
      showJiraConnectorUnavailableNotice(
        jiraConnectorStatus.message || project?.config?.jira?.last_sync_error,
      );
      return;
    }

    const lastError = project?.config?.jira?.last_sync_error;
    if (jiraConnectorStatus.available
      && project?.config?.jira?.last_sync_status === 'error'
      && lastError) {
      showJiraSyncFailedNotice(lastError);
    }
  }, [
    activeTab,
    isJiraKnowledgeSource,
    jiraConnectorStatusLoading,
    jiraConnectorStatus,
    jiraNeedsProjectSetup,
    project?.config?.jira?.last_sync_status,
    project?.config?.jira?.last_sync_error,
    showJiraConnectorUnavailableNotice,
    showJiraSyncFailedNotice,
  ]);

  // Load file pipeline when the File Pipeline tab is active and project is hydrated (bucket ref updates without recreating this callback).
  useEffect(() => {
    if (!token || !projectId) return;
    if (!project?.id || project.id !== projectId) return;
    if (activeTab !== 'file_pipeline' && activeTab !== 'graphrag') return;
    loadPipelineData(false, { probeS3Permissions: activeTab === 'file_pipeline' });
    if (activeTab === 'file_pipeline' && isJiraKnowledgeSource) {
      loadJiraConnectorStatus();
    }
  }, [project?.id, projectId, token, activeTab, loadPipelineData, isJiraKnowledgeSource, loadJiraConnectorStatus]);

  // Sequential polling: next tick only after the previous request finishes (no setInterval pile-up).
  useEffect(() => {
    if ((activeTab !== 'file_pipeline' && activeTab !== 'graphrag') || !token) return;
    let cancelled = false;
    const delayMs = () => {
      if (jiraMonitorActiveUpdateRef.current && !jiraMonitorActiveUpdateRef.current.server_acknowledged) {
        return 1500;
      }
      if (confluenceMonitorActiveUpdateRef.current && !confluenceMonitorActiveUpdateRef.current.server_acknowledged) {
        return 1500;
      }
      if (azureWikiMonitorActiveUpdateRef.current && !azureWikiMonitorActiveUpdateRef.current.server_acknowledged) {
        return 1500;
      }
      const files = filePipelineFilesRef.current;
      const sched = filePipelineScheduleRef.current;
      if (files.some((f) => isPipelineDeleting(f) || ['processing', 'picked'].includes(String(f?.processing_status || '').toLowerCase()))) return 1500;
      if (files.some((f) => getGraphRagStatus(f) === 'ingesting')) return 2000;
      if (files.some((f) => String(f?.processing_status || '').toLowerCase() === 'pending')) return 3000;
      if (sched?.enabled) return 10000;
      return 30000;
    };
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const loop = async () => {
      while (!cancelled) {
        await sleep(delayMs());
        if (cancelled) break;
        await loadPipelineData(true);
      }
    };
    loop();
    return () => {
      cancelled = true;
    };
  }, [activeTab, token, projectId, loadPipelineData]);

  // Azure Wiki has no webhook path (unlike Confluence). While the RAG Pipeline tab is open,
  // silently poll the wiki scope so new pages appear as Pending without clicking Sync.
  useEffect(() => {
    if (activeTab !== 'file_pipeline' || !isAzureWikiKnowledgeSource || !token || !projectId || !canEdit) {
      return undefined;
    }
    let cancelled = false;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const AUTO_SYNC_INTERVAL_MS = 60 * 1000;

    const runSilentSync = async () => {
      if (cancelled || azureWikiAutoSyncInFlightRef.current) return;
      if (azureWikiMonitorConsentOpenRef.current || azureWikiMonitorApplyingRef.current) return;
      azureWikiAutoSyncInFlightRef.current = true;
      try {
        await API.syncKnowledgeSource(token, projectId, { source: 'azure_wiki', mode: 'incremental' });
        if (!cancelled) {
          await loadPipelineData(true);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Azure Wiki auto-sync failed:', error);
        }
      } finally {
        azureWikiAutoSyncInFlightRef.current = false;
      }
    };

    const loop = async () => {
      // Immediate sync when opening the tab (Confluence webhook equivalent for UX).
      await runSilentSync();
      while (!cancelled) {
        await sleep(AUTO_SYNC_INTERVAL_MS);
        if (cancelled) break;
        await runSilentSync();
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    isAzureWikiKnowledgeSource,
    token,
    projectId,
    canEdit,
    loadPipelineData,
  ]);

  const handleApplyJiraMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.issue_key) return;
    const optimisticRow = (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowIssueKey = String(row?.source_meta?.issue_key || '').trim();
      if (change.file_id && rowId === String(change.file_id).trim()) return true;
      return rowIssueKey === String(change.issue_key).trim();
    }) || null;

    setJiraMonitorApplying(true);
    setJiraMonitorConsentOpen(false);
    setJiraMonitorActiveUpdate({
      issue_key: change.issue_key,
      file_id: change.file_id || null,
      started_at: Date.now(),
      has_started_processing: false,
      server_acknowledged: false,
    });
    if (optimisticRow) {
      patchFilePipelineRow(optimisticRow, {
        processing_status: 'pending',
        processing_stage: null,
        event_type: 'updated',
        progress_percent: 0,
      });
    }
    try {
      const res = await API.applyJiraMonitoredUpdate(token, projectId, change.issue_key, change.file_id);
      const reprocessed = res?.data?.reprocessed || {};
      const resolvedFileId = res?.data?.file_id || change.file_id || null;
      setJiraMonitorActiveUpdate({
        issue_key: change.issue_key,
        file_id: resolvedFileId,
        started_at: Date.now(),
        has_started_processing: false,
        server_acknowledged: true,
      });
      if (resolvedFileId) {
        const patchTarget = optimisticRow
          || (activeFilePipelineFiles || []).find((row) => String(row?._id || '') === String(resolvedFileId))
          || { _id: resolvedFileId };
        patchFilePipelineRow(patchTarget, {
          processing_status: reprocessed.processing_status || 'pending',
          processing_stage: reprocessed.processing_stage || null,
          event_type: reprocessed.event_type || 'updated',
          progress_percent: 0,
          processing_detail: 'Re-index queued…',
        });
      }
      message.success(`Re-index queued for ${change.issue_key}.`);
      jiraMonitorHandledKeysRef.current.set(change.issue_key, Date.now() + 5 * 60 * 1000);
      setJiraMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.issue_key !== change.issue_key);
        if (!next.length) setJiraMonitorConsentOpen(false);
        return next;
      });
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setJiraMonitorActiveUpdate(null);
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not apply update for ${change.issue_key}.`);
      setJiraMonitorConsentOpen(true);
    } finally {
      setJiraMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData, activeFilePipelineFiles]);

  const handleDismissJiraMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.issue_key) return;
    setJiraMonitorApplying(true);
    try {
      const res = await API.dismissJiraMonitoredUpdate(token, projectId, change.issue_key);
      const monitored = res?.data?.monitored_issues || {};
      setJiraMonitoredIssues(monitored);
      jiraMonitorHandledKeysRef.current.set(change.issue_key, Date.now() + 5 * 60 * 1000);
      setJiraMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.issue_key !== change.issue_key);
        if (!next.length) setJiraMonitorConsentOpen(false);
        return next;
      });
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              jira: {
                ...(prev.config?.jira || {}),
                monitored_issues: monitored,
              },
            },
          }
          : prev
      ));
      message.info(`Keeping current index for ${change.issue_key}.`);
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not dismiss update for ${change.issue_key}.`);
    } finally {
      setJiraMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData]);

  useEffect(() => {
    jiraMonitorConsentOpenRef.current = jiraMonitorConsentOpen;
  }, [jiraMonitorConsentOpen]);

  useEffect(() => {
    if (!jiraMonitorConsentOpen) return;
    if (pipelineJiraPanelOpen) {
      setPipelineJiraPanelOpen(false);
    }
  }, [jiraMonitorConsentOpen, pipelineJiraPanelOpen]);

  useEffect(() => {
    if (!confluenceMonitorConsentOpen) return;
    if (pipelineJiraPanelOpen) {
      setPipelineJiraPanelOpen(false);
    }
  }, [confluenceMonitorConsentOpen, pipelineJiraPanelOpen]);

  useEffect(() => {
    jiraMonitorApplyingRef.current = jiraMonitorApplying;
  }, [jiraMonitorApplying]);

  useEffect(() => {
    jiraMonitorActiveUpdateRef.current = jiraMonitorActiveUpdate;
  }, [jiraMonitorActiveUpdate]);

  useEffect(() => {
    if (!token || !projectId || !isJiraKnowledgeSource) return undefined;
    if (!jiraMonitoredIssueKeys.size) return undefined;

    let cancelled = false;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const runCheck = async () => {
      if (cancelled || jiraMonitorCheckInFlightRef.current) return;
      if (jiraMonitorConsentOpenRef.current || jiraMonitorApplyingRef.current) return;
      jiraMonitorCheckInFlightRef.current = true;
      try {
        const res = await API.checkJiraMonitoredUpdates(token, projectId);
        if (!cancelled) {
          mergeJiraMonitorChanges(res?.data?.changes || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Jira monitor check failed:', error);
        }
      } finally {
        jiraMonitorCheckInFlightRef.current = false;
      }
    };

    const loop = async () => {
      await sleep(jiraMonitorIntervalMs);
      while (!cancelled) {
        await runCheck();
        await sleep(jiraMonitorIntervalMs);
      }
    };
    loop();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    projectId,
    isJiraKnowledgeSource,
    jiraMonitoredIssueKeys.size,
    jiraMonitorIntervalMs,
    mergeJiraMonitorChanges,
  ]);

  useEffect(() => {
    if (!jiraMonitorActiveUpdate) return;
    const status = String(jiraMonitorActiveRow?.processing_status || '').toLowerCase();
    const inProgress = status === 'pending' || status === 'processing' || status === 'picked';
    if (inProgress && !jiraMonitorActiveUpdate.has_started_processing) {
      setJiraMonitorActiveUpdate((prev) => (prev
        ? { ...prev, has_started_processing: true }
        : prev));
      return;
    }
    // Ignore stale "completed" state from the previous run until we actually
    // observe this reindex enter pending/processing once.
    if (!jiraMonitorActiveUpdate.has_started_processing) {
      return;
    }
    if (status === 'completed' || status === 'failed') {
      if (status === 'completed') {
        message.success(`RAG conversion completed for ${jiraMonitorActiveUpdate.issue_key}.`);
      } else {
        message.warning(`RAG conversion finished with failure for ${jiraMonitorActiveUpdate.issue_key}. Check row details.`);
      }
      setJiraMonitorActiveUpdate(null);
    }
  }, [jiraMonitorActiveUpdate, jiraMonitorActiveRow, message]);

  const handleApplyConfluenceMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.page_id) return;
    const optimisticRow = (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowPageId = String(row?.source_meta?.page_id || '').trim();
      if (change.file_id && rowId === String(change.file_id).trim()) return true;
      return rowPageId === String(change.page_id).trim();
    }) || null;

    setConfluenceMonitorApplying(true);
    setConfluenceMonitorConsentOpen(false);
    setConfluenceMonitorActiveUpdate({
      page_id: change.page_id,
      file_id: change.file_id || null,
      started_at: Date.now(),
      has_started_processing: false,
      server_acknowledged: false,
    });
    if (optimisticRow) {
      patchFilePipelineRow(optimisticRow, {
        processing_status: 'pending',
        processing_stage: null,
        event_type: 'updated',
        progress_percent: 0,
      });
    }
    try {
      const res = await API.applyConfluenceMonitoredUpdate(token, projectId, change.page_id, change.file_id);
      const reprocessed = res?.data?.reprocessed || {};
      const resolvedFileId = res?.data?.file_id || change.file_id || null;
      setConfluenceMonitorActiveUpdate({
        page_id: change.page_id,
        file_id: resolvedFileId,
        started_at: Date.now(),
        has_started_processing: false,
        server_acknowledged: true,
      });
      if (resolvedFileId) {
        const patchTarget = optimisticRow
          || (activeFilePipelineFiles || []).find((row) => String(row?._id || '') === String(resolvedFileId))
          || { _id: resolvedFileId };
        patchFilePipelineRow(patchTarget, {
          processing_status: reprocessed.processing_status || 'pending',
          processing_stage: reprocessed.processing_stage || null,
          event_type: reprocessed.event_type || 'updated',
          progress_percent: 0,
          processing_detail: 'Re-index queued…',
        });
      }
      message.success(`Re-index queued for Confluence page ${change.page_id}.`);
      confluenceMonitorHandledKeysRef.current.set(change.page_id, Date.now() + 5 * 60 * 1000);
      setConfluenceMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.page_id !== change.page_id);
        if (!next.length) setConfluenceMonitorConsentOpen(false);
        return next;
      });
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setConfluenceMonitorActiveUpdate(null);
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not apply update for page ${change.page_id}.`);
      setConfluenceMonitorConsentOpen(true);
    } finally {
      setConfluenceMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData, activeFilePipelineFiles]);

  const handleDismissConfluenceMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.page_id) return;
    setConfluenceMonitorApplying(true);
    try {
      const res = await API.dismissConfluenceMonitoredUpdate(token, projectId, change.page_id);
      const monitored = res?.data?.monitored_pages || {};
      setConfluenceMonitoredPages(monitored);
      confluenceMonitorHandledKeysRef.current.set(change.page_id, Date.now() + 5 * 60 * 1000);
      setConfluenceMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.page_id !== change.page_id);
        if (!next.length) setConfluenceMonitorConsentOpen(false);
        return next;
      });
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              confluence: {
                ...(prev.config?.confluence || {}),
                monitored_pages: monitored,
              },
            },
          }
          : prev
      ));
      message.info(`Keeping current index for page ${change.page_id}.`);
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not dismiss update for page ${change.page_id}.`);
    } finally {
      setConfluenceMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData]);

  useEffect(() => {
    confluenceMonitorConsentOpenRef.current = confluenceMonitorConsentOpen;
  }, [confluenceMonitorConsentOpen]);

  useEffect(() => {
    confluenceMonitorApplyingRef.current = confluenceMonitorApplying;
  }, [confluenceMonitorApplying]);

  useEffect(() => {
    confluenceMonitorActiveUpdateRef.current = confluenceMonitorActiveUpdate;
  }, [confluenceMonitorActiveUpdate]);

  useEffect(() => {
    if (!token || !projectId || !isConfluenceKnowledgeSource) return undefined;
    if (!confluenceMonitoredPageKeys.size) return undefined;

    let cancelled = false;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const runCheck = async () => {
      if (cancelled || confluenceMonitorCheckInFlightRef.current) return;
      if (confluenceMonitorConsentOpenRef.current || confluenceMonitorApplyingRef.current) return;
      confluenceMonitorCheckInFlightRef.current = true;
      try {
        const res = await API.checkConfluenceMonitoredUpdates(token, projectId);
        if (!cancelled) {
          mergeConfluenceMonitorChanges(res?.data?.changes || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Confluence monitor check failed:', error);
        }
      } finally {
        confluenceMonitorCheckInFlightRef.current = false;
      }
    };

    const loop = async () => {
      await sleep(confluenceMonitorIntervalMs);
      while (!cancelled) {
        await runCheck();
        await sleep(confluenceMonitorIntervalMs);
      }
    };
    loop();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    projectId,
    isConfluenceKnowledgeSource,
    confluenceMonitoredPageKeys.size,
    confluenceMonitorIntervalMs,
    mergeConfluenceMonitorChanges,
  ]);

  useEffect(() => {
    if (!confluenceMonitorActiveUpdate) return;
    const status = String(confluenceMonitorActiveRow?.processing_status || '').toLowerCase();
    const inProgress = status === 'pending' || status === 'processing' || status === 'picked';
    if (inProgress && !confluenceMonitorActiveUpdate.has_started_processing) {
      setConfluenceMonitorActiveUpdate((prev) => (prev
        ? { ...prev, has_started_processing: true }
        : prev));
      return;
    }
    if (!confluenceMonitorActiveUpdate.has_started_processing) {
      return;
    }
    if (status === 'completed' || status === 'failed') {
      if (status === 'completed') {
        message.success(`RAG conversion completed for page ${confluenceMonitorActiveUpdate.page_id}.`);
      } else {
        message.warning(`RAG conversion finished with failure for page ${confluenceMonitorActiveUpdate.page_id}. Check row details.`);
      }
      setConfluenceMonitorActiveUpdate(null);
    }
  }, [confluenceMonitorActiveUpdate, confluenceMonitorActiveRow, message]);

  const handleApplyAzureWikiMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.page_id) return;
    const optimisticRow = (activeFilePipelineFiles || []).find((row) => {
      const rowId = String(row?._id || '').trim();
      const rowPageId = String(row?.source_meta?.page_id || '').trim();
      if (change.file_id && rowId === String(change.file_id).trim()) return true;
      return rowPageId === String(change.page_id).trim();
    }) || null;

    setAzureWikiMonitorApplying(true);
    setAzureWikiMonitorConsentOpen(false);
    setAzureWikiMonitorActiveUpdate({
      page_id: change.page_id,
      file_id: change.file_id || null,
      started_at: Date.now(),
      has_started_processing: false,
      server_acknowledged: false,
    });
    if (optimisticRow) {
      patchFilePipelineRow(optimisticRow, {
        processing_status: 'pending',
        processing_stage: null,
        event_type: 'updated',
        progress_percent: 0,
      });
    }
    try {
      const res = await API.applyAzureWikiMonitoredUpdate(token, projectId, change.page_id, change.file_id);
      const reprocessed = res?.data?.reprocessed || {};
      const resolvedFileId = res?.data?.file_id || change.file_id || null;
      setAzureWikiMonitorActiveUpdate({
        page_id: change.page_id,
        file_id: resolvedFileId,
        started_at: Date.now(),
        has_started_processing: false,
        server_acknowledged: true,
      });
      if (resolvedFileId) {
        const patchTarget = optimisticRow
          || (activeFilePipelineFiles || []).find((row) => String(row?._id || '') === String(resolvedFileId))
          || { _id: resolvedFileId };
        patchFilePipelineRow(patchTarget, {
          processing_status: reprocessed.processing_status || 'pending',
          processing_stage: reprocessed.processing_stage || null,
          event_type: reprocessed.event_type || 'updated',
          progress_percent: 0,
          processing_detail: 'Re-index queued…',
        });
      }
      message.success(`Re-index queued for Azure Wiki page ${change.page_id}.`);
      azureWikiMonitorHandledKeysRef.current.set(change.page_id, Date.now() + 5 * 60 * 1000);
      setAzureWikiMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.page_id !== change.page_id);
        if (!next.length) setAzureWikiMonitorConsentOpen(false);
        return next;
      });
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setAzureWikiMonitorActiveUpdate(null);
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not apply update for page ${change.page_id}.`);
      setAzureWikiMonitorConsentOpen(true);
    } finally {
      setAzureWikiMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData, activeFilePipelineFiles]);

  const handleDismissAzureWikiMonitorUpdate = useCallback(async (change) => {
    if (!token || !projectId || !change?.page_id) return;
    setAzureWikiMonitorApplying(true);
    try {
      const res = await API.dismissAzureWikiMonitoredUpdate(token, projectId, change.page_id);
      const monitored = res?.data?.monitored_pages || {};
      setAzureWikiMonitoredPages(monitored);
      azureWikiMonitorHandledKeysRef.current.set(change.page_id, Date.now() + 5 * 60 * 1000);
      setAzureWikiMonitorPendingUpdates((prev) => {
        const next = (prev || []).filter((row) => row.page_id !== change.page_id);
        if (!next.length) setAzureWikiMonitorConsentOpen(false);
        return next;
      });
      setProject((prev) => (
        prev
          ? {
            ...prev,
            config: {
              ...(prev.config || {}),
              azure_wiki: {
                ...(prev.config?.azure_wiki || {}),
                monitored_pages: monitored,
              },
            },
          }
          : prev
      ));
      message.info(`Keeping current index for page ${change.page_id}.`);
      void loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : `Could not dismiss update for page ${change.page_id}.`);
    } finally {
      setAzureWikiMonitorApplying(false);
    }
  }, [token, projectId, message, loadPipelineData]);

  useEffect(() => {
    azureWikiMonitorConsentOpenRef.current = azureWikiMonitorConsentOpen;
  }, [azureWikiMonitorConsentOpen]);

  useEffect(() => {
    if (!azureWikiMonitorConsentOpen) return;
    if (pipelineJiraPanelOpen) {
      setPipelineJiraPanelOpen(false);
    }
  }, [azureWikiMonitorConsentOpen, pipelineJiraPanelOpen]);

  useEffect(() => {
    azureWikiMonitorApplyingRef.current = azureWikiMonitorApplying;
  }, [azureWikiMonitorApplying]);

  useEffect(() => {
    azureWikiMonitorActiveUpdateRef.current = azureWikiMonitorActiveUpdate;
  }, [azureWikiMonitorActiveUpdate]);

  useEffect(() => {
    if (!token || !projectId || !isAzureWikiKnowledgeSource) return undefined;
    if (!azureWikiMonitoredPageKeys.size) return undefined;

    let cancelled = false;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const runCheck = async () => {
      if (cancelled || azureWikiMonitorCheckInFlightRef.current) return;
      if (azureWikiMonitorConsentOpenRef.current || azureWikiMonitorApplyingRef.current) return;
      azureWikiMonitorCheckInFlightRef.current = true;
      try {
        const res = await API.checkAzureWikiMonitoredUpdates(token, projectId);
        if (!cancelled) {
          mergeAzureWikiMonitorChanges(res?.data?.changes || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Azure Wiki monitor check failed:', error);
        }
      } finally {
        azureWikiMonitorCheckInFlightRef.current = false;
      }
    };

    const loop = async () => {
      await sleep(azureWikiMonitorIntervalMs);
      while (!cancelled) {
        await runCheck();
        await sleep(azureWikiMonitorIntervalMs);
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    projectId,
    isAzureWikiKnowledgeSource,
    azureWikiMonitoredPageKeys.size,
    azureWikiMonitorIntervalMs,
    mergeAzureWikiMonitorChanges,
  ]);

  useEffect(() => {
    if (!azureWikiMonitorActiveUpdate) return;
    const status = String(azureWikiMonitorActiveRow?.processing_status || '').toLowerCase();
    const inProgress = status === 'pending' || status === 'processing' || status === 'picked';
    if (inProgress && !azureWikiMonitorActiveUpdate.has_started_processing) {
      setAzureWikiMonitorActiveUpdate((prev) => (prev
        ? { ...prev, has_started_processing: true }
        : prev));
      return;
    }
    if (!azureWikiMonitorActiveUpdate.has_started_processing) {
      return;
    }
    if (status === 'completed' || status === 'failed') {
      if (status === 'completed') {
        message.success(`RAG conversion completed for page ${azureWikiMonitorActiveUpdate.page_id}.`);
      } else {
        message.warning(`RAG conversion finished with failure for page ${azureWikiMonitorActiveUpdate.page_id}. Check row details.`);
      }
      setAzureWikiMonitorActiveUpdate(null);
    }
  }, [azureWikiMonitorActiveUpdate, azureWikiMonitorActiveRow, message]);

  const handleGraphRagFileToggle = async (record, enabled) => {
    if (!token || !record?._id) return;
    const rowKey = record._id;
    if (enabled) {
      const st = String(record?.processing_status || '').toLowerCase();
      if (st !== 'completed') {
        message.warning('Only completed files can be added to Neo4j.');
        return;
      }
      const gr = getGraphRagStatus(record);
      if (gr === 'ingested' || gr === 'ingesting') return;
      patchFilePipelineRow(record, { graphrag_status: 'ingesting', graphrag_last_error: null });
      try {
        setFilePipelineRowActionLoading(`graphrag-toggle:${rowKey}`);
        const res = await API.ingestGraphRagFiles(token, projectId, { file_ids: [record._id] });
        const data = res?.data || {};
        const ingested = Number(data.ingested || 0);
        const failed = Number(data.failed || 0);
        if (ingested > 0 && failed === 0) {
          message.success(formatNeo4jPushSuccess(ingested));
        } else if (ingested > 0) {
          message.warning(formatNeo4jPushPartialSuccess(ingested, failed));
        } else {
          message.error(
            humanizeApiError(data.errors?.[0], { context: ERROR_CONTEXT.NEO4J_PUSH }),
          );
        }
        await loadPipelineData(true);
      } catch (e) {
        patchFilePipelineRow(record, {
          graphrag_status: 'failed',
          graphrag_last_error: e?.response?.data?.detail || 'Neo4j push failed',
        });
        message.error(humanizeApiError(e, { context: ERROR_CONTEXT.NEO4J_PUSH }));
        await loadPipelineData(true);
      } finally {
        setFilePipelineRowActionLoading(null);
      }
      return;
    }

    if (getGraphRagStatus(record) !== 'ingested') {
      return;
    }
    confirmDelete({
      title: 'Remove from Neo4j?',
      content: `Remove "${record?.file_name || record._id}" from the knowledge graph? Vector search data is kept.`,
      okText: 'Remove',
      onOk: async () => {
        try {
          setFilePipelineRowActionLoading(`graphrag-toggle:${rowKey}`);
          const res = await API.removeGraphRagFiles(token, projectId, { file_ids: [record._id] });
          const data = res?.data || {};
          const removed = Number(data.removed || 0);
          if (removed > 0) {
            message.success(formatNeo4jRemoveSuccess(removed));
          } else {
            message.error(
              humanizeApiError(data.errors?.[0], { context: ERROR_CONTEXT.NEO4J_REMOVE }),
            );
          }
          await loadPipelineData(true);
        } catch (e) {
          message.error(humanizeApiError(e, { context: ERROR_CONTEXT.NEO4J_REMOVE }));
        } finally {
          setFilePipelineRowActionLoading(null);
        }
      },
    });
  };

  const handleRunFilePipeline = async () => {
    if (filePipelineLoading || filePipelineActionLoading) return;
    if (!token) {
      message.error('You must be logged in to run the pipeline.');
      return;
    }
    try {
      setFilePipelineActionLoading('run');
      const selectedFileIds = (selectedFilePipelineRowKeys || []).filter((id) => typeof id === 'string' && id.trim());
      const runPayload = selectedFileIds.length > 0 ? { file_ids: selectedFileIds } : {};
      const runRes = await API.runFilePipelineNow(token, projectId, runPayload);
      const runStatus = String(runRes?.data?.status || '').toLowerCase();
      const runMsg = humanizePipelineRunResponse(runRes?.data);
      if (runStatus === 'already_running') {
        message.warning(runMsg);
      } else {
        message.success(runMsg);
      }
      if (selectedFileIds.length > 0) {
        setSelectedFilePipelineRowKeys([]);
      }
      await loadPipelineData(true);
    } catch (e) {
      console.error('Error running file pipeline', e);
      message.error(humanizeApiError(e, { context: ERROR_CONTEXT.PIPELINE_RUN }));
    } finally {
      setFilePipelineActionLoading(null);
    }
  };

  const persistPipelineSchedule = async (values, { successMessage } = {}) => {
    if (filePipelineLoading || filePipelineActionLoading) return false;
    if (!token) {
      message.error('You must be logged in to update the schedule.');
      return false;
    }
    try {
      setFilePipelineActionLoading('save');
      const payload = {
        enabled: true,
        interval_unit: values.interval_unit || 'hours',
        interval_value: values.interval_value || 1,
      };
      await API.updateFilePipelineSchedule(token, projectId, payload);
      message.success(successMessage || 'RAG pipeline schedule updated');
      await loadPipelineData(true);
      return true;
    } catch (e) {
      console.error('Error updating file pipeline schedule', e);
      message.error(humanizeApiError(e, { context: ERROR_CONTEXT.SCHEDULE }));
      return false;
    } finally {
      setFilePipelineActionLoading(null);
    }
  };

  const handleSavePipelineSchedule = async (values) => {
    await persistPipelineSchedule({ ...values, enabled: true });
  };

  const handleRunVectorRetention = async () => {
    if (!token) return;
    setVectorRetentionRunning(true);
    try {
      const res = await API.runVectorRetention(token, projectId);
      const r = res.data || {};
      const n = Array.isArray(r.removed) ? r.removed.length : 0;
      if (r.skipped) {
        message.info(
          r.reason === 'no_files_with_retention_enabled'
            ? 'Turn on retention on at least one file below and click Save, or nothing will be purged.'
            : 'Nothing to do.',
        );
      } else if (n > 0) {
        message.success(formatVectorRetentionRemoved(n));
      } else {
        message.success(
          'Retention check completed; no documents exceeded their per-file policy.',
        );
      }
      loadPipelineData(true);
    } catch (e) {
      message.error(humanizeApiError(e, { context: ERROR_CONTEXT.RETENTION }));
    } finally {
      setVectorRetentionRunning(false);
    }
  };

  const withFileRowAction = async (actionKey, run) => {
    if (filePipelineActionLoading || filePipelineRowActionLoading) return;
    setFilePipelineRowActionLoading(actionKey);
    try {
      await run();
    } finally {
      setFilePipelineRowActionLoading(null);
    }
  };

  const isPreviewableInModal = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.txt', '.md', '.csv'].some((ext) => value.endsWith(ext));
  };

  const isTextBasedFile = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.txt', '.md', '.csv', '.json', '.log', '.xml', '.yaml', '.yml'].some((ext) => value.endsWith(ext));
  };

  const isOfficePreviewableInModal = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.ppt', '.pptx', '.doc', '.docx', '.xls', '.xlsx'].some((ext) => value.endsWith(ext));
  };

  const isConfluencePipelineFile = (record, fileKey) => (
    isConfluenceKnowledgeSource
    || record?.source_type === 'confluence'
    || String(fileKey || '').startsWith('confluence/')
  );

  const isGithubPipelineFile = (record, fileKey) => (
    isGithubKnowledgeSource
    || record?.source_type === 'github'
    || String(fileKey || '').startsWith('github/')
  );

  const isJiraPipelineFile = (record, fileKey) => (
    isJiraKnowledgeSource
    || record?.source_type === 'jira'
    || String(fileKey || '').startsWith('jira/')
  );

  const isAzureWikiPipelineFile = (record, fileKey) => (
    isAzureWikiKnowledgeSource
    || record?.source_type === 'azure_wiki'
    || String(fileKey || '').startsWith('azure_wiki/')
  );

  const isPollKnowledgePipelineFile = (record, fileKey) => (
    isConfluencePipelineFile(record, fileKey)
    || isGithubPipelineFile(record, fileKey)
    || isJiraPipelineFile(record, fileKey)
    || isAzureWikiPipelineFile(record, fileKey)
  );

  const handleViewFile = async (record) => {
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    if (!token || !fileKey) {
      message.error('Unable to open file preview.');
      return;
    }
    if (filePreviewBlobUrlRef.current) {
      URL.revokeObjectURL(filePreviewBlobUrlRef.current);
      filePreviewBlobUrlRef.current = '';
    }
    const fileName = record?.file_name || fileKey;
    const previewLabel = record?.file_key || record?.file_name || fileKey;
    setFilePreviewModal({
      open: true,
      title: fileName,
      url: '',
      textContent: '',
      provider: 'office',
      loading: true,
      error: '',
    });

    if (isConfluencePipelineFile(record, fileKey)) {
      try {
        const resp = await API.getConfluenceFilePreview(token, projectId, {
          file_key: record?.file_key || (String(fileKey).startsWith('confluence/') ? fileKey : undefined),
          doc_id: record?.doc_id,
        });
        const data = resp?.data || {};
        setFilePreviewModal({
          open: true,
          title: data.title || fileName,
          url: '',
          textContent: data.content || '',
          provider: 'office',
          loading: false,
          error: data.content ? '' : 'No preview content available.',
          contentFormat: data.content ? 'markdown' : '',
          sourceKind: 'confluence',
          webUrl: data.web_url || '',
          fileKey: data.file_key || record?.file_key || '',
          docId: record?.doc_id || '',
          showRawMarkdown: false,
          pdfLoading: false,
        });
      } catch (e) {
        setFilePreviewModal({
          ...EMPTY_FILE_PREVIEW_MODAL,
          open: true,
          title: fileName,
          error: e?.response?.data?.detail || 'Failed to load Confluence preview.',
          sourceKind: 'confluence',
        });
      }
      return;
    }

    if (isGithubPipelineFile(record, fileKey)) {
      try {
        const resp = await API.getGithubFilePreview(token, projectId, {
          file_key: record?.file_key || (String(fileKey).startsWith('github/') ? fileKey : undefined),
          doc_id: record?.doc_id,
        });
        const data = resp?.data || {};
        setFilePreviewModal({
          open: true,
          title: data.title || fileName,
          url: '',
          textContent: data.content || '',
          provider: 'office',
          loading: false,
          error: data.content ? '' : 'No preview content available.',
          contentFormat: data.content ? 'markdown' : '',
          sourceKind: 'github',
          webUrl: data.web_url || '',
          fileKey: data.file_key || record?.file_key || '',
          docId: record?.doc_id || '',
          showRawMarkdown: false,
          pdfLoading: false,
        });
      } catch (e) {
        setFilePreviewModal({
          ...EMPTY_FILE_PREVIEW_MODAL,
          open: true,
          title: fileName,
          error: e?.response?.data?.detail || 'Failed to load GitHub preview.',
          sourceKind: 'github',
        });
      }
      return;
    }

    if (isJiraPipelineFile(record, fileKey)) {
      try {
        const resp = await API.getJiraFilePreview(token, projectId, {
          file_key: record?.file_key || (String(fileKey).startsWith('jira/') ? fileKey : undefined),
          doc_id: record?.doc_id,
        });
        const data = resp?.data || {};
        setFilePreviewModal({
          open: true,
          title: data.title || fileName,
          url: '',
          textContent: data.content || '',
          provider: 'office',
          loading: false,
          error: data.content ? '' : 'No preview content available.',
          contentFormat: data.content ? 'markdown' : '',
          sourceKind: 'jira',
          webUrl: data.web_url || '',
          fileKey: data.file_key || record?.file_key || '',
          docId: record?.doc_id || '',
          showRawMarkdown: false,
          pdfLoading: false,
        });
      } catch (e) {
        setFilePreviewModal({
          ...EMPTY_FILE_PREVIEW_MODAL,
          open: true,
          title: fileName,
          error: e?.response?.data?.detail || 'Failed to load Jira preview.',
          sourceKind: 'jira',
        });
      }
      return;
    }

    if (isAzureWikiPipelineFile(record, fileKey)) {
      try {
        const resp = await API.getAzureWikiFilePreview(token, projectId, {
          file_key: record?.file_key || (String(fileKey).startsWith('azure_wiki/') ? fileKey : undefined),
          doc_id: record?.doc_id,
        });
        const data = resp?.data || {};
        setFilePreviewModal({
          open: true,
          title: data.title || fileName,
          url: '',
          textContent: data.content || '',
          provider: 'office',
          loading: false,
          error: data.content ? '' : 'No preview content available.',
          contentFormat: data.content ? 'markdown' : '',
          sourceKind: 'azure_wiki',
          webUrl: data.web_url || '',
          fileKey: data.file_key || record?.file_key || '',
          docId: record?.doc_id || '',
          showRawMarkdown: false,
          pdfLoading: false,
        });
      } catch (e) {
        setFilePreviewModal({
          ...EMPTY_FILE_PREVIEW_MODAL,
          open: true,
          title: fileName,
          error: e?.response?.data?.detail || 'Failed to load Azure Wiki preview.',
          sourceKind: 'azure_wiki',
        });
      }
      return;
    }

   
    try {
      const needsPublicPreviewProxy =
        isOfficePreviewableInModal(previewLabel)
        || isPdfFile(previewLabel)
        || isPreviewableInModal(previewLabel);
      const debugStart = {
        stage: 'view_button:start',
        projectId,
        at: new Date().toISOString(),
        fileKey,
        fileName,
        needsPublicPreviewProxy,
        bucket: pipelineS3Bucket || null,
        record: {
          id: record?._id || null,
          s3_key: record?.s3_key || null,
          file_key: record?.file_key || null,
          doc_id: record?.doc_id || null,
          file_name: record?.file_name || null,
        },
      };
      window.__viewFileDebug = window.__viewFileDebug || [];
      window.__viewFileDebug.push(debugStart);
      // eslint-disable-next-line no-console
      console.info('[VIEW_FILE_DEBUG]', debugStart);

      const resp = needsPublicPreviewProxy
        ? await API.getS3PreviewUrl(token, fileKey, pipelineS3Bucket || undefined)
        : await API.getS3DownloadUrl(token, fileKey, pipelineS3Bucket || undefined);
      const url = resp?.data?.url || '';
      if (!url) throw new Error('Missing preview URL');
      const debugUrl = {
        stage: 'view_button:url_received',
        projectId,
        at: new Date().toISOString(),
        fileKey,
        fileName,
        needsPublicPreviewProxy,
        bucket: pipelineS3Bucket || null,
        responsePayload: resp?.data || {},
        url,
        officeEmbedUrl: isOfficePreviewableInModal(previewLabel)
          ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
          : null,
      };
      window.__viewFileDebug.push(debugUrl);
      // eslint-disable-next-line no-console
      console.info('[VIEW_FILE_DEBUG]', debugUrl);

      // Text: fetch and render inline. PDF: blob URL + embed (iframe often triggers download in Chrome/Edge).
      if (isTextBasedFile(previewLabel)) {
        try {
          const textResp = await fetch(url);
          if (!textResp.ok) {
            throw new Error(`Failed to load file (${textResp.status})`);
          }
          const text = await textResp.text();
          const trimmed = text.trimStart().toLowerCase();
          if (trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html')) {
            throw new Error('Preview returned the app shell instead of file content. Check PUBLIC_API_BASE_URL points at the backend API host.');
          }
          setFilePreviewModal((prev) => ({ ...prev, textContent: text, url, loading: false }));
        } catch (textErr) {
          setFilePreviewModal((prev) => ({
            ...prev,
            loading: false,
            error: textErr?.message || 'Failed to load text preview.',
          }));
        }
      } else if (isPdfFile(previewLabel)) {
        try {
          const pdfResp = await fetch(url);
          if (!pdfResp.ok) {
            throw new Error(`Failed to load PDF (${pdfResp.status})`);
          }
          const rawBlob = await pdfResp.blob();
          const pdfBlob = rawBlob.type === 'application/pdf'
            ? rawBlob
            : new Blob([await rawBlob.arrayBuffer()], { type: 'application/pdf' });
          if (filePreviewBlobUrlRef.current) {
            URL.revokeObjectURL(filePreviewBlobUrlRef.current);
          }
          const blobUrl = URL.createObjectURL(pdfBlob);
          filePreviewBlobUrlRef.current = blobUrl;
          setFilePreviewModal((prev) => ({
            ...prev,
            url: blobUrl,
            blobUrl,
            loading: false,
            provider: 'pdf',
            sourceKind: 's3',
            fileKey: record?.file_key || record?.s3_key || fileKey,
            docId: record?.doc_id || '',
          }));
        } catch (pdfErr) {
          // eslint-disable-next-line no-console
          console.error('[VIEW_FILE_DEBUG] pdf_blob_failed', pdfErr);
          setFilePreviewModal((prev) => ({
            ...prev,
            loading: false,
            error: 'Failed to load PDF preview.',
          }));
        }
      } else {
        setFilePreviewModal((prev) => ({
          ...prev,
          url,
          loading: false,
          provider: 'office',
          sourceKind: 's3',
          fileKey: record?.file_key || record?.s3_key || fileKey,
          docId: record?.doc_id || '',
        }));
      }
    } catch (e) {
      const debugErr = {
        stage: 'view_button:error',
        projectId,
        at: new Date().toISOString(),
        fileKey,
        fileName,
        bucket: pipelineS3Bucket || null,
        message: e?.message || String(e),
        detail: e?.response?.data?.detail || null,
      };
      window.__viewFileDebug = window.__viewFileDebug || [];
      window.__viewFileDebug.push(debugErr);
      // eslint-disable-next-line no-console
      console.error('[VIEW_FILE_DEBUG]', debugErr);
      setFilePreviewModal((prev) => ({
        ...prev,
        loading: false,
        error: e?.response?.data?.detail || 'Failed to open file preview.',
      }));
    }
  };

  const handleDownloadPipelineFile = async (record) => {
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    if (!token || !fileKey) {
      message.error('File key not available for download.');
      return;
    }
    await withFileRowAction(`download:${record?._id || fileKey}`, async () => {
      try {
        if (isConfluencePipelineFile(record, fileKey)) {
          const resp = await API.getConfluenceFilePreview(token, projectId, {
            file_key: record?.file_key || (String(fileKey).startsWith('confluence/') ? fileKey : undefined),
            doc_id: record?.doc_id,
          });
          const data = resp?.data || {};
          const content = data.content || '';
          const downloadName = `${(data.title || record?.file_name || 'page').replace(/[/\\?%*:|"<>]/g, '_')}.md`;
          const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          return;
        }
        if (isGithubPipelineFile(record, fileKey)) {
          const resp = await API.getGithubFilePreview(token, projectId, {
            file_key: record?.file_key || (String(fileKey).startsWith('github/') ? fileKey : undefined),
            doc_id: record?.doc_id,
          });
          const data = resp?.data || {};
          const content = data.content || '';
          const baseName = (data.title || record?.file_name || 'file').replace(/[/\\?%*:|"<>]/g, '_');
          const ext = String(record?.file_name || baseName).includes('.')
            ? ''
            : '.txt';
          const downloadName = `${baseName}${ext}`;
          const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          return;
        }
        if (isAzureWikiPipelineFile(record, fileKey)) {
          const resp = await API.getAzureWikiFilePreview(token, projectId, {
            file_key: record?.file_key || (String(fileKey).startsWith('azure_wiki/') ? fileKey : undefined),
            doc_id: record?.doc_id,
          });
          const data = resp?.data || {};
          const content = data.content || '';
          const baseName = (data.title || record?.file_name || 'page').replace(/[/\\?%*:|"<>]/g, '_');
          const ext = String(record?.file_name || baseName).includes('.') ? '' : '.md';
          const downloadName = `${baseName}${ext}`;
          const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          return;
        }
        const response = await API.getS3DownloadUrl(token, fileKey, pipelineS3Bucket || undefined, true);
        const downloadUrl = response?.data?.url;
        if (!downloadUrl) {
          message.error('Download URL not available');
          return;
        }
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.style.display = 'none';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        message.error(humanizeApiError(e, { context: ERROR_CONTEXT.FILE_DOWNLOAD }));
      }
    });
  };

  const handleDeleteFileFromS3 = (record) => {
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    if (!fileKey) {
      message.error('File key not available for this row.');
      return;
    }
    if (!token) {
      message.error('You must be logged in to delete files from S3.');
      return;
    }
    confirmDelete({
      title: 'Delete file from S3?',
      content: `This will remove "${record?.file_name || fileKey}" from the S3 bucket.`,
      onOk: async () => {
        await withFileRowAction(`delete-s3:${record?._id || fileKey}`, async () => {
          await API.deleteS3File(token, fileKey, pipelineS3Bucket || undefined);
          message.success(formatS3FileDeletedFromBucket(record?.file_name || fileKey));
          await loadPipelineData(true);
        });
      },
    });
  };

  const handleDeleteFileComplete = (record) => {
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    const fileLabel = record?.file_name || fileKey || record?._id;
    const isConfluenceRow = isConfluencePipelineFile(record, fileKey);
    const isGithubRow = isGithubPipelineFile(record, fileKey);
    const isJiraRow = isJiraPipelineFile(record, fileKey);
    const isAzureWikiRow = isAzureWikiPipelineFile(record, fileKey);
    const isPollKnowledgeRow = isConfluenceRow || isGithubRow || isJiraRow || isAzureWikiRow;
    if (!token) {
      message.error('You must be logged in to delete files.');
      return;
    }
    const status = String(record?.processing_status || '').toLowerCase();
    const hasS3Key = !!fileKey;
    const hasMongoRecord = !!record?._id;
    const isAlreadyDeletedFromS3 = status === 'deleted';
    const graphragIngestUrl = (project?.config?.graphrag?.ingest_endpoint || '').trim();
    const mayRemoveFromGraphRag =
      !!project?.config?.graphrag?.enabled || !!graphragIngestUrl;
    confirmCritical({
      title: `Delete "${fileLabel}"?`,
      content: (
        <div>
          <p style={{ margin: '0 0 8px 0' }}>
            {isPollKnowledgeRow
              ? `This removes the file from FastRAG only (${isJiraRow ? 'Jira' : isGithubRow ? 'GitHub' : isAzureWikiRow ? 'Azure Wiki' : 'Confluence'} source stays unchanged):`
              : 'This will permanently delete the file and all its records:'}
          </p>
          <ul style={{ paddingLeft: 20, margin: '0 0 8px 0', color: colors.textSecondary }}>
            {isPollKnowledgeRow ? (
              <li>Remove indexed chunks, vectors, and local copies</li>
            ) : (
              hasS3Key && !isAlreadyDeletedFromS3 && <li>Remove file from S3 bucket</li>
            )}
            {hasMongoRecord && <li>Remove pipeline record from database</li>}
            {!isPollKnowledgeRow && <li>Remove associated chunks and vectors</li>}
            {isConfluenceRow && <li>Page remains in Confluence; sync can add it back</li>}
            {isGithubRow && <li>File remains in GitHub; sync can add it back</li>}
            {isJiraRow && <li>Issue remains in Jira; sync can add it back</li>}
            {isAzureWikiRow && <li>Page remains in Azure Wiki; sync can add it back</li>}
            {mayRemoveFromGraphRag && <li>Remove knowledge graph episodes from Neo4j</li>}
          </ul>
        </div>
      ),
      okText: 'Delete permanently',
      onOk: async () => {
        patchFilePipelineRow(record, {
          __ui_deleting: true,
          event_type: 'deleted',
          last_event_type: 'deleted',
          processing_status: 'processing',
          processing_stage: 'deleting',
          processing_detail: isPollKnowledgeRow
            ? 'Removing from FastRAG index…'
            : 'Removing file and indexed data…',
          progress_percent: 5,
        });
        try {
          if (isPollKnowledgeRow && hasMongoRecord && !isAlreadyDeletedFromS3) {
            await API.removeKnowledgeFileFromIndex(token, projectId, record._id);
            message.success('Removed from FastRAG index. Cleaning up…');
            await loadPipelineData(true);
            (async () => {
              try {
                const waitResult = await waitForPipelineFileDeleted(record);
                if (!waitResult.ok) {
                  message.warning('Cleanup is still running. The table will update when it finishes.');
                  return;
                }
                await removePipelineRecordIfExists(record._id);
                message.success('File removed from pipeline');
                await loadPipelineData(true);
              } catch (bgErr) {
                console.error('Background knowledge-source delete cleanup failed:', bgErr);
                const cleanupMsg = humanizeApiError(bgErr, { context: ERROR_CONTEXT.FILE_CLEANUP });
                if (cleanupMsg) message.error(cleanupMsg);
                patchFilePipelineRow(record, { __ui_deleting: false });
              }
            })();
            return;
          }
          if (hasS3Key && !isAlreadyDeletedFromS3) {
            await API.deleteS3File(token, fileKey, pipelineS3Bucket || undefined);
          } else if (hasMongoRecord && isAlreadyDeletedFromS3) {
            await removePipelineRecordIfExists(record._id);
            message.success('File deleted successfully');
            await loadPipelineData(true);
            return;
          }
          message.success(formatS3FileRemovedPendingCleanup(record?.file_name || fileKey));
          await loadPipelineData(true);
          (async () => {
            try {
              const waitResult = await waitForPipelineFileDeleted(record);
              if (!waitResult.ok) {
                message.warning('Cleanup is still running. The table will update when it finishes.');
                return;
              }
              // S3 sync prunes missing keys from Mongo during loadPipelineData — no extra delete needed.
              message.success('File deleted successfully');
              await loadPipelineData(true);
            } catch (bgErr) {
              console.error('Background delete cleanup failed:', bgErr);
              const cleanupMsg = humanizeApiError(bgErr, { context: ERROR_CONTEXT.FILE_CLEANUP });
              if (cleanupMsg) message.error(cleanupMsg);
              patchFilePipelineRow(record, { __ui_deleting: false });
            }
          })();
        } catch (e) {
          patchFilePipelineRow(record, { __ui_deleting: false });
          message.error(humanizeApiError(e, { context: ERROR_CONTEXT.FILE_DELETE }));
          throw e;
        }
      },
    });
  };

  const handleReuploadFileToS3 = async (record, file) => {
    const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
    if (!fileKey) {
      message.error('File key not available for this row.');
      return false;
    }
    if (!token) {
      message.error('You must be logged in to reupload files to S3.');
      return false;
    }
    await withFileRowAction(`reupload-s3:${record?._id || fileKey}`, async () => {
      await API.reuploadS3File(token, fileKey, file, pipelineS3Bucket || undefined);
      message.success(formatReuploadSuccess(file.name || record?.file_name));
      await loadPipelineData(true);
    });
    return false;
  };

  const handleDeleteFileRecordFromMongo = (record) => {
    if (!token) {
      message.error('You must be logged in to delete file records.');
      return;
    }
    const status = String(record?.processing_status || '').toLowerCase();
    if (status !== 'deleted') {
      message.error('Only records with deleted status can be removed from MongoDB.');
      return;
    }
    const fileLabel = record?.file_name || record?.file_key || record?._id;
    confirmDelete({
      title: `Remove record for "${fileLabel}"?`,
      content: 'This permanently removes the pipeline record from the database.',
      okText: 'Remove record',
      onOk: async () => {
        await withFileRowAction(`delete-record:${record?._id}`, async () => {
          await removePipelineRecordIfExists(record._id);
          message.success('File record removed');
          await loadPipelineData(true);
        });
      },
    });
  };

  const handleRetryFailedFile = async (record) => {
    if (!token) {
      message.error('You must be logged in to retry file records.');
      return;
    }
    if (!record?._id) {
      message.error('Invalid file record id.');
      return;
    }
    const status = String(record?.processing_status || '').toLowerCase();
    if (status !== 'failed') {
      message.error('Only failed records can be retried.');
      return;
    }
    await withFileRowAction(`retry-file:${record._id}`, async () => {
      await API.retryFilePipelineFile(token, projectId, record._id);
      message.success('Retry queued. Pipeline resumed from available artifacts.');
      await loadPipelineData(true);
    });
  };

  const handleCancelFileProcessing = async (record) => {
    if (!token) {
      message.error('You must be logged in to cancel file processing.');
      return;
    }
    if (!record?._id) {
      message.error('Invalid file record id.');
      return;
    }
    const status = String(record?.processing_status || '').toLowerCase();
    if (!['processing', 'picked'].includes(status)) {
      message.error('Only in-progress files can be cancelled.');
      return;
    }
    await withFileRowAction(`cancel-file:${record._id}`, async () => {
      await API.cancelFilePipelineFile(token, projectId, record._id);
      message.success('Processing cancelled. File returned to pending.');
      await loadPipelineData(true);
    });
  };

  const handleReprocessFile = async (record) => {
    if (!token) {
      message.error('You must be logged in to reprocess files.');
      return;
    }
    if (!record?._id) {
      message.error('Invalid file record id.');
      return;
    }
    const status = String(record?.processing_status || '').toLowerCase();
    if (!['completed', 'failed', 'pending'].includes(status)) {
      message.error('This file cannot be reprocessed in its current state.');
      return;
    }
    const fileLabel = record?.file_name || record?.file_key || record?._id;
    confirmCritical({
      title: `Reprocess "${fileLabel}"?`,
      content: 'This will re-run chunking, embedding, and vector indexing for this file.',
      okText: 'Reprocess',
      onOk: async () => {
        await withFileRowAction(`reprocess-file:${record._id}`, async () => {
          await API.reprocessFilePipelineFile(token, projectId, record._id);
          message.success('Reprocess queued. Pipeline run started.');
          await loadPipelineData(true);
        });
      },
    });
  };

  const getSelectedPipelineRecords = useCallback(() => {
    const keySet = new Set(
      (selectedFilePipelineRowKeys || []).filter((id) => typeof id === 'string' && id.trim()),
    );
    if (!keySet.size) return [];
    return (filePipelineFiles || []).filter((row) => {
      const key = row?._id || row?.s3_key || row?.file_key || row?.doc_id;
      return key && keySet.has(key);
    });
  }, [selectedFilePipelineRowKeys, filePipelineFiles]);

  const handleBulkReprocessSelected = () => {
    if (!token) {
      message.error('You must be logged in to reprocess files.');
      return;
    }
    const selected = getSelectedPipelineRecords();
    const eligible = selected.filter((row) => {
      const status = String(row?.processing_status || '').toLowerCase();
      return row?._id && ['completed', 'failed', 'pending'].includes(status);
    });
    if (!eligible.length) {
      message.warning('No selected files are eligible to reprocess.');
      return;
    }
    confirmCritical({
      title: `Reprocess ${eligible.length} selected file${eligible.length === 1 ? '' : 's'}?`,
      content: 'This will re-run chunking, embedding, and vector indexing for each selected file.',
      okText: 'Reprocess',
      onOk: async () => {
        setFilePipelineActionLoading('bulk-reprocess');
        let okCount = 0;
        let failCount = 0;
        try {
          for (const row of eligible) {
            try {
              await API.reprocessFilePipelineFile(token, projectId, row._id);
              okCount += 1;
            } catch (e) {
              failCount += 1;
              console.error('Bulk reprocess failed for', row?._id, e);
            }
          }
          if (okCount > 0 && failCount === 0) {
            message.success(`Reprocess queued for ${okCount} file${okCount === 1 ? '' : 's'}.`);
          } else if (okCount > 0) {
            message.warning(`Reprocess queued for ${okCount}; ${failCount} failed.`);
          } else {
            message.error('Failed to reprocess selected files.');
          }
          setSelectedFilePipelineRowKeys([]);
          await loadPipelineData(true);
        } finally {
          setFilePipelineActionLoading(null);
        }
      },
    });
  };

  const handleBulkDeleteSelected = () => {
    if (!token) {
      message.error('You must be logged in to delete files.');
      return;
    }
    const selected = getSelectedPipelineRecords().filter((row) => {
      const status = String(row?.processing_status || '').toLowerCase();
      return status !== 'deleted';
    });
    if (!selected.length) {
      message.warning('No selected files are available to delete.');
      return;
    }
    const graphragIngestUrl = (project?.config?.graphrag?.ingest_endpoint || '').trim();
    const mayRemoveFromGraphRag =
      !!project?.config?.graphrag?.enabled || !!graphragIngestUrl;
    confirmCritical({
      title: `Delete ${selected.length} selected file${selected.length === 1 ? '' : 's'}?`,
      content: (
        <div>
          <p style={{ margin: '0 0 8px 0' }}>
            This permanently removes the selected files and their indexed data from FastRAG.
          </p>
          <ul style={{ paddingLeft: 20, margin: '0 0 8px 0', color: colors.textSecondary }}>
            <li>Remove pipeline records and vectors</li>
            <li>Remove source objects from S3 when applicable</li>
            {mayRemoveFromGraphRag && <li>Remove knowledge graph episodes from Neo4j when configured</li>}
          </ul>
        </div>
      ),
      okText: 'Delete permanently',
      onOk: async () => {
        setFilePipelineActionLoading('bulk-delete');
        let okCount = 0;
        let failCount = 0;
        try {
          for (const record of selected) {
            const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
            const isConfluenceRow = isConfluencePipelineFile(record, fileKey);
            const isGithubRow = isGithubPipelineFile(record, fileKey);
            const isJiraRow = isJiraPipelineFile(record, fileKey);
            const isAzureWikiRow = isAzureWikiPipelineFile(record, fileKey);
            const isPollKnowledgeRow = isConfluenceRow || isGithubRow || isJiraRow || isAzureWikiRow;
            const hasS3Key = !!fileKey;
            const hasMongoRecord = !!record?._id;
            const status = String(record?.processing_status || '').toLowerCase();
            const isAlreadyDeletedFromS3 = status === 'deleted';
            try {
              patchFilePipelineRow(record, {
                __ui_deleting: true,
                event_type: 'deleted',
                last_event_type: 'deleted',
                processing_status: 'processing',
                processing_stage: 'deleting',
                processing_detail: isPollKnowledgeRow
                  ? 'Removing from FastRAG index…'
                  : 'Removing file and indexed data…',
                progress_percent: 5,
              });
              if (isPollKnowledgeRow && hasMongoRecord && !isAlreadyDeletedFromS3) {
                await API.removeKnowledgeFileFromIndex(token, projectId, record._id);
              } else if (hasS3Key && !isAlreadyDeletedFromS3) {
                await API.deleteS3File(token, fileKey, pipelineS3Bucket || undefined);
              } else if (hasMongoRecord && isAlreadyDeletedFromS3) {
                await removePipelineRecordIfExists(record._id);
              } else if (!hasS3Key && hasMongoRecord) {
                await removePipelineRecordIfExists(record._id);
              } else {
                throw new Error('No deletable source found for this file.');
              }
              okCount += 1;
            } catch (e) {
              failCount += 1;
              patchFilePipelineRow(record, { __ui_deleting: false });
              console.error('Bulk delete failed for', record?._id || fileKey, e);
            }
          }
          if (okCount > 0 && failCount === 0) {
            message.success(`Deleted ${okCount} file${okCount === 1 ? '' : 's'}. Cleanup may continue in the background.`);
          } else if (okCount > 0) {
            message.warning(`Deleted ${okCount}; ${failCount} failed.`);
          } else {
            message.error('Failed to delete selected files.');
          }
          setSelectedFilePipelineRowKeys([]);
          await loadPipelineData(true);
        } finally {
          setFilePipelineActionLoading(null);
        }
      },
    });
  };

  const handleVerifyS3 = async () => {
    try {
      const values = await form.validateFields([
        ['config', 's3', 'bucket'],
        ['config', 's3', 'region'],
      ]);

      const bucket = values?.config?.s3?.bucket;
      const region = values?.config?.s3?.region;

      // If both fields are empty, nothing to verify – treat as not configured
      if (!bucket && !region) {
        setS3Dirty(false);
        setS3Verified(false);
        setS3VerifyError('');
        message.info('Knowledge Source details are empty. Nothing to verify.');
        return;
      }

      if (!token) {
        message.error('You must be logged in to verify Knowledge Source configuration.');
        return;
      }

      setS3Verifying(true);
      setS3VerifyError('');

      const verifiedS3 = {
        bucket: String(bucket || '').trim(),
        region: String(region || '').trim(),
      };

      await API.verifyS3Config(token, verifiedS3);

      // Verify only checks credentials — it does not persist. Keep the typed values in
      // the form; reloading project config here used to overwrite them with the old bucket.
      const savedS3 = project?.config?.s3 || {};
      const savedBucket = String(savedS3.bucket || savedS3.name || '').trim();
      const savedRegion = String(savedS3.region || '').trim();
      const pendingSave =
        verifiedS3.bucket !== savedBucket || verifiedS3.region !== savedRegion;

      form.setFieldsValue({
        config: {
          ...form.getFieldValue('config'),
          s3: {
            ...(form.getFieldValue(['config', 's3']) || {}),
            ...verifiedS3,
          },
        },
      });

      setS3Verified(true);
      setS3Dirty(pendingSave);
      setS3VerifyError('');

      message.success(
        formatKnowledgeSourceVerified(verifiedS3.bucket, { pendingSave }),
        pendingSave ? 6 : undefined,
      );
    } catch (error) {
      console.error('Error verifying S3 configuration:', error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        const friendly = humanizeApiError(detail, { context: ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY });
        setS3VerifyError(friendly);
        message.error(friendly);
      } else {
        setS3VerifyError('Failed to verify Knowledge Source configuration. Please check source name and region.');
        message.error('Failed to verify Knowledge Source configuration. Please check source name and region.');
      }
    } finally {
      setS3Verifying(false);
    }
  };

  const loadLocalVectorCacheAssessment = useCallback(async () => {
    if (!token || !projectId) return null;
    try {
      const [assessmentResult, healthResult] = await Promise.allSettled([
        API.assessLocalVectorCache(token, projectId),
        API.checkVectorHealth(projectId),
      ]);

      const assessment = assessmentResult.status === 'fulfilled' ? assessmentResult.value.data || null : null;
      const health = healthResult.status === 'fulfilled' ? healthResult.value.data || null : null;
      if (assessmentResult.status === 'rejected') {
        console.error('Failed to assess local vector cache:', assessmentResult.reason);
      }
      if (healthResult.status === 'rejected') {
        console.error('Failed to check vector DB health:', healthResult.reason);
      }
      setLocalVectorCacheAssessment(assessment);
      setVectorDbHealth(health);
      return assessment;
    } catch (err) {
      console.error('Failed to assess local vector cache:', err);
      setLocalVectorCacheAssessment(null);
      setVectorDbHealth(null);
      return null;
    }
  }, [token, projectId]);

  const vectorDbVectorCount = Number(vectorDbHealth?.vector_count || 0);
  const vectorDbAlreadyHealthy = Boolean(vectorDbHealth?.healthy && vectorDbVectorCount > 0);
  const vectorDbRebuildDisabledMessage = vectorDbAlreadyHealthy
    ? 'Vector DB is healthy. Rebuild is disabled because recovery is not needed.'
    : '';

  const handleRebuildVectorsFromCache = async () => {
    if (!token) {
      message.error('You must be logged in to rebuild vectors.');
      return;
    }
    setRebuildingVectorsFromCache(true);
    try {
      const assessment = localVectorCacheAssessment || (await loadLocalVectorCacheAssessment());
      const rebuildable = assessment?.rebuildable_chunks ?? 0;
      if (!rebuildable) {
        message.warning(
          'No local embeddings found to push. Complete the embedding step first, or check app/storage/projects on the server.',
        );
        return;
      }
      let health = vectorDbHealth;
      if (!health) {
        try {
          health = (await API.checkVectorHealth(projectId)).data;
          setVectorDbHealth(health || null);
        } catch (healthErr) {
          console.error('Failed to check vector DB health before rebuild:', healthErr);
        }
      }
      const currentVectorCount = Number(health?.vector_count || 0);
      if (health?.healthy && currentVectorCount > 0) {
        message.info(formatVectorDbHealthySkip(currentVectorCount));
        return;
      }
      const res = await API.rebuildVectorsFromCache(token, projectId);
      const pushed = res.data?.push_result?.pushed_count ?? 0;
      const after = res.data?.milvus_entities_after;
      message.success(formatRebuildVectorsSuccess(pushed, after));
      const response = await API.getProject(projectId, { token });
      const inputGuardNames = response.data.config.guardrails?.input_guards?.map(g => g.guard_name) || [];
      const preEmbeddingGuardNames = response.data.config.guardrails?.pre_embedding_guards?.map(g => g.guard_name) || [];
      const contextGuardNames = response.data.config.guardrails?.context_guards?.map(g => g.guard_name) || [];
      const outputGuardNames = response.data.config.guardrails?.output_guards?.map(g => g.guard_name) || [];

      const inputGuardsConfig = {};
      response.data.config.guardrails?.input_guards?.forEach(guard => {
        inputGuardsConfig[guard.guard_name] = guard.parameters;
      });

      const outputGuardsConfig = {};
      response.data.config.guardrails?.output_guards?.forEach(guard => {
        outputGuardsConfig[guard.guard_name] = guard.parameters;
      });
      const preEmbeddingGuardsConfig = {};
      response.data.config.guardrails?.pre_embedding_guards?.forEach(guard => {
        preEmbeddingGuardsConfig[guard.guard_name] = guard.parameters;
      });
      const contextGuardsConfig = {};
      response.data.config.guardrails?.context_guards?.forEach(guard => {
        contextGuardsConfig[guard.guard_name] = guard.parameters;
      });

      const updated = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          guardrails: {
            ...response.data.config.guardrails,
            input_guards: inputGuardNames,
            pre_embedding_guards: preEmbeddingGuardNames,
            context_guards: contextGuardNames,
            output_guards: outputGuardNames,
            input_guards_config: inputGuardsConfig,
            pre_embedding_guards_config: preEmbeddingGuardsConfig,
            context_guards_config: contextGuardsConfig,
            output_guards_config: outputGuardsConfig,
          },
        },
        meta: response.data.meta,
      };
      setProject(updated);
      form.setFieldsValue(updated);
      setSelectedInputGuards(inputGuardNames);
      setSelectedPreEmbeddingGuards(preEmbeddingGuardNames);
      setSelectedContextGuards(contextGuardNames);
      setSelectedOutputGuards(outputGuardNames);
      await loadLocalVectorCacheAssessment();
    } catch (err) {
      console.error('Rebuild vectors from cache failed:', err);
      message.error(humanizeApiError(err, { context: ERROR_CONTEXT.REBUILD_VECTORS }));
    } finally {
      setRebuildingVectorsFromCache(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'vector_store' || !canEdit || !token || !projectId) return;
    loadLocalVectorCacheAssessment();
  }, [activeTab, canEdit, token, projectId, loadLocalVectorCacheAssessment]);

  const collectionCheckTimerRef = useRef(null);
  const collectionCheckSeqRef = useRef(0);

  const checkCollectionAvailability = useCallback(async (name, vectorStoreType) => {
    if (!name || !name.trim()) {
      setShowCollectionStatus(false);
      setCollectionConnectionError('');
      return;
    }
    if (!token) {
      return;
    }

    const username = localStorage.getItem('username') || '';
    const formattedCollectionName = formatCollectionNameWithUser(name, username);
    const requestSeq = ++collectionCheckSeqRef.current;

    setCheckingCollection(true);
    setCollectionConnectionError('');
    try {
      const response = await API.checkCollectionExists(formattedCollectionName, vectorStoreType, token);
      if (requestSeq !== collectionCheckSeqRef.current) {
        return;
      }

      const result = interpretCollectionCheckResponse(response.data, vectorStoreType);
      applyCollectionCheckResult(result, {
        setCollectionAvailable,
        setCollectionConnectionError,
        setShowCollectionStatus,
      });
    } catch (error) {
      if (requestSeq !== collectionCheckSeqRef.current) {
        return;
      }
      console.error('Error checking collection:', error);
      const result = interpretCollectionCheckResponse(error.response?.data, vectorStoreType);
      applyCollectionCheckResult(result, {
        setCollectionAvailable,
        setCollectionConnectionError,
        setShowCollectionStatus,
      });
      if (result.kind === 'connection_error') {
        message.error(result.message);
      }
    } finally {
      if (requestSeq === collectionCheckSeqRef.current) {
        setCheckingCollection(false);
      }
    }
  }, [token]);

  const scheduleCollectionAvailabilityCheck = useCallback((name, storeType) => {
    if (collectionCheckTimerRef.current) {
      clearTimeout(collectionCheckTimerRef.current);
    }
    if (!name || !String(name).trim()) {
      setShowCollectionStatus(false);
      setCollectionConnectionError('');
      return;
    }
    collectionCheckTimerRef.current = setTimeout(() => {
      checkCollectionAvailability(name, storeType);
    }, 450);
  }, [checkCollectionAvailability]);

  useEffect(() => () => {
    if (collectionCheckTimerRef.current) {
      clearTimeout(collectionCheckTimerRef.current);
    }
  }, []);

  const executeConfigSave = async (values, vectorMigration) => {
    setConfigSaving(true);
    try {
      // Check collection name availability if vector_store config is being updated
      if (values.config?.vector_store?.collection_name) {
        const newCollectionName = values.config.vector_store.collection_name;
        const oldCollectionName = project?.config?.vector_store?.collection_name;
        const vectorStoreType = values.config.vector_store.type || project?.config?.vector_store?.type || 'milvus';
        
        // Only check if collection name is changing
        if (newCollectionName !== oldCollectionName) {
          // Check availability synchronously before saving
          try {
            // Get username from localStorage
            const username = localStorage.getItem('username') || '';
            
            // Format collection name with username prefix (same as backend)
            const formattedCollectionName = formatCollectionNameWithUser(newCollectionName, username);
            
            const response = await API.checkCollectionExists(formattedCollectionName, vectorStoreType, token);
            const available = response.data.available;

            if (!available) {
              message.error(
                humanizeCollectionExistsError(newCollectionName, formattedCollectionName, vectorStoreType),
              );
              return;
            }
          } catch (error) {
            console.error('Error checking collection:', error);
            // On error, show warning but allow save (backend will also validate)
            if (error.response?.data?.error) {
              message.warning(`Warning: Could not verify collection availability: ${error.response.data.error}. Backend will validate on save.`);
            }
          }
        }
      }
      
      // Build guard configs using React state (selectedXxxGuards) as source of truth
      // for guard names, and form values with project fallback for parameters
      const getGuardParams = (type, guardName) => {
        // Try form value first, then fall back to project state
        const fromForm = form.getFieldValue(['config', 'guardrails', `${type}_guards_config`, guardName]);
        if (fromForm && Object.keys(fromForm).length > 0) return fromForm;
        const fromProject = project?.config?.guardrails?.[`${type}_guards_config`]?.[guardName];
        if (fromProject && Object.keys(fromProject).length > 0) return fromProject;
        // Default params
        const defaults = {};
        defaults.on_fail = (type === 'pre_embedding' || type === 'context') && guardName === 'DetectPII' ? 'fix' : guardName === 'DetectPII' ? 'noop' : 'exception';
        if (guardName === 'DetectPII') { defaults.entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER']; defaults.pii_score_threshold = 0.7; }
        return defaults;
      };

      const inputGuards = selectedInputGuards.map(guardName => ({
        guard_name: guardName,
        parameters: getGuardParams('input', guardName),
      }));
      const preEmbeddingGuards = selectedPreEmbeddingGuards.map(guardName => ({
        guard_name: guardName,
        parameters: getGuardParams('pre_embedding', guardName),
      }));
      const contextGuards = selectedContextGuards.map(guardName => ({
        guard_name: guardName,
        parameters: getGuardParams('context', guardName),
      }));
      const outputGuards = selectedOutputGuards.map(guardName => ({
        guard_name: guardName,
        parameters: getGuardParams('output', guardName),
      }));
      
      // Build config update - include name and description if they changed
      const embeddingFromForm = values.config?.embedding || project?.config?.embedding || {};
      const includeEmbedding = vectorMigration?.change_type === 'embedding_change'
        || (
          (embeddingFromForm.model != null && embeddingFromForm.model !== project?.config?.embedding?.model)
          || (
            embeddingFromForm.model_name != null
            && embeddingFromForm.model_name !== project?.config?.embedding?.model_name
          )
        );
      const { s3: _formS3, ...configRest } = values.config || {};
      const configUpdate = {
        ...configRest,
        embedding: includeEmbedding ? embeddingFromForm : (project?.config?.embedding || embeddingFromForm),
        guardrails: {
          enabled: values.config?.guardrails?.enabled || false,
          input_guards: inputGuards,
          pre_embedding_guards: preEmbeddingGuards,
          context_guards: contextGuards,
          output_guards: outputGuards
        }
      };
      
      // Add name and description if they exist in form values (top-level fields)
      if (values.name !== undefined) {
        configUpdate.name = values.name;
      }
      if (values.description !== undefined) {
        configUpdate.description = values.description;
      }
      // Only send S3 when bucket/region actually changed (avoid spurious backend validation)
      if (values.config?.s3 != null && hasS3ValuesChanged(values.config.s3, project?.config?.s3)) {
        const s3 = values.config.s3;
        configUpdate.s3 = {
          bucket: String(s3.bucket || s3.name || '').trim(),
          region: String(s3.region || '').trim(),
        };
      }
      // Include GraphRAG config
      if (values.config?.graphrag != null) {
        configUpdate.graphrag = values.config.graphrag;
      }

      if (vectorMigration) {
        configUpdate.vector_migration = vectorMigration;
      }

      if (!token) {
        message.error('You must be logged in to update the project.');
        return;
      }

      // Require verify only when this save actually applies a new S3 config
      if (configUpdate.s3 && !s3Verified) {
        message.error('Please verify Knowledge Source configuration before saving.');
        return;
      }

      const configResponse = await API.updateConfig(token, projectId, configUpdate);
      const response = await API.getProject(projectId, { token });
      const inputGuardNames = response.data.config.guardrails?.input_guards?.map(g => g.guard_name) || [];
      const preEmbeddingGuardNames = response.data.config.guardrails?.pre_embedding_guards?.map(g => g.guard_name) || [];
      const contextGuardNames = response.data.config.guardrails?.context_guards?.map(g => g.guard_name) || [];
      const outputGuardNames = response.data.config.guardrails?.output_guards?.map(g => g.guard_name) || [];
      
      const inputGuardsConfig = {};
      response.data.config.guardrails?.input_guards?.forEach(guard => {
        inputGuardsConfig[guard.guard_name] = guard.parameters;
      });
      
      const outputGuardsConfig = {};
      response.data.config.guardrails?.output_guards?.forEach(guard => {
        outputGuardsConfig[guard.guard_name] = guard.parameters;
      });
      const preEmbeddingGuardsConfig = {};
      response.data.config.guardrails?.pre_embedding_guards?.forEach(guard => {
        preEmbeddingGuardsConfig[guard.guard_name] = guard.parameters;
      });
      const contextGuardsConfig = {};
      response.data.config.guardrails?.context_guards?.forEach(guard => {
        contextGuardsConfig[guard.guard_name] = guard.parameters;
      });
      
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
          guardrails: {
            ...response.data.config.guardrails,
            input_guards: inputGuardNames,
            pre_embedding_guards: preEmbeddingGuardNames,
            context_guards: contextGuardNames,
            output_guards: outputGuardNames,
            input_guards_config: inputGuardsConfig,
            pre_embedding_guards_config: preEmbeddingGuardsConfig,
            context_guards_config: contextGuardsConfig,
            output_guards_config: outputGuardsConfig
          }
        },
        meta: response.data.meta
      };
      setProject(updatedProject);
      form.setFieldsValue(updatedProject);
      setSelectedInputGuards(inputGuardNames);
      setSelectedPreEmbeddingGuards(preEmbeddingGuardNames);
      setSelectedContextGuards(contextGuardNames);
      setSelectedOutputGuards(outputGuardNames);
      setEditMode(false);
      message.success(humanizeConfigSaveResponse(configResponse?.data, {
        changeType: vectorMigration?.change_type || (configUpdate.s3 ? 's3_change' : undefined),
        newBucket: configUpdate.s3?.bucket || values?.config?.s3?.bucket,
      }));
      if (
        configResponse?.data?.pipeline_reindex?.pipeline_triggered
        || configResponse?.data?.status_reset
        || configResponse?.data?.graphrag_purge
      ) {
        loadPipelineData(true);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        message.error(humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }));
      } else {
        message.error('Failed to update project. Please try again.');
      }
    } finally {
      setConfigSaving(false);
    }
  };

  const loadJiraProjectCatalog = useCallback(async ({
    credentialPayload = {},
    savedKeys = [],
    markVerified = false,
  } = {}) => {
    if (!token || !projectId) return null;
    const seq = ++jiraProjectLoadSeqRef.current;
    setJiraVerifying(true);
    setJiraVerifyError('');
    try {
      const res = await API.listJiraProjectsForProject(token, projectId, credentialPayload);
      if (seq !== jiraProjectLoadSeqRef.current) return null;
      const data = res.data || {};
      const listedProjects = normalizeJiraProjectList(data.projects);
      const mergedProjects = mergeJiraProjectOptions(
        listedProjects,
        savedKeys.map((key) => ({ key, name: key })),
      );
      if (listedProjects.length === 0 && mergedProjects.length <= savedKeys.length) {
        return { mergedProjects, listedProjects, data, empty: true };
      }
      if (credentialPayload?.jira_api_token) {
        jiraCredentialResetLockRef.current = true;
        try {
          if (data.credential_id) setJiraCredentialId(data.credential_id);
          if (data.normalized) {
            form.setFieldsValue({
              config: {
                ...(form.getFieldValue('config') || {}),
                jira: {
                  ...(form.getFieldValue(['config', 'jira']) || {}),
                  ...data.normalized,
                  credential_id: data.credential_id,
                },
              },
            });
          }
        } finally {
          jiraCredentialResetLockRef.current = false;
        }
      } else if (data.credential_id) {
        setJiraCredentialId(data.credential_id);
      }
      setJiraProjects(mergedProjects);
      if (markVerified) {
        setJiraVerified(true);
      }
      return { mergedProjects, listedProjects, data, empty: false };
    } catch (error) {
      if (seq !== jiraProjectLoadSeqRef.current) return null;
      const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
      if (rawDetail) {
        setJiraVerifyError(formatS3Error(rawDetail).replace(/S3/gi, 'Jira'));
      }
      return null;
    } finally {
      if (seq === jiraProjectLoadSeqRef.current) {
        setJiraVerifying(false);
      }
    }
  }, [token, projectId, form]);

  useEffect(() => {
    if (!pipelineJiraPanelOpen || !isJiraKnowledgeSource || !token || !projectId) return;
    void loadJiraProjectCatalog({ savedKeys: pipelineConfiguredProjectKeys });
    // Load catalog once when the modal opens; selected keys are merged into options locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pipelineJiraPanelOpen,
    isJiraKnowledgeSource,
    token,
    projectId,
    loadJiraProjectCatalog,
  ]);

  useEffect(() => {
    if (!pipelineJiraPanelOpen || !isJiraKnowledgeSource || !token || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.getJiraMonitoredIssues(token, projectId);
        if (!cancelled) {
          setJiraMonitoredIssues(res?.data?.monitored_issues || {});
        }
      } catch {
        if (!cancelled) {
          const monitored = project?.config?.jira?.monitored_issues;
          setJiraMonitoredIssues(monitored && typeof monitored === 'object' ? monitored : {});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineJiraPanelOpen, isJiraKnowledgeSource, token, projectId]);

  useEffect(() => {
    if (
      !pipelineJiraPanelOpen
      || sourceSettingsActiveKey !== 'azure_wiki'
      || !isAzureWikiKnowledgeSource
      || !token
      || !projectId
    ) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API.getAzureWikiMonitoredPages(token, projectId);
        if (!cancelled) {
          setAzureWikiMonitoredPages(res?.data?.monitored_pages || {});
        }
      } catch {
        if (!cancelled) {
          const monitored = project?.config?.azure_wiki?.monitored_pages;
          setAzureWikiMonitoredPages(monitored && typeof monitored === 'object' ? monitored : {});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pipelineJiraPanelOpen,
    sourceSettingsActiveKey,
    isAzureWikiKnowledgeSource,
    token,
    projectId,
  ]);

  const fetchJiraIssueScopeForProject = useCallback(
    (payload) => API.getJiraIssueScopeForProject(token, projectId, payload),
    [token, projectId],
  );

  const syncJiraScopesToForm = useCallback((keys, scopeMap) => {
    form.setFieldsValue({
      config: {
        ...(form.getFieldValue('config') || {}),
        jira: {
          ...(form.getFieldValue(['config', 'jira']) || {}),
          scopes: buildJiraScopesFormValue(keys, scopeMap),
        },
      },
    });
  }, [form]);

  const handleEnterEditMode = () => {
    if (activeTab === 's3' && isJiraKnowledgeSource) {
      const jiraScopes = normalizeJiraScopesFromConfig(project?.config?.jira || {});
      const projectKeys = jiraScopes
        .map((scope) => String(scope?.project_key || '').trim())
        .filter(Boolean);
      const scopeMap = scopeMapFromJiraScopes(jiraScopes);
      setJiraProjects(projectKeys.map((key) => ({ key, name: scopeMap[key]?.name || key })));
      setSelectedJiraProjectKeys(projectKeys);
      setJiraScopeByProjectKey(scopeMap);
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          jira: {
            ...(form.getFieldValue(['config', 'jira']) || {}),
            scopes: buildJiraScopesFormValue(projectKeys, scopeMap),
          },
        },
      });
      setJiraVerified(false);
      setJiraVerifyError('');
      setJiraCredentialId(project?.config?.jira?.credential_id || '');
      setEditMode(true);
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          jira: {
            ...(form.getFieldValue(['config', 'jira']) || {}),
            jira_api_token: '',
          },
        },
      });
      if (token && projectId) {
        void loadJiraProjectCatalog({
          savedKeys: projectKeys,
          markVerified: false,
        });
      }
      return;
    }
    if (activeTab === 's3' && isAzureWikiKnowledgeSource) {
      setAzureWikiVerified(false);
      setAzureWikiVerifyError('');
      setAzureWikiCredentialId(project?.config?.azure_wiki?.credential_id || '');
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          azure_wiki: {
            ...(form.getFieldValue(['config', 'azure_wiki']) || {}),
            ...(project?.config?.azure_wiki || {}),
            organization_url: project?.config?.azure_wiki?.organization_url || '',
            pat: '',
          },
        },
      });
      setEditMode(true);
      return;
    }
    if (activeTab === 's3' && isGithubKnowledgeSource) {
      setGithubVerified(false);
      setGithubVerifyError('');
      setGithubCredentialId(project?.config?.github?.credential_id || '');
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          github: {
            ...(form.getFieldValue(['config', 'github']) || {}),
            ...(project?.config?.github || {}),
            github_base_url: project?.config?.github?.github_base_url || '',
            pat: '',
          },
        },
      });
      setEditMode(true);
      return;
    }
    setEditMode(true);
  };

  const handleVerifyJiraKnowledgeSource = async () => {
    try {
      const fields = [
        ['config', 'jira', 'jira_base_url'],
        ['config', 'jira', 'jira_user_email'],
        ['config', 'jira', 'jira_api_token'],
      ];
      const values = await form.validateFields(fields);
      const jf = values?.config?.jira || {};
      if (!token) {
        message.error('You must be logged in to verify Jira configuration.');
        return;
      }
      const existingScopes = normalizeJiraScopesFromConfig(form.getFieldValue(['config', 'jira']) || {});
      const existingMap = scopeMapFromJiraScopes(existingScopes);
      const preservedKeys = (selectedJiraProjectKeys.length > 0
        ? selectedJiraProjectKeys
        : existingScopes.map((scope) => String(scope?.project_key || '').trim()).filter(Boolean)
      ).filter((key, index, arr) => arr.indexOf(key) === index);
      const result = await loadJiraProjectCatalog({
        credentialPayload: {
          jira_base_url: jf.jira_base_url,
          jira_user_email: jf.jira_user_email,
          jira_api_token: jf.jira_api_token,
        },
        savedKeys: preservedKeys,
        markVerified: true,
      });
      if (!result) {
        message.error('Failed to verify Jira configuration.');
        return;
      }
      if (result.empty) {
        message.warning('Account verified, but the project list could not be refreshed. Try Connect again.');
        return;
      }
      const { mergedProjects, listedProjects, data } = result;
      const nextScopeMap = { ...existingMap };
      preservedKeys.forEach((key) => {
        if (!nextScopeMap[key]) nextScopeMap[key] = { ...createDefaultJiraScope() };
      });
      const scopesForForm = buildJiraScopesFormValue(preservedKeys, nextScopeMap);
      jiraCredentialResetLockRef.current = true;
      try {
        form.setFieldsValue({
          config: {
            ...(form.getFieldValue('config') || {}),
            jira: {
              ...(form.getFieldValue(['config', 'jira']) || {}),
              ...data.normalized,
              credential_id: data.credential_id,
              scopes: scopesForForm,
            },
          },
        });
        setSelectedJiraProjectKeys(preservedKeys);
        setJiraScopeByProjectKey(nextScopeMap);
      } finally {
        jiraCredentialResetLockRef.current = false;
      }
      if (mergedProjects.length === 0) {
        message.warning('Account verified but no Jira projects were returned. Check API token permissions.');
      } else if (listedProjects.length === 0) {
        message.warning('Account verified, but the project list could not be refreshed. Try Connect again.');
      } else {
        message.success(`Jira account verified. ${mergedProjects.length} project(s) available.`);
      }
    } catch (error) {
      const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
      const friendly = formatS3Error(rawDetail).replace(/S3/gi, 'Jira');
      setJiraVerifyError(friendly);
      message.error(friendly);
    }
  };

  const executeAzureWikiSave = async (payload) => {
    if (!token) {
      message.error('You must be logged in to update Azure Wiki configuration.');
      return;
    }
    setConfigSaving(true);
    try {
      const updateRes = await API.updateAzureWikiConfig(token, projectId, payload);
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue({
        ...updatedProject,
        config: {
          ...updatedProject.config,
          azure_wiki: {
            ...(updatedProject.config?.azure_wiki || {}),
            pat: '',
          },
        },
      });
      setAzureWikiVerified(false);
      setAzureWikiVerifyError('');
      setAzureWikiCredentialId(updatedProject.config?.azure_wiki?.credential_id || '');
      setEditMode(false);
      message.success(
        updateRes.data?.message
          || 'Azure Wiki connection saved. Vector store is being cleared and reprocessed in the background.',
        6,
      );
      loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update Azure Wiki connection.');
      throw error;
    } finally {
      setConfigSaving(false);
    }
  };

  const executeGithubSave = async (payload) => {
    if (!token) {
      message.error('You must be logged in to update GitHub configuration.');
      return;
    }
    setConfigSaving(true);
    try {
      const updateRes = await API.updateGithubConfig(token, projectId, payload);
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue({
        ...updatedProject,
        config: {
          ...updatedProject.config,
          github: {
            ...(updatedProject.config?.github || {}),
            pat: '',
          },
        },
      });
      setGithubVerified(false);
      setGithubVerifyError('');
      setGithubCredentialId(updatedProject.config?.github?.credential_id || '');
      setEditMode(false);
      message.success(
        updateRes.data?.message
          || 'GitHub connection saved. Vector store is being cleared and reprocessed in the background.',
        6,
      );
      loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update GitHub connection.');
      throw error;
    } finally {
      setConfigSaving(false);
    }
  };

  const executeJiraSave = async (payload) => {
    if (!token) {
      message.error('You must be logged in to update Jira configuration.');
      return;
    }
    setConfigSaving(true);
    try {
      const updateRes = await API.updateJiraConfig(token, projectId, payload);
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue(updatedProject);
      setPipelineJiraScopeByKey(
        scopeMapFromJiraScopes(normalizeJiraScopesFromConfig(updatedProject.config?.jira || {})),
      );
      const monitored = updatedProject.config?.jira?.monitored_issues;
      setJiraMonitoredIssues(monitored && typeof monitored === 'object' ? monitored : {});
      setJiraVerified(false);
      setJiraVerifyError('');
      setEditMode(false);
      if (updateRes.data?.scope_changed) {
        message.success(
          updateRes.data?.message
            || 'Jira scope saved. Vector store is being cleared and reprocessed in the background.',
          6,
        );
      } else if (updateRes.data?.pipeline_reindex?.pipeline_triggered) {
        message.success(
          'Jira scope saved. Sync started and pipeline processing is running in the background.',
          6,
        );
      } else {
        message.success(updateRes.data?.message || 'Jira configuration updated.');
      }
      loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update Jira configuration.');
      throw error;
    } finally {
      setConfigSaving(false);
    }
  };

  const savePipelineJiraScope = useCallback(async () => {
    if (!pipelineSelectedJiraProjectKey || !canEdit || !token) return false;
    const savedJira = project?.config?.jira || {};
    const projectKeys = pipelineConfiguredProjectKeys.length
      ? pipelineConfiguredProjectKeys
      : normalizeJiraScopesFromConfig(savedJira)
        .map((scope) => String(scope?.project_key || '').trim())
        .filter(Boolean);
    if (!projectKeys.length) {
      message.error('Select at least one Jira project key.');
      return false;
    }
    const scope = pipelineJiraScopeByKey[pipelineSelectedJiraProjectKey] || createDefaultJiraScope();
    if (scope.type === JIRA_SCOPE.JQL && !String(scope.jql_query || '').trim()) {
      message.error(`Enter Custom JQL for project ${pipelineSelectedJiraProjectKey}.`);
      return false;
    }
    const payload = {
      credential_id: savedJira.credential_id,
      jira_base_url: savedJira.jira_base_url,
      jira_user_email: savedJira.jira_user_email,
      scopes: projectKeys.map((projectKey) => (
        buildJiraScopePayload(projectKey, pipelineJiraScopeByKey[projectKey] || createDefaultJiraScope())
      )),
    };
    const scopeChanged = hasJiraScopeChanged(savedJira, projectKeys, pipelineJiraScopeByKey);
    if (scopeChanged) {
      setPendingJiraSave(payload);
      setPendingChangeType('jira_scope_change');
      setDataLossConsent(false);
      setDataLossModalOpen(true);
      return true;
    }
    await executeJiraSave(payload);
    return true;
  }, [
    pipelineSelectedJiraProjectKey,
    pipelineConfiguredProjectKeys,
    canEdit,
    token,
    project?.config?.jira,
    pipelineJiraScopeByKey,
    message,
  ]);

  const handleProceedPipelineJiraScope = useCallback(async () => {
    const didStartSaveFlow = await savePipelineJiraScope();
    if (didStartSaveFlow) {
      setPipelineJiraPanelOpen(false);
    }
  }, [savePipelineJiraScope]);

  const savePipelineConfluenceScope = useCallback(async () => {
    if (!canEdit || !token || !projectId) return false;
    // Persist the currently edited space before saving all scopes.
    if (pipelineSelectedConfluenceSpaceKey) {
      applyConfluenceFolderSelection(
        selectedConfluenceFolderIds,
        confluenceFolders,
        pipelineSelectedConfluenceSpaceKey,
      );
    }
    const spaceKeys = pipelineConfiguredConfluenceSpaceKeys.length
      ? pipelineConfiguredConfluenceSpaceKeys
      : normalizeConfluenceScopesFromConfig(project?.config?.confluence || {})
        .map((scope) => String(scope?.space_key || '').trim())
        .filter(Boolean);
    if (!spaceKeys.length) {
      message.error('Select at least one Confluence space key.');
      return false;
    }

    const scopesPayload = [];
    for (const spaceKey of spaceKeys) {
      const spaceMeta = (confluenceSpaces || []).find((row) => String(row.key) === spaceKey);
      const entry = pipelineConfluenceScopeByKey[spaceKey]
        || createDefaultConfluenceSpaceScope(spaceKey, spaceMeta);
      const folderIds = (entry.folderIds?.length
        ? entry.folderIds
        : getConfluenceSelectedFolderIds(entry.sync_scope))
        .map((id) => String(id || '').trim())
        .filter(Boolean);
      // Include in-progress edits for the active space.
      const effectiveFolderIds = spaceKey === pipelineSelectedConfluenceSpaceKey
        && selectedConfluenceFolderIds.length
        ? selectedConfluenceFolderIds
        : folderIds;
      if (!effectiveFolderIds.length) {
        message.error(`Select at least one folder or page for space ${spaceKey}.`);
        setPipelineSelectedConfluenceSpaceKey(spaceKey);
        return false;
      }
      const syncScope = buildConfluenceSyncScope(
        effectiveFolderIds,
        spaceKey === pipelineSelectedConfluenceSpaceKey ? confluenceFolders : [],
        entry.sync_scope || {},
      );
      scopesPayload.push({
        space_key: spaceKey,
        space_id: spaceMeta?.id || entry.space_id || undefined,
        space_name: spaceMeta?.name || entry.space_name || spaceKey,
        parent_page_ids: effectiveFolderIds,
        folder_labels: syncScope.folder_labels,
        root_types: syncScope.root_types,
      });
    }

    setConfigSaving(true);
    try {
      const scopeRes = await API.updateConfluenceSyncScope(token, projectId, {
        scopes: scopesPayload,
      });
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue(updatedProject);
      message.success(scopeRes.data?.message || 'Confluence source settings updated.');
      loadPipelineData(true);
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update Confluence settings.');
      return false;
    } finally {
      setConfigSaving(false);
    }
  }, [
    canEdit,
    token,
    projectId,
    form,
    selectedConfluenceFolderIds,
    confluenceFolders,
    confluenceSpaces,
    pipelineSelectedConfluenceSpaceKey,
    pipelineConfiguredConfluenceSpaceKeys,
    pipelineConfluenceScopeByKey,
    applyConfluenceFolderSelection,
    project?.config?.confluence,
    message,
    loadPipelineData,
  ]);

  const savePipelineAzureWikiScope = useCallback(async () => {
    if (!canEdit || !token || !projectId) return false;
    const wikiIds = pipelineConfiguredAzureWikiWikiIds;
    if (!wikiIds.length) {
      message.error('Select at least one Azure Wiki.');
      return false;
    }
    const scopesPayload = wikiIds
      .map((wikiId) => {
        const entry = pipelineAzureWikiScopeByWikiId[wikiId] || {};
        const meta = (azureWikiWikis || []).find((w) => String(w.id) === wikiId);
        return {
          project: entry.project || meta?.project || '',
          wiki_id: wikiId,
          wiki_name: entry.wiki_name || meta?.name || wikiId,
          page_paths: entry.page_paths || [],
        };
      })
      .filter((scope) => (scope.page_paths || []).length > 0);

    if (!scopesPayload.length) {
      message.error('Select at least one wiki page for each configured wiki, then click Proceed.');
      return false;
    }

    const missingProject = scopesPayload.find((scope) => !String(scope.project || '').trim());
    if (missingProject) {
      message.error('Azure DevOps project name is missing for one of the selected wikis. Re-open Source Settings and try again.');
      return false;
    }

    setConfigSaving(true);
    try {
      const scopeRes = await API.updateAzureWikiSyncScope(token, projectId, {
        scopes: scopesPayload,
      });
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue(updatedProject);
      message.success(scopeRes.data?.message || 'Azure Wiki source settings updated.');
      loadPipelineData(true);
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update Azure Wiki settings.');
      return false;
    } finally {
      setConfigSaving(false);
    }
  }, [
    canEdit,
    token,
    projectId,
    form,
    pipelineConfiguredAzureWikiWikiIds,
    pipelineAzureWikiScopeByWikiId,
    azureWikiWikis,
    message,
    loadPipelineData,
  ]);

  const handlePipelineGithubReposChange = useCallback((nextRepoKeysRaw) => {
    const keys = Array.from(new Set((nextRepoKeysRaw || []).map((key) => String(key || '').trim()).filter(Boolean)));
    setPipelineConfiguredGithubRepoKeys(keys);
    setPipelineGithubScopeByScopeKey((prev) => {
      const next = {};
      const allowedRepoPrefixes = new Set(keys.map((repoKey) => `${repoKey}@`));
      Object.entries(prev || {}).forEach(([scopeKey, scope]) => {
        const keep = Array.from(allowedRepoPrefixes).some((prefix) => scopeKey.startsWith(prefix));
        if (keep) next[scopeKey] = scope;
      });
      keys.forEach((repoKey) => {
        const hasScope = Object.keys(next).some((scopeKey) => scopeKey.startsWith(`${repoKey}@`));
        if (hasScope) return;
        const [owner, repo] = repoKey.split('/');
        const defaultScopeKey = `${repoKey}@main`;
        next[defaultScopeKey] = {
          key: defaultScopeKey,
          owner: owner || '',
          repo: repo || '',
          branch: 'main',
          path_prefix: '',
          include_globs: [],
          selected_paths: [],
        };
      });
      return next;
    });
    setPipelineSelectedGithubRepoKey((prev) => (keys.includes(prev) ? prev : (keys[0] || '')));
    setPipelineConfiguredGithubScopeKeys((prev) => {
      const next = [];
      const seen = new Set();
      keys.forEach((repoKey) => {
        const existing = (prev || []).filter((scopeKey) => scopeKey.startsWith(`${repoKey}@`));
        if (existing.length) {
          existing.forEach((scopeKey) => {
            if (!seen.has(scopeKey)) {
              seen.add(scopeKey);
              next.push(scopeKey);
            }
          });
        } else {
          const fallback = `${repoKey}@main`;
          if (!seen.has(fallback)) {
            seen.add(fallback);
            next.push(fallback);
          }
        }
      });
      return next;
    });
  }, []);

  const savePipelineGithubScope = useCallback(async () => {
    if (!canEdit || !token || !projectId) return false;
    if (!pipelineConfiguredGithubRepoKeys.length) {
      message.error('Select at least one GitHub repository.');
      return false;
    }
    const scopes = pipelineConfiguredGithubScopeKeys
      .map((scopeKey) => pipelineGithubScopeByScopeKey[scopeKey] || {})
      .map((scope) => ({
        owner: String(scope.owner || '').trim(),
        repo: String(scope.repo || '').trim(),
        branch: String(scope.branch || 'main').trim() || 'main',
        path_prefix: String(scope.path_prefix || '').trim(),
        include_globs: Array.isArray(scope.include_globs) ? scope.include_globs.map((p) => String(p || '').trim()).filter(Boolean) : [],
        selected_paths: Array.isArray(scope.selected_paths) ? scope.selected_paths.map((p) => String(p || '').trim()).filter(Boolean) : [],
      }))
      .filter((row) => row.owner && row.repo)
      .filter((row) => pipelineConfiguredGithubRepoKeys.includes(`${row.owner}/${row.repo}`));
    if (!scopes.length) {
      message.error('Select at least one branch for selected GitHub repositories.');
      return false;
    }
    const missingPaths = scopes.filter((scope) => !(scope.selected_paths || []).length);
    if (missingPaths.length) {
      message.error('Select at least one folder or file for each selected branch.');
      return false;
    }
    setConfigSaving(true);
    try {
      const scopeRes = await API.updateGithubSyncScope(token, projectId, { scopes });
      const response = await API.getProject(projectId, { token });
      const updatedProject = {
        id: response.data.project_id,
        name: response.data.config.name,
        description: response.data.config.description,
        deployed: isProjectChatAvailable(response.data),
        config: {
          ...response.data.config,
          s3: normalizeProjectS3ForForm(response.data.config?.s3),
        },
        meta: response.data.meta,
      };
      setProject(updatedProject);
      form.setFieldsValue(updatedProject);
      const stats = scopeRes.data?.sync_stats || {};
      const filesInRepo = Number(stats.files_in_repo || 0);
      const changed = Number(stats.seeded_from_source || 0) + Number(stats.updated_from_source || 0);
      if (filesInRepo === 0) {
        message.warning('GitHub scope saved, but no matching files were found for the selected paths.');
      } else if (changed > 0) {
        message.success(`GitHub source settings updated. ${changed} file(s) ready in the pipeline.`);
      } else {
        message.success(`GitHub source settings updated. ${filesInRepo} file(s) in scope.`);
      }
      loadPipelineData(true);
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.GITHUB_VERIFY }) : 'Failed to update GitHub source settings.');
      return false;
    } finally {
      setConfigSaving(false);
    }
  }, [
    canEdit,
    token,
    projectId,
    pipelineConfiguredGithubRepoKeys,
    pipelineConfiguredGithubScopeKeys,
    pipelineGithubScopeByScopeKey,
    message,
    loadPipelineData,
    form,
  ]);

  const handleProceedPipelineAzureWikiScope = useCallback(async () => {
    const didSave = await savePipelineAzureWikiScope();
    if (didSave) {
      setPipelineJiraPanelOpen(false);
    }
  }, [savePipelineAzureWikiScope]);

  const handleProceedPipelineGithubScope = useCallback(async () => {
    const didSave = await savePipelineGithubScope();
    if (didSave) {
      setPipelineJiraPanelOpen(false);
    }
  }, [savePipelineGithubScope]);

  const handleSyncGithubNow = useCallback(async () => {
    if (!token || !projectId) return;
    setGithubSyncing(true);
    try {
      const res = await API.syncKnowledgeSource(token, projectId, { source: 'github' });
      const stats = res?.data?.stats || {};
      const filesInRepo = Number(stats.files_in_repo || 0);
      const changed = Number(stats.seeded_from_source || 0) + Number(stats.updated_from_source || 0);
      if (filesInRepo === 0) {
        message.warning('No matching GitHub files found for the current scope.');
      } else if (changed > 0) {
        message.success(`GitHub sync completed. ${changed} file(s) updated in the pipeline.`);
      } else {
        message.success(`GitHub sync completed. ${filesInRepo} file(s) in scope.`);
      }
      await loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.GITHUB_VERIFY }) : 'Failed to sync from GitHub.');
    } finally {
      setGithubSyncing(false);
    }
  }, [token, projectId, message, loadPipelineData]);

  const handleSyncAzureWikiNow = useCallback(async () => {
    if (!token || !projectId) return;
    setAzureWikiSyncing(true);
    try {
      await API.syncKnowledgeSource(token, projectId, { source: 'azure_wiki' });
      message.success('Azure Wiki sync started. New pages will appear in the file list shortly.');
      await loadPipelineData(true);
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.message;
      message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to sync from Azure Wiki.');
    } finally {
      setAzureWikiSyncing(false);
    }
  }, [token, projectId, message, loadPipelineData]);

  const handleProceedPipelineConfluenceScope = useCallback(async () => {
    const didSave = await savePipelineConfluenceScope();
    if (didSave) {
      setPipelineJiraPanelOpen(false);
    }
  }, [savePipelineConfluenceScope]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editMode && activeTab === 's3' && canEdit && isConfluenceKnowledgeSource) {
        const formScope = values.config?.confluence?.sync_scope;
        const savedScope = project?.config?.confluence?.sync_scope;
        if (!hasConfluenceSyncScopeChanged(formScope, savedScope)) {
          message.info('No folder selection changes to save.');
          setEditMode(false);
          return;
        }
        const folderIds = getConfluenceSelectedFolderIds(formScope);
        if (folderIds.length === 0) {
          message.error('Select at least one Confluence folder to process.');
          return;
        }
        if (!token) {
          message.error('You must be logged in to update folder selection.');
          return;
        }
        setConfigSaving(true);
        try {
          const scopeRes = await API.updateConfluenceSyncScope(token, projectId, {
            parent_page_ids: folderIds,
            folder_labels: formScope?.folder_labels,
            root_types: formScope?.root_types,
          });
          const response = await API.getProject(projectId, { token });
          const updatedProject = {
            id: response.data.project_id,
            name: response.data.config.name,
            description: response.data.config.description,
            deployed: isProjectChatAvailable(response.data),
            config: {
              ...response.data.config,
              s3: normalizeProjectS3ForForm(response.data.config?.s3),
            },
            meta: response.data.meta,
          };
          setProject(updatedProject);
          form.setFieldsValue(updatedProject);
          setEditMode(false);
          message.success(scopeRes.data?.message || 'Folder selection updated.');
          loadPipelineData(true);
        } catch (error) {
          const detail = error?.response?.data?.detail;
          message.error(detail ? humanizeApiError(detail, { context: ERROR_CONTEXT.PROJECT_UPDATE }) : 'Failed to update folder selection.');
        } finally {
          setConfigSaving(false);
        }
        return;
      }
      if (editMode && activeTab === 's3' && canEdit && isAzureWikiKnowledgeSource) {
        if (!hasAzureWikiKnowledgeSourceEdits) {
          message.info('No Azure Wiki connection changes to save.');
          setEditMode(false);
          return;
        }
        if (!azureWikiVerified || !azureWikiCredentialId) {
          message.error('Please connect Azure Wiki before saving.');
          return;
        }
        if (!token) {
          message.error('You must be logged in to update Azure Wiki configuration.');
          return;
        }
        const formAw = values?.config?.azure_wiki || {};
        const payload = {
          organization_url: formAw.organization_url || project?.config?.azure_wiki?.organization_url,
          credential_id: azureWikiCredentialId,
        };
        setPendingAzureWikiSave(payload);
        setPendingChangeType('azure_wiki_connection_change');
        setDataLossConsent(false);
        setDataLossModalOpen(true);
        return;
      }
      if (editMode && activeTab === 's3' && canEdit && isGithubKnowledgeSource) {
        if (!hasGithubKnowledgeSourceEdits) {
          message.info('No GitHub connection changes to save.');
          setEditMode(false);
          return;
        }
        if (!githubVerified || !githubCredentialId) {
          message.error('Please connect GitHub before saving.');
          return;
        }
        if (!token) {
          message.error('You must be logged in to update GitHub configuration.');
          return;
        }
        const formGh = values?.config?.github || {};
        const payload = {
          github_base_url: formGh.github_base_url || project?.config?.github?.github_base_url,
          credential_id: githubCredentialId,
          api_base_url: formGh.api_base_url || project?.config?.github?.api_base_url,
        };
        setPendingGithubSave(payload);
        setPendingChangeType('github_connection_change');
        setDataLossConsent(false);
        setDataLossModalOpen(true);
        return;
      }
      if (editMode && activeTab === 's3' && canEdit && isJiraKnowledgeSource) {
        const formJira = values.config?.jira || {};
        const savedJira = project?.config?.jira || {};
        if (!hasJiraConfigChanged(formJira, savedJira)) {
          message.info('No Jira configuration changes to save.');
          setEditMode(false);
          return;
        }
        if (!jiraVerified || !jiraCredentialId) {
          message.error('Please verify Jira configuration before saving.');
          return;
        }
        if (!token) {
          message.error('You must be logged in to update Jira configuration.');
          return;
        }
        for (const key of selectedJiraProjectKeys) {
          const scope = jiraScopeByProjectKey[key] || createDefaultJiraScope();
          if (scope.type === JIRA_SCOPE.JQL && !String(scope.jql_query || '').trim()) {
            message.error(`Enter Custom JQL for project ${key}.`);
            return;
          }
        }
        const payload = {
          credential_id: jiraCredentialId,
          jira_base_url: formJira.jira_base_url,
          jira_user_email: formJira.jira_user_email,
          scopes: selectedJiraProjectKeys.map((projectKey) => (
            buildJiraScopePayload(projectKey, jiraScopeByProjectKey[projectKey])
          )),
        };
        const scopeChanged = hasJiraScopeChanged(savedJira, selectedJiraProjectKeys, jiraScopeByProjectKey);
        if (scopeChanged) {
          setPendingJiraSave(payload);
          setPendingChangeType('jira_scope_change');
          setDataLossConsent(false);
          setDataLossModalOpen(true);
          return;
        }
        await executeJiraSave(payload);
        return;
      }
      if (
        editMode
        && activeTab === 's3'
        && canEdit
        && !isConfluenceKnowledgeSource
        && !isJiraKnowledgeSource
        && !isAzureWikiKnowledgeSource
        && !isGithubKnowledgeSource
        && !hasS3ValuesChanged(values.config?.s3, project?.config?.s3)
      ) {
        message.info('No Knowledge Source changes to save.');
        setEditMode(false);
        setS3Dirty(false);
        setS3Verified(false);
        setS3VerifyError('');
        return;
      }
      if (
        editMode
        && activeTab === 's3'
        && canEdit
        && !isConfluenceKnowledgeSource
        && !isJiraKnowledgeSource
        && hasS3ValuesChanged(values.config?.s3, project?.config?.s3)
        && !s3Verified
      ) {
        message.error('Please verify Knowledge Source configuration before saving.');
        return;
      }
      const sensitive = detectSensitiveConfigChange(values);
      if (sensitive.required && canEdit) {
        setPendingConfigValues(values);
        setPendingChangeType(sensitive.changeType);
        setPendingEmbeddingAlsoChanged(Boolean(sensitive.embeddingAlsoChanged));
        setDataLossConsent(false);
        setCopyVectorsToNewCollection(null);
        if (sensitive.changeType === 'collection_rename') {
          if (sensitive.embeddingAlsoChanged) {
            setCopyVectorsToNewCollection(false);
            setDataLossModalOpen(true);
          } else {
            setMigrateChoiceModalOpen(true);
          }
        } else if (sensitive.changeType === 's3_change') {
          const previousBucket = project?.config?.s3?.bucket || project?.config?.s3?.name;
          setDeleteOldS3Files(null);
          if (previousBucket) {
            setS3ChangeModalOpen(true);
          } else {
            setDeleteOldS3Files(false);
            setDataLossModalOpen(true);
          }
        } else {
          setDataLossModalOpen(true);
        }
        return;
      }
      await executeConfigSave(values, null);
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error validating project form:', error);
    }
  };

  const confirmSensitiveSave = async () => {
    if (!dataLossConsent) {
      message.warning('Please confirm that you understand vectors will be cleared or replaced.');
      return;
    }
    if (pendingChangeType === 'jira_scope_change') {
      if (!pendingJiraSave) return;
      setDataLossModalOpen(false);
      try {
        await executeJiraSave({
          ...pendingJiraSave,
          acknowledge_data_loss: true,
        });
      } finally {
        setPendingJiraSave(null);
        setPendingChangeType(null);
        setDataLossConsent(false);
      }
      return;
    }
    if (pendingChangeType === 'azure_wiki_connection_change') {
      if (!pendingAzureWikiSave) return;
      setDataLossModalOpen(false);
      try {
        await executeAzureWikiSave({
          ...pendingAzureWikiSave,
          acknowledge_data_loss: true,
        });
      } finally {
        setPendingAzureWikiSave(null);
        setPendingChangeType(null);
        setDataLossConsent(false);
      }
      return;
    }
    if (pendingChangeType === 'github_connection_change') {
      if (!pendingGithubSave) return;
      setDataLossModalOpen(false);
      try {
        await executeGithubSave({
          ...pendingGithubSave,
          acknowledge_data_loss: true,
        });
      } finally {
        setPendingGithubSave(null);
        setPendingChangeType(null);
        setDataLossConsent(false);
      }
      return;
    }
    if (!pendingConfigValues) return;
    const migration = {
      acknowledge_data_loss: true,
      change_type: pendingChangeType,
    };
    if (pendingChangeType === 'collection_rename') {
      if (pendingEmbeddingAlsoChanged) {
        migration.copy_vectors_to_new_collection = false;
      } else if (copyVectorsToNewCollection === null) {
        message.warning('Choose whether to copy vectors into the new collection or start empty.');
        return;
      } else {
        migration.copy_vectors_to_new_collection = copyVectorsToNewCollection;
      }
    }
    if (pendingChangeType === 's3_change') {
      const previousBucket = project?.config?.s3?.bucket || project?.config?.s3?.name;
      if (previousBucket && deleteOldS3Files === null) {
        message.warning('Choose whether to delete files from the previous Knowledge Source bucket.');
        return;
      }
      migration.delete_old_s3_files = Boolean(deleteOldS3Files);
    }
    setDataLossModalOpen(false);
    setMigrateChoiceModalOpen(false);
    setS3ChangeModalOpen(false);
    await executeConfigSave(pendingConfigValues, migration);
    setPendingConfigValues(null);
    setPendingChangeType(null);
    setCopyVectorsToNewCollection(null);
    setPendingEmbeddingAlsoChanged(false);
    setDeleteOldS3Files(null);
    setDataLossConsent(false);
  };

  const handleCancel = () => {
    form.setFieldsValue({
      ...project,
      config: {
        ...project.config,
        s3: normalizeProjectS3ForForm(project.config?.s3),
      },
    });
    setChunkingStrategy(project.config?.chunking?.strategy || 'recursive');
    setSelectedLlmProvider(project.config?.llm?.provider || 'openai');
    setSelectedInputGuards(project.config?.guardrails?.input_guards || []);
    setSelectedPreEmbeddingGuards(project.config?.guardrails?.pre_embedding_guards || []);
    setSelectedContextGuards(project.config?.guardrails?.context_guards || []);
    setSelectedOutputGuards(project.config?.guardrails?.output_guards || []);
    setS3Dirty(false);
    setS3Verified(false);
    setS3VerifyError('');
    setJiraVerified(false);
    setJiraVerifyError('');
    setJiraCredentialId(project?.config?.jira?.credential_id || '');
    const jiraScopes = normalizeJiraScopesFromConfig(project?.config?.jira || {});
    const projectKeys = jiraScopes
      .map((scope) => String(scope?.project_key || '').trim())
      .filter(Boolean);
    const scopeMap = scopeMapFromJiraScopes(jiraScopes);
    setJiraProjects(projectKeys.map((key) => ({ key, name: key })));
    setSelectedJiraProjectKeys(projectKeys);
    setJiraScopeByProjectKey(scopeMap);
    setAzureWikiVerified(false);
    setAzureWikiVerifyError('');
    setAzureWikiCredentialId(project?.config?.azure_wiki?.credential_id || '');
    if (isAzureWikiKnowledgeSource) {
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          azure_wiki: {
            ...(project?.config?.azure_wiki || {}),
            organization_url: project?.config?.azure_wiki?.organization_url || '',
            pat: '',
          },
        },
      });
    }
    setGithubVerified(false);
    setGithubVerifyError('');
    setGithubCredentialId(project?.config?.github?.credential_id || '');
    if (isGithubKnowledgeSource) {
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          github: {
            ...(project?.config?.github || {}),
            github_base_url: project?.config?.github?.github_base_url || '',
            pat: '',
          },
        },
      });
    }
    setEditMode(false);
  };

  const handleDeleteProject = () => {
    setDeleteConfirmInput('');
    setDeleteEmptyS3(false);
    setDeleteModalVisible(true);
  };

  const projectDeleteDisplayName = formatProjectDisplayName(project?.name, { fallback: '' });
  const deleteConfirmNameMatches = (input) => {
    const value = (input || '').trim();
    const rawName = (project?.name || '').trim();
    return value === rawName || value === projectDeleteDisplayName;
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmNameMatches(deleteConfirmInput)) {
      message.error(`Project name does not match. Please type "${projectDeleteDisplayName}".`);
      return;
    }
    if (!token) {
      message.error('You must be logged in to delete a project.');
      return;
    }
    try {
      if (rbacInfo?.rbac_id) {
        await API.deleteRbacProject(token, rbacInfo.rbac_id, deleteEmptyS3);
      } else {
        await API.deleteProject(token, projectId, deleteEmptyS3);
      }
      message.success(formatProjectDeleted({
        emptyBucket: deleteEmptyS3,
        bucketName: project?.config?.s3?.bucket || project?.config?.s3?.name,
      }));
      setDeleteModalVisible(false);
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      message.error(humanizeApiError(error, { context: ERROR_CONTEXT.PROJECT_DELETE }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteConfirmInput('');
    setDeleteEmptyS3(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!project) return null;

  const isFilePipelineTab = activeTab === 'file_pipeline';

  const editableTabs = ['details', 'graphrag', 'retriever', 'llm'];
  const adminSensitiveTabs = ['s3', 'embedding', 'vector_store'];
  const tabSupportsEdit = editableTabs.includes(activeTab)
    || (canEdit && adminSensitiveTabs.includes(activeTab));

  const detectSensitiveConfigChange = (values) => {
    const oldEmb = project?.config?.embedding || {};
    const newEmb = values?.config?.embedding || {};
    const embeddingChanged = values?.config?.embedding != null && (
      (newEmb.model != null && newEmb.model !== oldEmb.model)
      || (newEmb.model_name != null && newEmb.model_name !== oldEmb.model_name)
    );

    const s3Changed = values?.config?.s3 != null
      && hasS3ValuesChanged(values.config.s3, project?.config?.s3);

    const oldCollRaw = extractRawCollectionName(project?.config?.vector_store?.collection_name || '');
    const newCollRaw = String(values?.config?.vector_store?.collection_name || '').trim();
    const username = localStorage.getItem('username') || '';
    const formattedNew = newCollRaw
      ? formatCollectionNameWithUser(newCollRaw, username)
      : '';
    const oldFormatted = project?.config?.vector_store?.collection_name || '';
    const collectionRenamed = Boolean(
      values?.config?.vector_store?.collection_name != null
      && newCollRaw
      && formattedNew !== oldFormatted,
    );

    if (collectionRenamed) {
      return {
        required: true,
        changeType: 'collection_rename',
        embeddingAlsoChanged: embeddingChanged,
      };
    }
    if (embeddingChanged) return { required: true, changeType: 'embedding_change' };
    if (s3Changed) return { required: true, changeType: 's3_change' };
    return { required: false, changeType: null };
  };

  const saveDisabled =
    !knowledgeSourceHasTabEdits
    || (
      editMode
      && canEdit
      && activeTab === 's3'
      && isConfluenceKnowledgeSource
      && selectedConfluenceFolderIds.length === 0
      && knowledgeSourceHasTabEdits
    )
    || (
      editMode
      && canEdit
      && activeTab === 's3'
      && isAzureWikiKnowledgeSource
      && knowledgeSourceHasTabEdits
      && !azureWikiVerified
    )
    || (
      editMode
      && canEdit
      && activeTab === 's3'
      && isGithubKnowledgeSource
      && knowledgeSourceHasTabEdits
      && !githubVerified
    )
    || (editMode && canEdit && activeTab === 's3' && !isConfluenceKnowledgeSource && !isJiraKnowledgeSource && !isAzureWikiKnowledgeSource && !isGithubKnowledgeSource && hasS3ConfigChanges && !s3Verified)
    || (editMode && canEdit && activeTab === 's3' && isJiraKnowledgeSource && knowledgeSourceHasTabEdits && !jiraVerified)
    || s3Verifying
    || jiraVerifying
    || azureWikiVerifying
    || githubVerifying
    || confluenceFoldersLoading;
  const showVectorRebuildHeaderAction = SHOW_VECTOR_DB_REBUILD && canEdit && activeTab === 'vector_store';
  const vectorRebuildHeaderButton = (
    <Tooltip title={vectorDbRebuildDisabledMessage}>
      <span style={{ display: 'inline-block' }}>
        <Popconfirm
          title="Rebuild vector index from local cache?"
          description="This resets vector push flags and re-inserts embeddings from disk into Milvus/Pinecone."
          onConfirm={handleRebuildVectorsFromCache}
          okText="Rebuild"
          cancelText="Cancel"
          disabled={vectorDbAlreadyHealthy}
        >
          <Button
            icon={<DatabaseOutlined />}
            loading={rebuildingVectorsFromCache}
            disabled={vectorDbAlreadyHealthy}
          >
            Vector DB Rebuild
          </Button>
        </Popconfirm>
      </span>
    </Tooltip>
  );

  const activeJiraMonitorChange = jiraMonitorPendingUpdates[0] || null;
  const jiraMonitorBannerRow = jiraMonitorActiveRow || jiraLiveProcessingRow;
  const jiraMonitorBannerIssueKey = String(
    jiraMonitorActiveUpdate?.issue_key
      || jiraMonitorBannerRow?.source_meta?.issue_key
      || '',
  ).trim();
  const jiraMonitorActiveStatus = String(jiraMonitorBannerRow?.processing_status || '').toLowerCase();
  const jiraMonitorActiveStage = formatPipelineStage(
    jiraMonitorBannerRow?.processing_stage,
    jiraMonitorBannerRow,
  );
  const jiraMonitorIsWorking = jiraMonitorActiveStatus === 'processing'
    || jiraMonitorActiveStatus === 'picked';

  const activeConfluenceMonitorChange = confluenceMonitorPendingUpdates[0] || null;
  const confluenceMonitorActiveStatus = String(confluenceMonitorActiveRow?.processing_status || '').toLowerCase();
  const confluenceMonitorActiveStage = formatPipelineStage(
    confluenceMonitorActiveRow?.processing_stage,
    confluenceMonitorActiveRow,
  );
  const confluenceMonitorIsWorking = confluenceMonitorActiveStatus === 'processing'
    || confluenceMonitorActiveStatus === 'picked';
  const confluenceMonitorBannerPageId = String(
    confluenceMonitorActiveUpdate?.page_id
      || confluenceMonitorActiveRow?.source_meta?.page_id
      || '',
  ).trim();
  const confluenceMonitorBannerTitle = String(
    confluenceMonitorActiveRow?.source_meta?.title
      || confluenceMonitorActiveRow?.file_name
      || '',
  ).trim();

  const activeAzureWikiMonitorChange = azureWikiMonitorPendingUpdates[0] || null;
  const azureWikiMonitorActiveStatus = String(azureWikiMonitorActiveRow?.processing_status || '').toLowerCase();
  const azureWikiMonitorActiveStage = formatPipelineStage(
    azureWikiMonitorActiveRow?.processing_stage,
    azureWikiMonitorActiveRow,
  );
  const azureWikiMonitorIsWorking = azureWikiMonitorActiveStatus === 'processing'
    || azureWikiMonitorActiveStatus === 'picked';
  const azureWikiMonitorBannerPageId = String(
    azureWikiMonitorActiveUpdate?.page_id
      || azureWikiMonitorActiveRow?.source_meta?.page_id
      || '',
  ).trim();
  const azureWikiMonitorBannerTitle = String(
    azureWikiMonitorActiveRow?.source_meta?.title
      || azureWikiMonitorActiveRow?.file_name
      || '',
  ).trim();

  return (
    <div style={{  height: '100%', overflow: 'auto' }}>
      <Card
       
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
              <Tooltip title="Project name">
                <FolderOutlined
                  aria-hidden
                  style={{
                    fontSize: 22,
                    color: themeStyles.textSecondary,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: themeStyles.textPrimary,
                  lineHeight: 1.25,
                  textShadow: 'none',
                  wordBreak: 'break-word',
                }}
              >
                {formatProjectDisplayName(project.name)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {editMode && canEdit && tabSupportsEdit ? (
                <>
                  <Button onClick={handleCancel} disabled={configSaving}>Cancel</Button>
                  <Button type="primary" onClick={handleSave} disabled={saveDisabled || configSaving} loading={configSaving}>
                    {configSaving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <>
                  {showVectorRebuildHeaderAction && vectorRebuildHeaderButton}
                  {canEdit && tabSupportsEdit && (
                    <Button onClick={handleEnterEditMode}>Edit</Button>
                  )}
                  {activeTab === 'file_pipeline' && (
                    <Button onClick={() => setFilePipelineSettingsOpen(true)}>Schedule</Button>
                  )}
                  {canEdit && (
                    <Button danger onClick={handleDeleteProject}>Delete</Button>
                  )}
                  {/* {project.deployed && activeTab !== 'file_pipeline' && !isSuperAdmin && (
                    <Button
                      type={themeStyles.isLight ? 'default' : 'primary'}
                      className={themeStyles.isLight ? 'light-theme-cta-chat-header' : undefined}
                      onClick={() => navigate(`/chat/${project.id}`)}
                    >
                      Chat with Project
                    </Button>
                  )} */}
                </>
              )}
            </div>
          </div>
        }
        styles={{
          header: {
            background: themeStyles.isLight ? '#ffffff' : 'transparent',
            borderBottom: 'none',
          },
          body: {
            background: themeStyles.isLight ? '#ffffff' : 'transparent',
            borderTop: 'none',
            borderBottom: 'none',
            padding: 0,
            paddingLeft: 16,
            borderRadius: 0,
          },
        }}
        style={
          themeStyles.isLight
            ? {
                background: '#ffffff',
                border: 'none',
                borderRadius: 0,
                boxShadow: themeStyles.shadowCard,
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(148, 22, 127, 0.1) 0%, rgba(21, 60, 180, 0.1) 100%)',
                border: 'none',
                borderRadius: 0,
              }
        }
      >
        <Tabs
          className="project-detail-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'details',
              label: 'Project Details',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  {rbacInfo?.group_name != null && rbacInfo.group_name !== '' && (
                    <Form.Item label="Group">
                      <span style={{ color: themeStyles.textPrimary }}>{rbacInfo.group_name}</span>
                    </Form.Item>
                  )}
                  {isSuperAdmin && Array.isArray(rbacInfo?.admin_names) && rbacInfo.admin_names.length > 0 && (
                    <Form.Item label="Admin">
                      <span style={{ color: themeStyles.textPrimary }}>{rbacInfo.admin_names.join(', ')}</span>
                    </Form.Item>
                  )}
                  <Form.Item label="Project Name" name="name">
                    <Input placeholder="Enter project name" />
                  </Form.Item>
                  <Form.Item label="Description" name="description">
                    <TextArea rows={4} placeholder="Enter project description" maxLength={500} showCount />
                  </Form.Item>
                </Form>
              ) : (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Project ID">
                    <span title={project.id}>{formatReadableProjectId(project.id)}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">{project.description}</Descriptions.Item>
                  {rbacInfo?.group_name != null && rbacInfo.group_name !== '' && (
                    <Descriptions.Item label="Group">{rbacInfo.group_name}</Descriptions.Item>
                  )}
                  {isSuperAdmin && Array.isArray(rbacInfo?.admin_names) && rbacInfo.admin_names.length > 0 && (
                    <Descriptions.Item label="Admin">
                      {rbacInfo.admin_names.join(', ')}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Created At">
                    {(() => {
                      const ts =
                        project?.meta?.created_at ||
                        project?.created_at ||
                        project?.meta?.updated_at ||
                        project?.updated_at ||
                        null;
                      return ts ? (formatUtcInstantForDisplay(ts) || '—') : '—';
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated At">
                    {(() => {
                      const ts = project?.meta?.updated_at || project?.updated_at || null;
                      return ts ? (formatUtcInstantForDisplay(ts) || '—') : '—';
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Document Count">{project.meta?.document_count}</Descriptions.Item>
                  <Descriptions.Item label="Chunk Count">{project.meta?.chunk_count}</Descriptions.Item>
                  <Descriptions.Item label="Embedding Status">
                    <span
                      style={{
                        color: project.meta?.embedding_status === 'completed' ? '#52C41A' : '#FADB14',
                        fontWeight: 500,
                      }}
                    >
                      {formatStatusLabel(project.meta?.embedding_status, { fallback: 'Unknown' })}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Vector Status">
                    <span
                      style={{
                        color: project.meta?.vector_status === 'completed' ? '#52C41A' : '#FADB14',
                        fontWeight: 500,
                      }}
                    >
                      {formatStatusLabel(project.meta?.vector_status, { fallback: 'Unknown' })}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <span
                      style={{
                        color: project.deployed ? '#52C41A' : '#FADB14',
                        fontWeight: 500,
                      }}
                    >
                      {project.deployed ? 'Deployed' : 'Not Deployed'}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'embedding',
              label: 'Embedding',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    // message="Administrator only"
                     description="Changing the embedding model will delete the existing Milvus collection and its data. Documents will need to be re-ingested."
                  />
                  <Form.Item label="Embedding Model" name={['config', 'embedding', 'model']}>
                    <Select
                      optionLabelProp="label"
                      onChange={(value) => {
                        const nextModelName =
                          value === 'bge' ? 'BAAI/bge-base-en-v1.5' : 'text-embedding-3-small';
                        form.setFieldsValue({
                          config: {
                            ...(form.getFieldValue('config') || {}),
                            embedding: {
                              ...(form.getFieldValue(['config', 'embedding']) || {}),
                              model_name: nextModelName,
                            },
                          },
                        });
                      }}
                    >
                      <Select.Option value="openai" label="OpenAI">
                        <div>
                          <div>OpenAI</div>
                          <div style={selectOptionHintStyleCompact}>
                            High-quality embeddings from OpenAI (requires API key)
                          </div>
                        </div>
                      </Select.Option>
                      <Select.Option value="bge" label="HuggingFace BGE">
                        <div>
                          <div>HuggingFace BGE</div>
                          <div style={selectOptionHintStyleCompact}>
                            Open-source embeddings via HuggingFace (no OpenAI API cost)
                          </div>
                        </div>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues?.config?.embedding?.model !== curValues?.config?.embedding?.model
                    }
                  >
                    {() => {
                      const embeddingProvider =
                        form.getFieldValue(['config', 'embedding', 'model']) ||
                        project?.config?.embedding?.model ||
                        'openai';
                      return (
                        <Form.Item label="Model Name" name={['config', 'embedding', 'model_name']}>
                          <Select optionLabelProp="label">
                            {embeddingProvider === 'bge' ? (
                              <>
                                <Select.Option value="BAAI/bge-base-en-v1.5" label="BAAI/bge-base-en-v1.5">
                                  <div>
                                    <div>BAAI/bge-base-en-v1.5</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      HuggingFace BGE base model (balanced speed/quality)
                                    </div>
                                  </div>
                                </Select.Option>
                                <Select.Option value="BAAI/bge-large-en-v1.5" label="BAAI/bge-large-en-v1.5">
                                  <div>
                                    <div>BAAI/bge-large-en-v1.5</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      Higher-quality BGE model with higher compute usage
                                    </div>
                                  </div>
                                </Select.Option>
                                <Select.Option value="BAAI/bge-m3" label="BAAI/bge-m3">
                                  <div>
                                    <div>BAAI/bge-m3</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      Multilingual BGE model for broad language coverage
                                    </div>
                                  </div>
                                </Select.Option>
                              </>
                            ) : (
                              <>
                                <Select.Option value="text-embedding-3-small" label="text-embedding-3-small">
                                  <div>
                                    <div>text-embedding-3-small</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      OpenAI recommended default for balanced quality and cost
                                    </div>
                                  </div>
                                </Select.Option>
                                <Select.Option value="text-embedding-3-large" label="text-embedding-3-large">
                                  <div>
                                    <div>text-embedding-3-large</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      Higher-quality OpenAI embeddings with larger vector size
                                    </div>
                                  </div>
                                </Select.Option>
                                <Select.Option value="text-embedding-ada-002" label="text-embedding-ada-002">
                                  <div>
                                    <div>text-embedding-ada-002</div>
                                    <div style={selectOptionHintStyleCompact}>
                                      Legacy OpenAI embedding model (compatibility)
                                    </div>
                                  </div>
                                </Select.Option>
                              </>
                            )}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Form>
              ) : (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Embedding Model">{project.config?.embedding?.model}</Descriptions.Item>
                  <Descriptions.Item label="Model Name">{project.config?.embedding?.model_name}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'vector_store',
              label: 'Vector Store',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    // message="Administrator only"
                    description="Renaming the collection will remove the old Milvus collection. You can either copy the existing vectors to the new collection or create an empty collection. Please confirm before proceeding."
                  />
                  <Form.Item label="Vector Store Type" name={['config', 'vector_store', 'type']} initialValue="milvus">
                    <Select disabled>
                      <Select.Option value="milvus">Milvus</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Collection Name"
                    name={['config', 'vector_store', 'collection_name']}
                    rules={[{ required: true, message: 'Collection name is required' }]}
                  >
                    <Input
                      placeholder="Collection name"
                      onChange={(e) => {
                        const name = e.target.value;
                        const storeType = form.getFieldValue(['config', 'vector_store', 'type']) || 'milvus';
                        scheduleCollectionAvailabilityCheck(name, storeType);
                      }}
                    />
                  </Form.Item>
                  {!collectionAvailable && !collectionNameUnchanged && (
                    <Alert type="error" showIcon message="Collection name is not available" style={{ marginBottom: 12 }} />
                  )}
                  {SHOW_VECTOR_RETENTION_UI && (
                    <div style={{ fontSize: 12, color: themeStyles.textSecondary }}>
                      Configure per-file vector retention in the RAG Pipeline tab.
                    </div>
                  )}
                </Form>
              ) : (
                <>
                  <Descriptions column={1} bordered>
                    <Descriptions.Item label="Vector Store Type">
                      {project.config?.vector_store?.type}
                    </Descriptions.Item>
                    <Descriptions.Item label="Collection Name">
                      {project.config?.vector_store?.collection_name}
                    </Descriptions.Item>
                    {SHOW_VECTOR_RETENTION_UI && (
                      <Descriptions.Item label="Per-file vector retention">
                        Configure retention per uploaded file in the RAG Pipeline tab (each row).
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </>
              ),
            },
            {
              key: 's3',
              label: 'Knowledge Source',
              children: editMode && canEdit && isConfluenceKnowledgeSource ? (
                <div>
                  <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Provider">Confluence</Descriptions.Item>
                    <Descriptions.Item label="Wiki Base URL">
                      {project.config?.confluence?.base_url || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Space Key">
                      {project.config?.confluence?.space_key || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Space Name">
                      {project.config?.confluence?.space_name || '—'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Select folders or sections to sync"
                    description={
                      String(project.config?.confluence?.sync_scope?.type || 'space').toLowerCase() === 'space'
                        ? 'This project currently syncs the entire space. Choose one or more folders or top-level pages below to limit what is processed.'
                        : 'Only content inside the selected folders/sections (and their sub-pages) is synced. Changing selection updates the file list; removed items are pruned from the pipeline.'
                    }
                  />
                  {confluenceFoldersLoading ? (
                    <div style={{ padding: '12px 0' }}>
                      <Spin size="small" /> Loading folders…
                    </div>
                  ) : null}
                  {confluenceFoldersError ? (
                    <Alert type="error" showIcon message={confluenceFoldersError} style={{ marginBottom: 12 }} />
                  ) : null}
                  {!confluenceFoldersLoading && confluenceFolders.length > 0 ? (
                    <Checkbox.Group
                      value={selectedConfluenceFolderIds}
                      onChange={(checked) => applyConfluenceFolderSelection(checked)}
                      style={{ width: '100%' }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {confluenceFolders.map((folder) => (
                          <Checkbox key={folder.id} value={folder.id}>
                            <span style={{ fontWeight: 500 }}>{folder.title}</span>
                            <Tag style={{ marginLeft: 8 }} color={folder.type === 'folder' ? 'blue' : 'default'}>
                              {folder.type === 'folder' ? 'Folder' : 'Page'}
                            </Tag>
                            {typeof folder.child_count === 'number' ? (
                              <span style={{ marginLeft: 8, color: themeStyles.textSecondary, fontSize: 12 }}>
                                {folder.child_count} item{folder.child_count === 1 ? '' : 's'}
                              </span>
                            ) : null}
                          </Checkbox>
                        ))}
                      </div>
                    </Checkbox.Group>
                  ) : null}
                  {!confluenceFoldersLoading && selectedConfluenceFolderIds.length === 0 ? (
                    <div style={{ marginTop: 12, color: '#fa8c16', fontSize: 13 }}>
                      Select at least one folder, then click Save.
                    </div>
                  ) : null}
                </div>
              ) : editMode && canEdit && isJiraKnowledgeSource ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Update Jira scope"
                    description="Change the project key or JQL filter, verify with your API token, then Save. Existing indexed issues outside the new scope are pruned on sync."
                  />
                  <Form.Item
                    label="Jira Base URL"
                    name={['config', 'jira', 'jira_base_url']}
                    rules={[{ required: true, message: 'Jira base URL is required' }]}
                  >
                    <Input
                      placeholder="https://your-domain.atlassian.net"
                      onChange={() => {
                        if (jiraCredentialResetLockRef.current) return;
                        setJiraVerified(false);
                        setJiraVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Atlassian Account Email"
                    name={['config', 'jira', 'jira_user_email']}
                    rules={[{ required: true, message: 'Account email is required' }]}
                  >
                    <Input
                      placeholder="you@company.com"
                      onChange={() => {
                        if (jiraCredentialResetLockRef.current) return;
                        setJiraVerified(false);
                        setJiraVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="API Token"
                    name={['config', 'jira', 'jira_api_token']}
                    rules={[{ required: true, message: 'Enter your API token, then click Connect' }]}
                    validateTrigger="onSubmit"
                  >
                    <Input.Password
                      placeholder="ATATT..."
                      onChange={() => {
                        if (jiraCredentialResetLockRef.current) return;
                        setJiraVerified(false);
                        setJiraVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item name={['config', 'jira', 'credential_id']} hidden>
                    <Input />
                  </Form.Item>
                  {jiraVerifyError ? (
                    <Alert type="error" showIcon message={jiraVerifyError} style={{ marginBottom: 12 }} />
                  ) : !jiraVerified ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Connect to Jira"
                      description="Enter your API token above and click Connect."
                    />
                  ) : null}
                  <Button
                    type={jiraVerified ? 'primary' : 'default'}
                    onClick={handleVerifyJiraKnowledgeSource}
                    loading={jiraVerifying}
                    disabled={jiraVerifying || jiraVerified}
                    style={{
                      marginBottom: 8,
                      background: jiraVerified
                        ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                        : undefined,
                      borderColor: jiraVerified ? '#52c41a' : undefined,
                      color: jiraVerified ? '#ffffff' : undefined,
                    }}
                  >
                    {jiraVerified ? 'Connected' : 'Connect'}
                  </Button>
                  {jiraVerified ? (
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginTop: 8, marginBottom: 16 }}
                      message="Verified — not saved yet"
                      description={
                        jiraProjects.length > 1
                          ? `${jiraProjects.length} projects available. Click Save (top right) to store the updated Jira configuration.`
                          : 'Click Save (top right) to store the updated Jira configuration.'
                      }
                    />
                  ) : null}
                  {jiraVerified && selectedJiraProjectKeys.length === 0 ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Project keys"
                      description="Choose Jira project keys and issue scope from the RAG Pipeline tab (Jira icon)."
                    />
                  ) : null}
                  {selectedJiraProjectKeys.map((projectKey) => {
                    const scope = jiraScopeByProjectKey[projectKey] || createDefaultJiraScope();
                    const jiraForm = form.getFieldValue(['config', 'jira']) || {};
                    const jiraCredentials = {
                      jira_base_url: jiraForm.jira_base_url,
                      jira_user_email: jiraForm.jira_user_email,
                      jira_api_token: jiraForm.jira_api_token,
                    };
                    return (
                      <div
                        key={projectKey}
                        style={{
                          marginBottom: 12,
                          padding: 12,
                          border: '1px solid rgba(148, 22, 127, 0.2)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>{projectKey}</div>
                        <Select
                          value={scope.type}
                          options={[
                            { value: JIRA_SCOPE.ISSUE_FILTER, label: 'Issue types & statuses' },
                            { value: JIRA_SCOPE.PROJECT, label: 'Entire project' },
                            { value: JIRA_SCOPE.JQL, label: 'Custom JQL' },
                          ]}
                          style={{ width: '100%', marginBottom: 8 }}
                          onChange={(value) => {
                            setJiraScopeByProjectKey((prev) => {
                              const next = {
                                ...prev,
                                [projectKey]: switchJiraScopeType(prev[projectKey], value),
                              };
                              syncJiraScopesToForm(selectedJiraProjectKeys, next);
                              return next;
                            });
                          }}
                        />
                        {scope.type === JIRA_SCOPE.ISSUE_FILTER ? (
                          <JiraIssueScopePicker
                            projectKey={projectKey}
                            credentials={jiraCredentials}
                            value={scope.issue_type_filter || DEFAULT_JIRA_ISSUE_TYPE_FILTER}
                            disabled={!jiraVerified}
                            requireApiToken={false}
                            fetchIssueScope={fetchJiraIssueScopeForProject}
                            onChange={(issueTypeFilter, meta) => {
                              setJiraScopeByProjectKey((prev) => {
                                const next = {
                                  ...prev,
                                  [projectKey]: {
                                    ...(prev[projectKey] || createDefaultJiraScope()),
                                    type: JIRA_SCOPE.ISSUE_FILTER,
                                    issue_type_filter: issueTypeFilter,
                                    preview_jql: meta?.preview_jql || prev[projectKey]?.preview_jql || '',
                                  },
                                };
                                syncJiraScopesToForm(selectedJiraProjectKeys, next);
                                return next;
                              });
                            }}
                            monitoredIssueKeys={jiraMonitoredIssueKeys}
                            onToggleMonitor={jiraVerified
                              ? (issueKey, enabled) => handleToggleJiraIssueMonitor(issueKey, enabled, projectKey)
                              : null}
                            onCheckMonitoredUpdates={jiraVerified ? handleCheckJiraMonitoredUpdatesNow : null}
                            monitorCheckLoading={jiraMonitorChecking}
                            monitoredIssueCount={jiraMonitoredIssueKeys.size}
                          />
                        ) : null}
                        {scope.type === JIRA_SCOPE.JQL ? (
                          <Input.TextArea
                            rows={3}
                            placeholder={`project = ${projectKey} AND status != Done`}
                            value={scope.jql_query || ''}
                            onChange={(e) => {
                              const nextValue = e?.target?.value || '';
                              setJiraScopeByProjectKey((prev) => {
                                const next = {
                                  ...prev,
                                  [projectKey]: { ...(prev[projectKey] || {}), type: JIRA_SCOPE.JQL, jql_query: nextValue },
                                };
                                syncJiraScopesToForm(selectedJiraProjectKeys, next);
                                return next;
                              });
                            }}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </Form>
              ) : editMode && canEdit && isAzureWikiKnowledgeSource ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Connect Azure Wiki"
                    description="Update the organization URL and PAT here, then Connect and Save. Choose projects, wikis, and pages in RAG Pipeline → Source Settings."
                  />
                  <Form.Item
                    label="Organization URL"
                    name={['config', 'azure_wiki', 'organization_url']}
                    rules={[{ required: true, message: 'Azure DevOps organization URL is required' }]}
                  >
                    <Input
                      placeholder="https://dev.azure.com/your-organization"
                      onChange={() => {
                        setAzureWikiVerified(false);
                        setAzureWikiVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Personal Access Token (PAT)"
                    name={['config', 'azure_wiki', 'pat']}
                    rules={[{ required: true, message: 'Enter your PAT, then click Connect' }]}
                    validateTrigger="onSubmit"
                  >
                    <Input.Password
                      placeholder="Azure DevOps PAT"
                      onChange={() => {
                        setAzureWikiVerified(false);
                        setAzureWikiVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item name={['config', 'azure_wiki', 'credential_id']} hidden>
                    <Input />
                  </Form.Item>
                  {azureWikiVerifyError ? (
                    <Alert type="error" showIcon message={azureWikiVerifyError} style={{ marginBottom: 12 }} />
                  ) : !azureWikiVerified ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Connect to Azure Wiki"
                      description="Enter your PAT above and click Connect."
                    />
                  ) : null}
                  <Button
                    type={azureWikiVerified ? 'primary' : 'default'}
                    loading={azureWikiVerifying}
                    disabled={azureWikiVerifying || azureWikiVerified}
                    onClick={async () => {
                      try {
                        const fields = await form.validateFields([
                          ['config', 'azure_wiki', 'organization_url'],
                          ['config', 'azure_wiki', 'pat'],
                        ]);
                        const aw = fields?.config?.azure_wiki || {};
                        if (!token) {
                          message.error('You must be logged in to connect Azure Wiki.');
                          return;
                        }
                        setAzureWikiVerifying(true);
                        setAzureWikiVerifyError('');
                        const res = await API.connectAzureWiki(token, {
                          organization_url: aw.organization_url,
                          pat: aw.pat,
                        });
                        const data = res.data || {};
                        const normalized = data.normalized || {};
                        setAzureWikiCredentialId(data.credential_id || '');
                        form.setFieldsValue({
                          config: {
                            ...(form.getFieldValue('config') || {}),
                            azure_wiki: {
                              ...(form.getFieldValue(['config', 'azure_wiki']) || {}),
                              ...normalized,
                              credential_id: data.credential_id,
                            },
                          },
                        });
                        setAzureWikiVerified(true);
                        message.success('Azure Wiki connected. Click Save, then manage pages in Source Settings.');
                      } catch (error) {
                        const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                        const friendly = rawDetail
                          ? humanizeApiError(rawDetail, { context: ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY })
                          : 'Failed to connect to Azure Wiki. Please check the organization URL and PAT.';
                        setAzureWikiVerifyError(friendly);
                        message.error(friendly);
                      } finally {
                        setAzureWikiVerifying(false);
                      }
                    }}
                    style={{
                      marginBottom: 8,
                      background: azureWikiVerified
                        ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                        : undefined,
                      borderColor: azureWikiVerified ? '#52c41a' : undefined,
                      color: azureWikiVerified ? '#ffffff' : undefined,
                    }}
                  >
                    {azureWikiVerified ? 'Connected' : 'Connect'}
                  </Button>
                  {azureWikiVerified ? (
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginTop: 8, marginBottom: 16 }}
                      message="Verified — not saved yet"
                      description="Click Save (top right) to store the connection. Use Source Settings on the RAG Pipeline tab to choose projects, wikis, and pages."
                    />
                  ) : null}
                  <Descriptions column={1} bordered size="small" style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Configured Wikis">
                      {(() => {
                        const scopes = normalizeAzureWikiScopesFromConfig(project.config?.azure_wiki || {});
                        if (!scopes.length) return '— (set in Source Settings)';
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {scopes.map((scope) => (
                              <Tag key={scope.wiki_id}>
                                {scope.project ? `${scope.project} / ${scope.wiki_name}` : scope.wiki_name}
                              </Tag>
                            ))}
                          </div>
                        );
                      })()}
                    </Descriptions.Item>
                  </Descriptions>
                </Form>
              ) : editMode && canEdit && isGithubKnowledgeSource ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Connect GitHub"
                    description="Update the GitHub base URL and PAT here, then Connect and Save. Choose repositories, branches, and files in RAG Pipeline → Source Settings."
                  />
                  <Form.Item
                    label="GitHub base URL"
                    name={['config', 'github', 'github_base_url']}
                    rules={[{ required: true, message: 'GitHub base URL is required' }]}
                  >
                    <Input
                      placeholder="https://github.com/your-org"
                      onChange={() => {
                        setGithubVerified(false);
                        setGithubVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Personal Access Token (PAT)"
                    name={['config', 'github', 'pat']}
                    rules={[{ required: true, message: 'Enter your PAT, then click Connect' }]}
                    validateTrigger="onSubmit"
                  >
                    <Input.Password
                      placeholder="github_pat_... or ghp_..."
                      onChange={() => {
                        setGithubVerified(false);
                        setGithubVerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item name={['config', 'github', 'credential_id']} hidden>
                    <Input />
                  </Form.Item>
                  {githubVerifyError ? (
                    <Alert type="error" showIcon message={githubVerifyError} style={{ marginBottom: 12 }} />
                  ) : !githubVerified ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Connect to GitHub"
                      description="Enter your PAT above and click Connect."
                    />
                  ) : null}
                  <Button
                    type={githubVerified ? 'primary' : 'default'}
                    loading={githubVerifying}
                    disabled={githubVerifying || githubVerified}
                    onClick={async () => {
                      try {
                        const fields = await form.validateFields([
                          ['config', 'github', 'github_base_url'],
                          ['config', 'github', 'pat'],
                        ]);
                        const gh = fields?.config?.github || {};
                        if (!token) {
                          message.error('You must be logged in to connect GitHub.');
                          return;
                        }
                        setGithubVerifying(true);
                        setGithubVerifyError('');
                        const res = await API.connectGithub(token, {
                          github_base_url: gh.github_base_url,
                          pat: gh.pat,
                        });
                        const data = res.data || {};
                        const normalized = data.normalized || {};
                        setGithubCredentialId(data.credential_id || '');
                        form.setFieldsValue({
                          config: {
                            ...(form.getFieldValue('config') || {}),
                            github: {
                              ...(form.getFieldValue(['config', 'github']) || {}),
                              ...normalized,
                              credential_id: data.credential_id,
                            },
                          },
                        });
                        setGithubVerified(true);
                        message.success('GitHub connected. Click Save, then manage repos in Source Settings.');
                      } catch (error) {
                        const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                        const friendly = rawDetail
                          ? humanizeApiError(rawDetail, { context: ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY })
                          : 'Failed to connect to GitHub. Please check the base URL and PAT.';
                        setGithubVerifyError(friendly);
                        message.error(friendly);
                      } finally {
                        setGithubVerifying(false);
                      }
                    }}
                    style={{
                      marginBottom: 8,
                      background: githubVerified
                        ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                        : undefined,
                      borderColor: githubVerified ? '#52c41a' : undefined,
                      color: githubVerified ? '#ffffff' : undefined,
                    }}
                  >
                    {githubVerified ? 'Connected' : 'Connect'}
                  </Button>
                  {githubVerified ? (
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginTop: 8, marginBottom: 16 }}
                      message="Verified — not saved yet"
                      description="Click Save (top right) to store the connection. Use Source Settings on the RAG Pipeline tab to choose repositories, branches, and files."
                    />
                  ) : null}
                  <Descriptions column={1} bordered size="small" style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Repositories">
                      {(() => {
                        const scopes = normalizeGithubScopesFromConfig(project.config?.github || {});
                        const repos = Array.from(new Set(scopes.map((s) => `${s.owner}/${s.repo}`)));
                        if (!repos.length) return '— (set in Source Settings)';
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {repos.map((repo) => <Tag key={repo}>{repo}</Tag>)}
                          </div>
                        );
                      })()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Branches">
                      {(() => {
                        const scopes = normalizeGithubScopesFromConfig(project.config?.github || {});
                        if (!scopes.length) return '— (set in Source Settings)';
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {scopes.map((scope) => (
                              <Tag key={scope.key}>{`${scope.owner}/${scope.repo}@${scope.branch}`}</Tag>
                            ))}
                          </div>
                        );
                      })()}
                    </Descriptions.Item>
                  </Descriptions>
                </Form>
              ) : editMode && canEdit && !isPollKnowledgeSource ? (
                <Form form={form} layout="vertical">
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    // message="Administrator only"
                    description="Changing the S3 bucket will delete the existing Milvus collection and its data. Enter the new bucket details and save to continue."
                  />
                  <Form.Item
                    label="S3 Bucket"
                    name={['config', 's3', 'bucket']}
                    rules={[{ required: true, message: 'S3 bucket is required' }]}
                  >
                    <Input
                      placeholder="my-project-bucket"
                      onChange={() => {
                        setS3Verified(false);
                        setS3VerifyError('');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="S3 Region"
                    name={['config', 's3', 'region']}
                    rules={[{ required: true, message: 'Please select the S3 region' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select region"
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label || '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={() => {
                        setS3Verified(false);
                        setS3VerifyError('');
                      }}
                      options={buildS3RegionSelectOptions(project?.config?.s3?.region)}
                    />
                  </Form.Item>
                  {s3VerifyError ? (
                    <Alert type="error" showIcon message={s3VerifyError} style={{ marginBottom: 12 }} />
                  ) : null}
                  <Button loading={s3Verifying} onClick={handleVerifyS3} style={{ marginBottom: 8 }}>
                    Verify Knowledge Source
                  </Button>
                  {s3Verified && s3Dirty ? (
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="Verified — not saved yet"
                      description="Access to this bucket was confirmed. Click Save (top right) to store the new Knowledge Source and complete migration."
                    />
                  ) : null}
                </Form>
              ) : isJiraKnowledgeSource ? (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Provider">Jira</Descriptions.Item>
                  <Descriptions.Item label="Jira Base URL">
                    {project.config?.jira?.jira_base_url || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project Key">
                    {(() => {
                      const scopes = normalizeJiraScopesFromConfig(project.config?.jira || {});
                      if (scopes.length === 0) return '—';
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {scopes.map((scope) => {
                            const key = String(scope?.project_key || '').trim();
                            const jql = String(scope?.jql_query || '').trim();
                            const label = jql ? `${key || 'Custom'} (Custom JQL)` : `${key} (Entire Project)`;
                            return <Tag key={`${key}-${jql}`}>{label}</Tag>;
                          })}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Email">
                    {project.config?.jira?.jira_user_email || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Bucket">
                    {project.config?.s3?.bucket || 'hrms-aisolutions'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Region">
                    {project.config?.s3?.region || 'us-east-2'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project folder (prefix)">
                    <code>{project.config?.s3?.prefix || '—'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last sync">
                    {project.config?.jira?.last_sync_at || '—'}
                    {project.config?.jira?.last_sync_status
                      ? ` (${project.config.jira.last_sync_status})`
                      : ''}
                  </Descriptions.Item>
                </Descriptions>
              ) : isGithubKnowledgeSource ? (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Provider">GitHub</Descriptions.Item>
                  <Descriptions.Item label="GitHub base URL">
                    {project.config?.github?.github_base_url || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Repositories">
                    {(() => {
                      const scopes = normalizeGithubScopesFromConfig(project.config?.github || {});
                      const repos = Array.from(new Set(scopes.map((s) => `${s.owner}/${s.repo}`)));
                      if (!repos.length) {
                        return project.config?.github?.owner && project.config?.github?.repo
                          ? `${project.config.github.owner}/${project.config.github.repo}`
                          : '—';
                      }
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {repos.map((repo) => <Tag key={repo}>{repo}</Tag>)}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Branches">
                    {(() => {
                      const scopes = normalizeGithubScopesFromConfig(project.config?.github || {});
                      if (!scopes.length) {
                        return project.config?.github?.default_branch || 'main';
                      }
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {scopes.map((scope) => (
                            <Tag key={scope.key}>{`${scope.owner}/${scope.repo}@${scope.branch}`}</Tag>
                          ))}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Bucket">
                    {project.config?.s3?.bucket || 'hrms-aisolutions'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Region">
                    {project.config?.s3?.region || 'us-east-2'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project folder (prefix)">
                    <code>{project.config?.s3?.prefix || '—'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last sync">
                    {project.config?.github?.last_sync_at || '—'}
                    {project.config?.github?.last_sync_status
                      ? ` (${project.config.github.last_sync_status})`
                      : ''}
                  </Descriptions.Item>
                </Descriptions>
              ) : isConfluenceKnowledgeSource ? (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Provider">Confluence</Descriptions.Item>
                  <Descriptions.Item label="Wiki Base URL">
                    {project.config?.confluence?.base_url || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Space Keys">
                    {(() => {
                      const scopes = normalizeConfluenceScopesFromConfig(project.config?.confluence || {});
                      if (!scopes.length) return project.config?.confluence?.space_key || '—';
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {scopes.map((scope) => (
                            <Tag key={scope.space_key}>
                              {scope.space_name && scope.space_name !== scope.space_key
                                ? `${scope.space_key} — ${scope.space_name}`
                                : scope.space_key}
                            </Tag>
                          ))}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Selected folders">
                    {renderConfluenceSelectedFolders(
                      project.config?.confluence?.sync_scope,
                      project.config?.confluence,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Bucket">
                    {project.config?.s3?.bucket || 'hrms-aisolutions'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Region">
                    {project.config?.s3?.region || 'us-east-2'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project folder (prefix)">
                    <code>{project.config?.s3?.prefix || '—'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last sync">
                    {project.config?.confluence?.last_sync_at || '—'}
                    {project.config?.confluence?.last_sync_status
                      ? ` (${project.config.confluence.last_sync_status})`
                      : ''}
                  </Descriptions.Item>
                  <Descriptions.Item label="Webhook (auto-sync list)">
                    {confluenceWebhookSetup?.webhook_configured ? (
                      <Tag color="green">Configured on server</Tag>
                    ) : (
                      <Tag color="default">Set CONFLUENCE_WEBHOOK_SECRET in API .env</Tag>
                    )}
                    {confluenceWebhookSetup?.callback_url ? (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                          {confluenceWebhookSetup.callback_url}
                        </code>
                        <Button
                          type="link"
                          size="small"
                          icon={<CopyOutlined />}
                          style={{ padding: 0, height: 'auto', alignSelf: 'flex-start' }}
                          onClick={() => {
                            navigator.clipboard.writeText(confluenceWebhookSetup.callback_url);
                            message.success('Webhook URL copied');
                          }}
                        >
                          Copy URL
                        </Button>
                        <span style={{ fontSize: 11, color: themeStyles.textSecondary }}>
                          Register once per Confluence site (Admin → Webhooks). Updates the file list only;
                          Run now still ingests into search.
                        </span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: themeStyles.textSecondary }}>
                        Set CONFLUENCE_WEBHOOK_PUBLIC_BASE_URL on the API to show the callback URL.
                      </span>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              ) : isAzureWikiKnowledgeSource ? (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Provider">Azure Wiki</Descriptions.Item>
                  <Descriptions.Item label="Organization URL">
                    {project.config?.azure_wiki?.organization_url || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Configured Wikis">
                    {(() => {
                      const scopes = normalizeAzureWikiScopesFromConfig(project.config?.azure_wiki || {});
                      if (!scopes.length) return '—';
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {scopes.map((scope) => (
                            <Tag key={scope.wiki_id}>
                              {scope.project ? `${scope.project} / ${scope.wiki_name}` : scope.wiki_name}
                            </Tag>
                          ))}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Selected pages">
                    {(() => {
                      const scopes = normalizeAzureWikiScopesFromConfig(project.config?.azure_wiki || {});
                      const allPaths = scopes.flatMap((scope) => scope.page_paths || []);
                      if (!allPaths.length) return '—';
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {allPaths.map((path) => <Tag key={path}>{path}</Tag>)}
                        </div>
                      );
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Bucket">
                    {project.config?.s3?.bucket || 'hrms-aisolutions'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Artifact S3 Region">
                    {project.config?.s3?.region || 'us-east-2'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project folder (prefix)">
                    <code>{project.config?.s3?.prefix || '—'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last sync">
                    {project.config?.azure_wiki?.last_sync_at || '—'}
                    {project.config?.azure_wiki?.last_sync_status
                      ? ` (${project.config.azure_wiki.last_sync_status})`
                      : ''}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label={<FieldLabel label="S3 Bucket" />}>{project.config?.s3?.bucket || project.config?.s3?.name || '—'}</Descriptions.Item>
                  <Descriptions.Item label={<FieldLabel label="S3 Region" />}>{project.config?.s3?.region || '—'}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'graphrag',
              label: 'Graph RAG',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  <Form.Item label="Enable Graph RAG" name={['config', 'graphrag', 'enabled']} valuePropName="checked">
                    <Switch onChange={(checked) => {
                      if (checked) {
                        const current = form.getFieldValue(['config', 'graphrag']) || {};
                        form.setFieldsValue({
                          config: {
                            ...form.getFieldValue('config'),
                            graphrag: {
                              ...current,
                              enabled: true,
                              ingest_endpoint: current.ingest_endpoint || 'https://backend.graphrag.thbscoetg.com/ingest',
                              timeout: current.timeout || 300,
                            }
                          }
                        });
                      }
                    }} />
                  </Form.Item>
                  {graphragFormEnabled && (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Vector indexing runs in the RAG Pipeline tab. Use the file list below to choose which completed files are uploaded to Neo4j."
                      />
                      <Form.Item label="Ingest Endpoint" name={['config', 'graphrag', 'ingest_endpoint']}>
                    <Input placeholder="https://backend.graphrag.thbscoetg.com/ingest" />
                  </Form.Item>
                      <Form.Item label="Group ID" name={['config', 'graphrag', 'group_id']}>
                    <Input placeholder="Leave empty to use project ID" />
                  </Form.Item>
                      <Form.Item label="Timeout (seconds)" name={['config', 'graphrag', 'timeout']}>
                    <InputNumber min={30} max={900} style={{ width: '100%' }} />
                  </Form.Item>
                    </>
                  )}
                  {renderGraphRagFilesPanel()}
                </Form>
              ) : (
                <>
                  <Descriptions column={1} bordered>
                    <Descriptions.Item label="Enabled">
                      <Tag color={project.config?.graphrag?.enabled ? 'success' : 'default'}>
                        {project.config?.graphrag?.enabled ? 'Yes' : 'No'}
                      </Tag>
                    </Descriptions.Item>
                    {project.config?.graphrag?.enabled && (
                      <>
                        <Descriptions.Item label="Ingestion mode">
                          Vector indexing runs in RAG Pipeline. Toggle files below to include them in Neo4j.
                        </Descriptions.Item>
                        <Descriptions.Item label="Ingest Endpoint">{project.config?.graphrag?.ingest_endpoint || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Group ID">{project.config?.graphrag?.group_id || project.id || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Timeout">{project.config?.graphrag?.timeout || 300}s</Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                  {renderGraphRagFilesPanel()}
                </>
              )
            },
            {
              key: 'retriever',
              label: 'Retriever',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  <Form.Item label={<FieldLabel label="Search Type" />} name={['config', 'retriever', 'search_type']}>
                    <RetrieverSearchTypeSelect hintStyle={selectOptionHintStyleCompact} />
                  </Form.Item>
                  <Form.Item label={<FieldLabel label="Top K" />} name={['config', 'retriever', 'top_k']}>
                    <InputNumber min={1} max={20} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues?.config?.retriever?.search_type !== curValues?.config?.retriever?.search_type
                    }
                  >
                    {() => {
                      const searchType =
                        form.getFieldValue(['config', 'retriever', 'search_type']) ||
                        project?.config?.retriever?.search_type ||
                        DEFAULT_RETRIEVER_SEARCH_TYPE;
                      if (searchType === 'mmr') {
                        return (
                          <>
                            <Form.Item label={<FieldLabel label="MMR Fetch K" />} name={['config', 'retriever', 'fetch_k']}>
                              <InputNumber min={1} max={200} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item label={<FieldLabel label="MMR Lambda" />} name={['config', 'retriever', 'lambda_mult']}>
                              <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                            </Form.Item>
                          </>
                        );
                      }
                      if (searchType === 'hybrid') {
                        return (
                          <>
                            <Form.Item label={<FieldLabel label="BM25 K" />} name={['config', 'retriever', 'bm25_k']}>
                              <InputNumber min={1} max={200} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item label={<FieldLabel label="Vector K" />} name={['config', 'retriever', 'vector_k']}>
                              <InputNumber min={1} max={200} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                              label={<FieldLabel label="Hybrid Alpha" />}
                              name={['config', 'retriever', 'hybrid_alpha']}
                            >
                              <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                            </Form.Item>
                          </>
                        );
                      }
                      return (
                        <>
                          <Form.Item
                            label="Cosine Similarity Threshold (optional)"
                            name={['config', 'retriever', 'score_threshold']}
                          >
                            <InputNumber
                              min={0}
                              max={1}
                              step={0.01}
                              style={{ width: '100%' }}
                              placeholder="e.g. 0.75"
                            />
                          </Form.Item>
                          <Form.Item
                            label="Use Cosine Similarity"
                            name={['config', 'retriever', 'cosine_similarity']}
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </>
                      );
                    }}
                  </Form.Item>
                </Form>
              ) : (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label={<FieldLabel label="Search Type" />}>
                    {retrieverSearchTypeLabel(project.config?.retriever?.search_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label={<FieldLabel label="Top K" />}>{project.config?.retriever?.top_k ?? 5}</Descriptions.Item>
                  {project.config?.retriever?.search_type === 'mmr' ? (
                    <>
                      <Descriptions.Item label={<FieldLabel label="MMR Fetch K" />}>{project.config?.retriever?.fetch_k ?? 20}</Descriptions.Item>
                      <Descriptions.Item label={<FieldLabel label="MMR Lambda" />}>{project.config?.retriever?.lambda_mult ?? 0.5}</Descriptions.Item>
                    </>
                  ) : project.config?.retriever?.search_type === 'hybrid' ? (
                    <>
                      <Descriptions.Item label={<FieldLabel label="BM25 K" />}>{project.config?.retriever?.bm25_k ?? 6}</Descriptions.Item>
                      <Descriptions.Item label={<FieldLabel label="Vector K" />}>{project.config?.retriever?.vector_k ?? 6}</Descriptions.Item>
                      <Descriptions.Item label={<FieldLabel label="Hybrid Alpha" />}>{project.config?.retriever?.hybrid_alpha ?? 0.6}</Descriptions.Item>
                    </>
                  ) : (
                    <>
                      <Descriptions.Item label="Cosine Similarity Threshold">
                        {project.config?.retriever?.score_threshold ?? '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Use Cosine Similarity">
                        {project.config?.retriever?.cosine_similarity === false ? 'No' : 'Yes'}
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              )
            },
            {
              key: 'llm',
              label: 'LLM & Guardrails',
              children: editMode && canEdit ? (
                <Form form={form} layout="vertical">
                  <Form.Item label="LLM Provider" name={['config', 'llm', 'provider']}>
                    <Select
                      optionLabelProp="label"
                      loading={modelCatalogLoading}
                      onChange={(value) => {
                        setSelectedLlmProvider(value);
                        const providerDefaultModel = modelCatalog?.providers
                          ?.find((p) => p.id === value)
                          ?.models?.[0]?.id;
                        if (providerDefaultModel) {
                          form.setFieldsValue({
                            config: {
                              ...(form.getFieldValue('config') || {}),
                              llm: {
                                ...(form.getFieldValue(['config', 'llm']) || {}),
                                model: providerDefaultModel,
                              },
                            },
                          });
                        }
                      }}
                    >
                      {mergeLlmProvidersForUi(modelCatalog?.providers, [
                        { id: 'openai', label: 'OpenAI' },
                        { id: 'bedrock', label: 'Amazon Bedrock' },
                      ]).map((provider) => (
                        <Select.Option key={provider.id} value={provider.id} label={provider.label} disabled={provider.disabled}>
                          <div>
                            <div style={{ color: provider.disabled ? themeStyles.textTertiary : undefined }}>{provider.label}</div>
                            {provider.id === 'openai' && (
                              <div style={selectOptionHintStyleCompact}>
                                GPT models for generating responses (uses server-side API key)
                              </div>
                            )}
                            {provider.id === 'bedrock' && (
                              <div style={selectOptionHintStyleCompact}>
                                Amazon Bedrock hosted LLMs (keys managed on server)
                              </div>
                            )}
                            {provider.id === 'ollama' && (
                              <div style={selectOptionHintStyleCompact}>
                                Local models via Ollama (Llama 3.2, Qwen 3.5 9B)
                              </div>
                            )}
                            {provider.id === 'qwen' && (
                              <div style={selectOptionHintStyleCompact}>
                                Qwen (QWEN_BASE_URL; qwen3.5:9b only)
                              </div>
                            )}
                            {provider.id === 'vllm' && (
                              <div style={selectOptionHintStyleCompact}>
                                vLLM OpenAI-compatible API (VLLM_BASE_URL on server)
                              </div>
                            )}
                            {provider.description && (
                              <div style={selectOptionHintStyleCompact}>
                                {provider.description}
                              </div>
                            )}
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Model" name={['config', 'llm', 'model']}>
                    {modelCatalog && modelCatalog.providers ? (
                      <Select
                        showSearch
                        placeholder="Select model"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {(modelCatalog.providers.find((p) => p.id === selectedLlmProvider)?.models ||
                          modelCatalog.providers.flatMap((p) => p.models)).map((model) => (
                          <Select.Option key={model.id} value={model.id}>
                            {model.label}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <Input placeholder="Enter model name" />
                    )}
                  </Form.Item>
                  <Form.Item label="Temperature" name={['config', 'llm', 'temperature']}>
                    <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Enable Guardrails" name={['config', 'guardrails', 'enabled']} valuePropName="checked" style={{ marginTop: '24px' }}>
                    <Switch 
                      onChange={(checked) => {
                        if (!checked) {
                          form.setFieldsValue({
                            config: {
                              guardrails: {
                                enabled: false,
                                input_guards: [],
                                pre_embedding_guards: [],
                                context_guards: [],
                                output_guards: [],
                                input_guards_config: {},
                                pre_embedding_guards_config: {},
                                context_guards_config: {},
                                output_guards_config: {}
                              }
                            }
                          });
                          setSelectedInputGuards([]);
                          setSelectedPreEmbeddingGuards([]);
                          setSelectedContextGuards([]);
                          setSelectedOutputGuards([]);
                        }
                      }}
                    />
                  </Form.Item>
                  <Collapse
                    ghost
                    destroyInactivePanel={false}
                    style={{ marginTop: '8px' }}
                    items={[
                      {
                        key: 'input',
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <SafetyOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                            <div>
                              <div style={{ fontWeight: 600, color: themeStyles.textPrimary, fontSize: '14px' }}>Input Guards</div>
                              <div style={selectOptionHintStyle}>Validate user queries before processing</div>
                            </div>
                            {selectedInputGuards.length > 0 && (
                              <Tag color="blue" style={{ marginLeft: 'auto' }}>{selectedInputGuards.length} active</Tag>
                            )}
                          </div>
                        ),
                        children: (
                          <div style={{ padding: '8px 0' }}>
                            <Form.Item label="Select Guards" name={['config', 'guardrails', 'input_guards']}>
                    <Select 
                      mode="multiple" 
                      placeholder="Select input guards" 
                      disabled={!guardrailsEnabled}
                      onChange={(values) => {
                        setSelectedInputGuards(values);
                        const currentInputConfig = form.getFieldValue(['config', 'guardrails', 'input_guards_config']) || {};
                        const updatedInputConfig = { ...currentInputConfig };
                        values.forEach(guardName => {
                                    if (!updatedInputConfig[guardName]) updatedInputConfig[guardName] = {};
                          updatedInputConfig[guardName].on_fail = guardName === 'DetectPII' ? 'noop' : 'exception';
                          if (guardName === 'DetectPII') {
                            updatedInputConfig[guardName].entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
                            updatedInputConfig[guardName].pii_score_threshold = 0.7;
                          }
                        });
                        Object.keys(updatedInputConfig).forEach(guardName => {
                                    if (!values.includes(guardName)) delete updatedInputConfig[guardName];
                        });
                                  form.setFieldValue(['config', 'guardrails', 'input_guards_config'], updatedInputConfig);
                      }}
                    >
                      <Select.Option value="DetectPII">Detect PII</Select.Option>
                      <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                      <Select.Option value="NSFWText">NSFW Text</Select.Option>
                    </Select>
                  </Form.Item>
                      {selectedInputGuards.map(guard => renderGuardConfig(guard, 'input'))}
                    </div>
                        ),
                      },
                      {
                        key: 'pre_embedding',
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FileProtectOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                            <div>
                              <div style={{ fontWeight: 600, color: themeStyles.textPrimary, fontSize: '14px' }}>Pre-Embedding Guards</div>
                              <div style={selectOptionHintStyle}>Sanitize documents before indexing into vector DB</div>
                            </div>
                            {selectedPreEmbeddingGuards.length > 0 && (
                              <Tag color="green" style={{ marginLeft: 'auto' }}>{selectedPreEmbeddingGuards.length} active</Tag>
                            )}
                          </div>
                        ),
                        children: (
                          <div style={{ padding: '8px 0' }}>
                            <Form.Item label="Select Guards" name={['config', 'guardrails', 'pre_embedding_guards']}>
                    <Select
                      mode="multiple"
                      placeholder="Select pre-embedding guards"
                      disabled={!guardrailsEnabled}
                      onChange={(values) => {
                        setSelectedPreEmbeddingGuards(values);
                        const currentConfig = form.getFieldValue(['config', 'guardrails', 'pre_embedding_guards_config']) || {};
                        const updatedConfig = { ...currentConfig };
                        values.forEach(guardName => {
                                    if (!updatedConfig[guardName]) updatedConfig[guardName] = {};
                          updatedConfig[guardName].on_fail = guardName === 'DetectPII' ? 'fix' : 'exception';
                          if (guardName === 'DetectPII') {
                            updatedConfig[guardName].entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
                            updatedConfig[guardName].pii_score_threshold = 0.7;
                          }
                        });
                        Object.keys(updatedConfig).forEach(guardName => {
                                    if (!values.includes(guardName)) delete updatedConfig[guardName];
                        });
                                  form.setFieldValue(['config', 'guardrails', 'pre_embedding_guards_config'], updatedConfig);
                      }}
                    >
                      <Select.Option value="DetectPII">Detect PII</Select.Option>
                      <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                      <Select.Option value="NSFWText">NSFW Text</Select.Option>
                    </Select>
                  </Form.Item>
                      {selectedPreEmbeddingGuards.map(guard => renderGuardConfig(guard, 'pre_embedding'))}
                    </div>
                        ),
                      },
                      {
                        key: 'context',
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FilterOutlined style={{ fontSize: '16px', color: '#13c2c2' }} />
                            <div>
                              <div style={{ fontWeight: 600, color: themeStyles.textPrimary, fontSize: '14px' }}>Context (Retrieval) Guards</div>
                              <div style={selectOptionHintStyle}>Filter or redact each retrieved chunk before it is sent to the LLM</div>
                            </div>
                            {selectedContextGuards.length > 0 && (
                              <Tag color="cyan" style={{ marginLeft: 'auto' }}>{selectedContextGuards.length} active</Tag>
                            )}
                          </div>
                        ),
                        children: (
                          <div style={{ padding: '8px 0' }}>
                            <Form.Item label="Select Guards" name={['config', 'guardrails', 'context_guards']}>
                              <Select
                                mode="multiple"
                                placeholder="Select context guards"
                                disabled={!guardrailsEnabled}
                                onChange={(values) => {
                                  setSelectedContextGuards(values);
                                  const currentConfig = form.getFieldValue(['config', 'guardrails', 'context_guards_config']) || {};
                                  const updatedConfig = { ...currentConfig };
                                  values.forEach(guardName => {
                                    if (!updatedConfig[guardName]) updatedConfig[guardName] = {};
                                    updatedConfig[guardName].on_fail = guardName === 'DetectPII' ? 'fix' : 'exception';
                                    if (guardName === 'DetectPII') {
                                      updatedConfig[guardName].entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
                                      updatedConfig[guardName].pii_score_threshold = 0.7;
                                    }
                                  });
                                  Object.keys(updatedConfig).forEach(guardName => {
                                    if (!values.includes(guardName)) delete updatedConfig[guardName];
                                  });
                                  form.setFieldValue(['config', 'guardrails', 'context_guards_config'], updatedConfig);
                                }}
                              >
                                <Select.Option value="DetectPII">Detect PII</Select.Option>
                                <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                                <Select.Option value="NSFWText">NSFW Text</Select.Option>
                              </Select>
                            </Form.Item>
                            {selectedContextGuards.map(guard => renderGuardConfig(guard, 'context'))}
                          </div>
                        ),
                      },
                      {
                        key: 'output',
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <LockOutlined style={{ fontSize: '16px', color: '#722ed1' }} />
                            <div>
                              <div style={{ fontWeight: 600, color: themeStyles.textPrimary, fontSize: '14px' }}>Output Guards</div>
                              <div style={selectOptionHintStyle}>Filter AI responses before sending to user</div>
                            </div>
                            {selectedOutputGuards.length > 0 && (
                              <Tag color="purple" style={{ marginLeft: 'auto' }}>{selectedOutputGuards.length} active</Tag>
                            )}
                          </div>
                        ),
                        children: (
                          <div style={{ padding: '8px 0' }}>
                            <Form.Item label="Select Guards" name={['config', 'guardrails', 'output_guards']}>
                    <Select 
                      mode="multiple" 
                      placeholder="Select output guards" 
                      disabled={!guardrailsEnabled}
                      onChange={(values) => {
                        setSelectedOutputGuards(values);
                        const currentOutputConfig = form.getFieldValue(['config', 'guardrails', 'output_guards_config']) || {};
                        const updatedOutputConfig = { ...currentOutputConfig };
                        values.forEach(guardName => {
                                    if (!updatedOutputConfig[guardName]) updatedOutputConfig[guardName] = {};
                          updatedOutputConfig[guardName].on_fail = guardName === 'DetectPII' ? 'noop' : 'exception';
                          if (guardName === 'DetectPII') {
                            updatedOutputConfig[guardName].entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
                            updatedOutputConfig[guardName].pii_score_threshold = 0.7;
                          }
                        });
                        Object.keys(updatedOutputConfig).forEach(guardName => {
                                    if (!values.includes(guardName)) delete updatedOutputConfig[guardName];
                        });
                                  form.setFieldValue(['config', 'guardrails', 'output_guards_config'], updatedOutputConfig);
                      }}
                    >
                      <Select.Option value="DetectPII">Detect PII</Select.Option>
                      <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                      <Select.Option value="NSFWText">NSFW Text</Select.Option>
                    </Select>
                  </Form.Item>
                      {selectedOutputGuards.map(guard => renderGuardConfig(guard, 'output'))}
                    </div>
                        ),
                      },
                    ]}
                  />
                </Form>
              ) : (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="LLM Provider">{project.config?.llm?.provider}</Descriptions.Item>
                  <Descriptions.Item label="LLM Model">{project.config?.llm?.model}</Descriptions.Item>
                  <Descriptions.Item label="Temperature">{project.config?.llm?.temperature}</Descriptions.Item>
                  <Descriptions.Item label="Guardrails Enabled">
                    <Tag color={project.config?.guardrails?.enabled ? 'success' : 'error'}>
                      {project.config?.guardrails?.enabled ? 'Yes' : 'No'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Input Guards">
                    {project.config?.guardrails?.input_guards?.length > 0 ? (
                      <div>
                        {project.config.guardrails.input_guards.map((guardName, idx) => (
                          <div key={idx} style={{ marginBottom: '8px' }}>
                            <Tag color="blue">{guardName}</Tag>
                            <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                              {Object.entries(project.config?.guardrails?.input_guards_config?.[guardName] || {}).map(([key, value]) => (
                                <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                  {key}: {JSON.stringify(value)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : 'None'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Output Guards">
                    {project.config?.guardrails?.output_guards?.length > 0 ? (
                      <div>
                        {project.config.guardrails.output_guards.map((guardName, idx) => (
                          <div key={idx} style={{ marginBottom: '8px' }}>
                            <Tag color="purple">{guardName}</Tag>
                            <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                              {Object.entries(project.config?.guardrails?.output_guards_config?.[guardName] || {}).map(([key, value]) => (
                                <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                  {key}: {JSON.stringify(value)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : 'None'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pre-Embedding Guards">
                    {project.config?.guardrails?.pre_embedding_guards?.length > 0 ? (
                      <div>
                        {project.config.guardrails.pre_embedding_guards.map((guardName, idx) => (
                          <div key={idx} style={{ marginBottom: '8px' }}>
                            <Tag color="gold">{guardName}</Tag>
                            <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                              {Object.entries(project.config?.guardrails?.pre_embedding_guards_config?.[guardName] || {}).map(([key, value]) => (
                                <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                  {key}: {JSON.stringify(value)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : 'None'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Context (Retrieval) Guards">
                    {project.config?.guardrails?.context_guards?.length > 0 ? (
                      <div>
                        {project.config.guardrails.context_guards.map((guardName, idx) => (
                          <div key={idx} style={{ marginBottom: '8px' }}>
                            <Tag color="cyan">{guardName}</Tag>
                            <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                              {Object.entries(project.config?.guardrails?.context_guards_config?.[guardName] || {}).map(([key, value]) => (
                                <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                  {key}: {JSON.stringify(value)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : 'None'}
                  </Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'file_pipeline',
              label: filePipelineCounts.failed > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  RAG Pipeline
                  {/* <Tag color="error" style={{ marginInlineEnd: 0 }}>{filePipelineCounts.failed} failed</Tag> */}
                </span>
              ) : 'RAG Pipeline',
                
              children: (
                <div
                  className={`file-pipeline-tab${showAdvancedPipelineColumns ? ' file-pipeline-tab--scroll-x' : ''}`}
                >

                    <div
                      style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <Input
                      allowClear
                      size="small"
                      placeholder="Search files..."
                      value={filePipelineSearch}
                      onChange={(e) => setFilePipelineSearch(e.target.value)}
                      style={{ width: 240 }}
                    />
                    {configuredKnowledgeSources.map((source) => {
                      const active = tableSourceFilterKey === source.key;
                      return (
                        <Tooltip
                          key={`table-filter-${source.key}`}
                          title={active ? `Showing ${source.label} rows` : `Filter table to ${source.label} rows`}
                        >
                          <button
                            type="button"
                            aria-label={`Toggle ${source.label} table filter`}
                            aria-pressed={active}
                            onClick={() => setTableSourceFilterKey((prev) => (prev === source.key ? '' : source.key))}
                            style={{
                              padding: 2,
                              borderRadius: 8,
                              border: active
                                ? `2px solid ${themeStyles.borderAccent}`
                                : `1px solid ${themeStyles.borderColor}`,
                              background: active
                                ? themeStyles.elevatedBg
                                : themeStyles.inputBg,
                              boxShadow: active ? themeStyles.shadowSm : 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'border-color 0.2s, box-shadow 0.2s',
                            }}
                          >
                            <ProviderLogo id={source.key} className="logo-container--compact" />
                          </button>
                        </Tooltip>
                      );
                    })}
                    {tableSourceFilterKey === 'jira' ? (
                      <>
                        <Select
                          size="small"
                          value={jiraProjectFilter}
                          onChange={setJiraProjectFilter}
                          options={jiraProjectFilterOptions}
                          style={{ minWidth: 150 }}
                        />
                        <Select
                          size="small"
                          value={jiraIssueTypeFilter}
                          onChange={setJiraIssueTypeFilter}
                          options={jiraIssueTypeFilterOptions}
                          style={{ minWidth: 170 }}
                        />
                        <Select
                          size="small"
                          value={jiraIssueStatusFilter}
                          onChange={setJiraIssueStatusFilter}
                          options={jiraStatusFilterOptions}
                          style={{ minWidth: 170 }}
                        />
                      </>
                    ) : null}
                    {tableSourceFilterKey === 'confluence' ? (
                      <Select
                        size="small"
                        showSearch
                        value={confluenceSpaceFilter}
                        onChange={setConfluenceSpaceFilter}
                        options={confluenceSpaceFilterOptions}
                        placeholder="Space keys"
                        optionFilterProp="label"
                        style={{ minWidth: 200 }}
                      />
                    ) : null}
                    {filePipelineFiltersActive && (
                      <Button
                        size="small"
                        onClick={() => {
                          setFilePipelineSearch('');
                          setFilePipelineStatusFilter('all');
                          setTableSourceFilterKey('');
                          setJiraProjectFilter('__all__');
                          setJiraIssueTypeFilter('__all__');
                          setJiraIssueStatusFilter('__all__');
                          setConfluenceSpaceFilter('__all__');
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {configuredKnowledgeSources.length > 0 ? (
                        <Tooltip title="Open source settings">
                          <Button
                            size="small"
                            onClick={() => {
                              const preferred = tableSourceFilterKey
                                || (isConfluenceKnowledgeSource ? 'confluence' : null)
                                || (isJiraKnowledgeSource ? 'jira' : null)
                                || (isAzureWikiKnowledgeSource ? 'azure_wiki' : null)
                                || configuredKnowledgeSources[0]?.key
                                || 'jira';
                              setSourceSettingsActiveKey(preferred);
                              setPipelineJiraPanelOpen(true);
                            }}
                          >
                            Source Settings
                          </Button>
                        </Tooltip>
                      ) : null}
                      {isConfluenceKnowledgeSource && canEdit && confluenceMonitoredPageKeys.size > 0 ? (
                        <Tooltip title="Check monitored Confluence pages for updates">
                          <Button
                            size="small"
                            icon={<SyncOutlined />}
                            loading={confluenceMonitorChecking}
                            onClick={() => { void handleCheckConfluenceMonitoredUpdatesNow(); }}
                          >
                            Check for updates
                          </Button>
                        </Tooltip>
                      ) : null}
                      {isAzureWikiKnowledgeSource && canEdit && azureWikiMonitoredPageKeys.size > 0 ? (
                        <Tooltip title="Check monitored Azure Wiki pages for updates">
                          <Button
                            size="small"
                            icon={<SyncOutlined />}
                            loading={azureWikiMonitorChecking}
                            onClick={() => { void handleCheckAzureWikiMonitoredUpdatesNow(); }}
                          >
                            Check for updates
                          </Button>
                        </Tooltip>
                      ) : null}
                      {isAzureWikiKnowledgeSource && canEdit ? (
                        <Tooltip title="Force-refresh now. New wiki pages under your selected scope are also discovered automatically while this tab is open.">
                          <Button
                            size="small"
                            icon={<SyncOutlined />}
                            loading={azureWikiSyncing}
                            onClick={() => { void handleSyncAzureWikiNow(); }}
                          >
                            Sync from Azure Wiki
                          </Button>
                        </Tooltip>
                      ) : null}
                      {isGithubKnowledgeSource && canEdit ? (
                        <Tooltip title="Refresh the file list from your selected GitHub repositories, branches, and paths.">
                          <Button
                            size="small"
                            icon={<SyncOutlined />}
                            loading={githubSyncing}
                            onClick={() => { void handleSyncGithubNow(); }}
                          >
                            Sync from GitHub
                          </Button>
                        </Tooltip>
                      ) : null}
                      {pipelineS3Bucket && canEdit && (() => {
                        const missingTriggers = !filePipelineTriggerStatus?.has_required_triggers;
                        const missingPerms = filePipelineS3Permissions?.all_required_granted === false;
                        const uploadBlocked =
                          !canEdit || !token || filePipelineS3Uploading || missingTriggers || missingPerms;
                        const blockedReason = !canEdit
                          ? 'Read-only access.'
                          : !token
                            ? 'Sign in required.'
                            : missingTriggers
                              ? 'Enable S3 upload/ delete event triggers first.'
                              : missingPerms
                                ? 'Grant required S3 permissions first.'
                                : 'Files upload to prefix upload/ so S3 events can run the pipeline.';
                        return (
                          <Tooltip title={blockedReason}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <Select
                            size="small"
                            value={uploadCategoryId}
                            onChange={setUploadCategoryId}
                            options={documentCategoryOptions}
                            style={{ minWidth: 160 }}
                            disabled={uploadBlocked}
                          />
                          <Upload
                            multiple
                            showUploadList={false}
                              disabled={uploadBlocked}
                            beforeUpload={(file) => {
                              (async () => {
                                setFilePipelineS3Uploading(true);
                                try {
                                  await API.uploadS3File(token, file, pipelineS3Bucket, uploadCategoryId);
                                  message.success(`${file.name} uploaded under upload/`);
                                  await loadPipelineData(true);
                                } catch (e) {
                                  const detail = e?.response?.data?.detail;
                                  message.error(
                                    typeof detail === 'string'
                                      ? formatS3Error(detail)
                                      : detail || 'Upload failed',
                                  );
                                } finally {
                                  setFilePipelineS3Uploading(false);
                                }
                              })();
                              return false;
                            }}
                          >
                            <Button
                              type="primary"
                              className="file-pipeline-subtle-primary"
                              icon={<UploadOutlined />}
                              loading={filePipelineS3Uploading}
                                disabled={uploadBlocked}
                                style={{
                                  ...pipelinePrimaryButtonStyle,
                                  height: 30,
                                  minWidth: 132,
                                  padding: '0 12px',
                                }}
                            >
                              Upload files to S3
                            </Button>
                          </Upload>
                          </div>
                          </Tooltip>
                        );
                      })()}
                      {canEdit ? (
                        <Tooltip
                          title={
                            selectedFilePipelineRowKeys.length > 0
                              ? `Run pipeline for ${selectedFilePipelineRowKeys.length} selected file(s)`
                              : 'Select files in the table, or run pipeline for all eligible files'
                          }
                        >
                        <Button
                          type="primary"
                          className="file-pipeline-subtle-primary"
                          icon={<PlayCircleOutlined />}
                          onClick={handleRunFilePipeline}
                          loading={filePipelineActionLoading === 'run'}
                          disabled={!!filePipelineActionLoading && filePipelineActionLoading !== 'run'}
                    style={{
                            ...pipelinePrimaryButtonStyle,
                            height: 30,
                            minWidth: 108,
                            padding: '0 12px',
                          }}
                        >
                          {selectedFilePipelineRowKeys.length > 0
                            ? `Run now (${selectedFilePipelineRowKeys.length})`
                            : 'Run now'}
                      </Button>
                        </Tooltip>
                      ) : null}
                      {canEdit && selectedFilePipelineRowKeys.length > 0 ? (
                        <Dropdown
                          trigger={['click']}
                          placement="bottomRight"
                          disabled={Boolean(filePipelineActionLoading || filePipelineRowActionLoading)}
                          menu={{
                            items: [
                              {
                                key: 'bulk-reprocess',
                                icon: <ReloadOutlined />,
                                label: `Reprocess (${selectedFilePipelineRowKeys.length})`,
                                disabled: Boolean(filePipelineActionLoading || filePipelineRowActionLoading),
                                onClick: handleBulkReprocessSelected,
                              },
                              { type: 'divider' },
                              {
                                key: 'bulk-delete',
                                icon: <DeleteOutlined />,
                                label: `Delete (${selectedFilePipelineRowKeys.length})`,
                                danger: true,
                                disabled: Boolean(filePipelineActionLoading || filePipelineRowActionLoading),
                                onClick: handleBulkDeleteSelected,
                              },
                            ],
                          }}
                        >
                          <Button
                            loading={
                              filePipelineActionLoading === 'bulk-reprocess'
                              || filePipelineActionLoading === 'bulk-delete'
                            }
                            disabled={Boolean(filePipelineActionLoading || filePipelineRowActionLoading)}
                            style={{ height: 30, padding: '0 12px' }}
                          >
                            Actions <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                          </Button>
                        </Dropdown>
                      ) : null}
                      <Tooltip title="Show Score, Cost, Last run, and Error columns">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: themeStyles.textSecondary }}>See more</span>
                          <Switch
                            size="small"
                            checked={showAdvancedPipelineColumns}
                            onChange={setShowAdvancedPipelineColumns}
                            aria-label="Show extra pipeline columns"
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                  <>
                  {isJiraKnowledgeSource && jiraNeedsProjectSetup && !pipelineJiraPanelOpen ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 10 }}
                      message="Configure Jira project"
                      description="Select Jira project keys and issue scope to start syncing issues into the pipeline."
                      action={canEdit ? (
                        <Button size="small" type="primary" onClick={openPipelineJiraSetup}>
                          Configure
                        </Button>
                      ) : null}
                    />
                  ) : null}
                  {(jiraMonitorActiveUpdate || jiraLiveProcessingRow) ? (
                    <Alert
                      type={jiraMonitorActiveStatus === 'failed' ? 'warning' : 'info'}
                      showIcon
                      style={{ marginBottom: 10 }}
                      message={`Jira conversion ${jiraMonitorIsWorking ? 'in progress' : 'queued'}: ${jiraMonitorBannerIssueKey || 'selected issue'}`}
                      description={
                        jiraMonitorIsWorking
                          ? `Updating markdown and vector index${jiraMonitorActiveStage ? ` (${jiraMonitorActiveStage})` : ''}. You can continue using the page while this runs.`
                          : 'Queued for markdown conversion and vector indexing. You can continue using the page.'
                      }
                    />
                  ) : null}
                  {confluenceMonitorActiveUpdate ? (
                    <Alert
                      type={confluenceMonitorActiveStatus === 'failed' ? 'warning' : 'info'}
                      showIcon
                      style={{ marginBottom: 10 }}
                      message={`Confluence conversion ${confluenceMonitorIsWorking ? 'in progress' : 'queued'}: ${confluenceMonitorBannerTitle || confluenceMonitorBannerPageId || 'selected page'}`}
                      description={
                        confluenceMonitorIsWorking
                          ? `Updating markdown and vector index${confluenceMonitorActiveStage ? ` (${confluenceMonitorActiveStage})` : ''}. You can continue using the page while this runs.`
                          : 'Queued for markdown conversion and vector indexing. You can continue using the page.'
                      }
                    />
                  ) : null}
                  {azureWikiMonitorActiveUpdate ? (
                    <Alert
                      type={azureWikiMonitorActiveStatus === 'failed' ? 'warning' : 'info'}
                      showIcon
                      style={{ marginBottom: 10 }}
                      message={`Azure Wiki conversion ${azureWikiMonitorIsWorking ? 'in progress' : 'queued'}: ${azureWikiMonitorBannerTitle || azureWikiMonitorBannerPageId || 'selected page'}`}
                      description={
                        azureWikiMonitorIsWorking
                          ? `Updating markdown and vector index${azureWikiMonitorActiveStage ? ` (${azureWikiMonitorActiveStage})` : ''}. You can continue using the page while this runs.`
                          : 'Queued for markdown conversion and vector indexing. You can continue using the page.'
                      }
                    />
                  ) : null}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    {FILE_PIPELINE_STATUS_TAG_FILTERS.map(({ key, label, countKey, color }) => {
                      const active = filePipelineStatusFilter === key;
                      const count = filePipelineCounts[countKey];
                      return (
                        <Tag
                          key={key}
                          color={active ? color : undefined}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setFilePipelineStatusFilter((prev) => (prev === key ? 'all' : key));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setFilePipelineStatusFilter((prev) => (prev === key ? 'all' : key));
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            marginInlineEnd: 0,
                            fontWeight: active ? 600 : 500,
                            border: active
                              ? undefined
                              : `1px solid ${themeStyles.borderColor}`,
                            background: active ? undefined : themeStyles.elevatedBg,
                            color: active ? undefined : themeStyles.textSecondary,
                          }}
                        >
                          {label}: {count}
                        </Tag>
                      );
                    })}
                  </div>

                  <Table
                    size="small"
                    tableLayout="fixed"
                    scroll={showAdvancedPipelineColumns ? { x: 'max-content' } : undefined}
                    loading={filePipelineLoading}
                    dataSource={filteredFilePipelineFiles}
                    rowSelection={canEdit ? {
                      selectedRowKeys: selectedFilePipelineRowKeys,
                      onChange: (keys) => {
                        setSelectedFilePipelineRowKeys(
                          (keys || []).filter((k) => typeof k === 'string' && k.trim()),
                        );
                      },
                      getCheckboxProps: (record) => ({
                        disabled: !record?._id
                          || String(record?.processing_status || '').toLowerCase() === 'deleted',
                      }),
                      columnWidth: 40,
                      preserveSelectedRowKeys: true,
                    } : undefined}
                    rowKey={(record) => record?._id || record?.s3_key || record?.file_key || record?.doc_id}
                    pagination={{ pageSize: 5, position: ['bottomLeft'], hideOnSinglePage: true }}
                    locale={{
                      emptyText: filePipelineLoading ? (
                        <div style={{ padding: '24px 0', textAlign: 'center' }}>
                          <Spin />
                          <div style={{ marginTop: 8, fontSize: 12, color: themeStyles.textSecondary }}>
                            Loading RAG pipeline...
                          </div>
                        </div>
                      ) : filePipelineFiltersActive && activeFilePipelineFiles.length > 0 ? (
                        <div style={{ padding: '12px 0', color: themeStyles.textSecondary }}>
                          No files match your search or filters.
                        </div>
                      ) : (
                        <div style={{ padding: '12px 0' }}>
                          {isConfluenceKnowledgeSource
                            ? 'No pages synced yet. Click Sync from Confluence, then run the pipeline.'
                            : isGithubKnowledgeSource
                              ? 'No files synced yet. Click Sync from GitHub, then run the pipeline.'
                              : isJiraKnowledgeSource
                                ? (jiraNeedsProjectSetup ? (
                                  <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                                    <div style={{ display: 'inline-flex', marginBottom: 12 }}>
                                      <ProviderLogo id="jira" />
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 15, color: themeStyles.textPrimary }}>
                                      Configure Jira project
                                    </div>
                                    <div
                                      style={{
                                        marginTop: 8,
                                        marginBottom: 16,
                                        fontSize: 13,
                                        color: themeStyles.textSecondary,
                                        maxWidth: 420,
                                        marginInline: 'auto',
                                        lineHeight: 1.55,
                                      }}
                                    >
                                      No Jira issues are synced yet. Choose project keys and issue scope, then save to start importing issues.
                                    </div>
                                    {canEdit ? (
                                      <Button type="primary" onClick={openPipelineJiraSetup}>
                                        Configure Jira project
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : (jiraConnectorStatus?.available === false
                                  ? 'Jira is not reachable. Check credentials in Source Settings, then sync again.'
                                  : 'No issues synced yet. Run the pipeline after Jira scope is saved.'))
                                : isAzureWikiKnowledgeSource
                                  ? 'No pages synced yet. Save page scope in Source Settings — new pages are discovered automatically, then run the pipeline.'
                                  : 'No files found. Upload files to your Knowledge Source to start the pipeline.'}
                        </div>
                      ),
                    }}
                    columns={[
                      ...(isConfluenceKnowledgeSource && !filePipelineCompactView ? [{
                        title: 'Folder',
                        key: 'confluence_folder',
                        width: 130,
                        ellipsis: true,
                        render: (_, record) => {
                          const folder = getConfluencePipelineFolderLabel(record);
                          if (!folder) return '—';
                          return (
                            <Tooltip title={folder} placement="topLeft">
                              <span
                                style={{
                                  display: 'block',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {folder}
                              </span>
                            </Tooltip>
                          );
                        },
                      }] : []),
                      {
                        title: isConfluenceKnowledgeSource ? 'Page' : 'File',
                        dataIndex: 'file_name',
                        key: 'file_name',
                        fixed: 'left',
                        width: 220,
                        ellipsis: true,
                        render: (t, record) => {
                          const name = t || record.file_key || record.doc_id || '—';
                          return (
                            <Tooltip title={name} placement="topLeft">
                              <span
                                style={{
                                  display: 'block',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {name}
                              </span>
                            </Tooltip>
                          );
                        },
                      },
                      ...(!filePipelineCompactView ? [{
                        title: 'Size',
                        key: 'file_size',
                        width: 72,
                        sorter: (a, b) => {
                          const av = getPipelineFileSizeBytes(a);
                          const bv = getPipelineFileSizeBytes(b);
                          if (av == null && bv == null) return 0;
                          if (av == null) return 1;
                          if (bv == null) return -1;
                          return av - bv;
                        },
                        showSorterTooltip: true,
                        render: (_, record) => formatFileSize(record),
                      }] : []),
                      ...(!filePipelineCompactView && canEdit
                        ? [{
                          title: 'Role',
                          key: 'role_id',
                          width: 120,
                          render: (_, record) => {
                            const current = (record?.role_id || record?.category_id)
                              ? String(record.role_id || record.category_id)
                              : null;
                            if (!record?._id) {
                              return current ? (categoryNameById[current] || current) : 'Unassigned';
                            }
                            return (
                              <Select
                                size="small"
                                value={current || undefined}
                                placeholder="Unassigned"
                                options={documentCategoryOptions}
                                style={{ width: '100%', maxWidth: 120 }}
                                onChange={async (val) => {
                                  try {
                                    await API.patchFilePipelineFileRole(token, projectId, record._id, val);
                                    message.success('Role updated');
                                    await loadPipelineData(true);
                                  } catch (e) {
                                    message.error(e?.response?.data?.detail || 'Failed to update role');
                                  }
                                }}
                              />
                            );
                          },
                        }]
                        : []),
                      ...(!filePipelineCompactView && SHOW_VECTOR_RETENTION_UI
                        ? [{
                          title: 'Retention',
                          key: 'vector_retention',
                          width: 100,
                          ellipsis: true,
                          onHeaderCell: () => ({
                            style: {
                              paddingLeft: 6,
                              paddingRight: 4,
                            },
                          }),
                          onCell: () => ({
                            style: {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              paddingLeft: 6,
                              paddingRight: 4,
                            },
                          }),
                          render: (_, record) => (
                            record?._id ? (
                            <FileVectorRetentionEditor
                              record={record}
                              token={token}
                              projectId={projectId}
                              disabled={!canEdit}
                              onAfterSave={() => loadPipelineData(true)}
                                textColor={themeStyles.textPrimary}
                            />
                            ) : '—'
                          ),
                        }]
                        : []),
                      {
                        title: 'Status',
                        dataIndex: 'processing_status',
                        key: 'processing_status',
                        width: 88,
                        render: (status, record) => (
                          <span style={{ color: getPipelineStatusTextColor(status, record), fontWeight: 500 }}>
                            {formatPipelineStatusLabel(status, record)}
                          </span>
                        ),
                      },
                      {
                        title: 'Progress',
                        key: 'progress_percent',
                        width: 200,
                        className: 'file-pipeline-progress-col',
                        onCell: () => ({ className: 'file-pipeline-progress-col' }),
                        render: (_, record) => {
                          const detail = getPipelineProgressDetail(record);
                          const st = (record?.processing_status || '').toLowerCase();
                          const deleting = isPipelineDeleting(record);
                          const isProcessing = deleting || st === 'processing' || st === 'picked';

                          if (st === 'pending' && !deleting) {
                            const queued = isJiraMonitorQueuedRow(record) || isConfluenceMonitorQueuedRow(record);
                            return (
                              <div style={FILE_PIPELINE_PROGRESS_BLOCK_STYLE} className="file-pipeline-progress-cell">
                                <Progress percent={0} size="small" strokeWidth={4} status="normal" showInfo={false} />
                                <span style={{ fontSize: 11, color: themeStyles.textSecondary }}>
                                  {queued ? 'Queued' : 'Not started'}
                                </span>
                              </div>
                            );
                          }

                          const percent = Number.isFinite(record?.progress_percent)
                            ? Math.max(0, Math.min(100, record.progress_percent))
                            : (record?.processing_status === 'completed' ? 100 : 0);

                          let progressStatus = 'normal';
                          if (deleting) progressStatus = 'active';
                          if (record?.processing_status === 'failed') progressStatus = 'exception';
                          if (record?.processing_status === 'completed') progressStatus = 'success';

                          const underBar = getProgressUnderBarLabel(record);

                          return (
                            <div style={FILE_PIPELINE_PROGRESS_BLOCK_STYLE} className="file-pipeline-progress-cell">
                              <div style={{ width: '100%' }}>
                                <Progress
                                  percent={percent}
                                  size="small"
                                  strokeWidth={4}
                                  status={progressStatus}
                                  showInfo={false}
                                />
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  minWidth: 0,
                                  width: '100%',
                                }}
                              >
                                {underBar?.text ? (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: themeStyles.textSecondary,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {underBar.text}
                                  </span>
                                ) : isProcessing ? (
                                  <span style={{ fontSize: 11, color: themeStyles.textTertiary, flex: 1 }}>
                                    Estimating…
                                  </span>
                                ) : (
                                  <span style={{ flex: 1 }} />
                                )}
                                {detail ? (
                                  <Button
                                    type="link"
                                    size="small"
                                    style={{
                                      padding: 0,
                                      height: 'auto',
                                      fontSize: 11,
                                      flexShrink: 0,
                                    }}
                                    onClick={() =>
                                      setPipelineDetailModal({
                                        open: true,
                                        title: record?.file_name || record?.file_key || 'Pipeline details',
                                        detail,
                                      })
                                    }
                                  >
                                    Details
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          );
                        },
                      },
                      ...(showAdvancedPipelineColumns
                        ? [
                            {
                              title: 'Event',
                              dataIndex: 'event_type',
                              key: 'event_type',
                              width: 92,
                              ellipsis: true,
                              render: (t) => formatStatusLabel(t),
                            },
                            {
                              title: 'Step',
                              dataIndex: 'processing_stage',
                              key: 'processing_stage',
                              width: 132,
                              ellipsis: true,
                              render: (stage, record) => {
                                const rowStatus = String(record?.processing_status || '').toLowerCase();
                                if (rowStatus === 'pending' && !isPipelineDeleting(record)) {
                                  return (isJiraMonitorQueuedRow(record) || isConfluenceMonitorQueuedRow(record))
                                    ? 'Queued'
                                    : '—';
                                }
                                return formatPipelineStage(stage, record);
                              },
                            },
                            {
                              title: 'Chunking',
                              dataIndex: 'chosen_strategy',
                              key: 'chosen_strategy',
                              width: 100,
                              ellipsis: true,
                              render: (strategy, record) => (
                                hasFinalPipelineMetrics(record)
                                  ? formatStatusLabel(strategy)
                                  : '—'
                              ),
                            },
                            {
                              title: 'Score',
                              key: 'strategy_score',
                              width: 56,
                              ellipsis: true,
                              render: (_, record) => {
                                if (!hasFinalPipelineMetrics(record)) return '—';
                                const winnerScore = record.strategy_details?.winner_score;
                                if (typeof winnerScore === 'number') {
                                  return winnerScore.toFixed(3);
                                }
                                const chosen = record.chosen_strategy;
                                const scores = record.strategy_details?.scores;
                                if (chosen && scores && typeof scores[chosen] === 'number') {
                                  return scores[chosen].toFixed(3);
                                }
                                return '—';
                              },
                            },
                            {
                              title: 'Cost',
                              key: 'processing_cost_usd',
                              width: 80,
                              sorter: (a, b, sortOrder) => {
                                const av = getPipelineCostSortValue(a);
                                const bv = getPipelineCostSortValue(b);
                                const aE = av == null;
                                const bE = bv == null;
                                if (aE && bE) return 0;
                                if (aE) return sortOrder === 'descend' ? -1 : 1;
                                if (bE) return sortOrder === 'descend' ? 1 : -1;
                                return av - bv;
                              },
                              showSorterTooltip: true,
                              render: (_, record) => {
                                if (!hasFinalPipelineMetrics(record)) return '—';
                                const v = record?.processing_cost_usd ?? record?.cost_tracking?.total_usd;
                                if (typeof v === 'number' && Number.isFinite(v)) {
                                  return `$${v.toFixed(v < 0.01 ? 6 : 4)}`;
                                }
                                return '—';
                              },
                            },
                            {
                              title: 'Last run',
                              dataIndex: 'last_run_at',
                              key: 'last_run_at',
                              width: 130,
                              sorter: (a, b, sortOrder) => {
                                const av = getPipelineLastRunSortValue(a);
                                const bv = getPipelineLastRunSortValue(b);
                                const aE = av == null;
                                const bE = bv == null;
                                if (aE && bE) return 0;
                                if (aE) return sortOrder === 'descend' ? -1 : 1;
                                if (bE) return sortOrder === 'descend' ? 1 : -1;
                                return av - bv;
                              },
                              showSorterTooltip: true,
                              render: (v, record) => {
                                if (!hasFinalPipelineMetrics(record)) return '—';
                                const text = v ? new Date(v).toLocaleString() : '—';
                                return (
                                  <Tooltip title={text} placement="topLeft">
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {text}
                                    </span>
                                  </Tooltip>
                                );
                              },
                            },
                            {
                              title: 'Error',
                              key: 'last_error',
                              width: 140,
                              ellipsis: true,
                              render: (_, record) => {
                                if (!hasFinalPipelineMetrics(record)) return '—';
                                const summary = formatPipelineError(record);
                                if (!summary) return '—';
                                const maxChars = 18;
                                const shortError =
                                  typeof summary === 'string' && summary.length > maxChars
                                    ? `${summary.slice(0, maxChars)}...`
                                    : summary;
                                const reference = getPipelineErrorReference(record);
                                const tooltipTitle = reference
                                  ? `${summary}\n\nDetails: ${reference}`
                                  : summary;
                                return (
                                  <Tooltip title={tooltipTitle} placement="topLeft">
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'bottom',
                                      }}
                                    >
                                      {shortError}
                                    </span>
                                  </Tooltip>
                                );
                              },
                            },
                          ]
                        : []),
                      {
                        title: 'Actions',
                        key: 'actions',
                        fixed: 'right',
                        width: 72,
                        align: 'center',
                        render: (_, record) => {
                          const fileKey = record?.s3_key || record?.file_key || record?.doc_id;
                          const rowId = record?._id || fileKey || 'row';
                          const status = String(record?.processing_status || '').toLowerCase();
                          const canRetryFailed = status === 'failed';
                          const canCancelProcessing = ['processing', 'picked'].includes(status);
                          const canReprocess = ['completed', 'failed', 'pending'].includes(status);
                          const actionBusy = Boolean(filePipelineActionLoading || filePipelineRowActionLoading);

                          const menuItems = [
                            {
                              key: 'view',
                              icon: <EyeOutlined />,
                              label: 'View',
                              disabled: !token || !fileKey || actionBusy,
                              onClick: () => handleViewFile(record),
                            },
                            {
                              key: 'download',
                              icon: <DownloadOutlined />,
                              label: 'Download',
                              disabled: !token || !fileKey || actionBusy,
                              onClick: () => handleDownloadPipelineFile(record),
                            },
                            ...(canEdit && !isPollKnowledgePipelineFile(record, fileKey) ? [{
                              key: 'reupload',
                              icon: <UploadOutlined />,
                              label: 'Reupload',
                              disabled: !token || !fileKey || actionBusy,
                              onClick: () => {
                                reuploadRecordRef.current = record;
                                reuploadFileRef.current?.click();
                              },
                            }] : []),
                            ...(canEdit && canCancelProcessing ? [{
                              key: 'cancel',
                              icon: <StopOutlined />,
                              label: 'Cancel',
                              disabled: !token || !record?._id || actionBusy,
                              onClick: () => handleCancelFileProcessing(record),
                            }] : []),
                            ...(canEdit && canReprocess ? [{
                              key: 'reprocess',
                              icon: <ReloadOutlined />,
                              label: 'Reprocess',
                              disabled: !token || !record?._id || actionBusy,
                              onClick: () => handleReprocessFile(record),
                            }] : []),
                            ...(canEdit
                              && isConfluenceKnowledgeSource
                              && String(record?.source_meta?.content_kind || '').toLowerCase() !== 'attachment'
                              && String(record?.source_meta?.page_id || '').trim()
                              ? [{
                                key: 'monitor',
                                icon: <RadarChartOutlined />,
                                label: confluenceMonitoredPageKeys.has(String(record.source_meta.page_id).trim())
                                  ? 'Stop monitoring'
                                  : 'Monitor for updates',
                                disabled: !token || actionBusy,
                                onClick: () => {
                                  const pageId = String(record.source_meta.page_id).trim();
                                  const enabled = !confluenceMonitoredPageKeys.has(pageId);
                                  void handleToggleConfluencePageMonitor(
                                    pageId,
                                    enabled,
                                    record?.source_meta?.space_key,
                                  );
                                },
                              }]
                              : []),
                            ...(canEdit
                              && isAzureWikiKnowledgeSource
                              && String(record?.source_meta?.page_id || '').trim()
                              ? [{
                                key: 'azure-wiki-monitor',
                                icon: <RadarChartOutlined />,
                                label: azureWikiMonitoredPageKeys.has(String(record.source_meta.page_id).trim())
                                  ? 'Stop monitoring'
                                  : 'Monitor for updates',
                                disabled: !token || actionBusy,
                                onClick: () => {
                                  const pageId = String(record.source_meta.page_id).trim();
                                  const enabled = !azureWikiMonitoredPageKeys.has(pageId);
                                  void handleToggleAzureWikiPageMonitor(
                                    pageId,
                                    enabled,
                                    {
                                      path: record?.source_meta?.path || record?.source_meta?.page_path,
                                      title: record?.source_meta?.title || record?.file_name,
                                      wiki_id: record?.source_meta?.wiki_id,
                                      project: record?.source_meta?.project,
                                    },
                                  );
                                },
                              }]
                              : []),
                            ...(canEdit && canRetryFailed ? [{
                              key: 'retry',
                              icon: <ReloadOutlined />,
                              label: 'Retry',
                              disabled: !token || !record?._id || actionBusy,
                              onClick: () => handleRetryFailedFile(record),
                            }] : []),
                            ...(canEdit ? [{ type: 'divider' }, {
                              key: 'delete',
                              icon: <DeleteOutlined />,
                              label: 'Delete',
                              danger: true,
                              disabled: !token || actionBusy,
                              onClick: () => handleDeleteFileComplete(record),
                            }] : []),
                          ].filter((item) => item.type === 'divider' || !item.disabled);

                          const visibleMenuItems = menuItems.filter((item, index, items) => {
                            if (item.type !== 'divider') return true;
                            if (index === 0 || index === items.length - 1) return false;
                            return items[index - 1]?.type !== 'divider';
                          });

                          if (!visibleMenuItems.length) {
                            return '—';
                          }

                          return (
                            <Dropdown
                              menu={{ items: visibleMenuItems }}
                              trigger={['click']}
                              placement="bottomRight"
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined style={{ fontSize: 18 }} />}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              />
                            </Dropdown>
                          );
                        },
                      },
                    ]}
                  />
                  </>
                  {project.deployed && !isSuperAdmin && (
                    <Tooltip title="Chat with project" placement="left">
                      <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        className="project-fab-chat-btn"
                        aria-label="Chat with project"
                        icon={<MessageOutlined style={{ fontSize: 22 }} />}
                        onClick={() => navigate(`/chat/${project.id}`)}
                        style={{
                          position: 'fixed',
                          right: 24,
                          bottom: 48,
                          zIndex: 1050,
                          width: 56,
                          height: 56,
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          /* Brand FAB: neon pink gradient in both themes (matches dark theme look on light UI) */
                          background: 'linear-gradient(135deg, #F62E97 0%, #E93479 42%, #94167F 100%)',
                          boxShadow:
                            '0 6px 20px rgba(246, 46, 151, 0.5), 0 2px 10px rgba(148, 22, 127, 0.45)',
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              ),
            }
          ].filter(Boolean).sort(
            (a, b) => PROJECT_DETAIL_TAB_ORDER.indexOf(a.key) - PROJECT_DETAIL_TAB_ORDER.indexOf(b.key),
          )}
        />
      </Card>

      {/* Hidden file input for reupload from dropdown menu */}
      <input
        type="file"
        ref={reuploadFileRef}
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          const record = reuploadRecordRef.current;
          if (file && record) {
            await handleReuploadFileToS3(record, file);
          }
          e.target.value = '';
          reuploadRecordRef.current = null;
        }}
      />

      <Modal
        title="Source Settings"
        open={pipelineJiraPanelOpen}
        onCancel={() => setPipelineJiraPanelOpen(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {configuredKnowledgeSources.map((source) => {
            const active = sourceSettingsActiveKey === source.key;
            return (
              <Tooltip key={source.key} title={source.label}>
                <button
                  type="button"
                  aria-label={`Select ${source.label}`}
                  onClick={() => setSourceSettingsActiveKey(source.key)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 4,
                    borderRadius: 8,
                    border: active
                      ? `2px solid ${themeStyles.borderAccent}`
                      : `1px solid ${themeStyles.borderColor}`,
                    background: active ? themeStyles.elevatedBg : themeStyles.inputBg,
                    cursor: 'pointer',
                  }}
                >
                  <ProviderLogo id={source.key} className="logo-container--compact" />
                </button>
              </Tooltip>
            );
          })}
        </div>
        {sourceSettingsActiveKey === 'jira' ? (
          <>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, flex: 1, minWidth: 260 }}>
            <div style={{ minWidth: 220, flex: '1 1 220px' }}>
              <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                Project keys
              </Text>
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select Jira project keys"
                value={pipelineConfiguredProjectKeys}
                disabled={!canEdit || jiraVerifying}
                loading={jiraVerifying}
                options={jiraProjectSelectOptions}
                filterOption={filterJiraProjectOption}
                onChange={handlePipelineProjectKeysChange}
                style={{ width: '100%' }}
              />
            </div>
            {pipelineConfiguredProjectKeys.length > 1 ? (
              <div style={{ minWidth: 160, flex: '0 1 180px' }}>
                <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                  Edit scope for
                </Text>
                <Select
                  value={pipelineSelectedJiraProjectKey || undefined}
                  disabled={!pipelineConfiguredProjectKeys.length}
                  options={pipelineConfiguredProjectKeys.map((key) => ({
                    value: key,
                    label: key,
                  }))}
                  onChange={setPipelineSelectedJiraProjectKey}
                  style={{ width: '100%' }}
                />
              </div>
            ) : null}
          </div>
        </div>
        {pipelineSelectedJiraProjectKey ? (
          <JiraIssueScopePicker
            projectKey={pipelineSelectedJiraProjectKey}
            credentials={{
              jira_base_url: project?.config?.jira?.jira_base_url,
              jira_user_email: project?.config?.jira?.jira_user_email,
            }}
            value={
              pipelineJiraScopeByKey[pipelineSelectedJiraProjectKey]?.issue_type_filter
              || DEFAULT_JIRA_ISSUE_TYPE_FILTER
            }
            disabled={!canEdit}
            requireApiToken={false}
            fetchIssueScope={fetchJiraIssueScopeForProject}
            onChange={(issueTypeFilter, meta) => {
              setPipelineJiraScopeByKey((prev) => ({
                ...prev,
                [pipelineSelectedJiraProjectKey]: {
                  ...(prev[pipelineSelectedJiraProjectKey] || createDefaultJiraScope()),
                  type: JIRA_SCOPE.ISSUE_FILTER,
                  issue_type_filter: issueTypeFilter,
                  preview_jql: meta?.preview_jql
                    || prev[pipelineSelectedJiraProjectKey]?.preview_jql
                    || '',
                },
              }));
            }}
            monitoredIssueKeys={jiraMonitoredIssueKeys}
            onToggleMonitor={canEdit
              ? (issueKey, enabled) => handleToggleJiraIssueMonitor(
                issueKey,
                enabled,
                pipelineSelectedJiraProjectKey,
              )
              : null}
            onCheckMonitoredUpdates={canEdit ? handleCheckJiraMonitoredUpdatesNow : null}
            monitorCheckLoading={jiraMonitorChecking}
            monitoredIssueCount={jiraMonitoredIssueKeys.size}
            selectedPanelFooter={canEdit ? (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleProceedPipelineJiraScope}
                loading={configSaving}
                disabled={!pipelineConfiguredProjectKeys.length}
                style={pipelineSaveButtonStyle}
              >
                Proceed
              </Button>
            ) : null}
          />
        ) : (
          <Text type="secondary">Select at least one project key above.</Text>
        )}
          </>
        ) : sourceSettingsActiveKey === 'confluence' ? (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, flex: 1, minWidth: 260 }}>
                <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                    Space keys
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Select Confluence space keys"
                    value={pipelineConfiguredConfluenceSpaceKeys}
                    disabled={!canEdit || confluenceSpacesLoading}
                    loading={confluenceSpacesLoading}
                    options={confluenceSpaceSelectOptions}
                    filterOption={(input, option) => {
                      const q = String(input || '').trim().toLowerCase();
                      if (!q) return true;
                      const label = String(option?.label ?? '').toLowerCase();
                      const value = String(option?.value ?? '').toLowerCase();
                      return label.includes(q) || value.includes(q);
                    }}
                    onChange={handlePipelineConfluenceSpaceKeysChange}
                    style={{ width: '100%' }}
                  />
                </div>
                {pipelineConfiguredConfluenceSpaceKeys.length > 1 ? (
                  <div style={{ minWidth: 160, flex: '0 1 180px' }}>
                    <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                      Edit scope for
                    </Text>
                    <Select
                      value={pipelineSelectedConfluenceSpaceKey || undefined}
                      disabled={!pipelineConfiguredConfluenceSpaceKeys.length}
                      options={pipelineConfiguredConfluenceSpaceKeys.map((key) => ({
                        value: key,
                        label: key,
                      }))}
                      onChange={handlePipelineConfluenceEditSpaceChange}
                      style={{ width: '100%' }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            {confluenceSpacesError ? (
              <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                {confluenceSpacesError}
              </div>
            ) : null}

            {pipelineSelectedConfluenceSpaceKey ? (
              <ConfluencePageScopePicker
                spaceKey={pipelineSelectedConfluenceSpaceKey}
                value={
                  pipelineConfluenceScopeByKey[pipelineSelectedConfluenceSpaceKey]?.folderIds
                  || selectedConfluenceFolderIds
                }
                disabled={!canEdit}
                fetchPageTree={fetchConfluencePageTreeForProject}
                onChange={(rootIds, meta) => {
                  const pages = meta?.pages || [];
                  if (pages.length) {
                    setConfluenceFolders(pages);
                  }
                  applyConfluenceFolderSelection(
                    rootIds,
                    pages.length ? pages : confluenceFolders,
                    pipelineSelectedConfluenceSpaceKey,
                  );
                }}
                monitoredPageIds={confluenceMonitoredPageKeys}
                onToggleMonitor={canEdit
                  ? (pageId, enabled) => handleToggleConfluencePageMonitor(
                    pageId,
                    enabled,
                    pipelineSelectedConfluenceSpaceKey || project?.config?.confluence?.space_key,
                  )
                  : null}
                onCheckMonitoredUpdates={canEdit ? handleCheckConfluenceMonitoredUpdatesNow : null}
                monitorCheckLoading={confluenceMonitorChecking}
                monitoredPageCount={confluenceMonitoredPageKeys.size}
                selectedPanelFooter={canEdit ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleProceedPipelineConfluenceScope}
                    loading={configSaving}
                    disabled={!pipelineConfiguredConfluenceSpaceKeys.length}
                    style={pipelineSaveButtonStyle}
                  >
                    Proceed
                  </Button>
                ) : null}
              />
            ) : (
              <Text type="secondary">Select at least one Confluence space key above.</Text>
            )}
          </div>
        ) : sourceSettingsActiveKey === 'azure_wiki' ? (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, flex: 1, minWidth: 260 }}>
                <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                    Azure DevOps projects
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Select Azure DevOps projects"
                    value={pipelineSelectedAzureWikiProjects}
                    disabled={!canEdit || azureWikiProjectsLoading}
                    loading={azureWikiProjectsLoading}
                    options={azureWikiProjectSelectOptions}
                    filterOption={(input, option) => {
                      const q = String(input || '').trim().toLowerCase();
                      if (!q) return true;
                      const label = String(option?.label ?? '').toLowerCase();
                      const value = String(option?.value ?? '').toLowerCase();
                      return label.includes(q) || value.includes(q);
                    }}
                    onChange={handlePipelineAzureWikiProjectsChange}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                    Wiki names
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Select Azure Wiki names"
                    value={pipelineConfiguredAzureWikiWikiIds}
                    disabled={!canEdit || azureWikiWikisLoading || !pipelineSelectedAzureWikiProjects.length}
                    loading={azureWikiWikisLoading}
                    options={azureWikiWikiSelectOptions}
                    filterOption={(input, option) => {
                      const q = String(input || '').trim().toLowerCase();
                      if (!q) return true;
                      const label = String(option?.label ?? '').toLowerCase();
                      const value = String(option?.value ?? '').toLowerCase();
                      return label.includes(q) || value.includes(q);
                    }}
                    onChange={handlePipelineAzureWikiWikisChange}
                    style={{ width: '100%' }}
                  />
                </div>
                {pipelineConfiguredAzureWikiWikiIds.length > 1 ? (
                  <div style={{ minWidth: 160, flex: '0 1 180px' }}>
                    <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                      Edit scope for
                    </Text>
                    <Select
                      value={pipelineSelectedAzureWikiWikiId || undefined}
                      disabled={!pipelineConfiguredAzureWikiWikiIds.length}
                      options={pipelineConfiguredAzureWikiWikiIds.map((wikiId) => ({
                        value: wikiId,
                        label: pipelineAzureWikiScopeByWikiId[wikiId]?.wiki_name || wikiId,
                      }))}
                      onChange={handlePipelineAzureWikiEditWikiChange}
                      style={{ width: '100%' }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            {azureWikiProjectsError ? (
              <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                {azureWikiProjectsError}
              </div>
            ) : null}
            {azureWikiWikisError ? (
              <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                {azureWikiWikisError}
              </div>
            ) : null}
            {azureWikiPagesError ? (
              <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                {azureWikiPagesError}
              </div>
            ) : null}

            {pipelineSelectedAzureWikiWikiId ? (
              <AzureWikiPageScopePicker
                pages={azureWikiPages}
                loading={azureWikiPagesLoading}
                disabled={!canEdit}
                selectedPaths={pipelineAzureWikiScopeByWikiId[pipelineSelectedAzureWikiWikiId]?.page_paths || []}
                onChange={(paths) => applyAzureWikiPageSelection(paths)}
                monitoredPageIds={azureWikiMonitoredPageKeysForSelectedWiki}
                onToggleMonitor={canEdit
                  ? (pageId, enabled, pageNode, descendantNodes, options) => handleToggleAzureWikiPageMonitor(
                    pageId,
                    enabled,
                    pageNode,
                    descendantNodes,
                    options,
                  )
                  : null}
                onMonitorAllSelected={canEdit ? handleMonitorAllAzureWikiPages : null}
                onCheckMonitoredUpdates={canEdit ? handleCheckAzureWikiMonitoredUpdatesNow : null}
                monitorCheckLoading={azureWikiMonitorChecking}
                monitorAllLoading={azureWikiMonitorAllLoading}
                monitoredPageCount={azureWikiMonitoredPageKeys.size}
                selectedPanelFooter={canEdit ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleProceedPipelineAzureWikiScope}
                    loading={configSaving}
                    disabled={!pipelineConfiguredAzureWikiWikiIds.length}
                    style={pipelineSaveButtonStyle}
                  >
                    Proceed
                  </Button>
                ) : null}
              />
            ) : (
              <Text type="secondary">Select at least one Azure Wiki name above.</Text>
            )}
          </div>
        ) : sourceSettingsActiveKey === 'github' ? (
          <div>
            <div style={{ marginBottom: 10 }}>
              <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                Repositories
              </Text>
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select one or more repositories"
                value={pipelineConfiguredGithubRepoKeys}
                disabled={!canEdit || githubReposLoading}
                loading={githubReposLoading}
                options={(githubRepos || []).map((repo) => {
                  const key = `${repo.owner}/${repo.repo}`;
                  return { value: key, label: `${key}${repo.private ? ' (private)' : ''}` };
                })}
                onChange={handlePipelineGithubReposChange}
                style={{ width: '100%' }}
              />
            </div>
            {githubReposError ? (
              <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                {githubReposError}
              </div>
            ) : null}
            {pipelineConfiguredGithubRepoKeys.length > 1 ? (
              <div style={{ marginBottom: 10, maxWidth: 280 }}>
                <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                  Edit scope for
                </Text>
                <Select
                  value={pipelineSelectedGithubRepoKey || undefined}
                  options={pipelineConfiguredGithubRepoKeys.map((key) => ({ value: key, label: key }))}
                  onChange={setPipelineSelectedGithubRepoKey}
                  style={{ width: '100%' }}
                />
              </div>
            ) : null}
            {pipelineSelectedGithubRepoKey ? (
              <div style={{ border: `1px solid ${themeStyles.borderColor}`, borderRadius: 8, padding: 12 }}>
                <div style={{ marginBottom: 10 }}>
                  <Text strong>{pipelineSelectedGithubRepoKey}</Text>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                    <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                      Branches
                    </Text>
                    <Select
                      mode="multiple"
                      showSearch
                      maxTagCount="responsive"
                      placeholder="Select one or more branches"
                      loading={Boolean(githubBranchesLoadingByRepoKey[pipelineSelectedGithubRepoKey])}
                      value={pipelineConfiguredGithubScopeKeys
                        .map((scopeKey) => pipelineGithubScopeByScopeKey[scopeKey])
                        .filter((scope) => scope && `${scope.owner}/${scope.repo}` === pipelineSelectedGithubRepoKey)
                        .map((scope) => scope.branch)}
                      options={(githubBranchesByRepoKey[pipelineSelectedGithubRepoKey] || []).map((b) => ({
                        value: b.name,
                        label: b.name,
                      }))}
                      onFocus={() => { void loadGithubBranchesForRepo(pipelineSelectedGithubRepoKey); }}
                      onChange={(values) => {
                        const nextBranches = Array.from(new Set((values || []).map((v) => String(v || '').trim()).filter(Boolean)));
                        const [owner = '', repo = ''] = String(pipelineSelectedGithubRepoKey || '').split('/');
                        if (!owner || !repo) return;
                        const repoPrefix = `${pipelineSelectedGithubRepoKey}@`;
                        setPipelineGithubScopeByScopeKey((prev) => {
                          const next = { ...(prev || {}) };
                          const existingScopeKeys = Object.keys(next).filter((scopeKey) => scopeKey.startsWith(repoPrefix));
                          const existingByBranch = {};
                          existingScopeKeys.forEach((scopeKey) => {
                            const scope = next[scopeKey];
                            if (scope?.branch) existingByBranch[scope.branch] = scope;
                          });
                          existingScopeKeys.forEach((scopeKey) => {
                            delete next[scopeKey];
                          });
                          nextBranches.forEach((branch) => {
                            const scopeKey = `${pipelineSelectedGithubRepoKey}@${branch}`;
                            const existingScope = existingByBranch[branch];
                            next[scopeKey] = existingScope ? { ...existingScope, key: scopeKey } : {
                              key: scopeKey,
                              owner,
                              repo,
                              branch,
                              path_prefix: '',
                              include_globs: [],
                              selected_paths: [],
                            };
                          });
                          return next;
                        });
                        setPipelineConfiguredGithubScopeKeys((prev) => {
                          const withoutRepo = (prev || []).filter((scopeKey) => !scopeKey.startsWith(repoPrefix));
                          return [...withoutRepo, ...nextBranches.map((branch) => `${pipelineSelectedGithubRepoKey}@${branch}`)];
                        });
                        setPipelineSelectedGithubScopeKey((prev) => {
                          if (prev && nextBranches.some((branch) => prev === `${pipelineSelectedGithubRepoKey}@${branch}`)) return prev;
                          return nextBranches.length ? `${pipelineSelectedGithubRepoKey}@${nextBranches[0]}` : '';
                        });
                      }}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                    <Text style={{ display: 'block', fontSize: 11, color: themeStyles.textSecondary, marginBottom: 4 }}>
                      Edit branch scope
                    </Text>
                    <Select
                      value={pipelineSelectedGithubScopeKey || undefined}
                      placeholder="Pick branch scope"
                      options={pipelineConfiguredGithubScopeKeys
                        .map((scopeKey) => pipelineGithubScopeByScopeKey[scopeKey])
                        .filter((scope) => scope && `${scope.owner}/${scope.repo}` === pipelineSelectedGithubRepoKey)
                        .map((scope) => ({
                          value: scope.key,
                          label: scope.branch,
                        }))}
                      onChange={setPipelineSelectedGithubScopeKey}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                {pipelineSelectedGithubScopeKey ? (
                  <div style={{ marginTop: 12 }}>
                    {githubTreeErrorByScopeKey[pipelineSelectedGithubScopeKey] ? (
                      <div style={{ marginBottom: 8, color: '#ff4d4f', fontSize: 12 }}>
                        {githubTreeErrorByScopeKey[pipelineSelectedGithubScopeKey]}
                      </div>
                    ) : null}
                    <GithubFileScopePicker
                      nodes={githubTreeByScopeKey[pipelineSelectedGithubScopeKey] || []}
                      loading={Boolean(githubTreeLoadingByScopeKey[pipelineSelectedGithubScopeKey])}
                      disabled={!canEdit}
                      branchLabel={pipelineGithubScopeByScopeKey[pipelineSelectedGithubScopeKey]?.branch || ''}
                      selectedPaths={pipelineGithubScopeByScopeKey[pipelineSelectedGithubScopeKey]?.selected_paths || []}
                      onChange={applyGithubPathSelection}
                      selectedPanelFooter={canEdit ? (
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={handleProceedPipelineGithubScope}
                          loading={configSaving}
                          disabled={!pipelineConfiguredGithubRepoKeys.length}
                          style={pipelineSaveButtonStyle}
                        >
                          Proceed
                        </Button>
                      ) : null}
                    />
                  </div>
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginTop: 10 }}>
                    Select at least one branch to choose folders and files.
                  </Text>
                )}
                {!pipelineSelectedGithubScopeKey && canEdit ? (
                  <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleProceedPipelineGithubScope}
                      loading={configSaving}
                      disabled={!pipelineConfiguredGithubRepoKeys.length}
                      style={pipelineSaveButtonStyle}
                    >
                      Proceed
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <Text type="secondary">Select at least one repository above.</Text>
            )}
          </div>
        ) : (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Provider">S3</Descriptions.Item>
            <Descriptions.Item label="Bucket">{project?.config?.s3?.bucket || project?.config?.s3?.name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Region">{project?.config?.s3?.region || '—'}</Descriptions.Item>
            <Descriptions.Item label="Prefix">{project?.config?.s3?.prefix || '—'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={filePreviewModal.title || 'File preview'}
        open={filePreviewModal.open}
        footer={null}
        onCancel={resetFilePreviewModal}
        width={1000}
        destroyOnClose
        styles={{ body: { overflow: 'hidden', maxHeight: '78vh' } }}
      >
        {filePreviewModal.loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : filePreviewModal.error && !filePreviewModal.textContent && !filePreviewModal.url ? (
          <div style={{ color: '#ff7875' }}>{filePreviewModal.error}</div>
        ) : (
          <>
            {(filePreviewModal.contentFormat === 'markdown' || filePreviewModal.sourceKind === 'azure_wiki') && !isPdfFile(filePreviewModal.title) ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                }}
              >
                <Space wrap size={8}>
                  {filePreviewModal.contentFormat === 'markdown' ? (
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => setFilePreviewModal((prev) => ({
                        ...prev,
                        showRawMarkdown: !prev.showRawMarkdown,
                      }))}
                    >
                      {filePreviewModal.showRawMarkdown ? 'Rendered view' : 'Raw markdown'}
                    </Button>
                  ) : null}
                  {filePreviewModal.webUrl ? (
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => window.open(filePreviewModal.webUrl, '_blank', 'noopener,noreferrer')}
                    >
                      {filePreviewModal.sourceKind === 'azure_wiki'
                        ? 'Open in Azure Wiki'
                        : filePreviewModal.sourceKind === 'confluence'
                          ? 'Open in Confluence'
                          : filePreviewModal.sourceKind === 'jira'
                            ? 'Open in Jira'
                            : filePreviewModal.sourceKind === 'github'
                              ? 'Open on GitHub'
                              : 'Open source page'}
                    </Button>
                  ) : null}
                  {filePreviewModal.sourceKind === 'azure_wiki' ? (
                    <Button
                      size="small"
                      type="primary"
                      icon={<FilePdfOutlined />}
                      loading={filePreviewModal.pdfLoading}
                      onClick={() => { void handleOpenAzureWikiPreviewPdf(); }}
                    >
                      Open as PDF
                    </Button>
                  ) : null}
                </Space>
                {filePreviewModal.error ? (
                  <span style={{ color: '#ff7875', fontSize: 12 }}>{filePreviewModal.error}</span>
                ) : null}
              </div>
            ) : null}

            {filePreviewModal.textContent ? (
              filePreviewModal.contentFormat === 'markdown' && !filePreviewModal.showRawMarkdown ? (
                <div style={wikiMarkdownPreviewShellStyle(themeStyles.isLight)}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    urlTransform={azureWikiMarkdownUrlTransform}
                    components={{
                      h1: ({ children }) => (
                        <h1 style={{
                          fontSize: 26,
                          margin: '0 0 14px',
                          color: themeStyles.isLight ? '#0b5cab' : '#7eb6ff',
                          borderBottom: themeStyles.isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
                          paddingBottom: 8,
                        }}
                        >
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{
                          fontSize: 20,
                          margin: '22px 0 10px',
                          color: themeStyles.isLight ? '#163a5f' : '#c9d7f0',
                          borderBottom: themeStyles.isLight ? '1px solid #eaeef2' : '1px solid rgba(255,255,255,0.08)',
                          paddingBottom: 6,
                        }}
                        >
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ fontSize: 16, margin: '18px 0 8px', fontWeight: 600 }}>{children}</h3>
                      ),
                      p: ({ children }) => (
                        <p style={{ margin: '0 0 10px' }}>{children}</p>
                      ),
                      ul: ({ children }) => (
                        <ul style={{ margin: '0 0 10px 22px', padding: 0 }}>{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol style={{ margin: '0 0 10px 22px', padding: 0 }}>{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li style={{ marginBottom: 4 }}>{children}</li>
                      ),
                      table: ({ children }) => (
                        <div style={{ overflowX: 'auto', margin: '10px 0 14px' }}>
                          <table style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            fontSize: 13,
                          }}
                          >
                            {children}
                          </table>
                        </div>
                      ),
                      th: ({ children }) => (
                        <th style={{
                          border: themeStyles.isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.15)',
                          background: themeStyles.isLight ? '#f6f8fa' : 'rgba(255,255,255,0.06)',
                          padding: '6px 10px',
                          textAlign: 'left',
                        }}
                        >
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td style={{
                          border: themeStyles.isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
                          padding: '6px 10px',
                          verticalAlign: 'top',
                        }}
                        >
                          {children}
                        </td>
                      ),
                      code: ({ inline, children }) => (
                        inline ? (
                          <code style={{
                            fontFamily: 'Consolas, monospace',
                            fontSize: 12.5,
                            background: themeStyles.isLight ? '#f6f8fa' : 'rgba(255,255,255,0.08)',
                            padding: '1px 5px',
                            borderRadius: 4,
                          }}
                          >
                            {children}
                          </code>
                        ) : (
                          <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{children}</code>
                        )
                      ),
                      pre: ({ children }) => (
                        <pre style={{
                          background: themeStyles.isLight ? '#f6f8fa' : 'rgba(0,0,0,0.35)',
                          border: themeStyles.isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 6,
                          padding: 12,
                          overflowX: 'auto',
                          fontSize: 12.5,
                          lineHeight: 1.5,
                        }}
                        >
                          {children}
                        </pre>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote style={{
                          margin: '10px 0',
                          padding: '6px 12px',
                          borderLeft: '3px solid #0b5cab',
                          background: themeStyles.isLight ? '#f6f8fa' : 'rgba(11,92,171,0.12)',
                          color: themeStyles.isLight ? '#57606a' : '#c8d0dc',
                        }}
                        >
                          {children}
                        </blockquote>
                      ),
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noreferrer" style={{ color: '#0b5cab' }}>
                          {children}
                        </a>
                      ),
                      img: ({ src, alt }) => {
                        const wikiPath = parseAzureWikiImageSrc(src);
                        if (wikiPath && filePreviewModal.sourceKind === 'azure_wiki') {
                          return (
                            <AzureWikiAuthImage
                              src={src}
                              alt={alt}
                              token={token}
                              projectId={projectId}
                              fileKey={filePreviewModal.fileKey}
                              docId={filePreviewModal.docId}
                              registerBlobUrl={registerFilePreviewImageBlobUrl}
                            />
                          );
                        }
                        if (!src) return null;
                        return (
                          <img
                            src={src}
                            alt={alt || ''}
                            style={{
                              display: 'block',
                              maxWidth: '100%',
                              height: 'auto',
                              margin: '12px 0 8px',
                              borderRadius: 4,
                            }}
                          />
                        );
                      },
                      hr: () => (
                        <hr style={{
                          border: 'none',
                          borderTop: themeStyles.isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
                          margin: '16px 0',
                        }}
                        />
                      ),
                    }}
                  >
                    {filePreviewModal.textContent}
                  </ReactMarkdown>
                </div>
              ) : (
                <div style={wikiRawPreviewShellStyle(themeStyles.isLight)}>
                  {filePreviewModal.textContent}
                </div>
              )
            ) : filePreviewModal.url ? (
              isPdfFile(filePreviewModal.title) || filePreviewModal.provider === 'pdf' ? (
                <div>
                  {filePreviewModal.sourceKind === 'azure_wiki' ? (
                    <div style={{ marginBottom: 12 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          // Reload markdown view for the same page
                          const fakeRecord = {
                            file_key: filePreviewModal.fileKey,
                            doc_id: filePreviewModal.docId,
                            file_name: String(filePreviewModal.title || '').replace(/\.pdf$/i, ''),
                            source_type: 'azure_wiki',
                          };
                          void handleViewFile(fakeRecord);
                        }}
                      >
                        Back to rendered view
                      </Button>
                    </div>
                  ) : null}
                  <embed
                    src={filePreviewModal.url}
                    type="application/pdf"
                    title={filePreviewModal.title || 'PDF preview'}
                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                  />
                </div>
              ) : isPreviewableInModal(filePreviewModal.title || filePreviewModal.url) ? (
                <iframe
                  title={filePreviewModal.title || 'File preview'}
                  src={filePreviewModal.url}
                  style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                />
              ) : isOfficePreviewableInModal(filePreviewModal.title) ? (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                      marginBottom: 12,
                    }}
                  >
                    <Space wrap size={8}>
                      <Button
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(filePreviewModal.url, '_blank', 'noopener,noreferrer')}
                      >
                        Open original in new tab
                      </Button>
                      {filePreviewModal.docId ? (
                        <Button
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={async () => {
                            try {
                              const resp = await API.getS3FilePreview(token, projectId, {
                                file_key: filePreviewModal.fileKey || undefined,
                                doc_id: filePreviewModal.docId || undefined,
                              });
                              const data = resp?.data || {};
                              setFilePreviewModal((prev) => ({
                                ...prev,
                                textContent: data.content || '',
                                contentFormat: data.content ? 'markdown' : '',
                                url: '',
                                provider: 'office',
                                sourceKind: 's3',
                                showRawMarkdown: false,
                                error: data.content ? '' : 'No extracted text available yet.',
                              }));
                            } catch (e) {
                              message.warning(e?.response?.data?.detail || 'No extracted text available yet.');
                            }
                          }}
                        >
                          View extracted text
                        </Button>
                      ) : null}
                    </Space>
                  </div>
                  <iframe
                    title={filePreviewModal.title || 'File preview'}
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(filePreviewModal.url)}`}
                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                  />
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 12, color: themeStyles.textSecondary }}>
                    Inline preview is not supported for this file type. Open in a read-only browser tab.
                  </div>
                  <Button type="primary" onClick={() => window.open(filePreviewModal.url, '_blank', 'noopener,noreferrer')}>
                    Open file
                  </Button>
                </div>
              )
            ) : (
              <div style={{ color: themeStyles.textSecondary }}>No preview available.</div>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={pipelineDetailModal.title || 'Pipeline details'}
        open={pipelineDetailModal.open}
        footer={null}
        onCancel={() => setPipelineDetailModal({ open: false, title: '', detail: '' })}
        width={760}
        destroyOnClose
      >
        {(() => {
          const groups = parsePipelineDetailRows(pipelineDetailModal.detail);
          const sections = [
            { title: 'Summary', rows: groups.summary },
            { title: 'Markdown Conversion & Upload', rows: groups.sync },
            { title: 'Marker Progress', rows: groups.marker },
            { title: 'Processing Stages', rows: groups.processing },
            { title: 'Other', rows: groups.other },
          ].filter((section) => section.rows.length > 0);

          if (sections.length === 0) {
            return <div style={{ color: themeStyles.textSecondary }}>No details available.</div>;
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sections.map((section) => (
                <div
                  key={section.title}
          style={{
                    border: '1px solid rgba(148, 22, 127, 0.25)',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: themeStyles.textPrimary,
                      background: 'rgba(148, 22, 127, 0.14)',
                    }}
                  >
                    {section.title}
                  </div>
                  <div style={{ padding: '8px 12px' }}>
                    {section.rows.map((row) => (
                      <div
                        key={row.raw}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '230px 1fr',
                          gap: 10,
            fontSize: 12,
            lineHeight: 1.6,
            color: themeStyles.textPrimary,
                          padding: '2px 0',
          }}
        >
                        <span style={{ color: themeStyles.textSecondary }}>{row.label}</span>
                        <span style={{ fontWeight: row.label.toLowerCase().includes('total pipeline') ? 600 : 400 }}>
                          {row.value}
                        </span>
        </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>

      <Drawer
        title="File Pipeline Schedule"
        placement="right"
        width={420}
        open={filePipelineSettingsOpen}
        onClose={() => setFilePipelineSettingsOpen(false)}
        destroyOnClose={false}
        className="file-pipeline-schedule-drawer-root"
        styles={{ body: { padding: '16px 20px', overflowX: 'hidden', overflowY: 'auto' } }}
      >
        <div className="file-pipeline-schedule-drawer">
          <p className="file-pipeline-schedule-drawer__intro">
            Automatically sync and index files from your knowledge source so the latest content is
            available for AI search and chat.
          </p>
          <ul className="file-pipeline-schedule-drawer__features">
            {[
              'Detects new, updated, and deleted files',
              'Processes documents and generates embeddings',
              'Updates the vector index automatically',
            ].map((text) => (
              <li key={text} className="file-pipeline-schedule-drawer__feature">
                <CheckCircleOutlined className="file-pipeline-schedule-drawer__feature-icon" aria-hidden />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <Form
            form={pipelineScheduleForm}
            layout="vertical"
            requiredMark={false}
            onFinish={handleSavePipelineSchedule}
            initialValues={{
              interval_unit: 'hours',
              interval_value: 1,
            }}
            className="file-pipeline-schedule-drawer__form"
          >
            <div className="file-pipeline-schedule-drawer__interval">
              <span className="file-pipeline-schedule-drawer__interval-label">Run Every:</span>
              <Form.Item name="interval_value" className="file-pipeline-schedule-drawer__interval-value">
                <InputNumber min={1} disabled={!canEdit} />
              </Form.Item>
              <Form.Item name="interval_unit" className="file-pipeline-schedule-drawer__interval-unit">
                <Select disabled={!canEdit}>
                  <Select.Option value="minutes">Minutes</Select.Option>
                  <Select.Option value="hours">Hours</Select.Option>
                </Select>
              </Form.Item>
            </div>

            {canEdit ? (
              <div className="file-pipeline-schedule-drawer__actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={filePipelineActionLoading === 'save'}
                  disabled={!!filePipelineActionLoading && filePipelineActionLoading !== 'save'}
                >
                  Save Schedule
                </Button>
                {SHOW_VECTOR_RETENTION_UI && (
                  <Button
                    type="default"
                    htmlType="button"
                    icon={<DatabaseOutlined />}
                    onClick={handleRunVectorRetention}
                    loading={vectorRetentionRunning}
                    disabled={!token || !!filePipelineActionLoading || vectorRetentionRunning}
                  >
                    Run Vector Refresh
                  </Button>
                )}
              </div>
            ) : null}
          </Form>

          {!canEdit ? (
            <Alert
              type="info"
              showIcon
              message="Read-only access"
              description="You can review files and status, but schedule changes, uploads, and pipeline actions are restricted."
            />
          ) : null}

          {(filePipelineSchedule?.last_run_at || filePipelineSchedule?.next_run_at) ? (
            <div className="file-pipeline-schedule-drawer__status">
              {filePipelineSchedule?.last_run_at ? (
                <span className="file-pipeline-schedule-drawer__status-item">
                  <span className="file-pipeline-schedule-drawer__status-label">Last Run:</span>
                  {formatUtcInstantForDisplay(filePipelineSchedule.last_run_at)}
                </span>
              ) : null}
              {filePipelineSchedule?.next_run_at ? (
                <span className="file-pipeline-schedule-drawer__status-item">
                  <span className="file-pipeline-schedule-drawer__status-label">Next Run:</span>
                  {formatUtcInstantForDisplay(filePipelineSchedule.next_run_at)}
                </span>
              ) : null}
            </div>
          ) : null}

          <p className="file-pipeline-schedule-drawer__note">
            Only one pipeline execution can run at a time.
          </p>
        </div>
      </Drawer>

      <Modal
        title="Change Knowledge Source"
        open={s3ChangeModalOpen}
        onCancel={() => {
          setS3ChangeModalOpen(false);
          setPendingConfigValues(null);
          setDeleteOldS3Files(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setS3ChangeModalOpen(false);
              setPendingConfigValues(null);
              setDeleteOldS3Files(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="continue"
            type="primary"
            disabled={deleteOldS3Files === null}
            onClick={() => {
              if (deleteOldS3Files === null) {
                message.warning('Select how to handle files in the current bucket.');
                return;
              }
              setS3ChangeModalOpen(false);
              setDataLossConsent(false);
              setDataLossModalOpen(true);
            }}
          >
            Continue
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 16 }}>
          You are switching to a new Knowledge Source. Choose how to handle files stored in the{' '}
          <code>upload/</code> folder of the current bucket.
        </p>
        <Radio.Group
          value={deleteOldS3Files}
          onChange={(e) => setDeleteOldS3Files(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Radio value={true}>
            <span style={{ color: themeStyles.textPrimary }}>
              Remove files from current bucket
            </span>
            <div style={{ fontSize: 12, color: themeStyles.textSecondary, marginTop: 4, paddingLeft: 24 }}>
              Deletes all files in upload/, removes vector embeddings, and clears file records.
            </div>
          </Radio>
          <Radio value={false}>
            <span style={{ color: themeStyles.textPrimary }}>
              Keep files in current bucket.
            </span>
            <div style={{ fontSize: 12, color: themeStyles.textSecondary, marginTop: 4, paddingLeft: 24 }}>
              Keeps files in S3 but removes vector embeddings and file records associated with this project.
            </div>
          </Radio>
        </Radio.Group>
      </Modal>

      <Modal
        title="Copy Existing Vectors?"
        open={migrateChoiceModalOpen}
        onCancel={() => {
          setMigrateChoiceModalOpen(false);
          setPendingConfigValues(null);
          setPendingEmbeddingAlsoChanged(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setMigrateChoiceModalOpen(false);
            setPendingEmbeddingAlsoChanged(false);
          }}
          >
            Cancel
          </Button>,
          <Button
            key="empty"
            onClick={() => {
              setCopyVectorsToNewCollection(false);
              setMigrateChoiceModalOpen(false);
              setDataLossModalOpen(true);
            }}
          >
            Start Empty
          </Button>,
          <Button
            key="copy"
            type="primary"
            onClick={() => {
              setCopyVectorsToNewCollection(true);
              setMigrateChoiceModalOpen(false);
              setDataLossModalOpen(true);
            }}
          >
            Copy Vectors
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 0 }}>
          The collection name has changed. Do you want to copy the existing vectors to the new collection
          or start with an empty collection? The old collection will be removed.
        </p>
      </Modal>

      <Modal
        title="Jira issue updated"
        open={jiraMonitorConsentOpen && Boolean(activeJiraMonitorChange)}
        onCancel={() => {
          if (activeJiraMonitorChange) {
            void handleDismissJiraMonitorUpdate(activeJiraMonitorChange);
          } else {
            setJiraMonitorConsentOpen(false);
          }
        }}
        footer={[
          <Button
            key="dismiss"
            disabled={jiraMonitorApplying}
            loading={jiraMonitorApplying}
            onClick={() => {
              if (activeJiraMonitorChange) {
                void handleDismissJiraMonitorUpdate(activeJiraMonitorChange);
              }
            }}
          >
            Keep current index
          </Button>,
          <Button
            key="apply"
            type="primary"
            loading={jiraMonitorApplying}
            onClick={() => {
              if (activeJiraMonitorChange) {
                void handleApplyJiraMonitorUpdate(activeJiraMonitorChange);
              }
            }}
          >
            Update vector index
          </Button>,
        ]}
      >
        {activeJiraMonitorChange ? (
          <>
            <p style={{ margin: '0 0 12px', color: themeStyles.textPrimary }}>
              <strong>{activeJiraMonitorChange.issue_key}</strong>
              {activeJiraMonitorChange.title
                ? ` — ${activeJiraMonitorChange.title}`
                : ''}
              {' '}was updated in Jira.
            </p>
            <p style={{ margin: 0, color: themeStyles.textSecondary, lineHeight: 1.55 }}>
              Remove the previous version from the vector database and process the latest Jira content?
            </p>
            {jiraMonitorPendingUpdates.length > 1 ? (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: themeStyles.textSecondary }}>
                {jiraMonitorPendingUpdates.length - 1} more monitored issue
                {jiraMonitorPendingUpdates.length - 1 === 1 ? '' : 's'}
                {' '}will be shown after this one.
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        title="Confluence page updated"
        open={confluenceMonitorConsentOpen && Boolean(activeConfluenceMonitorChange)}
        onCancel={() => {
          if (activeConfluenceMonitorChange) {
            void handleDismissConfluenceMonitorUpdate(activeConfluenceMonitorChange);
          } else {
            setConfluenceMonitorConsentOpen(false);
          }
        }}
        footer={[
          <Button
            key="dismiss"
            disabled={confluenceMonitorApplying}
            loading={confluenceMonitorApplying}
            onClick={() => {
              if (activeConfluenceMonitorChange) {
                void handleDismissConfluenceMonitorUpdate(activeConfluenceMonitorChange);
              }
            }}
          >
            Keep current index
          </Button>,
          <Button
            key="apply"
            type="primary"
            loading={confluenceMonitorApplying}
            onClick={() => {
              if (activeConfluenceMonitorChange) {
                void handleApplyConfluenceMonitorUpdate(activeConfluenceMonitorChange);
              }
            }}
          >
            Update vector index
          </Button>,
        ]}
      >
        {activeConfluenceMonitorChange ? (
          <>
            <p style={{ margin: '0 0 12px', color: themeStyles.textPrimary }}>
              <strong>{activeConfluenceMonitorChange.title || activeConfluenceMonitorChange.page_id}</strong>
              {activeConfluenceMonitorChange.page_id
                ? ` (${activeConfluenceMonitorChange.page_id})`
                : ''}
              {' '}was updated in Confluence.
            </p>
            <p style={{ margin: 0, color: themeStyles.textSecondary, lineHeight: 1.55 }}>
              Remove the previous version from the vector database and process the latest Confluence content?
            </p>
            {confluenceMonitorPendingUpdates.length > 1 ? (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: themeStyles.textSecondary }}>
                {confluenceMonitorPendingUpdates.length - 1} more monitored page
                {confluenceMonitorPendingUpdates.length - 1 === 1 ? '' : 's'}
                {' '}will be shown after this one.
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        title="Azure Wiki page updated"
        open={azureWikiMonitorConsentOpen && Boolean(activeAzureWikiMonitorChange)}
        onCancel={() => {
          if (activeAzureWikiMonitorChange) {
            void handleDismissAzureWikiMonitorUpdate(activeAzureWikiMonitorChange);
          } else {
            setAzureWikiMonitorConsentOpen(false);
          }
        }}
        footer={[
          <Button
            key="dismiss"
            disabled={azureWikiMonitorApplying}
            loading={azureWikiMonitorApplying}
            onClick={() => {
              if (activeAzureWikiMonitorChange) {
                void handleDismissAzureWikiMonitorUpdate(activeAzureWikiMonitorChange);
              }
            }}
          >
            Keep current index
          </Button>,
          <Button
            key="apply"
            type="primary"
            loading={azureWikiMonitorApplying}
            onClick={() => {
              if (activeAzureWikiMonitorChange) {
                void handleApplyAzureWikiMonitorUpdate(activeAzureWikiMonitorChange);
              }
            }}
          >
            Update vector index
          </Button>,
        ]}
      >
        {activeAzureWikiMonitorChange ? (
          <>
            <p style={{ margin: '0 0 12px', color: themeStyles.textPrimary }}>
              <strong>{activeAzureWikiMonitorChange.title || activeAzureWikiMonitorChange.page_id}</strong>
              {activeAzureWikiMonitorChange.page_path
                ? ` (${activeAzureWikiMonitorChange.page_path})`
                : activeAzureWikiMonitorChange.page_id
                  ? ` (${activeAzureWikiMonitorChange.page_id})`
                  : ''}
              {' '}was updated in Azure Wiki.
            </p>
            <p style={{ margin: 0, color: themeStyles.textSecondary, lineHeight: 1.55 }}>
              Remove the previous version from the vector database and process the latest Azure Wiki content?
            </p>
            {azureWikiMonitorPendingUpdates.length > 1 ? (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: themeStyles.textSecondary }}>
                {azureWikiMonitorPendingUpdates.length - 1} more monitored page
                {azureWikiMonitorPendingUpdates.length - 1 === 1 ? '' : 's'}
                {' '}will be shown after this one.
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        title="Confirm Data Reset"
        open={dataLossModalOpen}
        onCancel={() => {
          setDataLossModalOpen(false);
          setPendingConfigValues(null);
          setPendingJiraSave(null);
          setPendingAzureWikiSave(null);
          setPendingGithubSave(null);
          setPendingChangeType(null);
          setPendingEmbeddingAlsoChanged(false);
          setDataLossConsent(false);
          setDeleteOldS3Files(null);
        }}
        okText="Rebuild Index"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !dataLossConsent, loading: configSaving }}
        onOk={confirmSensitiveSave}
      >
        <p style={{ margin: '0 0 12px', color: themeStyles.textPrimary }}>
          {pendingChangeType === 'jira_scope_change'
            ? 'Changing Jira project keys or scope will replace the current search index and knowledge graph.'
            : pendingChangeType === 'azure_wiki_connection_change'
              ? 'Updating the Azure Wiki connection will replace the current search index and knowledge graph, then reprocess wiki pages with the verified PAT.'
              : pendingChangeType === 'github_connection_change'
                ? 'Updating the GitHub connection will replace the current search index and knowledge graph, then reprocess repository files with the verified PAT.'
              : 'This action will replace the current search index and knowledge graph.'}
        </p>
        <p style={{ margin: '0 0 8px', fontWeight: 600, color: themeStyles.textPrimary }}>
          What happens next?
        </p>
        <ul className="confirm-data-reset-points">
          <li>Existing vector data in Milvus will be removed.</li>
          {pendingChangeType === 'jira_scope_change' ? (
            <li>Jira issues outside the new scope will be pruned from the project file list.</li>
          ) : null}
          {pendingChangeType === 'azure_wiki_connection_change' ? (
            <li>Azure Wiki pages will be re-synced and re-indexed with the saved connection.</li>
          ) : null}
          {pendingChangeType === 'github_connection_change' ? (
            <li>GitHub files will be re-synced and re-indexed with the saved connection.</li>
          ) : null}
          <li>If GraphRAG is enabled, related graph data will be cleared.</li>
          <li>Chat and search may be unavailable until re-indexing is complete.</li>
          <li>The data will be rebuilt from your source documents during the next pipeline run.</li>
        </ul>
        <Checkbox checked={dataLossConsent} onChange={(e) => setDataLossConsent(e.target.checked)}>
          I understand that the current index and graph data will be replaced and want to continue.
        </Checkbox>
      </Modal>

      <Modal
        title="Delete Project?"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !deleteConfirmNameMatches(deleteConfirmInput),
        }}
        destroyOnClose
      >
        <div>
          <p style={{ marginBottom: '16px' }}>
            You are about to delete &quot;{projectDeleteDisplayName}&quot;.
          </p>
          {(project?.config?.s3?.bucket || project?.config?.s3?.name) && (
            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255, 77, 79, 0.08)', borderRadius: '6px', border: '1px solid rgba(255, 77, 79, 0.25)' }}>
              <Checkbox
                checked={deleteEmptyS3}
                onChange={(e) => setDeleteEmptyS3(e.target.checked)}
              >
                <span style={{ color: themeStyles.textPrimary }}>
                  {(['confluence', 'github', 'jira', 'azure_wiki'].includes(String(project?.config?.knowledge_provider || ''))
                    || project?.config?.s3?.role === 'artifacts'
                    || project?.config?.s3?.prefix)
                    ? 'Also delete this project\'s files in the shared S3 artifact bucket'
                    : 'Also delete files in the S3 bucket'}
                </span>
              </Checkbox>
              <div style={{ marginTop: '6px', fontSize: '12px', color: themeStyles.textSecondary, paddingLeft: '24px' }}>
                Bucket: <code>{project?.config?.s3?.bucket || project?.config?.s3?.name}</code>
                {project?.config?.s3?.prefix ? (
                  <>
                    {' '}· Prefix: <code>{project.config.s3.prefix}</code>
                  </>
                ) : null}
              </div>
              {(['confluence', 'github', 'jira', 'azure_wiki'].includes(String(project?.config?.knowledge_provider || ''))
                || project?.config?.s3?.role === 'artifacts'
                || project?.config?.s3?.prefix) ? (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#cf1322', paddingLeft: '24px' }}>
                  This bucket is shared with other projects. Only this project&apos;s prefix/files will be removed — the whole bucket will not be emptied.
                </div>
              ) : null}
            </div>
          )}
          <p style={{ marginBottom: '8px', color: themeStyles.textPrimary }}>
            Type &quot;{projectDeleteDisplayName}&quot; to confirm.
          </p>
          <Input
            value={deleteConfirmInput}
            onChange={(e) => setDeleteConfirmInput(e.target.value)}
            onPressEnter={() => {
              if (deleteConfirmNameMatches(deleteConfirmInput)) {
                handleDeleteConfirm();
              }
            }}
            style={{ marginTop: '8px' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ViewProject;
